/**
 * content-builder-neo-ext.js — Lausanne additions to the AICO Neo renderer.
 *
 * The engine (`assets/content-builder-neo.js` → window.NewsNeo) is a VERBATIM
 * port of the aico `neo-static-pages` scaffold and must stay diffable against
 * upstream, so everything theme-specific lives here. Same pattern as the aico
 * kj-theme scaffold's `storefront-neo-blocks.js`. Loaded AFTER the engine
 * (both `defer`, so document order is execution order).
 *
 * WHY THIS EXISTS: /pages/club-des-130 rendered a single image. Its Neo
 * document uses {html, container, heading, text, columns, image, linked-form}
 * and the engine ships no `container` / `linked-form` renderer. An unknown type
 * returns '' — INCLUDING its children — and 10 of the 17 elements on that page
 * are nested inside 3 containers, so nearly everything vanished.
 *
 * What it adds:
 *   1. `container`      — generic wrapper div: element styles inline + children
 *                         rendered recursively (matches aiconeo's ElementRenderer
 *                         `case 'container'`: a plain styled div whose children
 *                         are direct siblings, no extra wrapper).
 *   2. unknown-with-children degradation — any unregistered type that carries a
 *                         `children` array is rewritten to `container` before
 *                         the engine sees it, so a wrapper-ish unknown degrades
 *                         to its content instead of deleting a subtree. Warns once
 *                         per type.
 *   3. nested `columns`  — the engine only handles `columns` at section level
 *                         (renderSection); a `columns` INSIDE a container would
 *                         otherwise render nothing. Reimplemented here.
 *   4. `linked-form`     — a real, working aico form (see the contract in
 *                         docs/aico-landing-pages.md).
 *   5. `styles.mobile`   — the engine inlines `styles.desktop` only. For the
 *                         types rendered here, the mobile override is emitted as
 *                         a scoped @media rule so containers don't keep their
 *                         160px desktop padding on a phone.
 *   6. SECTION pass      — the engine has no section wrapper at all, so section
 *                         backgrounds/padding, `layout.mode` (full/contained/
 *                         custom) and the page `settings.backgroundColor` were
 *                         all dropped. Re-rendered per section and wrapped here,
 *                         mirroring aiconeo's PublicPageRenderer. Also honours
 *                         `visibleFrom` / `visibleUntil`.
 *                         NOT implemented: `dividerTop` / `dividerBottom`
 *                         (clip-path polygons from renderers/SectionDivider) —
 *                         absent from the pilot document and not cheap.
 *   7. className/customId — the engine drops both; aiconeo stamps them on every
 *                         element. Without them /pages/ls-is-more lost its whole
 *                         @container-query layout system and its anchor targets.
 *   8. `button`          — the engine registers none, and unknown leaves have no
 *                         children to fall back to, so /pages/kids rendered zero
 *                         CTAs. Mirrors ElementRenderer's button, defaults and
 *                         two-node shape included. buttonShape / buttonSize are
 *                         presets (p59): present, they win on every tier; absent,
 *                         the style bag's borderRadius / padding win.
 *
 *   9. section-level `columns` — the one element the registry decorator cannot
 *                         reach (renderSection intercepts it before dispatch);
 *                         its styles/className/customId are patched into the
 *                         engine's output.
 *  10. loud failure     — an unrenderable LEAF now emits an HTML comment marker
 *                         in place, and each render logs ONE aggregated warning
 *                         listing every missing type with counts.
 *  11. Tier-1 elements  — spacer, badge, cta, hero, anchor-nav, marquee,
 *                         partner-logos, file-list, social-embed, multicolumn,
 *                         features, testimonial, faq, accordion,
 *                         feature-accordion. `accordion` carries look fields
 *                         (p59): itemStyle boxed|divided, dividerColor,
 *                         questionColor, questionHoverColor, answerColor,
 *                         answerFontSize, iconStyle chevron|plus, iconColor.
 *  12. size-container guard — an authored `container-type: inline-size` inside an
 *                         `align-items:flex-start` container collapses to width 0
 *                         and renders prose one word per line. Measured at
 *                         hydrate and given an inline size. (Its sibling fix, the
 *                         `.content-row` track lists, is CSS —
 *                         assets/content-builder-neo.css.)
 *  13. `image` effects  — the engine forwards only src/alt/caption + the desktop
 *                         style bag, so aiconeo's shape (clip-path), crop/fit and
 *                         overlay scrim were all dropped. /pages/hospitalites
 *                         authors a diagonal shape on its match photo and the
 *                         storefront drew a plain rectangle. An image WITH
 *                         children (content dropped onto the photo in the
 *                         builder) renders them over the photo, as aiconeo does.
 *  14. empty-column collapse — a column whose only element rendered to '' (an
 *                         `image` with no `content.src`, a `youtube`/`video`
 *                         with no id, an unrenderable leaf) still gets its
 *                         `.content-column` wrapper and still claims its half of
 *                         the row, so /pages/enzonet-lounge drew an empty dark
 *                         box beside its text. Detected and collapsed at hydrate.
 *  15. theme slots      — a generic mount point for Shopify sections that cannot
 *                         be authored in the builder at all (the jersey
 *                         configurator resolves products/variants/metafields in
 *                         Liquid). The author drops an html element holding
 *                         `<div data-ls-slot="name">` where the section should
 *                         appear; the theme stamps `data-ls-slot-source="name"`
 *                         on the section root, and hydrate MOVES the
 *                         server-rendered node into the slot. No slot → the
 *                         section stays where the template put it, visible.
 *                         Sources are placed DEEPEST-FIRST, so a band nested in
 *                         a section root can own a slot of its own (p47: the
 *                         match-centre score band over the Neo hero photo,
 *                         marker wrapper `.ls-slot-overlay`). A source sitting
 *                         in a slot also gets `.is-slotted` — the CSS hook for
 *                         "placed inside Neo" (`.is-placed` alone also marks a
 *                         source merely revealed in its template position).
 *                         The builder's native `theme-slot` element
 *                         (`content.slot`) is the same marker without Custom
 *                         HTML; dropped onto an image it makes that image the
 *                         section's editable cover photo (p55, home next-match),
 *                         shown only while the placed section is.
 *  15a. late placement signal — `ls:theme-slots-placed` after a move that
 *                         happened once consumers had already initialised.
 *  15b. theme binds     — the reverse direction: Neo elements with className
 *                         `ls-bind ls-bind--<key>` (usually in a hidden
 *                         `.ls-bind-source` sheet) feed text / image src /
 *                         background / link href / video poster into theme
 *                         markup stamped `data-ls-bind="<key>"` +
 *                         `data-ls-bind-kind`. Ends with
 *                         `ls:theme-binds-applied` (+ the window flag
 *                         `lsThemeBindsApplied`), also on every no-Neo path.
 *  15c. section feeds   — binds by STRUCTURE: a theme section stamped
 *                         `data-ls-feed-section="<neo section uuid>"` reads its
 *                         targets (`data-ls-feed="heading 2"`) from that
 *                         ordinary native section, which is then hidden.
 *  15d. optional slots   — a source stamped `data-ls-slot-optional` belongs to
 *                         the Neo document: when a rendered document has no
 *                         marker for it (element hidden with the eye toggle,
 *                         section scheduled off, marker deleted) it stays off
 *                         (`.is-slot-off`) instead of being revealed in its
 *                         template position. A document that never rendered
 *                         still reveals it (safety net unchanged).
 *  16. product-rail chevrons — prev/next buttons on authored shop rails.
 *  17. marquee fit       — clones a marquee's run until it covers the band.
 *  18. ready signal     — `aico-neo:ext-ready` for the URL-fallback path.
 *  19. locale resolution — the >64KB URL fallback fetches the RAW document,
 *                         whose per-language texts sit unapplied in
 *                         `localeOverrides`. `Neo.resolveLocale(doc, lang,
 *                         default)` ports aico's NeoLocaleResolver so the FR/EN
 *                         switcher works on oversized pages too (the <64KB
 *                         metafield keys arrive already resolved).
 *  20. `countdown`      — the engine has no renderer for the builder's native
 *                         countdown element, so ls-is-more's promo band
 *                         rendered an empty cream strip. Markup mirrors
 *                         aiconeo's ElementRenderer; ticking is a hydrate
 *                         pass (the engine paints via innerHTML, so an
 *                         authored <script> could never run).
 *  21. scroll reveal     — opt-in fade/rise-in of content as it scrolls into
 *                         view, keyed on the author classes `ls-reveal` and
 *                         `ls-reveal-group` (Enzonet Lounge feedback: the page
 *                         read as static). Dawn's own reveal is off theme-wide
 *                         and the renderer runs no page scripts, so it is a
 *                         hydrate pass + assets/content-builder-neo.css.
 *  21b. motion profiles — per-page choreography keyed on the body class,
 *                         anchored on structure (first-section hero image,
 *                         rail tracks, section uuids), never on author classes.
 *  22. builder-only fonts — drops the cms-* documents' URL @font-face style
 *                         (marker `/*ls-builder-fonts*\/`); fonts.css serves
 *                         the same faces on the storefront.
 *  23. `grid`          - the builder's cell grid: fr tracks from columns /
 *                         columnSizes / rowSizes, cells placed by span in flow,
 *                         a child finds its cell via content.columnIndex and
 *                         renders through the live registry; card cells draw
 *                         image + overlay + title with one link. Stacks to one
 *                         column below 750px.
 *  24. `news-list`     - the embedded newsItems snapshot as cards (image,
 *                         date in the theme's listing format, category, title,
 *                         excerpt) linked with the theme's news route +
 *                         ?view= fallback; a snapshot-less element that names
 *                         a category is filled from the CDN mirror at hydrate.
 *  25. `shop-the-look` - image with product pins per the aiconeo contract:
 *                         pins resolve by window.Shopify.shop domain, product
 *                         data is read live from /products/{handle}.js, the
 *                         card floats beside the pin (panel under the image
 *                         when narrow) and adds to cart when the product has
 *                         exactly one available variant.
 *  26. `product-grid`  - the "Products" tile: products resolve by handle for
 *                         this shop, load live, and render in the theme's
 *                         card vocabulary as grid / list / bento / carousel /
 *                         single card, with add-to-cart or product links.
 *  27. style-bag contract - a style bag is builder DATA, not a declaration
 *                         list. The Layout panel's structured `widthValue`
 *                         used to serialise as `width-value:[object Object]`;
 *                         it is now compiled into `width` per tier (and any
 *                         other object value is skipped, and a bare number on
 *                         a length property gets its px). Same helpers in the
 *                         engine file. See the block above stylesToInline.
 *  28. secondary mounts - the aiconeo SITE CHROME (global footer section,
 *                         snippets/aico-neo-chrome.liquid). A second, page-
 *                         independent Neo document painted into
 *                         `[data-ls-neo-secondary]` on EVERY page, including
 *                         pages that carry no Neo page document at all. Shares
 *                         the element hydration with the page mount and none
 *                         of its page-level semantics (no theme slots, no
 *                         theme binds, no `ls-neo-loading`); a failure stamps
 *                         the theme's own Liquid band back in.
 *  29. `table`         - the builder's structured table: header row, merged
 *                         cells (the occupancy map decides which grid column a
 *                         cell lands in), per-column widths and alignment, the
 *                         six presets and every granular override on top of
 *                         them, a caption, and limited inline HTML per cell
 *                         sanitised on the render path. Wide tables scroll
 *                         inside their own box rather than pushing the page
 *                         sideways; a `card` table stacks to one card per row
 *                         on a phone. Unregistered until now, which is why the
 *                         club's authors rebuilt tables by hand inside `html`
 *                         elements, 18 of them with 165 rows on one page.
 *  30. `cards`/`teaser` - the typed storefront pair, neither of which was
 *                         drawable here. 7 of the 23 live landing pages
 *                         hand-write the same photo-card-with-overlaid-title
 *                         tile as raw <style> + markup inside an `html`
 *                         element, because picking the real type drew nothing.
 *                         The scrim and the hover zoom are the renderer's own
 *                         CSS now: a hover state cannot be written as an
 *                         inline style, which is precisely why it kept coming
 *                         back as pasted CSS.
 *  31. `team`/`contact-info`/`crm-list` - the person row: a circular avatar
 *                         (photo, else the initial), a name, a role and a
 *                         bio. 5 of the 23 live landing pages paste that row
 *                         into `html` elements by hand, because all three of
 *                         the builder types that draw it were unregistered
 *                         here and drew nothing. `crm-list` is the only type
 *                         in this file whose PEOPLE are not in the document:
 *                         it carries a (entityType, group, tag) triple and
 *                         the roster is fetched at hydrate through the same
 *                         CDN-fronted CRM routes the squad and sponsor
 *                         sections already read.
 *  32. `tabs`/`pricing` - the last pair the palette offers that drew nothing
 *                         here. One page rebuilt BOTH by hand in two `html`
 *                         elements: 8 hidden radio inputs and 8 <label for>
 *                         tabs in 10'026 bytes of markup, driven by 48
 *                         `:checked` sibling rules inside a 62'632-byte
 *                         sibling <style>, plus a 3-column price card list.
 *                         Tabs switch with no JavaScript at all, on ONE native
 *                         radio group per element: the same "native control
 *                         instead of the Radix primitive" route faq/accordion
 *                         take with <details>.
 *  33. `carousel`/`quote`/`job-listings`/`announcement-bar` - the last four the
 *                         palette offers that drew nothing here. The rails are
 *                         CSS scroll-snap, not scripted sliders: authored JS
 *                         runs in neither renderer. `announcement-bar` stays in
 *                         flow rather than fixed, because a fixed descendant of
 *                         a transformed ancestor positions against the
 *                         transform and the builder canvas is transformed, so
 *                         a fixed bar lands mid-hero there.
 *  34. `interactions`  - the values that have NO inline form, so no style
 *                         control alone could ever have produced them: a
 *                         hover state, the list markers, the keyboard focus
 *                         ring and the motion that carries a hover. An audit
 *                         of the 23 live landing pages counted 146 `:hover`
 *                         rules across 21 pages, 154 `::before`/`::after`
 *                         across 22, 69 `transition` and 15 `:focus`, every
 *                         one of them hand-written as pasted CSS inside an
 *                         `html` element where the page's author cannot see
 *                         it. The builder writes a value on the element and
 *                         this file owns the rule. Emitted from
 *                         decorateElementHtml rather than per renderer,
 *                         because that wrapper is the one place that sees
 *                         EVERY type including the engine's own `text`, and a
 *                         list lives inside a `text`. Motion is emitted inside
 *                         `prefers-reduced-motion: no-preference` rather than
 *                         written and then cancelled under `reduce`, so a
 *                         reader who asked for less motion is never sent the
 *                         declaration at all.
 *  35. `layoutMode`     - a section whose children are placed by coordinate
 *                         instead of by document order. Nothing in the wrapper
 *                         chain was positioned, so an absolute child escaped to
 *                         the page chrome; a pixel coordinate means a different
 *                         place in a 1350px box than in the builder's 1200px
 *                         one, so the unit is a percentage; and a coordinate
 *                         authored at desktop width has no meaning on a phone,
 *                         so every rule sits inside `min-width: 750px` and
 *                         below it the section is stacked like a flow section.
 *                         The coordinate lands on a wrapper this file owns, not
 *                         on the element's own styled node: that node is an
 *                         <img>/<a>/inner div for several types and carries the
 *                         authored percent width, and moving it made photos
 *                         vanish and text overflow the page sideways. Which is
 *                         why a freeform section skips the engine's row pass
 *                         and renders its children itself. The canvas sits
 *                         inside contentPaddingTop so the vertical axis matches
 *                         the builder, clips like the builder's section root,
 *                         and isolates its layers so an authored z-index cannot
 *                         reach the shop's sticky header. Opt-in per section:
 *                         without `layoutMode: 'freeform'` not one extra byte
 *                         is emitted.
 *  36. `kicker`/`stats` - the two patterns the pages carry that had no ELEMENT
 *                         behind them at all, not merely no renderer here.
 *                         An audit of the 23 live landing pages counted 132
 *                         kickers on 22 of them, every one a `text` element
 *                         plus `.ls-kicker::after{width:40px;height:2px}`
 *                         pasted as raw CSS into a sibling `html` element; the
 *                         rule had to be a pseudo-element there because a
 *                         decorative span does not survive a rich-text
 *                         re-edit. The stats row appears twice, as a 3-cell
 *                         band with a hairline between cells and as a wrapping
 *                         hero chip row, both hand-built from `container` +
 *                         `text`. The builder now carries the values and this
 *                         file owns the look, including the one piece of
 *                         geometry the two renderers must agree on: an
 *                         intrinsic `grid-template-columns` that honours the
 *                         authored column count when there is room and reflows
 *                         when there is not, with no media query at all.
 *  37. `content-slider` - one slide at a time where every CHILD element is a
 *                         slide (a container, an image with content on it,
 *                         anything). Behaviour keys only (autoplay, interval,
 *                         loop, dots, arrows, pause); the slide motion and the
 *                         control look are the theme's. Replaces the home
 *                         Événements carousel hand-built from page CSS
 *                         (keyframes, radio `:has()` dots, a `::after` scrim).
 *  37b. rail layout     — `content.layout: 'rail'`: a native horizontal scroller
 *                         of fixed-width slides (slideWidth/-Mobile, slideGap/
 *                         -Mobile), optional right bleed with a theme fade, and
 *                         scroll-by-slide arrows. Replaces the Enzonet Lounge
 *                         `.ez-scroller/.ez-track` page CSS (p59).
 *  38. full-viewport hero — a `100vh`/`100svh` min-height in the page's first
 *                         section renders the screen minus the fixed header
 *                         (`--ls-neo-header-offset`), replacing the page-CSS
 *                         `calc(100svh - 98px)!important` patches (p59).
 *  39. `hidden` elements — the builder's eye toggle (Layers panel, canvas
 *                         chip, context menu) sets `element.hidden`; aiconeo's
 *                         ElementRenderer omits such an element and its whole
 *                         subtree outside edit mode, and the Shopify sync ships
 *                         the flag untouched. Pruned here before the engine
 *                         runs, so the shop matches the builder preview.
 *
 * Documents this has been verified against: landing pages 2 (club-des-130),
 * 5 (hospitalites), 12 (kids) and 14 (ls-is-more); Tier-1 types are covered by
 * synthetic fixtures in the offline harness, since no live document uses them
 * yet.
 *
 * The small style/escape helpers below replicate the engine's own — they are
 * IIFE-scoped there and intentionally not exported.
 */
(function (window, document) {
  'use strict';

  var Neo = window.NewsNeo;
  if (!Neo || typeof Neo.registerType !== 'function') {
    if (window.console && console.warn) {
      console.warn('content-builder-neo-ext: NewsNeo engine missing — load content-builder-neo.js first');
    }
    return;
  }

  /**
   * Legacy public submit endpoint, used ONLY when the authored element carries
   * no `submitEndpoint`. aiconeo bakes an absolute `submitEndpoint` into every
   * linked-form element it saves (LinkedFormRenderer.handleFormSelect →
   * buildFormSubmissionEndpoint), so this should never be needed.
   * FLAG: `28092` is the FC Lausanne-Sport aico tenant — this one constant is
   * the only tenant-specific value in the file. Everything else (including the
   * form-definition read URL) is derived from the authored endpoint.
   */
  var FALLBACK_SUBMIT_ENDPOINT = 'https://aicoapp.aico.swiss/28092/api/v1/news/form-submissions';

  // ============================================================
  // Helpers (mirroring content-builder-neo.js)
  // ============================================================

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function camelToKebab(s) {
    return s.replace(/([A-Z])/g, '-$1').toLowerCase();
  }

  // Prefix bare domains with https:// so they don't become relative paths;
  // drop javascript: URLs. Leaves #anchors and absolute/root paths untouched.
  function normalizeUrl(url) {
    if (!url) return '';
    var s = String(url).trim();
    if (!s) return '';
    if (/^javascript:/i.test(s)) return '';
    if (/^([a-z][a-z0-9+.\-]*:|\/\/|\/|#)/i.test(s)) return s;
    if (/^[^\s\/?]+\.[a-z]{2,}/i.test(s)) return 'https://' + s;
    return s;
  }

  // ============================================================
  // Style-bag contract: `widthValue`, object values, bare numbers
  //
  // A style bag is DATA from the builder, not a ready-made CSS declaration
  // list. Three shapes in it are not CSS and must never reach the DOM as-is:
  //
  //   1. `widthValue` — the Layout panel's STRUCTURED width, written next to
  //      the legacy `width` string: {mode:'auto'} | {mode:'fixed',value} |
  //      {mode:'percent',value} | {mode:'fluid',min,max}. Serialising the key
  //      emitted `width-value:[object Object]` on the storefront. Mirror
  //      upstream (pagebuilder/utils/responsiveStyles.ts, compileWidth /
  //      foldWidthValue): compile it into `width`, where it BEATS the raw
  //      legacy string, then drop the key. A `fixed` width becomes
  //      `min(<n>px, 100%)` on the MOBILE tier so it can never overflow a
  //      phone; `fixed`/`percent` with no numeric value leaves `width` alone.
  //   2. any OTHER object/array value is data as well (whatever structured
  //      control the builder grows next) — skipped, never stringified.
  //   3. a bare number on a length property means px. React applies the unit
  //      silently in the builder, so `width: 90` is what the author has been
  //      looking at, while `width:90` is invalid CSS this file would emit and
  //      the browser would drop. Unitless properties (opacity, zIndex,
  //      lineHeight, fontWeight, order, flexGrow…) are not in the list and
  //      keep their bare number; 0 is left alone, it is legal without a unit.
  // ============================================================

  // Upstream ElementRenderer.tsx PX_WHEN_BARE_NUMBER.
  var PX_WHEN_BARE_NUMBER = {
    width: 1, height: 1, minWidth: 1, minHeight: 1, maxWidth: 1, maxHeight: 1,
    top: 1, right: 1, bottom: 1, left: 1, gap: 1, rowGap: 1, columnGap: 1
  };

  // Structured WidthValue → CSS width string. `undefined` means "no opinion",
  // which leaves whatever `width` the bag already carries in place.
  function compileWidth(w, viewport) {
    if (!w || typeof w !== 'object' || Array.isArray(w)) return undefined;
    var min, max;
    switch (w.mode) {
      case 'auto':
        return 'auto';
      case 'fixed':
        if (typeof w.value !== 'number') return undefined;
        return viewport === 'mobile' ? 'min(' + w.value + 'px, 100%)' : w.value + 'px';
      case 'percent':
        return typeof w.value === 'number' ? w.value + '%' : undefined;
      case 'fluid':
        min = typeof w.min === 'number' ? w.min : 0;
        max = typeof w.max === 'number' ? w.max : 1200;
        return 'clamp(' + min + 'px, 100%, ' + max + 'px)';
      default:
        return undefined;
    }
  }

  // Fold a structured `widthValue` into `width` and strip it from the bag.
  // Returns the bag untouched when there is nothing to fold (no copy).
  function foldWidthValue(styleObj, viewport) {
    if (!styleObj || !styleObj.widthValue) return styleObj;
    var out = {};
    for (var k in styleObj) {
      if (Object.prototype.hasOwnProperty.call(styleObj, k) && k !== 'widthValue') out[k] = styleObj[k];
    }
    var compiled = compileWidth(styleObj.widthValue, viewport);
    if (compiled !== undefined) out.width = compiled;
    return out;
  }

  // Expand padding/margin shorthand into longhands when both are present.
  function normalizeShorthands(styleObj) {
    var out = {};
    for (var k in styleObj) {
      if (Object.prototype.hasOwnProperty.call(styleObj, k)) out[k] = styleObj[k];
    }
    var sides = ['Top', 'Right', 'Bottom', 'Left'];
    ['padding', 'margin'].forEach(function (base) {
      var hasIndividual = sides.some(function (s) { return (base + s) in out; });
      if (!hasIndividual || !(base in out)) return;
      // A non-string shorthand is not expandable; stylesToInline drops it.
      if (typeof out[base] !== 'string') return;
      var parts = out[base].trim().split(/\s+/);
      var top = parts[0];
      var right = parts[1] !== undefined ? parts[1] : top;
      var bottom = parts[2] !== undefined ? parts[2] : top;
      var left = parts[3] !== undefined ? parts[3] : right;
      var fill = { Top: top, Right: right, Bottom: bottom, Left: left };
      sides.forEach(function (s) { if (!(base + s in out)) out[base + s] = fill[s]; });
      delete out[base];
    });
    return out;
  }

  function stylesToInline(styleObj, viewport) {
    if (!styleObj || typeof styleObj !== 'object' || Array.isArray(styleObj)) return '';
    var normalized = normalizeShorthands(foldWidthValue(styleObj, viewport));
    var out = [];
    for (var key in normalized) {
      if (!Object.prototype.hasOwnProperty.call(normalized, key)) continue;
      var val = normalized[key];
      if (val === null || val === undefined || val === '') continue;
      // Structured data, not a declaration — never '[object Object]'.
      if (typeof val === 'object') continue;
      if (typeof val === 'number' && val !== 0 &&
          Object.prototype.hasOwnProperty.call(PX_WHEN_BARE_NUMBER, key)) {
        val = val + 'px';
      }
      out.push(camelToKebab(key) + ':' + val);
    }
    return out.join(';');
  }

  // The fold happens HERE, not at serialisation, so a renderer that overrides
  // `width` after reading the bag still wins — upstream resolves styles (and
  // folds widthValue) first and lets the element renderer write on top, e.g.
  // the button's `buttonFullWidth` → width:100%.
  function pickStyles(node, breakpoint) {
    if (!node || !node.styles) return {};
    var s = node.styles[breakpoint || 'desktop'];
    if (!s || Array.isArray(s)) return {};
    return foldWidthValue(s, breakpoint || 'desktop');
  }

  function inlineStyleAttr(node) {
    var s = stylesToInline(pickStyles(node));
    return s ? ' style="' + escapeAttr(s) + '"' : '';
  }

  // Only [a-z0-9-] survives, so the id can never break out of the attribute
  // selector it is interpolated into.
  function safeId(id) {
    return String(id == null ? '' : id).toLowerCase().replace(/[^a-z0-9\-]/g, '');
  }

  // The tier boundaries. 750-989px is already this file's own definition of a
  // tablet (newsColumnsStyleTag, pricingColumnsStyleTag) and the builder's
  // Tablet canvas is 768px, so both sides already agree on 989 as the edge.
  var TABLET_MAX_PX = 989;
  var MOBILE_MAX_PX = 749;

  /**
   * Properties the TABLET tier is not allowed to set.
   *
   * WHY ONE TIER IS FILTERED AND THE OTHER IS NOT. A tier can only set the
   * page's rail if every section carries that tier. The mobile tier does: below
   * 750px the rail is a single 16px value that the theme applies to everything,
   * so a mobile bag adds to a rule that is already uniform. The tablet tier does
   * not, and the measurement that says so is worth keeping:
   *
   *   Of fc-lausanne-sport's 23 published Neo documents, 213 elements carry a
   *   styles.tablet bag at all. 25 of those 213 carry a horizontal box property.
   *   23 of those 25 disagree with the SAME element's desktop value, and they
   *   disagree in one direction -- a 32px side rail against the theme's 40px
   *   (e.g. /pages/organisation's hero: desktop `92px 40px 56px`, tablet
   *   `80px 32px 48px`). They are proportional tablet bags the builder wrote for
   *   a minority of elements.
   *
   * Drawing them put the few sections that own a tablet bag on a 32px rail and
   * left every other section on 40px, so between 750px and 989px the frames
   * staggered by 8px and snapped back into line at 990px. Measured live on 13 of
   * this club's pages after the tier first shipped; the page x width
   * combinations where sections disagreed went 41 -> 65.
   *
   * So the rule is not "tablet padding is ignored". It is that a tier only some
   * elements carry cannot be allowed to move the content column, because the
   * elements that lack it stay where they were and the column splits in two. The
   * tier keeps everything that cannot split a column -- font-size (142 of the
   * 213 values and the reason the tier exists), gap, column-gap, min-height,
   * letter-spacing, aspect-ratio -- and gives up only the properties that set
   * the rail.
   *
   * padding-top / padding-bottom are deliberately NOT here: a vertical value
   * cannot move a rail, so the tier keeps it. A bare `padding` shorthand does go,
   * horizontal half and vertical half together, because the two cannot be
   * separated once it is a single declaration -- and dropping it whole returns
   * those elements to exactly the bytes they drew before the tier shipped, which
   * is the state being restored. min-width is here for the same reason as width
   * and max-width, though no document in this club currently authors one.
   *
   * If the tablet tier ever becomes something every element carries, this filter
   * is what should be reconsidered -- not the 989px edge above it.
   */
  var TABLET_TIER_BLOCKED = {
    'padding': 1, 'padding-left': 1, 'padding-right': 1,
    'margin': 1, 'margin-left': 1, 'margin-right': 1,
    'margin-top': 1, 'margin-bottom': 1,
    'width': 1, 'min-width': 1, 'max-width': 1
  };

  /** The same list as the bag is spelled, for the strip that happens first. */
  var TABLET_TIER_BLOCKED_KEYS = {
    padding: 1, paddingLeft: 1, paddingRight: 1,
    margin: 1, marginLeft: 1, marginRight: 1, marginTop: 1, marginBottom: 1,
    width: 1, minWidth: 1, maxWidth: 1, widthValue: 1
  };

  /**
   * Strip the blocked properties from a TABLET bag before it is serialised.
   *
   * This has to happen before stylesToInline, not only after it, because
   * normalizeShorthands expands `padding` into four longhands when the bag also
   * carries one of them -- and a filter that ran afterwards would drop
   * padding-left/right and keep the padding-bottom the expansion invented, so
   * the shop would draw a bottom padding the builder never shows. Stripping the
   * shorthand first means both sides drop the whole declaration, which is what
   * aiconeo's SHOPIFY_THEME_TABLET_UNPUBLISHED_KEYS does with the same list.
   *
   * widthValue is here rather than in TABLET_TIER_BLOCKED because foldWidthValue
   * turns it into `width` inside stylesToInline; blocked at both ends, it cannot
   * arrive under either name.
   */
  function tabletBagWithoutRail(bag) {
    if (!bag || typeof bag !== 'object' || Array.isArray(bag)) return bag;
    var out = {};
    for (var k in bag) {
      if (!Object.prototype.hasOwnProperty.call(bag, k)) continue;
      if (TABLET_TIER_BLOCKED_KEYS[k]) continue;
      out[k] = bag[k];
    }
    return out;
  }

  /**
   * `a:1;b:2` -> `a:1 !important;b:2 !important`, minus what this tier may not
   * set. Returns '' when the tier has nothing left to say, so a bag whose only
   * property was filtered emits no block at all rather than an empty one.
   */
  function tierDeclarations(css, viewport) {
    var out = [];
    css.split(';').forEach(function (d) {
      if (!d) return;
      if (viewport === 'tablet') {
        var colon = d.indexOf(':');
        if (colon > 0 && TABLET_TIER_BLOCKED[d.slice(0, colon).trim()]) return;
      }
      out.push(d + ' !important');
    });
    return out.join(';');
  }

  /** One tier as a scoped, !important rule on the element's own hook. */
  function tierRule(bag, viewport, id, maxWidthPx) {
    var css = stylesToInline(viewport === 'tablet' ? tabletBagWithoutRail(bag) : bag, viewport);
    if (!css || !id) return '';
    var declarations = tierDeclarations(css, viewport);
    if (!declarations) return '';
    return '<style>@media screen and (max-width: ' + maxWidthPx + 'px){' +
      '[data-neo-el="' + id + '"]{' + declarations + '}}</style>';
  }

  /**
   * Emit `styles.tablet` and `styles.mobile` as scoped @media blocks.
   *
   * The engine inlines `styles.desktop` only, so every other tier has to be a
   * rule keyed on the element's own `data-neo-el`. `!important` is required: it
   * has to beat the inline desktop style on the same node.
   *
   * WHY TABLET IS `max-width: 989px` AND NOT A 750-989 BAND: the builder's
   * resolver states its cascade in as many words -- "Desktop = base layer /
   * Tablet = base + tablet overrides / Mobile = base + tablet + mobile
   * overrides (Framer/Webflow cascade)" (aiconeo
   * src/components/pagebuilder/utils/responsiveStyles.ts). A tablet value
   * therefore has to keep applying on a phone unless the mobile tier overrides
   * that same property. A band would drop it again below 750, which is the
   * builder showing one thing and the shop drawing another -- the exact fault
   * this function exists to remove.
   *
   * ORDER MATTERS: tablet is emitted BEFORE mobile. Both rules carry
   * `!important` at the same specificity, so below 750px it is source order
   * that lets mobile win. Do not reorder these two.
   *
   * `extra` belongs to the MOBILE block only -- the image renderer uses it to
   * switch `aspect-ratio` off where a mobile-tier height takes over, which is
   * a statement about the phone tier and not about a tablet.
   *
   * Returns '' for a tier that is empty or an id that is unusable, so an
   * element authoring neither tier emits exactly the bytes it always did.
   */
  function viewportStyleTags(node, extra) {
    var id = safeId(node && node.id);
    if (!id) return '';
    var mobile = pickStyles(node, 'mobile');
    if (extra) {
      var merged = {};
      for (var mk in mobile) {
        if (Object.prototype.hasOwnProperty.call(mobile, mk)) merged[mk] = mobile[mk];
      }
      for (var ek in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, ek)) merged[ek] = extra[ek];
      }
      mobile = merged;
    }
    return tierRule(pickStyles(node, 'tablet'), 'tablet', id, TABLET_MAX_PX) +
      tierRule(mobile, 'mobile', id, MOBILE_MAX_PX);
  }

  function dataElAttr(node) {
    var id = safeId(node && node.id);
    return id ? ' data-neo-el="' + id + '"' : '';
  }

  // ============================================================
  // Child dispatch
  //
  // The engine's renderElement / renderColumnsRow are IIFE-scoped, so children
  // are dispatched here against the LIVE registry (Neo._renderers) — which
  // includes the types registered below, so containers nest recursively.
  // ============================================================

  function renderChild(el) {
    if (!el || typeof el !== 'object') return '';
    // Nested columns never enter the registry, so decorate them here instead.
    if (el.type === 'columns') return decorateElementHtml(el, renderNestedColumns(el));
    var fn = Neo._renderers[el.type];
    if (typeof fn !== 'function') {
      warnUnknownOnce(el.type);
      var fallback = renderChildren(el);
      // §35 (h): an unknown type draws no box of its own, so there is no node
      // to make a canvas of. Drop the pending mark rather than let the next
      // container tag claim a canvas it did not open. Dropped, not taken: a
      // mark that is taken is counted and numbered, and a number no tag
      // carries would put a dead height rule in the stylesheet.
      freeformCanvasPending = false;
      return fallback;
    }
    try {
      return fn(el);
    } catch (e) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: renderer for "' + el.type + '" threw', e);
      }
      return '';
    }
  }

  function renderChildren(el) {
    var children = (el && Array.isArray(el.children)) ? el.children
      : (el && Array.isArray(el.elements)) ? el.elements
        : [];
    var html = '';
    // §35 (h): outside a freeform section renderFreeChild is renderChild and
    // this stays false, so every existing page emits the bytes it always did.
    var placed = false;
    for (var i = 0; i < children.length; i++) {
      var out = renderFreeChild(children[i]);
      html += out.html;
      if (out.placed) placed = true;
    }
    freeformCanvasPending = placed;
    return html;
  }

  var warnedTypes = {};
  function warnUnknownOnce(type) {
    var key = String(type);
    if (warnedTypes[key]) return;
    warnedTypes[key] = true;
    if (window.console && console.warn) {
      console.warn('content-builder-neo-ext: no renderer for Neo element type "' + key +
        '" — rendering its children only');
    }
  }

  // ============================================================
  // 1. container
  // ============================================================

  Neo.registerType('container', function (el) {
    // Children first, then the opening tag: renderChildren is what discovers
    // whether this container took a positioned child, and freeformCanvasAttr
    // reads that answer. Written as one expression the two would evaluate in
    // the wrong order (§35 h).
    var inner = renderChildren(el);
    return viewportStyleTags(el) +
      '<div class="content-type-neo-container"' + freeformCanvasAttr(el) + dataElAttr(el) + inlineStyleAttr(el) + '>' +
      inner +
      '</div>';
  });

  // ============================================================
  // 8. button
  //
  // The engine registers no `button` renderer at all, and the unknown-type
  // degradation above only rescues elements WITH children — so leaf buttons
  // rendered nothing. /pages/kids lost all five of its CTAs that way.
  //
  // Mirrors ElementRenderer's __InlineEditableButtonOriginal exactly, including
  // its defaults, which are NOT cosmetic here: that renderer overrides the
  // authored borderRadius/padding/border with shape/size/borderWidth defaults
  // (filled + rounded + md + borderWidth 2 → 8px radius, 12px 24px padding,
  // 2px solid #000). The kids document knows this and compensates in its own
  // CSS — it literally says so:
  //
  //   "the renderer forces a 2px solid black border + 8px radius on every
  //    button; the LS kit's CTAs are square with a 1px transparent border"
  //   "the className lands on the element's OWN div AND the renderer nests an
  //    <a> inside it … The visible border belongs to the <a>/<button> only"
  //
  // So the two-node shape (classNamed wrapper > styled <a>) and the defaults
  // both have to be reproduced, or the author's `.kid-btn a` / `.kid-btn--ghost a`
  // !important rules stop matching and the buttons break differently.
  //
  // Not ported: `buttonIcon` (needs the lucide set) and the smooth-scroll
  // handler for `#section-…` hrefs — a native anchor jump already works, since
  // the section pass emits `id="section-<id>"`.
  //
  // Shape and size are PRESETS, not overrides (p59 T6, upstream
  // InlineEditableButton `hasExplicitShape` / `hasExplicitSize`): a
  // `content.buttonShape` / `content.buttonSize` the author picked wins on
  // every tier, the style-bag `borderRadius` / `padding` of that tier included;
  // with the content key ABSENT the bag value wins (desktop inline, mobile via
  // the scoped @media block), and only with neither does the old default
  // (8px / 12px 24px) apply. Border is unchanged (buttonBorderWidth/Color).
  // ============================================================

  var BUTTON_SHAPE_RADIUS = { square: '0px', rounded: '8px', pill: '9999px' };
  var BUTTON_SIZE_STYLES = {
    sm: { padding: '8px 16px', fontSize: '13px' },
    md: { padding: '12px 24px', fontSize: '15px' },
    lg: { padding: '16px 32px', fontSize: '17px' },
    xl: { padding: '20px 40px', fontSize: '19px' }
  };
  var BUTTON_SHADOWS = [
    'none',
    '0 1px 2px 0 rgba(0,0,0,0.05)',
    '0 4px 6px -1px rgba(0,0,0,0.1)',
    '0 10px 15px -3px rgba(0,0,0,0.1)',
    '0 20px 25px -5px rgba(0,0,0,0.1)'
  ];
  var BUTTON_ALIGN_TO_JUSTIFY = { left: 'flex-start', center: 'center', right: 'flex-end' };

  Neo.registerType('button', function (el) {
    var content = el.content || {};
    var text = content.text != null ? String(content.text) : '';
    var url = normalizeUrl(content.url);
    var openInNewTab = !!content.openInNewTab;

    // The homepage collage's "Voir tout" takes the theme's news route plus the
    // `?view=news` fallback (snippets/global-variables.liquid
    // newsPageHandle/newsViewParam), so it cannot render blank on a store where
    // the admin Page never got the template suffix. Scoped to that one class.
    if (/(^|\s)hm-news__all(\s|$)/.test(String(el.className || '')) &&
        /^(?:shopify:\/\/pages\/news|\/pages\/news)\/?$/.test(url)) {
      url = newsBasePath() + (window.newsViewParam || '');
    }

    // textAlign moves to the wrapper as justifyContent; everything else stays
    // on the button node.
    var authored = pickStyles(el, 'desktop');
    var buttonStyles = {};
    for (var k in authored) {
      if (Object.prototype.hasOwnProperty.call(authored, k) && k !== 'textAlign') {
        buttonStyles[k] = authored[k];
      }
    }

    var variant = content.buttonVariant || 'filled';
    var shape = content.buttonShape || 'rounded';
    var size = content.buttonSize || 'md';
    var bgColor = content.buttonBgColor || '';
    var textColor = content.buttonTextColor || '';
    var borderColor = content.buttonBorderColor || '';
    var borderWidth = content.buttonBorderWidth == null ? 2 : content.buttonBorderWidth;
    var fullWidth = !!content.buttonFullWidth;
    var shadowLevel = content.buttonShadow == null ? 0 : content.buttonShadow;
    var sizeStyle = BUTTON_SIZE_STYLES[size] || BUTTON_SIZE_STYLES.md;

    var hasExplicitShape = typeof content.buttonShape === 'string';
    var hasExplicitSize = typeof content.buttonSize === 'string';

    var computed = {};
    for (var b in buttonStyles) {
      if (Object.prototype.hasOwnProperty.call(buttonStyles, b)) computed[b] = buttonStyles[b];
    }
    computed.borderRadius = hasExplicitShape
      ? (BUTTON_SHAPE_RADIUS[shape] || '8px')
      : (bagHasValue(buttonStyles.borderRadius) ? buttonStyles.borderRadius : '8px');
    computed.padding = hasExplicitSize
      ? sizeStyle.padding
      : (bagHasValue(buttonStyles.padding) ? buttonStyles.padding : '12px 24px');
    computed.fontSize = buttonStyles.fontSize || sizeStyle.fontSize;
    computed.boxShadow = BUTTON_SHADOWS[shadowLevel] || 'none';
    computed.display = 'inline-flex';
    computed.alignItems = 'center';
    computed.gap = '8px';
    computed.textDecoration = 'none';
    computed.transition = 'all 0.2s ease';
    computed.fontWeight = buttonStyles.fontWeight || '600';
    computed.cursor = 'pointer';
    if (fullWidth) {
      computed.width = '100%';
      computed.justifyContent = 'center';
    }

    // A theme-token colour flips with light/dark mode and turns unreadable on a
    // solid button, so upstream ignores it there; an explicit buttonTextColor
    // still wins.
    var inheritedColor = (typeof buttonStyles.color === 'string' && buttonStyles.color.indexOf('var(--') !== -1)
      ? undefined
      : buttonStyles.color;

    if (variant === 'filled') {
      computed.backgroundColor = bgColor || buttonStyles.backgroundColor || '#000000';
      computed.color = textColor || inheritedColor || '#ffffff';
      computed.border = borderWidth > 0 ? borderWidth + 'px solid ' + (borderColor || '#000000') : 'none';
    } else if (variant === 'outline') {
      computed.backgroundColor = 'transparent';
      computed.color = textColor || borderColor || buttonStyles.color || '#000000';
      computed.border = (borderWidth > 0 ? borderWidth : 2) + 'px solid ' + (borderColor || textColor || '#000000');
    } else if (variant === 'ghost') {
      computed.backgroundColor = bgColor || 'rgba(0,0,0,0.05)';
      computed.color = textColor || inheritedColor || (bgColor ? '#ffffff' : '#000000');
      computed.border = borderWidth > 0 ? borderWidth + 'px solid ' + (borderColor || '#000000') : 'none';
    } else if (variant === 'link') {
      computed.backgroundColor = 'transparent';
      computed.color = textColor || buttonStyles.color || '#000000';
      computed.border = 'none';
      computed.padding = '0';
      // p56: a text decoration the style bag sets wins over the link default.
      computed.textDecoration = buttonStyles.textDecoration || 'underline';
      computed.boxShadow = 'none';
    }

    var wrapperStyle = authored.textAlign
      ? 'display:flex;justify-content:' + (BUTTON_ALIGN_TO_JUSTIFY[authored.textAlign] || 'flex-start')
      : '';

    var icon = buttonIconHtml(typeof content.buttonIcon === 'string' ? content.buttonIcon : '');
    var iconLeft = content.buttonIconPosition === 'left';

    var anchorAttrs = '';
    if (url) anchorAttrs += ' href="' + escapeAttr(url) + '"';
    if (openInNewTab) anchorAttrs += ' target="_blank" rel="noopener noreferrer"';

    // data-neo-el marks the STYLED node, so the styles.mobile @media override
    // lands on the <a> rather than the wrapper.
    return viewportStyleTags(buttonTierNode(el, hasExplicitShape, hasExplicitSize)) +
      '<div class="content-type-button content-type-neo-button"' +
      (wrapperStyle ? ' style="' + escapeAttr(wrapperStyle) + '"' : '') + '>' +
      '<a class="neo-button"' + anchorAttrs + dataElAttr(el) +
      ' style="' + escapeAttr(stylesToInline(computed)) + '">' +
      (icon && iconLeft ? icon : '') + escapeHtml(text) + (icon && !iconLeft ? icon : '') +
      '</a></div>';
  });

  /**
   * The node whose tier bags feed the button's @media blocks: the element
   * itself, or a shallow copy whose tablet AND mobile bags no longer carry the
   * borderRadius / padding an explicit shape / size preset owns (T6). A preset
   * wins on every tier, so it has to be dropped from every tier -- doing it for
   * the phone alone would let a tablet bag reinstate a radius the author's
   * shape already decided.
   */
  function buttonTierNode(el, dropRadius, dropPadding) {
    if (!el || !el.styles) return el;
    var tiers = ['tablet', 'mobile'];
    var changed = false;
    var styles = {};
    for (var t in el.styles) {
      if (Object.prototype.hasOwnProperty.call(el.styles, t)) styles[t] = el.styles[t];
    }
    for (var i = 0; i < tiers.length; i++) {
      var tier = tiers[i];
      var bagIn = el.styles[tier];
      if (!bagIn || Array.isArray(bagIn) || typeof bagIn !== 'object') continue;
      if (!(dropRadius && 'borderRadius' in bagIn) && !(dropPadding && 'padding' in bagIn)) continue;
      var bag = {};
      for (var k in bagIn) {
        if (!Object.prototype.hasOwnProperty.call(bagIn, k)) continue;
        if (dropRadius && k === 'borderRadius') continue;
        if (dropPadding && k === 'padding') continue;
        bag[k] = bagIn[k];
      }
      styles[tier] = bag;
      changed = true;
    }
    if (!changed) return el;
    return { id: el.id, styles: styles };
  }

  /**
   * `buttonIcon` glyphs the builder draws (lucide, `w-4 h-4 shrink-0`), as
   * inline SVG in currentColor. Only the ones documents use are ported; an
   * unknown id draws no icon, as upstream's 'none'.
   */
  var BUTTON_ICONS = {
    'arrow-right': '<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>'
  };

  function buttonIconHtml(id) {
    var paths = Object.prototype.hasOwnProperty.call(BUTTON_ICONS, id) ? BUTTON_ICONS[id] : '';
    if (!paths) return '';
    return '<svg class="neo-button__icon neo-button__icon--' + id + '" xmlns="http://www.w3.org/2000/svg" width="16" height="16"' +
      ' viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"' +
      ' aria-hidden="true" focusable="false" style="flex-shrink:0">' + paths + '</svg>';
  }

  // ============================================================
  // 10. Tier-1 content elements
  //
  // aiconeo defines 58 element types; the engine renders 10 and the blocks
  // above add 4. These are the ones a human clicking through the aiconeo
  // palette on a club marketing page is most likely to reach for, all of them
  // pure markup from `content` with no runtime data dependency.
  //
  // FIDELITY NOTE (applies to every renderer below): aiconeo's markup is
  // Tailwind utility classes + Radix primitives + lucide icons, none of which
  // exist in this theme. Reproducing its class strings is impossible and
  // meaningless here. What IS mirrored, and what the harness checks, is:
  //   - the exact `content` field names, defaults and fallback chains,
  //   - the DOM shape (which node is the wrapper, what nests inside what),
  //   - which node carries the element's inline styles.
  // Visual styling is supplied by content-builder-neo.css using the theme's
  // own tokens. Deviations beyond that are called out per renderer.
  //
  // Lucide `icon` name fields (multicolumn, features) cannot be resolved
  // without the icon set: an `iconUrl` is rendered as an <img>, a bare `icon`
  // name renders the icon frame without a glyph.
  // ============================================================

  /** Item arrays are authored as `content.items`; tolerate a missing/!array value. */
  function itemsOf(content, key) {
    var v = content && content[key || 'items'];
    return Array.isArray(v) ? v : [];
  }

  /** `style="…"` from a plain object, or '' when empty. Desktop tier unless told otherwise. */
  function styleAttrOf(obj, viewport) {
    var css = stylesToInline(obj, viewport);
    return css ? ' style="' + escapeAttr(css) + '"' : '';
  }

  /** Shared opening tag for a Tier-1 wrapper: styles + data-neo-el. */
  function wrapperOpen(el, cls, extraStyles, extraAttrs) {
    var styles = pickStyles(el, 'desktop');
    var merged = {};
    for (var k in styles) {
      if (Object.prototype.hasOwnProperty.call(styles, k)) merged[k] = styles[k];
    }
    if (extraStyles) {
      for (var e in extraStyles) {
        if (Object.prototype.hasOwnProperty.call(extraStyles, e)) merged[e] = extraStyles[e];
      }
    }
    return '<div class="' + cls + '"' + dataElAttr(el) + styleAttrOf(merged) + (extraAttrs || '') + '>';
  }

  // ---- spacer -------------------------------------------------
  // Upstream puts NO style on the wrapper and cssStyles + transparent
  // background on an inner div; height comes from styles.desktop (default 48px).
  Neo.registerType('spacer', function (el) {
    var styles = pickStyles(el, 'desktop');
    var merged = {};
    for (var k in styles) {
      if (Object.prototype.hasOwnProperty.call(styles, k)) merged[k] = styles[k];
    }
    merged.background = 'transparent';
    return viewportStyleTags(el) +
      '<div class="content-type-neo-spacer">' +
      '<div' + dataElAttr(el) + styleAttrOf(merged) + '></div></div>';
  });

  // ---- badge --------------------------------------------------
  // content: text, badgeStyle (primary|secondary|outline), badgePadding (12),
  // badgeAlign (left|center|right). Upstream derives the pill padding from
  // badgePadding as `max(2, p*0.35)px  p px` and picks a font size by threshold.
  Neo.registerType('badge', function (el) {
    var c = el.content || {};
    var style = c.badgeStyle || 'primary';
    var padding = c.badgePadding == null ? 12 : Number(c.badgePadding);
    var align = c.badgeAlign || 'left';
    var justify = { left: 'flex-start', center: 'center', right: 'flex-end' }[align] || 'flex-start';
    var fontSize = padding <= 10 ? '1.2rem' : padding <= 18 ? '1.4rem' : '1.6rem';
    var pill = {
      padding: Math.max(2, Math.round(padding * 0.35)) + 'px ' + padding + 'px',
      fontSize: fontSize
    };
    return viewportStyleTags(el) +
      wrapperOpen(el, 'content-type-neo-badge', { display: 'flex', justifyContent: justify }) +
      '<span class="neo-badge neo-badge--' + escapeAttr(style) + '"' + styleAttrOf(pill) + '>' +
      escapeHtml(c.text || 'Badge') +
      '</span></div>';
  });

  // ---- cta ----------------------------------------------------
  // content: headline, description, buttonText, buttonUrl, ctaHeadlineColor,
  // ctaDescColor, and buttonBgColor/buttonTextColor with the legacy
  // ctaButtonBgColor/ctaButtonTextColor fallback upstream still honours.
  Neo.registerType('cta', function (el) {
    var c = el.content || {};
    var url = normalizeUrl(c.buttonUrl);
    var btnBg = c.buttonBgColor || c.ctaButtonBgColor || '';
    var btnFg = c.buttonTextColor || c.ctaButtonTextColor || '';
    var html = viewportStyleTags(el) + wrapperOpen(el, 'content-type-neo-cta');
    if (c.headline) {
      html += '<h2 class="neo-cta__headline"' + styleAttrOf({ color: c.ctaHeadlineColor || undefined }) + '>' +
        escapeHtml(c.headline) + '</h2>';
    }
    if (c.description) {
      html += '<p class="neo-cta__description"' + styleAttrOf({ color: c.ctaDescColor || undefined }) + '>' +
        escapeHtml(c.description) + '</p>';
    }
    if (c.buttonText) {
      html += '<a class="neo-cta__button"' + (url ? ' href="' + escapeAttr(url) + '"' : '') +
        styleAttrOf({ backgroundColor: btnBg || undefined, color: btnFg || undefined }) + '>' +
        escapeHtml(c.buttonText) + '</a>';
    }
    return html + '</div>';
  });

  // ---- hero ---------------------------------------------------
  // content: headline, subheadline, buttonText, buttonUrl, backgroundImage,
  // backgroundPosition (center), overlayOpacity (0), contentPosition (center),
  // heroTextAlign (center), contentMaxWidth, heroHeight.
  // Upstream maps contentPosition through a 9-cell justify/align grid onto the
  // wrapper, which also carries the background image.
  // Deviation: upstream's `sectionBlocks` ordering/spacing model is not ported —
  // blocks render in the fixed heading → subheadline → button order, which is
  // its default when `sectionBlocks` is absent.
  var HERO_POSITIONS = {
    'top-left': ['flex-start', 'flex-start'], 'top-center': ['flex-start', 'center'], 'top-right': ['flex-start', 'flex-end'],
    'center-left': ['center', 'flex-start'], 'center': ['center', 'center'], 'center-right': ['center', 'flex-end'],
    'bottom-left': ['flex-end', 'flex-start'], 'bottom-center': ['flex-end', 'center'], 'bottom-right': ['flex-end', 'flex-end']
  };

  Neo.registerType('hero', function (el) {
    var c = el.content || {};
    var pos = HERO_POSITIONS[c.contentPosition || 'center'] || HERO_POSITIONS.center;
    var bg = c.backgroundImage ? 'url(' + String(c.backgroundImage).replace(/[()"']/g, '') + ')' : undefined;
    var extra = {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: pos[0],
      alignItems: pos[1],
      width: '100%',
      backgroundImage: bg,
      backgroundSize: bg ? 'cover' : undefined,
      backgroundPosition: bg ? (c.backgroundPosition || 'center') : undefined,
      minHeight: c.heroHeight || undefined
    };
    var overlay = Number(c.overlayOpacity || 0);
    var html = viewportStyleTags(el) + wrapperOpen(el, 'content-type-neo-hero', extra);
    if (overlay > 0) {
      html += '<div class="neo-hero__overlay"' +
        styleAttrOf({ backgroundColor: 'black', opacity: overlay }) + '></div>';
    }
    html += '<div class="neo-hero__content"' +
      styleAttrOf({ textAlign: c.heroTextAlign || 'center', maxWidth: c.contentMaxWidth || undefined }) + '>';
    if (c.headline) html += '<h1 class="neo-hero__headline">' + escapeHtml(c.headline) + '</h1>';
    if (c.subheadline) html += '<p class="neo-hero__subheadline">' + escapeHtml(c.subheadline) + '</p>';
    if (c.buttonText) {
      var url = normalizeUrl(c.buttonUrl);
      html += '<a class="neo-hero__button"' + (url ? ' href="' + escapeAttr(url) + '"' : '') + '>' +
        escapeHtml(c.buttonText) + '</a>';
    }
    return html + '</div></div>';
  });

  // ---- anchor-nav ---------------------------------------------
  // content: tabs [{id, label, targetSectionId}], activeColor, mode
  // (inline|overlay). Targets resolve to `#section-<targetSectionId>`, the id
  // the section pass emits — the same convention upstream's button scroll
  // handler assumes.
  // Deviation: `mode: overlay` (a floating, scroll-spy-driven bar) renders as
  // the inline bar plus a marker comment. `activeColor` is exposed as a custom
  // property so authored CSS and :target styling can use it, and it is what
  // paints the active tab.
  //
  // ACTIVE STATE: upstream marks the tab whose section is in view with an
  // underline (ElementRenderer.tsx:1304-1309, the `activeStyle: 'underline'`
  // default). The bar carries `data-neo-anchor-nav` so hydrateAnchorNavs can
  // find it and drive `aria-current` off an IntersectionObserver; the CSS
  // paints from that same attribute, so what is announced and what is drawn
  // can never disagree. Without JavaScript the bar is exactly what it is
  // today: plain links, no active tab.
  Neo.registerType('anchor-nav', function (el) {
    var c = el.content || {};
    var tabs = itemsOf(c, 'tabs');
    if (tabs.length === 0) return '';
    var overlayNote = (c.mode === 'overlay')
      ? '<!-- neo: anchor-nav mode "overlay" rendered inline (scroll-spy overlay not ported) -->'
      : '';
    var html = viewportStyleTags(el) + overlayNote +
      wrapperOpen(el, 'content-type-neo-anchor-nav', {
        '--neo-anchor-active': c.activeColor || undefined
      });
    html += '<nav class="neo-anchor-nav' + (c.layout === 'vertical' ? ' neo-anchor-nav--vertical' : '') +
      '" data-neo-anchor-nav>';
    for (var i = 0; i < tabs.length; i++) {
      var target = safeId(tabs[i] && tabs[i].targetSectionId);
      html += '<a class="neo-anchor-nav__tab"' +
        (target ? ' href="#section-' + escapeAttr(target) + '"' : '') + '>' +
        escapeHtml(tabs[i] && tabs[i].label ? tabs[i].label : '') + '</a>';
    }
    return html + '</nav></div>';
  });

  // ---- marquee ------------------------------------------------
  // content: items [{text, src}], speed (30), direction (left|right),
  // pauseOnHover (true), showBorder (true). Upstream animates a single flex
  // track; `direction: right` becomes animation-direction: reverse.
  // Optional sizes (p63; ints in px, a non-number is absent): logoHeight
  // (8-240) and logoHeightMobile (<=749px, absent = logoHeight) set the logo
  // height; gap (0-400) and gapMobile (absent = gap) set the run's gap plus
  // its trailing padding-right, so both runs stay equal and the -50% loop
  // stays seamless. Desktop values are inline, mobile ones a <style> keyed on
  // the element's data-neo-el. grayscale: true adds filter: grayscale(1) to
  // the logos (absent/false = none). With none of these fields the markup is
  // byte-identical to before (CSS defaults: 3.2rem logos, 4.8rem spacing).
  function marqueePx(raw, min, max) {
    if (typeof raw !== 'number' || !isFinite(raw)) return null;
    return Math.min(max, Math.max(min, Math.round(raw)));
  }

  function marqueeSizesOf(c) {
    var logo = marqueePx(c.logoHeight, 8, 240);
    var logoMobile = marqueePx(c.logoHeightMobile, 8, 240);
    var gap = marqueePx(c.gap, 0, 400);
    var gapMobile = marqueePx(c.gapMobile, 0, 400);
    return {
      logo: logo,
      logoMobile: logoMobile !== null && logoMobile !== logo ? logoMobile : null,
      gap: gap,
      gapMobile: gapMobile !== null && gapMobile !== gap ? gapMobile : null,
      grayscale: c.grayscale === true,
      any: logo !== null || logoMobile !== null || gap !== null || gapMobile !== null
    };
  }

  /** The <=749px sizes as a rule scoped to the element's own data-neo-el. */
  function marqueeMobileStyleTag(el, sizes) {
    var id = safeId(el && el.id);
    if (!id || (sizes.logoMobile === null && sizes.gapMobile === null)) return '';
    var scope = '[data-neo-el="' + id + '"] ';
    var css = '';
    if (sizes.gapMobile !== null) {
      css += scope + '.neo-marquee__run{gap:' + sizes.gapMobile + 'px !important;padding-right:' +
        sizes.gapMobile + 'px !important}';
    }
    if (sizes.logoMobile !== null) {
      css += scope + '.neo-marquee__item img{height:' + sizes.logoMobile + 'px !important}';
    }
    return '<style>@media screen and (max-width: 749px){' + css + '}</style>';
  }

  Neo.registerType('marquee', function (el) {
    var c = el.content || {};
    var items = itemsOf(c);
    var sizes = marqueeSizesOf(c);
    var runStyle = sizes.gap !== null ? styleAttrOf({ gap: sizes.gap + 'px', paddingRight: sizes.gap + 'px' }) : '';
    var itemStyle = sizes.any ? styleAttrOf({ padding: '0', height: 'auto' }) : '';
    var imgStyles = {};
    if (sizes.any) {
      if (sizes.logo !== null) imgStyles.height = sizes.logo + 'px';
      imgStyles.width = 'auto';
      imgStyles.maxWidth = 'none';
      imgStyles.objectFit = 'contain';
    }
    if (sizes.grayscale) imgStyles.filter = 'grayscale(1)';
    var imgStyle = styleAttrOf(imgStyles);
    var speed = Number(c.speed || 30);
    var reverse = c.direction === 'right';
    // The track scrolls by -50%, so it has to hold two identical runs — the
    // builder renders the list twice for exactly this reason. With one run the
    // logos slid out and left an empty band until the loop jumped back. A short
    // list is repeated inside each run so a run is wider than the band, and the
    // duration is stretched by the same factor so each logo still crosses the
    // band at the speed the author set.
    var repeat = items.length ? Math.max(1, Math.ceil(12 / items.length)) : 0;
    var html = viewportStyleTags(el) + marqueeMobileStyleTag(el, sizes) + wrapperOpen(el, 'content-type-neo-marquee');
    html += '<div class="neo-marquee' + (c.showBorder !== false ? ' neo-marquee--bordered' : '') + '">';
    html += '<div class="neo-marquee__track' + (c.pauseOnHover !== false ? ' neo-marquee__track--pausable' : '') + '"' +
      styleAttrOf({ animationDuration: (speed * Math.max(1, repeat)) + 's', animationDirection: reverse ? 'reverse' : 'normal' }) + '>';
    var run = '';
    for (var r = 0; r < repeat; r++) {
      for (var i = 0; i < items.length; i++) {
        var it = items[i] || {};
        run += '<div class="neo-marquee__item"' + itemStyle + '>';
        run += it.src
          ? '<img src="' + escapeAttr(it.src) + '" alt="' + escapeAttr(it.text || '') + '" loading="lazy"' + imgStyle + '>'
          : '<span>' + escapeHtml(it.text || '') + '</span>';
        run += '</div>';
      }
    }
    html += '<div class="neo-marquee__run"' + runStyle + '>' + run + '</div>';
    html += '<div class="neo-marquee__run" aria-hidden="true"' + runStyle + '>' + run + '</div>';
    return html + '</div></div></div>';
  });

  // ---- countdown ----------------------------------------------
  // content: targetDate (ISO string). Mirrors aiconeo's ElementRenderer DOM
  // (.flex.gap-4.justify-center > .text-center > .text-3xl digits + .text-sm
  // labels) so document CSS written against the builder markup — e.g.
  // ls-is-more's `.lsim-count` promo band rules — applies unchanged. The
  // Tailwind utility classes carry no CSS in the theme, so their computed
  // equivalents ride along inline; authored !important rules still win.
  // Upstream hard-codes English labels — here they follow the document lang
  // (authored CSS may replace them via ::after regardless). The engine inserts
  // via innerHTML so no script would run: ticking happens in
  // hydrateCountdowns(), keyed off data-neo-countdown.
  var COUNTDOWN_UNITS = ['days', 'hours', 'minutes', 'seconds'];
  Neo.registerType('countdown', function (el) {
    var c = el.content || {};
    var target = c.targetDate || '';
    var lang = (document.documentElement.lang || 'fr').toLowerCase();
    var labels = lang.indexOf('en') === 0
      ? ['Days', 'Hours', 'Minutes', 'Seconds']
      : ['Jours', 'Heures', 'Minutes', 'Secondes'];
    var html = viewportStyleTags(el) +
      wrapperOpen(el, 'content-type-neo-countdown') +
      '<div class="flex gap-4 justify-center" data-neo-countdown="' + escapeAttr(target) + '"' +
      styleAttrOf({ display: 'flex', gap: '16px', justifyContent: 'center' }) + '>';
    for (var i = 0; i < COUNTDOWN_UNITS.length; i++) {
      html += '<div class="text-center"' + styleAttrOf({ textAlign: 'center' }) + '>' +
        '<div class="text-3xl font-bold tabular-nums" data-cd-unit="' + COUNTDOWN_UNITS[i] + '"' +
        styleAttrOf({ fontSize: '30px', fontWeight: '700', fontVariantNumeric: 'tabular-nums' }) +
        '>00</div>' +
        '<div class="text-sm text-muted-foreground"' + styleAttrOf({ fontSize: '14px' }) + '>' +
        escapeHtml(labels[i]) + '</div>' +
        '</div>';
    }
    return html + '</div></div>';
  });

  // Tick every armed countdown once per second. Clamps at zero (the promo
  // band's copy is the author's job to swap once the offer ends). Idempotent
  // via data-cd-armed, like the layout guards.
  function hydrateCountdowns(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-neo-countdown]');
    for (var i = 0; i < nodes.length; i++) {
      (function (node) {
        if (node.getAttribute('data-cd-armed')) return;
        node.setAttribute('data-cd-armed', '1');
        var target = Date.parse(node.getAttribute('data-neo-countdown') || '');
        if (isNaN(target)) return;
        var fields = {};
        for (var u = 0; u < COUNTDOWN_UNITS.length; u++) {
          fields[COUNTDOWN_UNITS[u]] = node.querySelector('[data-cd-unit="' + COUNTDOWN_UNITS[u] + '"]');
        }
        function pad(n) { return (n < 10 ? '0' : '') + n; }
        function put(unit, value) {
          if (fields[unit]) fields[unit].textContent = pad(value);
        }
        var timer = null;
        function tick() {
          var left = Math.max(0, target - Date.now());
          var s = Math.floor(left / 1000);
          put('days', Math.floor(s / 86400));
          put('hours', Math.floor(s / 3600) % 24);
          put('minutes', Math.floor(s / 60) % 60);
          put('seconds', s % 60);
          if (!left && timer) window.clearInterval(timer);
        }
        tick();
        timer = window.setInterval(tick, 1000);
      })(nodes[i]);
    }
  }

  // ---- partner-logos ------------------------------------------
  // content: headline, logos [{name, src, url}], layout (row|grid),
  // columns (4), grayscale (true), logoHeight (sm|md|lg).
  // Authored data only — despite its `aico` palette category it makes no API call.
  // Deviation: a logo without an image is skipped. The builder's «+ logo»
  // adds a {name: 'Nouveau logo', src: ''} row, and rendering its name as text
  // put dozens of «Nouveau logo» cells on the live partners page (140065).
  // The list carries its logoHeight as a modifier so the storefront tile grid
  // (content-builder-neo.css, «partner-logos on content pages») can size a tier.
  Neo.registerType('partner-logos', function (el) {
    var c = el.content || {};
    var logos = itemsOf(c, 'logos');
    var size = c.logoHeight || 'md';
    var grid = c.layout === 'grid';
    var html = viewportStyleTags(el) + wrapperOpen(el, 'content-type-neo-partner-logos');
    if (c.headline) html += '<h2 class="neo-partner-logos__headline">' + escapeHtml(c.headline) + '</h2>';
    html += '<div class="neo-partner-logos__list neo-partner-logos__list--' + (grid ? 'grid' : 'row') +
      ' neo-partner-logos__list--size-' + escapeAttr(size) +
      (c.grayscale !== false ? ' is-grayscale' : '') + '"' +
      (grid ? styleAttrOf({ gridTemplateColumns: 'repeat(' + (Number(c.columns) || 4) + ', minmax(0, 1fr))' }) : '') + '>';
    for (var i = 0; i < logos.length; i++) {
      var lg = logos[i] || {};
      if (!lg.src) continue;
      // crossorigin only for aico storage (it answers with CORS headers): lets
      // sizePartnerLogo read the pixels to find the mark inside the file.
      var inner = '<img class="neo-partner-logos__img neo-partner-logos__img--' + escapeAttr(size) + '" src="' +
        escapeAttr(lg.src) + '" alt="' + escapeAttr(lg.name || '') + '" loading="lazy"' +
        (/^https:\/\/storage\.aico\.swiss\//.test(lg.src) ? ' crossorigin="anonymous"' : '') + '>';
      var url = normalizeUrl(lg.url);
      html += url
        ? '<a class="neo-partner-logos__item" href="' + escapeAttr(url) + '" target="_blank" rel="noopener noreferrer">' + inner + '</a>'
        : '<div class="neo-partner-logos__item">' + inner + '</div>';
    }
    return html + '</div></div>';
  });

  // ---- file-list ----------------------------------------------
  // content: title, subtitle, items [{id, name, fileUrl, fileType, fileSize}].
  // Deviation: upstream's editor-only upload affordances (drop zone, progress
  // bar) are omitted; the published view is a list of download links.
  Neo.registerType('file-list', function (el) {
    var c = el.content || {};
    var items = itemsOf(c);
    var html = viewportStyleTags(el) + wrapperOpen(el, 'content-type-neo-file-list');
    if (c.title || c.subtitle) {
      html += '<div class="neo-file-list__header">';
      if (c.title) html += '<h3 class="neo-file-list__title">' + escapeHtml(c.title) + '</h3>';
      if (c.subtitle) html += '<p class="neo-file-list__subtitle">' + escapeHtml(c.subtitle) + '</p>';
      html += '</div>';
    }
    html += '<ul class="neo-file-list__items">';
    for (var i = 0; i < items.length; i++) {
      var f = items[i] || {};
      var href = normalizeUrl(f.fileUrl);
      var meta = [f.fileType, f.fileSize].filter(Boolean).join(' · ');
      html += '<li class="neo-file-list__item">' +
        (href ? '<a class="neo-file-list__link" href="' + escapeAttr(href) + '" download>' : '<span class="neo-file-list__link">') +
        '<span class="neo-file-list__icon" aria-hidden="true">' + escapeHtml(String(f.fileType || 'file').toUpperCase().slice(0, 4)) + '</span>' +
        '<span class="neo-file-list__name">' + escapeHtml(f.name || '') + '</span>' +
        (meta ? '<span class="neo-file-list__meta">' + escapeHtml(meta) + '</span>' : '') +
        (href ? '</a>' : '</span>') +
        '</li>';
    }
    return html + '</ul></div>';
  });

  // ---- social-embed -------------------------------------------
  // content: platform, embedUrl, embedCode, fallbackText.
  // embedCode wins over embedUrl, exactly as upstream; the fallback box renders
  // when neither is set.
  Neo.registerType('social-embed', function (el) {
    var c = el.content || {};
    var html = viewportStyleTags(el) + wrapperOpen(el, 'content-type-neo-social-embed');
    if (c.embedCode) {
      html += '<div class="neo-social-embed__code">' + c.embedCode + '</div>';
    } else if (c.embedUrl) {
      html += '<iframe class="neo-social-embed__frame" src="' + escapeAttr(normalizeUrl(c.embedUrl)) +
        '" loading="lazy" allowfullscreen title="' + escapeAttr(c.platform || 'social') + '"></iframe>';
    } else {
      html += '<div class="neo-social-embed__fallback">' + escapeHtml(c.fallbackText || '') + '</div>';
    }
    return html + '</div>';
  });

  // ---- embed-widget: provider aico-calendar ----------------------------
  // aiconeo's «Match calendar (aico)» (EmbedWidgetRenderer.renderAicoCalendar):
  // the <aico-calendar-button variant="calendar"> card from aico's widget
  // bundle, one tab per feed key. Same grammar as aiconeo's
  // embedWidgetValidation.ts; anything else renders nothing. The bundle is
  // added once to <head> (a <script> inside innerHTML never runs). Other
  // providers (Podigee, AddEvent, privacy opt-out) stay unrendered, as before.
  var AICO_CALENDAR_WIDGET_SRC = 'https://aico-widgets.b-cdn.net/aico-calendar.v1.js';
  var AICO_CALENDAR_FEED_KEY = /^[A-Za-z0-9]{16,64}$/;
  var AICO_CALENDAR_API_BASE = /^https:\/\/[a-z0-9.-]+(?::\d+)?(?:\/[A-Za-z0-9_-]+)*$/;

  function ensureAicoCalendarBundle() {
    if (document.querySelector('script[data-aico-calendar-bundle]')) return;
    // Until the bundle defines the element, keep its fallback link hidden and hold the button's
    // height, so the page neither flashes the link nor jumps 16 -> 0 -> 37px (144437). Should the
    // bundle never load, the link appears after 3s and still opens the subscription page.
    var style = document.createElement('style');
    style.setAttribute('data-aico-calendar-style', '');
    style.textContent =
      'aico-calendar-button{display:inline-block;min-height:37px}' +
      'aico-calendar-button:not(:defined)>a{visibility:hidden;animation:aico-cal-fallback 0s 3s forwards}' +
      '@keyframes aico-cal-fallback{to{visibility:visible}}';
    document.head.appendChild(style);
    var script = document.createElement('script');
    script.src = AICO_CALENDAR_WIDGET_SRC;
    script.async = true;
    script.setAttribute('data-aico-calendar-bundle', '');
    document.head.appendChild(script);
  }

  Neo.registerType('embed-widget', function (el) {
    var c = el.content || {};
    if (c.provider !== 'aico-calendar') return '<!-- neo: embed-widget ' + escapeHtml(c.provider || '') + ' not rendered -->';
    var keys = (Array.isArray(c.aicoCalendarFeedKeys) ? c.aicoCalendarFeedKeys : []).filter(function (key, index, list) {
      return typeof key === 'string' && AICO_CALENDAR_FEED_KEY.test(key) && list.indexOf(key) === index;
    });
    var apiBase = typeof c.aicoCalendarApiBase === 'string' ? c.aicoCalendarApiBase : '';
    if (!keys.length || !AICO_CALENDAR_API_BASE.test(apiBase)) return '';
    ensureAicoCalendarBundle();
    // Always the LS visual-identity blue (Edoardo, 145009): it overrides the navy stored on pages 50/49
    // (aicoCalendarColor) and the feed colour set in the admin.
    var color = '#0165ba';
    var lang = (document.documentElement.lang || '').slice(0, 2).toLowerCase();
    // aicoCalendarVariant: "button" (one button opening the provider menu), "list" (button + next
    // matches), "modal" (a button opening the full card in a dialog, 144437) or the full card
    // (default). "button" and "list" take the first feed key only; the card variants show every
    // key as a tab.
    var variant = c.aicoCalendarVariant === 'button' || c.aicoCalendarVariant === 'list' || c.aicoCalendarVariant === 'modal'
      ? c.aicoCalendarVariant : 'calendar';
    var feedKeys = variant === 'calendar' || variant === 'modal' ? keys : keys.slice(0, 1);
    return viewportStyleTags(el) + wrapperOpen(el, 'content-type-neo-aico-calendar') +
      // LS ground for the card: the site's egg-shell, warm greys, the heading font for dates (Nic, 144437);
      // square corners everywhere and the LS body font on the button too (Edoardo, 145009).
      '<aico-calendar-button style="--aico-cal-card-bg:#fefcf4;--aico-cal-surface-soft:#f3efe4;--aico-cal-border:#e4dfd2;--aico-cal-heading-font:var(--font-heading-family);' +
      '--aico-cal-font:var(--font-body-family);--aico-cal-radius:0;--aico-cal-radius-menu:0;--aico-cal-radius-item:0;--aico-cal-radius-tile:0;--aico-cal-radius-pill:0;--aico-cal-radius-date:0"' +
      ' variant="' + variant + '" feed-key="' + escapeAttr(feedKeys.join(',')) + '" api-base="' + escapeAttr(apiBase) + '"' +
      (color ? ' color="' + escapeAttr(color) + '"' : '') +
      (/^(fr|de|it|en)$/.test(lang) ? ' locale="' + lang + '"' : '') + '>' +
      '<a href="' + escapeAttr(apiBase + '/cal/' + keys[0]) + '">' + escapeHtml(lang === 'en' ? 'Subscribe to the calendar' : "S'abonner au calendrier") + '</a>' +
      '</aico-calendar-button></div>';
  });

  /** Icon frame shared by multicolumn/features: an <img> when iconUrl is set,
   *  otherwise the empty frame (lucide `icon` names cannot be resolved here). */
  function iconFrame(item, cls, bgColor) {
    var url = item && item.iconUrl;
    return '<div class="' + cls + '"' + styleAttrOf({ backgroundColor: bgColor || undefined }) + '>' +
      (url ? '<img src="' + escapeAttr(url) + '" alt="" loading="lazy">' : '') +
      '</div>';
  }

  // ---- multicolumn --------------------------------------------
  // content: columns [{icon, iconUrl, title, description}], layout
  // (horizontal|vertical), iconStyle (circle), iconColor, titleColor,
  // descriptionColor. The grid itself comes from styles.desktop (display:grid).
  Neo.registerType('multicolumn', function (el) {
    var c = el.content || {};
    var cols = itemsOf(c, 'columns');
    var horizontal = c.layout === 'horizontal';
    var html = viewportStyleTags(el) + wrapperOpen(el, 'content-type-neo-multicolumn');
    for (var i = 0; i < cols.length; i++) {
      var col = cols[i] || {};
      html += '<div class="neo-multicolumn__item' + (horizontal ? ' neo-multicolumn__item--horizontal' : '') + '">';
      html += iconFrame(col, 'neo-multicolumn__icon' + (c.iconStyle === 'circle' ? ' is-circle' : ''), c.iconColor);
      html += '<div class="neo-multicolumn__body">';
      if (col.title) html += '<h4 class="neo-multicolumn__title"' + styleAttrOf({ color: c.titleColor || undefined }) + '>' + escapeHtml(col.title) + '</h4>';
      if (col.description) html += '<p class="neo-multicolumn__text"' + styleAttrOf({ color: c.descriptionColor || undefined }) + '>' + escapeHtml(col.description) + '</p>';
      html += '</div></div>';
    }
    return html + '</div>';
  });

  // ---- features -----------------------------------------------
  // content: items [{icon, iconUrl, image, title, description}],
  // featuresIconColor / featuresIconBgColor / featuresTitleColor /
  // featuresDescColor. Upstream forces
  // `gridTemplateColumns: repeat(<item count>, 1fr)` onto the wrapper.
  Neo.registerType('features', function (el) {
    var c = el.content || {};
    var items = itemsOf(c);
    var html = viewportStyleTags(el) +
      wrapperOpen(el, 'content-type-neo-features', {
        gridTemplateColumns: 'repeat(' + (items.length || 3) + ', minmax(0, 1fr))'
      });
    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      html += '<div class="neo-features__item">';
      if (it.image) html += '<img class="neo-features__image" src="' + escapeAttr(it.image) + '" alt="" loading="lazy">';
      html += iconFrame(it, 'neo-features__icon', c.featuresIconBgColor);
      if (it.title) html += '<h3 class="neo-features__title"' + styleAttrOf({ color: c.featuresTitleColor || undefined }) + '>' + escapeHtml(it.title) + '</h3>';
      if (it.description) html += '<p class="neo-features__text"' + styleAttrOf({ color: c.featuresDescColor || undefined }) + '>' + escapeHtml(it.description) + '</p>';
      html += '</div>';
    }
    return html + '</div>';
  });

  // ---- testimonial --------------------------------------------
  // content: items [{quote, author, role, avatar, rating}] with upstream's
  // legacy flat {quote, author, role, avatar, rating} fallback;
  // testimonialColumns, testimonialShowRating, testimonialShowAvatar,
  // testimonialCardBg / CardBorderColor / QuoteColor / AuthorColor /
  // CardRadius / CardPadding / CardShadow.
  // Deviation: `testimonialDisplayMode: slider` renders as the grid plus a
  // marker comment — the slider needs the carousel hydrator, which is Tier 2.
  var TESTIMONIAL_SHADOWS = {
    none: 'none', sm: '0 1px 2px rgba(0,0,0,0.06)',
    md: '0 4px 12px rgba(0,0,0,0.10)', lg: '0 10px 24px rgba(0,0,0,0.15)'
  };

  Neo.registerType('testimonial', function (el) {
    var c = el.content || {};
    var items = itemsOf(c);
    if (items.length === 0) {
      items = [{ quote: c.quote || '', author: c.author || '', role: c.role || '', avatar: c.avatar || '', rating: c.rating == null ? 5 : c.rating }];
    }
    var columns = c.testimonialColumns == null
      ? (items.length === 1 ? 1 : Math.min(items.length, 3))
      : Number(c.testimonialColumns);
    var showRating = c.testimonialShowRating !== false;
    var showAvatar = c.testimonialShowAvatar !== false;
    var shadow = c.testimonialCardShadow || 'none';
    var cardStyle = {
      backgroundColor: c.testimonialCardBg || undefined,
      borderColor: c.testimonialCardBorderColor || undefined,
      borderRadius: c.testimonialCardRadius != null ? c.testimonialCardRadius + 'px' : undefined,
      padding: c.testimonialCardPadding != null ? c.testimonialCardPadding + 'px' : undefined,
      boxShadow: shadow !== 'none' ? TESTIMONIAL_SHADOWS[shadow] : undefined
    };
    var sliderNote = (c.testimonialDisplayMode === 'slider')
      ? '<!-- neo: testimonial displayMode "slider" rendered as a grid (carousel not ported) -->'
      : '';

    var html = viewportStyleTags(el) + sliderNote +
      wrapperOpen(el, 'content-type-neo-testimonial', {
        display: 'grid',
        gridTemplateColumns: 'repeat(' + (columns || 1) + ', minmax(0, 1fr))'
      });
    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      html += '<figure class="neo-testimonial__card"' + styleAttrOf(cardStyle) + '>';
      html += '<blockquote class="neo-testimonial__quote"' + styleAttrOf({ color: c.testimonialQuoteColor || undefined }) + '>' +
        '“' + escapeHtml(it.quote || '') + '”</blockquote>';
      if (showRating && Number(it.rating || 0) > 0) {
        var stars = Math.max(0, Math.min(5, Math.round(Number(it.rating))));
        html += '<div class="neo-testimonial__rating" aria-label="' + stars + '/5">' +
          new Array(stars + 1).join('★') + new Array(6 - stars).join('☆') + '</div>';
      }
      html += '<figcaption class="neo-testimonial__author"' + styleAttrOf({ color: c.testimonialAuthorColor || undefined }) + '>';
      if (showAvatar && it.avatar) {
        html += '<img class="neo-testimonial__avatar" src="' + escapeAttr(it.avatar) + '" alt="" loading="lazy">';
      }
      html += '<span class="neo-testimonial__name">' + escapeHtml(it.author || '') + '</span>';
      if (it.role) html += '<span class="neo-testimonial__role">' + escapeHtml(it.role) + '</span>';
      html += '</figcaption></figure>';
    }
    return html + '</div>';
  });

  /**
   * faq / accordion / feature-accordion share one disclosure builder.
   *
   * Deviation: upstream uses the Radix Accordion primitive (a
   * `h3 > button[data-state]` tree driven by JS). This emits native
   * <details>/<summary>, which is the semantic equivalent with zero JS and
   * working keyboard/AT behaviour. Exclusive mode is the HTML `name` attribute
   * (one open at a time); browsers without it simply allow multiple open,
   * which is the graceful direction to fail.
   *
   * `opts.richText` mirrors how upstream treats the fields: faq renders its
   * question/answer through React (escaped), while accordion and
   * feature-accordion go through InlineEditableField, which is a TipTap
   * rich-text field written out with dangerouslySetInnerHTML.
   */
  function disclosureList(el, items, titleKey, bodyKey, opts) {
    var options = opts || {};
    var groupName = 'neo-acc-' + safeId(el.id);
    var exclusive = !options.allowMultiple;
    var out = options.richText ? function (v) { return v == null ? '' : String(v); } : escapeHtml;
    var look = options.look || null;
    var html = '<div class="neo-disclosure' + (look ? look.classes : '') + '"' +
      (look && look.vars ? ' style="' + escapeAttr(look.vars) + '"' : '') + '>';
    var opened = false;
    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      // Per-item `open: true` (authored in the doc) keeps a panel expanded at
      // load — pages migrated from `<details open>` markup carry it on the
      // item itself. In single-open mode only the FIRST flagged item opens
      // (aiconeo PR #3049 does the same).
      var wantsOpen = (options.firstOpen && i === 0) || it.open === true;
      if (wantsOpen && exclusive && opened) wantsOpen = false;
      if (wantsOpen) opened = true;
      var open = wantsOpen ? ' open' : '';
      html += '<details class="neo-disclosure__item"' +
        (exclusive ? ' name="' + escapeAttr(groupName) + '"' : '') + open +
        styleAttrOf({ backgroundColor: options.itemBg || undefined, borderColor: options.itemBorderColor || undefined }) + '>' +
        '<summary class="neo-disclosure__summary"' + styleAttrOf({ color: options.titleColor || undefined }) + '>' +
        out(it[titleKey] || '') + '</summary>' +
        '<div class="neo-disclosure__body"' + styleAttrOf({ color: options.bodyColor || undefined }) + '>' +
        out(it[bodyKey] || '') + '</div>' +
        '</details>';
    }
    return html + '</div>';
  }

  // ---- faq ----------------------------------------------------
  // content: items [{question, answer}], faqAllowMultiple (false),
  // faqFirstOpen (false), faqItemBg, faqItemBorderColor, faqQuestionColor,
  // faqAnswerColor.
  Neo.registerType('faq', function (el) {
    var c = el.content || {};
    return viewportStyleTags(el) +
      wrapperOpen(el, 'content-type-neo-faq') +
      disclosureList(el, itemsOf(c), 'question', 'answer', {
        allowMultiple: c.faqAllowMultiple === true,
        firstOpen: c.faqFirstOpen === true,
        itemBg: c.faqItemBg,
        itemBorderColor: c.faqItemBorderColor,
        titleColor: c.faqQuestionColor,
        bodyColor: c.faqAnswerColor
      }) +
      '</div>';
  });

  // ---- accordion ----------------------------------------------
  // content: items [{title, content}], allowMultiple (false), plus the look
  // fields below (p59 T3, shared contract with the aiconeo builder A3):
  //
  //   itemStyle          'boxed' | 'divided'   absent = boxed (today's card)
  //   dividerColor       colour of the 1px rule under each divided item
  //   questionColor      summary colour           questionHoverColor  on hover
  //   answerColor        answer body colour       answerFontSize      px
  //   iconStyle          'chevron' | 'plus'    absent = the theme's +/−
  //   iconColor          the icon's colour (default: the question colour)
  //
  // Question TYPOGRAPHY is the element's own style bag, which sits on the
  // wrapper and reaches the summaries by inheritance (a divided summary drops
  // the boxed 600 weight so the bag's weight wins). The answer must not
  // inherit a 26px/700/uppercase question look, so a divided list, or any list
  // that sets an answer colour/size, resets the answer body type (§ accordion
  // CSS). Values travel as custom properties on `.neo-disclosure`; the rules
  // that consume them (hover, the icon, the tier paddings) are the theme's.
  var ACCORDION_LOOK_VARS = [
    ['dividerColor', '--neo-acc-divider'],
    ['questionColor', '--neo-acc-q'],
    ['questionHoverColor', '--neo-acc-q-hover'],
    ['answerColor', '--neo-acc-a'],
    ['iconColor', '--neo-acc-icon']
  ];

  /** A colour value safe to drop into a declaration ('' when unusable). */
  function lookColor(v) {
    if (typeof v !== 'string') return '';
    var t = v.trim();
    return t && !/[;{}<>"\\]/.test(t) ? t : '';
  }

  function accordionLook(c) {
    var classes = '';
    var vars = '';
    var divided = c.itemStyle === 'divided';
    if (divided) classes += ' neo-disclosure--divided';
    if (c.iconStyle === 'chevron') classes += ' neo-disclosure--chevron';
    else if (c.iconStyle === 'plus') classes += ' neo-disclosure--plus';
    for (var i = 0; i < ACCORDION_LOOK_VARS.length; i++) {
      // dividerColor only means something on a divided list.
      if (ACCORDION_LOOK_VARS[i][0] === 'dividerColor' && !divided) continue;
      var val = lookColor(c[ACCORDION_LOOK_VARS[i][0]]);
      if (val) vars += ACCORDION_LOOK_VARS[i][1] + ':' + val + ';';
    }
    // An explicit answer colour is exact: no default 0.9 dimming on top.
    if (lookColor(c.answerColor)) classes += ' neo-disclosure--answer-exact';
    var size = Number(c.answerFontSize);
    // <= 0 (or not a number) = unset.
    var hasSize = c.answerFontSize != null && c.answerFontSize !== '' && isFinite(size) && size > 0;
    if (hasSize) vars += '--neo-acc-a-size:' + size + 'px;';
    if (divided || hasSize || lookColor(c.answerColor)) classes += ' neo-disclosure--answer-reset';
    return (classes || vars) ? { classes: classes, vars: vars } : null;
  }

  Neo.registerType('accordion', function (el) {
    var c = el.content || {};
    return viewportStyleTags(el) +
      wrapperOpen(el, 'content-type-neo-accordion') +
      disclosureList(el, itemsOf(c), 'title', 'content', { allowMultiple: c.allowMultiple === true, richText: true, look: accordionLook(c) }) +
      '</div>';
  });

  // ---- feature-accordion --------------------------------------
  // content: heading, items [{title, content}], image,
  // featureAccordionColumns ('40/60'), featureAccordionGap ('48px'),
  // featureAccordionDivider (false).
  Neo.registerType('feature-accordion', function (el) {
    var c = el.content || {};
    var ratio = String(c.featureAccordionColumns || '40/60').split('/');
    var left = parseInt(ratio[0], 10) || 40;
    var right = parseInt(ratio[1], 10) || (100 - left);
    var html = viewportStyleTags(el) + wrapperOpen(el, 'content-type-neo-feature-accordion');
    if (c.heading) html += '<h2 class="neo-feature-accordion__heading">' + escapeHtml(c.heading) + '</h2>';
    html += '<div class="neo-feature-accordion__grid"' + styleAttrOf({
      display: 'grid',
      gridTemplateColumns: left + 'fr ' + right + 'fr',
      gap: c.featureAccordionGap || '48px'
    }) + '>';
    html += '<div class="neo-feature-accordion__media">' +
      (c.image ? '<img src="' + escapeAttr(c.image) + '" alt="" loading="lazy">' : '') + '</div>';
    html += '<div class="neo-feature-accordion__items' + (c.featureAccordionDivider ? ' has-divider' : '') + '">' +
      disclosureList(el, itemsOf(c), 'title', 'content', { richText: true }) + '</div>';
    return html + '</div></div>';
  });

  // ============================================================
  // 13. image — the content bag the engine drops
  //
  // The engine's `image` renderer forwards `content.src/alt/caption` plus
  // `styles.desktop`, and nothing else. Everything aiconeo keeps on `content`
  // is lost. ElementRenderer's `case 'image'` (public-core bundle) computes:
  //
  //   clip-path  from imageShapeEnabled / …Type / …Edge / …Direction /
  //              …Intensity — the "shape" picker in the image inspector.
  //              /pages/hospitalites authors diagonal / both / left / 9 on its
  //              match photo, i.e. polygon(0 9%, 100% 0, 100% 91%, 0 100%),
  //              which reads as a slight tilt. The storefront drew a plain
  //              rectangle — the bug this section exists for.
  //   a size box  from aspectMode / customHeight — the builder's Size
  //              controls, and the primary height control for an image. The
  //              storefront honoured only the style bag, so an author who
  //              picked a ratio got one box on the canvas and another live
  //              (see imageSizeBox below).
  //   object-fit / object-position (+ scale() when cropZoom > 1) from
  //              objectFit / objectPosition / cropX / cropY / cropZoom.
  //   a scrim    <div class="absolute inset-0"> whose `background` comes from
  //              overlayOpacity + overlayColor + overlayDirection: a flat
  //              rgba(colour, opacity/100) when the direction is 'solid' or
  //              absent, otherwise a linear-gradient from that colour at the
  //              named edge to the same colour at alpha 0 on the opposite one
  //              (see imageOverlayBackground below — the builder's helper of
  //              the same name computes the identical string).
  //
  // Ported here DATA-DRIVEN: an element that authors none of them renders the
  // exact markup the engine produced before, down to the attribute order, so
  // the 20 documents whose global <style> blocks target `.x__img img` are
  // untouched. The clip is applied to the <img> rather than to an inserted
  // wrapper (aiconeo's own shape carries a wrapper) precisely to keep that DOM
  // contract: clip-path on a replaced element paints identically and, unlike a
  // transform, cannot overflow its parent, so no page needs extra room for it.
  //
  // Two builder DEFAULTS are deliberately NOT ported — they are editor
  // opinions that every LS document already fights:
  //   * `overlayOpacity ?? 30`, a 30% black scrim over EVERY image: 33 of the
  //     34 authored values across the 29 live documents are an explicit `0`
  //     written to switch it off, and pages 14/19/23 carry extra CSS hiding
  //     `div.absolute.inset-0` as a belt to that suspender. Honouring the
  //     default would darken the 25 images authored before it was found. The
  //     scrim is emitted only when the document asks for one (> 0).
  //   * `objectFit ?? 'cover'`, for the same reason (page 14's transparent
  //     logo has CSS undoing it). One exception, added with the size box
  //     below: an image the document gives an explicit aspect/height to has to
  //     crop into that box, so `cover` is the default THERE — as upstream —
  //     and an authored objectFit still wins.
  // Consequence for authors: keep writing an explicit `overlayOpacity: 0` in
  // the builder, so its canvas shows what the storefront will show.
  //
  // The scrim's `overlayColor` / `overlayDirection` DO take their builder
  // defaults (black, 'solid'), because those two defaults are what the scrim
  // already painted: a document written before the colour picker existed keeps
  // rendering the same flat black, and only a document that names a colour or
  // a direction gets a tint or a gradient. No opacity is implied either way —
  // the `> 0` gate above still decides whether a scrim exists at all.
  // ============================================================

  // aiconeo's named intensities, for documents that store a preset not a number.
  var IMAGE_SHAPE_INTENSITY = { sm: 5, md: 10, lg: 18 };

  function shapeIntensity(raw) {
    if (typeof raw === 'number' && isFinite(raw)) return raw;
    if (typeof raw === 'string' && IMAGE_SHAPE_INTENSITY[raw] !== undefined) {
      return IMAGE_SHAPE_INTENSITY[raw];
    }
    return 10;
  }

  /** The four shapes ElementRenderer can produce. '' when the element has none. */
  function imageClipPath(c) {
    if (!c || !c.imageShapeEnabled) return '';
    var type = c.imageShapeType || 'rectangle';
    if (type === 'rectangle') return '';

    var edge = c.imageShapeEdge || 'bottom';
    var dir = c.imageShapeDirection || 'left';
    var i = shapeIntensity(c.imageShapeIntensity);
    var top = edge === 'top' || edge === 'both';
    var bottom = edge === 'bottom' || edge === 'both';

    if (type === 'diagonal' || type === 'soft-diagonal') {
      return 'polygon(' +
        '0 ' + (top && dir === 'left' ? i : 0) + '%, ' +
        '100% ' + (top ? (dir === 'left' ? 0 : i) : 0) + '%, ' +
        '100% ' + (bottom && dir === 'left' ? 100 - i : 100) + '%, ' +
        '0 ' + (bottom ? (dir === 'left' ? 100 : 100 - i) : 100) + '%)';
    }
    if (type === 'wave') {
      var wt = top ? i : 0;
      var wb = bottom ? i : 0;
      return 'polygon(' +
        (top ? '0% ' + wt + '%, 25% 0%, 50% ' + wt + '%, 75% 0%, 100% ' + wt + '%'
          : '0% 0%, 100% 0%') + ', ' +
        (bottom ? '100% ' + (100 - wb) + '%, 75% 100%, 50% ' + (100 - wb) + '%, 25% 100%, 0% ' + (100 - wb) + '%'
          : '100% 100%, 0% 100%') + ')';
    }
    if (type === 'curve') {
      var ct = top ? i : 0;
      var cb = bottom ? i : 0;
      return 'polygon(' +
        (top ? '0% ' + ct + '%, 10% ' + (ct * 0.3) + '%, 30% 0%, 50% 0%, 70% 0%, 90% ' + (ct * 0.3) + '%, 100% ' + ct + '%'
          : '0% 0%, 100% 0%') + ', ' +
        (bottom ? '100% ' + (100 - cb) + '%, 90% ' + (100 - cb * 0.3) + '%, 70% 100%, 50% 100%, 30% 100%, 10% ' + (100 - cb * 0.3) + '%, 0% ' + (100 - cb) + '%'
          : '100% 100%, 0% 100%') + ')';
    }
    return '';
  }

  // The five overlay directions, mapped to the gradient axis they imply: the
  // value names the edge that holds the colour at full opacity, so it is the
  // edge the gradient runs *from* ('bottom' -> `to top`). 'solid' (and any
  // unknown value) is the absent case and paints a flat colour instead.
  var IMAGE_OVERLAY_GRADIENT_TO = {
    bottom: 'to top',
    top: 'to bottom',
    left: 'to right',
    right: 'to left'
  };

  /** '#rgb' / '#rrggbb' -> 'r,g,b'. Anything else -> black, as in the builder. */
  function imageOverlayRgb(hex) {
    var s = typeof hex === 'string' ? hex.replace(/^#/, '') : '';
    if (/^[0-9a-fA-F]{3}$/.test(s)) {
      s = s.charAt(0) + s.charAt(0) + s.charAt(1) + s.charAt(1) + s.charAt(2) + s.charAt(2);
    }
    if (!/^[0-9a-fA-F]{6}$/.test(s)) return '0,0,0';
    return parseInt(s.substring(0, 2), 16) + ',' +
      parseInt(s.substring(2, 4), 16) + ',' +
      parseInt(s.substring(4, 6), 16);
  }

  /**
   * The scrim's `background`, or '' when the document asks for no scrim.
   * Mirrors aiconeo's imageOverlayBackground(): overlayOpacity (no default,
   * see above), overlayColor (absent -> black, i.e. exactly what the scrim
   * painted before the colour existed) and overlayDirection. The `background`
   * shorthand carries both the flat colour and the gradient, so the two cases
   * go through one property and neither can leak into the other.
   *
   * `forcedOpacity` lets a caller that has already resolved the opacity itself
   * (renderImageWithChildren, which honours the builder's `?? 30`) reuse the
   * colour maths without inheriting this function's "no default" rule.
   */
  function imageOverlayBackground(c, forcedOpacity) {
    var opacity = Number(arguments.length > 1 ? forcedOpacity : (c && c.overlayOpacity));
    if (!isFinite(opacity) || opacity <= 0) return '';
    var rgb = imageOverlayRgb(c && c.overlayColor);
    var alpha = opacity / 100;
    var to = IMAGE_OVERLAY_GRADIENT_TO[c && c.overlayDirection];
    if (!to) return 'rgba(' + rgb + ',' + alpha + ')';
    return 'linear-gradient(' + to + ', rgba(' + rgb + ',' + alpha + ') 0%, rgba(' + rgb + ',0) 100%)';
  }

  /** object-fit / object-position / crop zoom, only for what the doc authored. */
  function imageFitStyles(c) {
    var out = {};
    if (!c) return out;

    var zoom = Number(c.cropZoom);
    var hasZoom = isFinite(zoom) && zoom > 1;
    var x = typeof c.cropX === 'number' ? c.cropX : 50;
    var y = typeof c.cropY === 'number' ? c.cropY : 50;
    var cropped = hasZoom || x !== 50 || y !== 50;

    if (c.objectFit) out.objectFit = c.objectFit;
    if (cropped) {
      // A crop only means something against a fitted box, so it implies cover.
      if (!out.objectFit) out.objectFit = 'cover';
      out.objectPosition = x + '% ' + y + '%';
    } else if (c.objectPosition) {
      out.objectPosition = c.objectPosition;
    }
    if (hasZoom) {
      out.transform = 'scale(' + zoom + ')';
      out.transformOrigin = x + '% ' + y + '%';
    }
    return out;
  }

  // Upstream ElementRenderer's `aspectRatioMap` (image branch), verbatim.
  var IMAGE_ASPECT_RATIOS = { hero: '4/1', wide: '16/9', landscape: '3/2', square: '1/1', portrait: '2/3' };

  /**
   * The SIZE BOX an image asks for through `content`, or null for "no opinion".
   *
   * `content.aspectMode` (auto | hero | wide | landscape | square | portrait |
   * custom) plus `content.customHeight` (px, only with `custom`) are what the
   * builder's Size controls write, and upstream spreads the box they produce
   * AFTER the style bag — so on desktop the CONTENT keys beat a bag
   * `aspectRatio` (preset) or a bag `height` (custom). A preset does NOT clear
   * a bag height and `custom` does NOT clear a bag aspectRatio: upstream leaves
   * the other property standing too, and CSS resolves the pair the same way in
   * both renderers (a definite height wins, the ratio then only sizes what is
   * left auto).
   *
   * Deviation, deliberate: upstream treats `custom` with no `customHeight`, and
   * any unknown mode, as "explicitly sized" and hands the box no size at all,
   * which collapses its absolutely-filled container to 0px. That is an editor
   * artefact of half-filled controls, never something to ship, so an unusable
   * mode is "no opinion" here and the photo keeps its natural height.
   */
  function imageSizeBox(c) {
    var mode = (c && c.aspectMode) || 'auto';
    if (mode === 'auto') return null;
    if (mode === 'custom') {
      var h = Number(c && c.customHeight);
      return isFinite(h) && h > 0 ? { height: h + 'px' } : null;
    }
    return IMAGE_ASPECT_RATIOS[mode] ? { aspectRatio: IMAGE_ASPECT_RATIOS[mode] } : null;
  }

  /** '' / null / undefined all mean "the bag says nothing about this property". */
  function bagHasValue(v) {
    return v !== undefined && v !== null && v !== '';
  }

  /**
   * An image that carries children is aiconeo's "content on the photo" block:
   * the builder accepts drops onto any image and draws the children over it
   * (ElementRenderer image branch, `imageChildren.length > 0`). The storefront
   * used to render the <img> alone and silently dropped those children, so an
   * editor saw text on the photo and the live page showed a bare picture.
   *
   * Mirrors that branch: style bag + aspect on a relative, clipped wrapper
   * (min-height 300px, in the stylesheet), the photo absolutely filled with fit/crop, the scrim
   * at overlayOpacity (default 30 here, as in the builder — this branch is the
   * one place the engine's default IS honoured) in overlayColor /
   * overlayDirection like any other image scrim, then a flex column
   * holding the children, placed by `overlayAlign` (default centre) with
   * 32px padding — none when a child brings its own layout: a site-header
   * riding on the photo, or a theme-slot whose Shopify section fills it (the
   * home next-match band on its CMS cover photo). Those children stretch.
   */
  // ---- phone image: content.mobileSrc / content.mobileObjectPosition --------
  //
  // aiconeo's image element can carry an optional second photo for phones
  // (task 142349: wide hero slides cropped to a sliver in a tall phone box).
  // Below MOBILE_MAX_PX the browser takes it from a <picture> <source>, with
  // its own focal point; the desktop crop pan/zoom belongs to `src` and is
  // switched off for it. The <picture> is `display: contents`
  // (content-builder-neo.css), so the <img> keeps every box, style, attribute
  // and selector it had (only a `.X > img` child selector would notice it).
  // An image WITHOUT mobileSrc renders byte for byte what it rendered before.

  /** Focal point keywords / percentages only — it lands inside a <style>. */
  var MOBILE_OBJECT_POSITION_RE = /^(?:(?:top|bottom|left|right|center|\d{1,3}(?:\.\d+)?%)\s*){1,2}$/i;

  /** {source, imgAttr, style} for an image with a phone photo, else null. */
  function imageMobileParts(el, c) {
    var mobile = c && typeof c.mobileSrc === 'string' ? c.mobileSrc.trim() : '';
    if (!mobile) return null;
    var id = safeId(el && el.id);
    var pos = String((c && c.mobileObjectPosition) || '').trim();
    if (!MOBILE_OBJECT_POSITION_RE.test(pos)) pos = 'center';
    // srcset is a comma/space separated list: a raw space or comma in the url
    // would split it.
    var srcset = mobile.replace(/\s/g, '%20').replace(/,/g, '%2C');
    return {
      source: '<source media="(max-width: ' + MOBILE_MAX_PX + 'px)" srcset="' + escapeAttr(srcset) + '">',
      imgAttr: id ? ' data-neo-mobile-img="' + id + '"' : '',
      style: id
        ? '<style>@media screen and (max-width: ' + MOBILE_MAX_PX + 'px){img[data-neo-mobile-img="' + id +
          '"]{object-position:' + pos + ' !important;transform:none !important}}</style>'
        : ''
    };
  }

  /** The <img> as is, or inside a <picture> with the phone <source>. */
  function imageWithMobileSource(parts, imgHtml) {
    return parts ? '<picture class="neo-image__picture">' + parts.source + imgHtml + '</picture>' : imgHtml;
  }

  /** True when any tier's style bag gives the image an explicit height. */
  function imageTierHasHeight(el) {
    var styles = (el && el.styles) || {};
    var tiers = ['desktop', 'tablet', 'mobile'];
    for (var i = 0; i < tiers.length; i++) {
      var bag = styles[tiers[i]];
      if (bag && typeof bag === 'object' && !Array.isArray(bag) &&
          bagHasValue(bag.height) && String(bag.height).trim() !== 'auto') return true;
    }
    return false;
  }

  function renderImageWithChildren(el, c, children) {
    var wrap = {};
    var desktop = pickStyles(el, 'desktop');
    for (var k in desktop) {
      if (Object.prototype.hasOwnProperty.call(desktop, k)) wrap[k] = desktop[k];
    }
    // position/min-height/overflow come from content-builder-neo.css, NOT inline:
    // page CSS keyed on the element's className (e.g. `.ls-hero-photo` pinning
    // the photo absolutely across a hero) must still be able to win.
    var box = imageSizeBox(c);
    if (box) {
      if (box.height) wrap.height = box.height;
      if (box.aspectRatio) wrap.aspectRatio = box.aspectRatio;
    }
    // A SIZED photo (the Size field's aspect / custom height, or a tier height
    // from the Layout panel) hands that size to its content layer too, as the
    // builder does (ElementRenderer image branch: the layer carries the same
    // containerStyle), so overlayAlign places the children against the whole
    // photo. Without it the layer kept its 300px floor at the TOP of a taller
    // photo and a centred hero read top-aligned on the site only. Unsized
    // photos (height from content or a min-height) are untouched.
    var sized = !!box || imageTierHasHeight(el);
    var clip = imageClipPath(c);
    if (clip) wrap.clipPath = clip;

    var img = { position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' };
    var fit = imageFitStyles(c);
    for (var f in fit) {
      if (Object.prototype.hasOwnProperty.call(fit, f)) img[f] = fit[f];
    }
    if (wrap.borderRadius) img.borderRadius = wrap.borderRadius;

    var mobile = c.src ? imageMobileParts(el, c) : null;
    var photo = c.src
      ? imageWithMobileSource(mobile, '<img src="' + escapeAttr(c.src) + '" alt="' + escapeAttr(c.alt || '') + '"' + imageLoadingAttrs(c.src) +
        (mobile ? mobile.imgAttr : '') + styleAttrOf(img) + '>')
      : '';

    var opacity = Number(c.overlayOpacity);
    if (!isFinite(opacity)) opacity = 30;
    var scrim = opacity > 0
      ? '<span class="neo-image__scrim" aria-hidden="true"' + styleAttrOf({ background: imageOverlayBackground(c, opacity) }) + '></span>'
      : '';

    var header = null;
    var fullBleed = false;
    for (var i = 0; i < children.length; i++) {
      var childType = children[i] && children[i].type;
      if (childType === 'site-header' || childType === 'theme-slot') fullBleed = true;
      if (childType === 'site-header' && !header) header = children[i];
    }
    var align = String(c.overlayAlign || '');
    var layer = {
      position: 'relative',
      zIndex: '1',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      minHeight: '300px',
      padding: fullBleed ? '0' : '32px',
      alignItems: fullBleed ? '' : (align.indexOf('left') !== -1 ? 'flex-start' : align.indexOf('right') !== -1 ? 'flex-end' : 'center'),
      justifyContent: (header && header.content && header.content.pinToTop) || align.indexOf('top') !== -1 ? 'flex-start'
        : align.indexOf('bottom') !== -1 ? 'flex-end' : 'center',
      textAlign: align.indexOf('left') !== -1 ? 'left' : align.indexOf('right') !== -1 ? 'right' : 'center'
    };

    // Children first, for the reason written on the container renderer: the
    // overlay layer is a box with children, so it is a canvas like any other
    // (§35 h). Upstream already renders them here, and nothing between this
    // line and the tag below renders another child.
    var childrenHtml = renderChildren(el);
    var href = safeHref(c.linkUrl);
    var link = '';
    if (href) {
      // The whole photo is the link (see imageLinkAttrs): an absolutely filled
      // anchor between the scrim and the content layer. The layer itself stops
      // taking the pointer so a click on the heading text falls through to the
      // photo link; its own controls take it back (content-builder-neo.css).
      var label = imageLinkLabel(childrenHtml) || String(c.alt || '').trim();
      link = '<a class="neo-image__link"' + imageLinkAttrs(href, c) +
        (label ? ' aria-label="' + escapeAttr(label) + '"' : '') + '></a>';
    }

    return viewportStyleTags(el) + (mobile ? mobile.style : '') +
      '<div class="content-type-neo-image content-type-neo-image--overlay' + (link ? ' content-type-neo-image--linked' : '') +
      (sized ? ' content-type-neo-image--sized' : '') +
      (el._lsFullViewport ? ' content-type-neo-image--viewport' : '') + '"' +
      dataElAttr(el) + styleAttrOf(wrap) + '>' +
      photo + scrim + link +
      '<div class="neo-image__overlay"' + freeformCanvasAttr(el) + styleAttrOf(layer) + '>' + childrenHtml + '</div>' +
      '</div>';
  }

  // ---- hero image priority (#141269 perf) -----------------------------------
  //
  // snippets/neo-preload-hints.liquid preloads the first section's photo
  // (<link rel=preload as=image data-neo-hero>) so it downloads from t≈0. The
  // <img> printing that same URL is the page's LCP: render it eager with high
  // priority instead of lazy, once; every other photo stays lazy.
  var heroHintUsed = false;
  function imageLoadingAttrs(src) {
    if (!heroHintUsed && src) {
      var hint = document.querySelector('link[rel="preload"][as="image"][data-neo-hero]');
      if (hint && (hint.getAttribute('href') === src || hint.href === src)) {
        heroHintUsed = true;
        return ' loading="eager" fetchpriority="high"';
      }
    }
    return ' loading="lazy"';
  }

  // ---- image links: content.linkUrl / content.openInNewTab -----------------
  //
  // The builder's ImageContentEditor writes both on every image; the storefront
  // ignored them, so a photo that was a link on the canvas was a dead picture on
  // the shop (the /pages/shop hero slides had to stay hand-written html for
  // exactly that reason). Same scheme guard as every other authored href in
  // this file (safeHref): an unsafe or empty url renders NO anchor at all, and
  // an image without a link renders byte for byte what it rendered before.
  //
  // Two shapes, one per image branch:
  //   plain          <div.content-type-neo-image><a.neo-image__link><img>scrim</a>
  //                  caption</div>. The <img> keeps every attribute, style and
  //                  data-neo-el it had, so page CSS keyed on `.X img` and the
  //                  mobile @media block still land on it; only a `.X > img`
  //                  selector would notice the wrapper, and none exists.
  //   with children  an empty, absolutely filled anchor over photo + scrim and
  //                  under the content layer. The layer is `pointer-events:none`
  //                  ONLY while linked (`--linked`), with its interactive
  //                  descendants re-enabled, so the heading on the photo is part
  //                  of the click target and a button riding on it still works.
  //                  An empty anchor has no name of its own: it takes the first
  //                  heading's text, else the image alt.

  function imageLinkAttrs(href, c) {
    return ' href="' + escapeAttr(href) + '"' +
      (c && c.openInNewTab === true ? ' target="_blank" rel="noopener noreferrer"' : '');
  }

  /** Plain text of the first <h1>-<h6> in rendered children markup, '' when none. */
  function imageLinkLabel(html) {
    var m = /<h[1-6]\b[^>]*>([\s\S]*?)<\/h[1-6]>/i.exec(String(html || ''));
    if (!m) return '';
    // DOMParser builds an inert document: nothing in the markup loads or runs,
    // and entities come back decoded, so escapeAttr cannot double-escape them.
    try {
      if (typeof DOMParser === 'function') {
        var doc = new DOMParser().parseFromString('<p>' + m[1] + '</p>', 'text/html');
        return String((doc.body && doc.body.textContent) || '').replace(/\s+/g, ' ').trim();
      }
    } catch (err) { /* fall through to the tag strip */ }
    return stripTags(m[1]);
  }

  Neo.registerType('image', function (el) {
    var c = (el && el.content) || {};
    var children = (el && Array.isArray(el.children)) ? el.children : [];
    if (children.length) return renderImageWithChildren(el, c, children);

    var src = c.src || '';
    if (!src) return '';

    var bag = {};
    var desktop = pickStyles(el, 'desktop');
    for (var k in desktop) {
      if (Object.prototype.hasOwnProperty.call(desktop, k)) bag[k] = desktop[k];
    }

    var clip = imageClipPath(c);
    if (clip) bag.clipPath = clip;

    var fit = imageFitStyles(c);
    for (var f in fit) {
      if (Object.prototype.hasOwnProperty.call(fit, f)) bag[f] = fit[f];
    }

    // ---- size box ------------------------------------------------------
    // The builder's Size controls (content.aspectMode / content.customHeight)
    // are the image's primary height control; the storefront honoured only the
    // style bag, so an author who picked "16/9" saw one box on the canvas and
    // another one live. Upstream builds a <div> box and fills it with an
    // absolutely positioned <img>; here the bag is on the <img> itself (the DOM
    // contract 20 documents' CSS is keyed on), so the box lands on the photo:
    //
    //   desktop   preset            -> aspect-ratio, beating a bag aspectRatio
    //             custom + height   -> height:<n>px, beating a bag height
    //   mobile    a mobile-tier height wins and switches the ratio off
    //             (mobileStyleTag's @media block, `extra` below) — upstream
    //             does the same on the phone viewport so a tall desktop photo
    //             can be shortened.
    //
    // width/display default to upstream's box, which is a block-level div
    // filling its parent: without them a ratio would be measured against the
    // photo's INTRINSIC width and a narrow source would not fill its column,
    // and `margin-inline: auto` (inline-level → 0) would stop centring. Both
    // are defaults only — a bag `width`/`display` still wins, as it does
    // upstream, where they style that same box.
    var sizeBox = imageSizeBox(c);
    var mobileExtra = null;
    if (sizeBox) {
      if (sizeBox.aspectRatio) {
        bag.aspectRatio = sizeBox.aspectRatio;
        var mobileHeight = pickStyles(el, 'mobile').height;
        if (bagHasValue(mobileHeight) && mobileHeight !== 'auto') mobileExtra = { aspectRatio: 'auto' };
      }
      if (sizeBox.height) bag.height = sizeBox.height;
      if (!bagHasValue(bag.width)) bag.width = '100%';
      if (!bagHasValue(bag.display)) bag.display = 'block';
      // A box that is not the photo's own ratio has to crop, so upstream's
      // `objectFit ?? 'cover'` applies here — the default this file otherwise
      // withholds (see the note above) is an opinion only while the box is the
      // picture's own shape. An objectFit on either content or the bag wins.
      if (!bagHasValue(bag.objectFit)) bag.objectFit = 'cover';
    }

    var opacity = Number(c.overlayOpacity);
    var scrim = '';
    if (isFinite(opacity) && opacity > 0) {
      // Sits inside .content-type-neo-image, which the stylesheet makes a
      // containing block for it; the clip is repeated so the scrim follows the
      // shape instead of squaring off the corners the image just cut away.
      var scrimStyles = { background: imageOverlayBackground(c) };
      if (clip) scrimStyles.clipPath = clip;
      scrim = '<span class="neo-image__scrim" aria-hidden="true"' + styleAttrOf(scrimStyles) + '></span>';
    }

    var caption = c.caption || '';
    var mobile = imageMobileParts(el, c);
    var photo = imageWithMobileSource(mobile, '<img src="' + escapeAttr(src) + '" alt="' + escapeAttr(c.alt || '') + '"' + imageLoadingAttrs(src) +
      dataElAttr(el) + (mobile ? mobile.imgAttr : '') + styleAttrOf(bag) + '>') +
      scrim;
    // content.linkUrl (see imageLinkAttrs above): the anchor wraps the photo and
    // its scrim, never the caption; the <img> inside is the unchanged node.
    var href = safeHref(c.linkUrl);
    if (href) {
      var label = String(c.alt || '').trim() ? '' : stripTags(caption);
      photo = '<a class="neo-image__link"' + imageLinkAttrs(href, c) +
        (label ? ' aria-label="' + escapeAttr(label) + '"' : '') + '>' + photo + '</a>';
    }

    return viewportStyleTags(el, mobileExtra) + (mobile ? mobile.style : '') +
      '<div class="content-type-neo-image' + (scrim ? ' content-type-neo-image--scrim' : '') + '">' +
      photo +
      (caption ? '<span class="caption">' + caption + '</span>' : '') +
      '</div>';
  });

  // ============================================================
  // 22-25. Data-backed elements: grid, news-list, shop-the-look, product-grid
  //
  // Four aiconeo elements the storefront must draw from data it did not
  // author itself: the grid holds other elements in cells, the news list
  // carries article snapshots, and the two shop elements name Shopify
  // products by handle and read them LIVE from the Ajax API (the aico prices
  // in their snapshots must never be shown, see docs/contentbuilder-json.md).
  //
  // The helpers below are shared by the shop elements: the shop the page is
  // rendered in, the product loader, the theme's money format and the cart
  // add. Everything that needs the DOM (product data, cards, cart) happens in
  // a hydrate pass, keyed off data attributes, because the engine paints via
  // innerHTML and no authored script could run.
  // ============================================================

  var warnedOnce = {};
  function warnOnce(key, message) {
    if (warnedOnce[key]) return;
    warnedOnce[key] = true;
    if (window.console && console.warn) console.warn('content-builder-neo-ext: ' + message);
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  /**
   * Stricter than normalizeUrl: the safe-href rule of the shop-the-look
   * contract (utils/safeHref.ts). Accepts http(s), mailto, tel, protocol-
   * relative, site-relative and #anchor targets, upgrades a bare domain to
   * https and rejects every other scheme. '' when unsafe or empty.
   */
  function safeHref(url) {
    if (url == null) return '';
    var s = String(url).replace(/[\t\r\n]/g, '').trim();
    if (!s) return '';
    var probe = s.replace(/%3a/gi, ':');
    if (/^(https?:|mailto:|tel:)/i.test(probe)) return s;
    if (/^([a-z][a-z0-9+.\-]*):/i.test(probe)) return '';
    if (/^(\/\/|\/|#)/.test(s)) return s;
    if (/^[^\s\/?#]+\.[a-z]{2,}(?:[\/?#]|$)/i.test(s)) return 'https://' + s;
    return '';
  }

  /** Small clamp helpers shared by the normalisers (gridModel.ts clampInt / clampFr). */
  function clampInt(value, min, max, fallback) {
    var n = Math.round(Number(value));
    if (!isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, n));
  }

  function toFiniteNumber(raw) {
    if (typeof raw === 'number') return isFinite(raw) ? raw : null;
    if (typeof raw === 'string' && raw.trim() !== '') {
      var parsed = Number(raw);
      return isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  function toPositiveInt(raw) {
    var value = toFiniteNumber(raw);
    if (value === null || Math.floor(value) !== value || value < 1) return null;
    return value;
  }

  function isRecord(raw) {
    return raw !== null && typeof raw === 'object' && !Array.isArray(raw);
  }

  /** The colour rule of the shop-the-look contract; null when invalid or empty. */
  var CSS_COLOUR = /^(?:#[0-9a-f]{3,8}|(?:rgb|hsl)a?\([0-9%.,\s\/deg]+\)|[a-z]{3,24})$/i;
  function cssColour(raw) {
    var colour = typeof raw === 'string' ? raw.trim() : '';
    return colour && CSS_COLOUR.test(colour) ? colour : null;
  }

  function stripTags(html) {
    return String(html == null ? '' : html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  // ---- 22. grid ----------------------------------------------
  // content: columns (2..12), columnSizes[] / rowSizes[] (fr weights), cells
  // [{id, colSpan, rowSpan, mode: free|card, colStart?, rowStart?, image,
  // cropX/Y, title, description, url, openInNewTab}], cardOverlay
  // (none|gradient|solid), cardOverlayOpacity, cardRadius, cellRadius,
  // cardMinHeight, preset. Port of utils/gridModel.ts + GridRenderer.tsx:
  //   - a child finds its cell through `content.columnIndex` (clamped), the
  //     same key the columns element uses; a free cell renders its children
  //     through renderChild, so text/image/news-list nest as anywhere else;
  //   - cells are placed by SPAN ONLY and flow (upstream's gridCellPlacement
  //     stopped emitting start lines); colStart/rowStart are read for the
  //     row count, which sizes the row tracks as minmax(160px * fr, auto)
  //     unless `rowSizing: 'content'` asks for `auto` tracks + align-items
  //     start instead (see the renderer); the key is ABSENT from every
  //     document authored before it existed and absent keeps the old floor,
  //     so no published page moves;
  //   - a card cell is image + overlay + title/description with one link
  //     over the whole surface, cardRadius / cardMinHeight on it and the
  //     grid-wide cellRadius on every cell.
  // Mobile (<750px): one column, spans neutralised, in DOM order, which for
  // span-only flow IS reading order (assets/content-builder-neo.css).
  // Cell content that cannot render leaves an empty cell, never removes a
  // sibling: the buckets are built before anything is rendered.
  var GRID_ROW_UNIT_PX = 160;
  var GRID_MAX_ROW_SPAN = 6;
  var GRID_MAX_ROWS = 40;

  function clampFr(value) {
    var n = Number(value);
    if (!isFinite(n) || n <= 0) return 1;
    return Math.max(0.3, Math.round(n * 100) / 100);
  }

  function normalizeSizes(raw, length) {
    var source = Array.isArray(raw) ? raw : [];
    var out = [];
    for (var i = 0; i < length; i++) out.push(i < source.length ? clampFr(source[i]) : 1);
    return out;
  }

  var GRID_CELL_TYPES = ['news', 'image', 'video', 'banner'];

  /**
   * The filter row. An option with neither a category nor tags is the "all"
   * entry, and an options list with nothing usable in it disables the row: a
   * dropdown with one dead entry is worse than no dropdown.
   */
  function normalizeGridFilter(raw) {
    var src = isRecord(raw) ? raw : {};
    var rawOptions = Array.isArray(src.options) ? src.options : [];
    var options = [];
    for (var i = 0; i < rawOptions.length; i++) {
      var opt = isRecord(rawOptions[i]) ? rawOptions[i] : {};
      var label = typeof opt.label === 'string' ? opt.label.trim() : '';
      if (!label) continue;
      options.push({
        id: typeof opt.id === 'string' && opt.id ? opt.id : 'opt-' + i,
        label: label,
        categoryId: toPositiveInt(opt.categoryId) || 0,
        tagNames: stringListOf(opt.tagNames)
      });
    }
    return {
      enabled: src.enabled === true && options.length > 0,
      options: options,
      // p56 native fields. `style` stays '' when the document does not say, so
      // a grid without it keeps whatever its renderer decided before.
      style: (src.style === 'menu' || src.style === 'chips') ? src.style : '',
      menuLabel: typeof src.menuLabel === 'string' ? src.menuLabel.trim() : ''
    };
  }

  function normalizeGridCell(raw, index, columns) {
    var src = isRecord(raw) ? raw : {};
    var cell = {
      id: (typeof src.id === 'string' && src.id) ? src.id : 'cell-' + index,
      colSpan: clampInt(src.colSpan, 1, columns, 1),
      rowSpan: clampInt(src.rowSpan, 1, GRID_MAX_ROW_SPAN, 1),
      mode: src.mode === 'card' ? 'card' : 'free'
    };
    if (src.colStart !== undefined) cell.colStart = clampInt(src.colStart, 0, columns - 1, 0);
    if (src.rowStart !== undefined) cell.rowStart = clampInt(src.rowStart, 0, GRID_MAX_ROWS - 1, 0);
    if (typeof src.image === 'string') cell.image = src.image;
    cell.cropX = src.cropX !== undefined ? clampInt(src.cropX, 0, 100, 50) : 50;
    cell.cropY = src.cropY !== undefined ? clampInt(src.cropY, 0, 100, 50) : 50;
    if (typeof src.title === 'string') cell.title = src.title;
    if (typeof src.description === 'string') cell.description = src.description;
    if (typeof src.url === 'string') cell.url = src.url;
    cell.openInNewTab = !!src.openInNewTab;
    // What THIS square holds, overriding the grid's own answer. `banner` takes
    // no child at all: it is the card plus a button whose target is the card's
    // url, which is why an empty buttonLabel has to mean "no button" rather
    // than "a button with no words on it".
    cell.cellType = GRID_CELL_TYPES.indexOf(src.cellType) === -1 ? '' : src.cellType;
    cell.buttonLabel = typeof src.buttonLabel === 'string' ? src.buttonLabel.trim() : '';
    return cell;
  }

  /** gridModel.ts resolvePlacements without the editor's priority/vacated
   *  arguments: explicit starts when free, else sparse row-major auto-flow. */
  function resolveGridPlacements(cells, columns) {
    var occupied = {};
    function isFree(col0, row0, colSpan, rowSpan) {
      for (var r = row0; r < row0 + rowSpan; r++) {
        for (var c = col0; c < col0 + colSpan; c++) if (occupied[r + ',' + c]) return false;
      }
      return true;
    }
    function take(col0, row0, colSpan, rowSpan) {
      for (var r = row0; r < row0 + rowSpan; r++) {
        for (var c = col0; c < col0 + colSpan; c++) occupied[r + ',' + c] = true;
      }
    }
    var cursorRow = 0;
    var cursorCol = 0;
    return cells.map(function (cell) {
      var colSpan = Math.min(Math.max(1, cell.colSpan), columns);
      var rowSpan = Math.min(Math.max(1, cell.rowSpan), GRID_MAX_ROW_SPAN);
      var placed = { colSpan: colSpan, rowSpan: rowSpan };
      if (cell.colStart !== undefined && cell.rowStart !== undefined) {
        var col0 = Math.min(Math.max(0, cell.colStart), columns - colSpan);
        var row0 = Math.min(Math.max(0, cell.rowStart), GRID_MAX_ROWS - rowSpan);
        if (isFree(col0, row0, colSpan, rowSpan)) {
          take(col0, row0, colSpan, rowSpan);
          placed.colStart = col0;
          placed.rowStart = row0;
          return placed;
        }
      }
      for (;;) {
        if (cursorCol + colSpan > columns) { cursorRow += 1; cursorCol = 0; continue; }
        if (!isFree(cursorCol, cursorRow, colSpan, rowSpan)) { cursorCol += 1; continue; }
        take(cursorCol, cursorRow, colSpan, rowSpan);
        placed.colStart = cursorCol;
        placed.rowStart = cursorRow;
        cursorCol += colSpan;
        return placed;
      }
    });
  }

  function normalizeGridContent(raw) {
    var src = isRecord(raw) ? raw : {};
    var columns = clampInt(src.columns, 2, 12, 3);
    var rawCells = Array.isArray(src.cells) ? src.cells : [];
    var cells = [];
    if (rawCells.length) {
      for (var i = 0; i < rawCells.length; i++) cells.push(normalizeGridCell(rawCells[i], i, columns));
    } else {
      for (var c = 0; c < columns; c++) cells.push(normalizeGridCell({ id: 'cell-' + c }, c, columns));
    }
    var placements = resolveGridPlacements(cells, columns);
    var rows = 1;
    for (var p = 0; p < placements.length; p++) rows = Math.max(rows, placements[p].rowStart + placements[p].rowSpan);
    return {
      columns: columns,
      cells: cells,
      columnSizes: normalizeSizes(src.columnSizes, columns),
      rowSizes: normalizeSizes(src.rowSizes, rows),
      cardOverlay: (src.cardOverlay === 'none' || src.cardOverlay === 'solid') ? src.cardOverlay : 'gradient',
      cardOverlayOpacity: clampInt(src.cardOverlayOpacity, 0, 100, 55),
      cardRadius: clampInt(src.cardRadius, 0, 64, 12),
      cellRadius: clampInt(src.cellRadius, 0, 64, 12),
      cardMinHeight: clampInt(src.cardMinHeight, 80, 900, 220),
      cellType: GRID_CELL_TYPES.indexOf(src.cellType) === -1 ? '' : src.cellType,
      // How tall a ROW is. '' (the key absent, which is every document authored
      // so far) is the fixed minmax(fr * 160px, auto) this element has always
      // emitted. 'content' sizes every row to what is in it. See the §22 note.
      rowSizing: src.rowSizing === 'content' ? 'content' : '',
      filter: normalizeGridFilter(src.filter),
      mobileLayout: src.mobileLayout === 'carousel' ? 'carousel' : 'stack',
      // p56 native fields; '' = absent = the look before they existed.
      cardOverlayColor: gridHexOrEmpty(src.cardOverlayColor),
      cardTextAlign: (src.cardTextAlign === 'center' || src.cardTextAlign === 'left') ? src.cardTextAlign : '',
      cardTitleSize: typeof src.cardTitleSize === 'string' && /^[^;{}<>"]+$/.test(src.cardTitleSize.trim())
        ? src.cardTitleSize.trim() : ''
    };
  }

  /** A `#rgb` / `#rrggbb` colour field, or '' (absent or unusable). */
  function gridHexOrEmpty(raw) {
    var v = typeof raw === 'string' ? raw.trim() : '';
    return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v) ? v : '';
  }

  /**
   * Whether a grid is authored with the p56 NATIVE layout fields — the ones the
   * aiconeo builder draws itself (news-list `cardLayout`, youtube `display`,
   * grid `filter.style`). Such a grid follows its own tracks and its children's
   * own fields, so every className-keyed skin (the homepage `.hm-news` collage:
   * flex rows, Figma ratios, overlay cards, dropdown, LS TV poster) stands down
   * for it: the renderer marks it `data-neo-native-layout` and both the CSS and
   * the `isHomeNews*` checks below key off that. A document without any of the
   * fields renders exactly as before.
   */
  function gridIsNativeLayout(el) {
    var content = isRecord(el && el.content) ? el.content : {};
    if (isRecord(content.filter) && typeof content.filter.style === 'string' && content.filter.style) return true;
    var children = Array.isArray(el && el.children) ? el.children : [];
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      var c = isRecord(child && child.content) ? child.content : {};
      if (child && child.type === 'news-list' && typeof c.cardLayout === 'string' && c.cardLayout) return true;
      if (child && child.type === 'youtube' && typeof c.display === 'string' && c.display) return true;
    }
    return false;
  }

  /** gridModel.ts cellIndexOf: `content.columnIndex`, clamped, never NaN. */
  function gridCellIndexOf(child, cellCount) {
    var raw = Number(child && child.content && child.content.columnIndex != null ? child.content.columnIndex : 0);
    if (!isFinite(raw)) return 0;
    return Math.min(Math.max(0, Math.floor(raw)), Math.max(0, cellCount - 1));
  }

  /**
   * The card title's typography. A property the GRID's style bag sets
   * (desktop or mobile tier) is inherited from the grid wrapper, where those
   * values already land, so the builder's typography controls reach the card
   * title; `cardTitleSize` sets its size. Nothing set = the stylesheet's title.
   */
  function gridCardTitleStyle(gridEl, grid) {
    var desktop = pickStyles(gridEl, 'desktop');
    var mobile = pickStyles(gridEl, 'mobile');
    var out = {};
    var keys = ['fontFamily', 'fontWeight', 'textTransform', 'letterSpacing'];
    for (var i = 0; i < keys.length; i++) {
      if (desktop[keys[i]] || mobile[keys[i]]) out[keys[i]] = 'inherit';
    }
    if (grid.cardTitleSize) out.fontSize = grid.cardTitleSize;
    return out;
  }

  function gridCardHtml(cell, grid, titleStyle) {
    var href = safeHref(cell.url);
    var tag = href ? 'a' : 'div';
    var attrs = href
      ? ' href="' + escapeAttr(href) + '"' + (cell.openInNewTab ? ' target="_blank" rel="noopener noreferrer"' : '')
      : '';
    var cardClass = 'neo-grid__card' + (grid.cardTextAlign === 'center' ? ' neo-grid__card--center' : '');
    var html = '<' + tag + ' class="' + cardClass + '"' + attrs +
      styleAttrOf({ borderRadius: grid.cardRadius + 'px', minHeight: grid.cardMinHeight + 'px' }) + '>';
    html += cell.image
      ? '<img class="neo-grid__card-img" src="' + escapeAttr(cell.image) + '" alt="' + escapeAttr(cell.title || '') +
        '" loading="lazy"' + styleAttrOf({ objectPosition: cell.cropX + '% ' + cell.cropY + '%' }) + '>'
      : '<span class="neo-grid__card-img neo-grid__card-img--empty" aria-hidden="true"></span>';
    if (grid.cardOverlay !== 'none') {
      html += '<span class="neo-grid__card-overlay neo-grid__card-overlay--' + grid.cardOverlay + '" aria-hidden="true"' +
        styleAttrOf({
          opacity: grid.cardOverlayOpacity / 100,
          '--neo-card-overlay-rgb': grid.cardOverlayColor ? imageOverlayRgb(grid.cardOverlayColor) : undefined
        }) + '></span>';
    }
    html += '<span class="neo-grid__card-body">';
    if (cell.title) html += '<h3 class="neo-grid__card-title"' + styleAttrOf(titleStyle || {}) + '>' + escapeHtml(cell.title) + '</h3>';
    if (cell.description) html += '<p class="neo-grid__card-text">' + escapeHtml(cell.description) + '</p>';
    // The banner's button. It is a span, not a nested anchor: the whole card
    // is already the link, and an <a> inside an <a> is invalid markup that
    // browsers silently unnest, which breaks the card's own click target.
    if (cell.buttonLabel) {
      html += '<span class="neo-grid__card-button">' + escapeHtml(cell.buttonLabel) + '</span>';
    }
    html += '</span>';
    return html + '</' + tag + '>';
  }

  Neo.registerType('grid', function (el) {
    var grid = normalizeGridContent(el.content);
    var native = gridIsNativeLayout(el);
    var homeSkin = !native && isHomeNewsGrid(el);
    // The filter as a menu: the document's own `filter.style`, else (legacy)
    // the homepage collage's className.
    var filterMenu = grid.filter.style ? grid.filter.style === 'menu' : homeSkin;
    // The chip row is an in-flow grid item spanning every column. It gets its
    // OWN auto track, so it never eats the first authored row floor and the
    // cells keep the rows they were authored in. (Only for a native grid: the
    // legacy chip row is left exactly where it was.)
    var chipTrack = native && grid.filter.enabled && !filterMenu;
    var children = Array.isArray(el.children) ? el.children : [];
    var buckets = [];
    for (var b = 0; b < grid.cells.length; b++) buckets.push([]);
    for (var k = 0; k < children.length; k++) {
      buckets[gridCellIndexOf(children[k], grid.cells.length)].push(children[k]);
    }

    // Row tracks. `rowSizing: 'content'` swaps the fixed floor for `auto` and
    // tops the cells out; anything else keeps minmax(fr * 160px, auto), which
    // is what every document written before the key existed renders as. The
    // TRACK COUNT is the same either way, chip row included: the builder's
    // resize overlay measures the resolved template and maps track index to row
    // index one-for-one, so a template of a different shape would put every row
    // handle one row off rather than fail.
    var contentRows = grid.rowSizing === 'content';
    var html = viewportStyleTags(el) + wrapperOpen(el, 'content-type-neo-grid', {
      gridTemplateColumns: grid.columnSizes.map(function (s) { return s + 'fr'; }).join(' '),
      gridTemplateRows: (chipTrack ? 'auto ' : '') + grid.rowSizes.map(function (s) {
        return contentRows ? 'auto' : 'minmax(' + Math.round(s * GRID_ROW_UNIT_PX) + 'px, auto)';
      }).join(' '),
      // The other half, and it is not optional. `auto` rows still take the
      // height of the TALLEST cell in the row, and the default `stretch` then
      // hands that height to every shorter cell in it, so the empty band
      // survives: it has only moved from the track to the cell. '' is dropped
      // by stylesToInline, so a fixed grid emits no align-items at all and its
      // markup is byte-identical to before.
      alignItems: contentRows ? 'start' : ''
    }, ' data-grid-mobile="' + grid.mobileLayout + '"' +
      (contentRows ? ' data-grid-rows="content"' : '') +
      (native ? ' data-neo-native-layout' : ''));

    // `filter.style: 'menu'` (or, legacy, the homepage collage's className)
    // wants the theme section's own dropdown, not the generic chip row: same
    // options, same behaviour, wrapped in a disclosure.
    if (grid.filter.enabled) html += gridFilterHtml(grid.filter, filterMenu);
    // The carousel wrapper is `display: contents` on every tier except the
    // phone, so on desktop the cells stay direct children of the grid and the
    // tracks still apply. It exists so the filter row can sit above a row that
    // scrolls, instead of scrolling away with the cards.
    var scrolls = grid.mobileLayout === 'carousel';
    if (scrolls) html += '<div class="neo-grid__scroller">';

    // Children of the homepage collage opt into the theme's own card formats
    // (see §22c: a `youtube` cell there is a poster card, not an iframe).
    // A native grid switches the skin OFF for everything inside it, whatever
    // classNames its children still carry.
    var outerSkin = gridSkinContext;
    if (native) gridSkinContext = 'native';
    else if (homeSkin) gridSkinContext = 'hm';
    // §35 (h): a cell that took a positioned child is a canvas, and the grid's
    // own host height goes on the grid root so the tracks divide it.
    var cellPlaced = false;
    var anyCellPlaced = false;
    try {
      for (var i = 0; i < grid.cells.length; i++) {
        var cell = grid.cells[i];
        var place = {
          gridColumn: cell.colSpan > 1 ? 'span ' + cell.colSpan : undefined,
          gridRow: cell.rowSpan > 1 ? 'span ' + cell.rowSpan : undefined,
          borderRadius: grid.cellRadius ? grid.cellRadius + 'px' : undefined
        };
        var isCard = cell.mode === 'card';
        var cellType = cell.cellType || grid.cellType || '';
        var classes = 'neo-grid__cell neo-grid__cell--' + (isCard ? 'card' : 'free');
        if (cellType) classes += ' neo-grid__cell--' + cellType;
        // §35 (h): the canvas is the CELL, and only a cell that actually took
        // a positioned child is marked as one, so the cell's own tag is built
        // AFTER its children, the same ordering the container renderer uses.
        var cellInner = '';
        cellPlaced = false;
        if (isCard) {
          cellInner = gridCardHtml(cell, grid, gridCardTitleStyle(el, grid));
        } else {
          for (var j = 0; j < buckets[i].length; j++) {
            var cellChild = renderFreeChild(buckets[i][j]);
            cellInner += cellChild.html;
            if (cellChild.placed) cellPlaced = true;
          }
        }
        html += '<div class="' + classes + '"' +
          (cellPlaced ? freeformCanvasMark() : '') +
          ' data-neo-grid-cell="' + i + '"' +
          (cellType ? ' data-grid-cell-type="' + escapeAttr(cellType) + '"' : '') +
          styleAttrOf(place) + '>';
        html += cellInner;
        html += '</div>';
        if (cellPlaced) anyCellPlaced = true;
      }
    } finally {
      gridSkinContext = outerSkin;
    }
    if (scrolls) html += '</div>';
    if (anyCellPlaced) freeformTrackHostHeight(el);
    return html + '</div>';
  });

  /**
   * The filter row. The options are buttons rather than a <select> because the
   * collage's dropdown is styled well past what a native control allows, and
   * because an option carries two different narrowings (a category id, or a
   * list of tag names) that a select's single value cannot hold.
   */
  /**
   * The label an option shows.
   *
   * The authored label travels in the document (the builder needs something to
   * draw), but this theme already translates the same vocabulary everywhere
   * else, so a recognised option is re-labelled from locales/*.json and a
   * visitor on /en reads "First team", not "Équipe première". An option the
   * theme does not recognise keeps what the author typed.
   */
  function gridFilterLabel(option) {
    var ids = window.newsCategoryIds || {};
    var id = String(option.categoryId || '');
    if (!id && !option.tagNames.length) return lsT('news.all_news', 'Toutes les news');
    if (id && id === String(ids.firstTeam || '1')) return lsT('team.first_team', 'Équipe première');
    if (id && id === String(ids.women || '3')) return lsT('team.women', 'LS Féminine');
    if (id && id === String(ids.academy || '2')) return lsT('team.academy', 'Académie');
    if (id && ids.events && id === String(ids.events)) return lsT('news.badge_events', 'Événements');
    if (id && ids.csr && id === String(ids.csr)) return lsT('news.badge_csr', 'RSE');
    if (id && ids.partners && id === String(ids.partners)) return lsT('news.badge_partners', 'Partenaires');
    if (!id && option.tagNames.length === 1 && option.tagNames[0].toLowerCase() === 'club') {
      return lsT('common.club', 'Club');
    }
    return option.label;
  }

  var GRID_FILTER_CHEVRON =
    '<svg class="neo-grid__filter-chevron" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M2.5 5l4.5 4.5L11.5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /**
   * The filter row. The options are buttons rather than a <select> because the
   * collage's dropdown is styled well past what a native control allows, and
   * because an option carries two different narrowings (a category id, or a
   * list of tag names) that a select's single value cannot hold.
   *
   * `asMenu` folds the SAME buttons into a disclosure — the theme section's own
   * "Filtrer par" control, which is what the homepage collage has always shown
   * and what its heading row has room for. The buttons, their data attributes
   * and their aria-pressed state are untouched, so enhanceGridFilter below
   * drives both forms without knowing which one it is looking at.
   */
  function gridFilterHtml(filter, asMenu) {
    var options = '';
    for (var i = 0; i < filter.options.length; i++) {
      var opt = filter.options[i];
      options += '<button type="button" class="neo-grid__filter-option" ' +
        'data-neo-grid-filter-option="' + escapeAttr(opt.id) + '" ' +
        'data-neo-grid-filter-category="' + escapeAttr(String(opt.categoryId || '')) + '" ' +
        'data-neo-grid-filter-tags="' + escapeAttr(JSON.stringify(opt.tagNames)) + '" ' +
        'aria-pressed="' + (i === 0 ? 'true' : 'false') + '">' +
        escapeHtml(asMenu ? gridFilterLabel(opt) : opt.label) + '</button>';
    }
    if (!asMenu) return '<div class="neo-grid__filter" role="group">' + options + '</div>';
    var resting = gridFilterMenuLabel(filter.menuLabel);
    var fallback = lsT('news.filter_by', 'Filtrer par');
    return '<div class="neo-grid__filter neo-grid__filter--menu" role="group" data-neo-grid-filter-menu' +
      (resting !== fallback ? ' data-neo-grid-filter-resting="' + escapeAttr(resting) + '"' : '') + '>' +
      '<button type="button" class="neo-grid__filter-btn" aria-haspopup="true" aria-expanded="false">' +
      '<span class="visually-hidden">' + escapeHtml(lsT('news.category_label', 'Catégorie :')) + '</span>' +
      '<span class="neo-grid__filter-label">' + escapeHtml(resting) + '</span>' +
      GRID_FILTER_CHEVRON + '</button>' +
      '<div class="neo-grid__filter-panel" hidden>' + options + '</div></div>';
  }

  /**
   * The menu's resting label: `filter.menuLabel`, else the theme's own
   * "Filtrer par". The theme's own words typed in (either language) are
   * re-labelled from locales like the options are, so /en reads "Filter by".
   */
  function gridFilterMenuLabel(menuLabel) {
    var fallback = lsT('news.filter_by', 'Filtrer par');
    var label = typeof menuLabel === 'string' ? menuLabel.trim() : '';
    if (!label) return fallback;
    var key = label.toLowerCase().replace(/[\s\u00a0:]+/g, ' ').trim();
    return (key === 'filtrer par' || key === 'filter by') ? fallback : label;
  }

  /**
   * Wire a grid's filter row to the news blocks inside it. The choice re-runs
   * every news query in THAT grid and nothing else on the page: a second grid
   * with its own filter has to stay where the visitor left it.
   */
  function enhanceGridFilter(gridEl) {
    if (gridEl.getAttribute('data-neo-grid-filter-ready')) return;
    var row = gridEl.querySelector('.neo-grid__filter');
    if (!row) return;
    gridEl.setAttribute('data-neo-grid-filter-ready', '1');

    row.addEventListener('click', function (event) {
      var button = event.target && event.target.closest
        ? event.target.closest('.neo-grid__filter-option')
        : null;
      if (!button || !row.contains(button)) return;

      var options = row.querySelectorAll('.neo-grid__filter-option');
      for (var i = 0; i < options.length; i++) {
        options[i].setAttribute('aria-pressed', options[i] === button ? 'true' : 'false');
      }

      var categoryId = toPositiveInt(button.getAttribute('data-neo-grid-filter-category')) || 0;
      var tagNames = [];
      try { tagNames = JSON.parse(button.getAttribute('data-neo-grid-filter-tags') || '[]'); } catch (err) { tagNames = []; }
      var override = (categoryId || tagNames.length) ? { categoryId: categoryId, tagNames: tagNames } : null;

      var hosts = gridEl.querySelectorAll('[data-neo-news-source="1"]');
      for (var h = 0; h < hosts.length; h++) runNewsQuery(hosts[h], override);
    });
  }

  /**
   * The disclosure around the SAME options, for the collage's "Filtrer par"
   * control. enhanceGridFilter above still owns the click that re-runs the
   * queries (the panel is inside the row, so the event reaches it); this only
   * opens and closes the panel and keeps the trigger's label in step. The
   * "all" entry restores the resting label rather than naming itself, which is
   * what the theme section does.
   */
  function enhanceGridFilterMenu(gridEl) {
    var menu = gridEl.querySelector('[data-neo-grid-filter-menu]');
    if (!menu || menu.getAttribute('data-neo-grid-filter-menu-ready')) return;
    var button = menu.querySelector('.neo-grid__filter-btn');
    var panel = menu.querySelector('.neo-grid__filter-panel');
    var label = menu.querySelector('.neo-grid__filter-label');
    if (!button || !panel) return;
    menu.setAttribute('data-neo-grid-filter-menu-ready', '1');
    var resting = menu.getAttribute('data-neo-grid-filter-resting') || lsT('news.filter_by', 'Filtrer par');

    function close(refocus) {
      panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      if (refocus) button.focus();
    }

    button.addEventListener('click', function () {
      var open = panel.hidden;
      panel.hidden = !open;
      button.setAttribute('aria-expanded', String(open));
    });

    panel.addEventListener('click', function (event) {
      var option = event.target && event.target.closest
        ? event.target.closest('.neo-grid__filter-option')
        : null;
      if (!option) return;
      if (label) {
        var isAll = !(option.getAttribute('data-neo-grid-filter-category') || '') &&
          (option.getAttribute('data-neo-grid-filter-tags') || '[]') === '[]';
        label.textContent = isAll ? resting : option.textContent.trim();
      }
      close(true);
    });

    document.addEventListener('click', function (event) {
      if (!menu.contains(event.target)) close(false);
    });
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || panel.hidden) return;
      close(true);
    });
  }

  /** A block the query left with nothing in it, so the grid can react. */
  function newsSyncEmptiness(host) {
    var cards = host.querySelectorAll('.neo-news__card:not(.neo-news__card--placeholder)');
    if (cards.length) host.removeAttribute('data-neo-news-empty');
    else host.setAttribute('data-neo-news-empty', '1');
    syncGridEmptiness(host.closest('.content-type-neo-grid'));
  }

  /**
   * A category with no article at all: the news cells are hidden and one line
   * takes their place, exactly like the theme section. Cells that are not news
   * — the video card, the banner — are never touched, which is the whole point
   * of hiding cells rather than the grid.
   */
  function syncGridEmptiness(gridEl) {
    if (!gridEl) return;
    var hosts = gridEl.querySelectorAll('[data-neo-news-source="1"]');
    if (!hosts.length) return;
    var allEmpty = true;
    for (var i = 0; i < hosts.length; i++) {
      var empty = hosts[i].hasAttribute('data-neo-news-empty');
      if (!empty) allEmpty = false;
      var cell = hosts[i].closest('.neo-grid__cell');
      if (!cell) continue;
      if (empty) cell.setAttribute('data-neo-cell-empty', '1');
      else cell.removeAttribute('data-neo-cell-empty');
    }
    var line = gridEl.__neoGridEmptyLine;
    if (allEmpty) {
      if (!line) {
        line = document.createElement('p');
        line.className = 'neo-grid__empty';
        line.textContent = lsT('news.no_news_in_category', 'Aucune actualité dans cette catégorie.');
        gridEl.appendChild(line);
        gridEl.__neoGridEmptyLine = line;
      }
      line.hidden = false;
    } else if (line) {
      line.hidden = true;
    }
  }

  function enhanceGridFilters(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var grids = scope.querySelectorAll('.content-type-neo-grid');
    for (var i = 0; i < grids.length; i++) {
      enhanceGridFilter(grids[i]);
      enhanceGridFilterMenu(grids[i]);
    }
  }

  // ---- 23. news-list -----------------------------------------
  // content: newsItems[] ({id, title, excerpt, imageUrl, publishDate, author,
  // categoryName, urlHandle, locale, fetchedAt} snapshots captured at pick
  // time), columns (1..6, default 3), showImage / showDate / showExcerpt
  // (default true), showCategory (default false), linkBase.
  // Article links take the THEME's shape, not linkBase: the news page route
  // plus the `?view=news` fallback every other news list in the theme
  // appends (snippets/global-variables.liquid newsPageHandle/newsViewParam),
  // so a card can never 404 on a guessed path. Dates read like the theme's
  // listings ("2 septembre 2026", uppercased by CSS) in the request locale.
  // Snapshot first; an element WITHOUT one that names a source (an aico news
  // category id, optionally a limit) is filled at hydrate from the CDN mirror
  // the way snippets/deep-scroll-news-js.liquid does. Neither: renders nothing.
  function newsBasePath() {
    var base = (typeof newsPageHandle !== 'undefined' && newsPageHandle) || '/pages/news';
    return String(base).replace(/\/+$/, '');
  }

  /** newsLink.ts languageOf: two letters out of a locale key. `fr_CH` -> `fr`. */
  function newsLanguageOf(locale) {
    if (!locale) return '';
    return String(locale).split(/[-_]/)[0].toLowerCase();
  }

  /**
   * newsLink.ts buildNewsHref, ported token for token so the card points where
   * the builder canvas says it points. `{urlHandle} {lang} {locale}
   * {langPrefix}`, and an unknown token is left standing rather than emptied:
   * a typo has to show up as one broken link, not as a page of cards that all
   * lead to the same wrong place.
   *
   * `{langPrefix}` is empty for the shop's own language. The theme knows that
   * as Shopify.routes.root, which is `/` on the primary language and `/en/` on
   * any other, so the prefix comes from there rather than from a locale
   * comparison the storefront cannot make.
   *
   * Deviation from the contract, kept from this file's original renderer: with
   * no template and no base, upstream draws a card that does not link, while
   * this falls back to the theme's own news route plus `newsViewParam`. A
   * guessed path is what ships 404s; this path is the one every other news
   * listing in the theme already uses, so it cannot be wrong.
   */
  function newsTemplateHref(handle, opts) {
    var urlHandle = String(handle == null ? '' : handle).trim();
    if (!urlHandle) return '';
    var options = opts || {};
    var locale = options.locale || (typeof currentLang !== 'undefined' ? currentLang : '');
    var language = newsLanguageOf(locale);

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var langPrefix = String(root).replace(/\/+$/, '');

    var template = String(options.template == null ? '' : options.template).trim();
    if (template) {
      return template
        .replace(/\{urlHandle\}/g, urlHandle)
        .replace(/\{langPrefix\}/g, langPrefix)
        .replace(/\{locale\}/g, locale || '')
        .replace(/\{lang\}/g, language);
    }

    var base = String(options.linkBase == null ? '' : options.linkBase).trim().replace(/\/+$/, '');
    if (base) return base + '/' + encodeURIComponent(urlHandle);
    return '';
  }

  function newsHref(handle, opts) {
    if (!handle) return '';
    var templated = newsTemplateHref(handle, opts);
    if (templated) return templated;
    return newsBasePath() + '/' + encodeURIComponent(String(handle)) + (window.newsViewParam || '');
  }

  function newsDateLabel(publishDate) {
    if (!publishDate) return '';
    var d = new Date(String(publishDate).trim().replace(' ', 'T'));
    if (isNaN(d)) return '';
    var months = lsT('common.month_names',
      'janvier, février, mars, avril, mai, juin, juillet, août, septembre, octobre, novembre, décembre').split(/,\s*/);
    return d.getDate() + ' ' + (months[d.getMonth()] || '') + ' ' + d.getFullYear();
  }

  /**
   * The file a card should paint.
   *
   * `imageUrl` is aiconeo's pick — `optimizedListImage` first, because a card in
   * a grid cell IS a list row and a print-resolution hero in a 300px tile is
   * wasted bytes. The collage is the exception: its lead and wide cards are up
   * to 1350px across, so it takes the theme section's own order
   * (`optimizedImage || image || …ListImage`, snippets/news-collage-js.liquid).
   * `flags.large` is set by the renderer for collage cards only.
   */
  function newsCardImage(item, flags) {
    if (flags && flags.large && item.imageUrlLarge) return item.imageUrlLarge;
    return item.imageUrl || item.imageUrlLarge || '';
  }

  /** A focal percentage (0..100) or null; accepts numbers and numeric strings. */
  function newsFocusPct(value) {
    if (value == null || value === '') return null;
    var n = Number(value);
    if (!isFinite(n)) return null;
    return Math.max(0, Math.min(100, Math.round(n * 100) / 100));
  }

  /**
   * The editor's focal point as `object-position` (142609): desktop pair on
   * `--neo-news-focus`, phone pair on `--neo-news-focus-m`, both read by
   * content-builder-neo.css. Every variant aico serves (image, optimized,
   * optimized_list) is the same 16:9 frame, so the percentages hold for all of
   * them. Two key spellings: the API row's `focusXPercentage` family and the
   * aiconeo snapshot's `focusX` family. Nothing set = no style = centre.
   */
  function newsFocusStyle(item) {
    var x = newsFocusPct(item.focusX != null ? item.focusX : item.focusXPercentage);
    var y = newsFocusPct(item.focusY != null ? item.focusY : item.focusYPercentage);
    if (x == null || y == null) return '';
    var mx = newsFocusPct(item.mobileFocusX != null ? item.mobileFocusX : item.mobileFocusXPercentage);
    var my = newsFocusPct(item.mobileFocusY != null ? item.mobileFocusY : item.mobileFocusYPercentage);
    var style = '--neo-news-focus:' + x + '% ' + y + '%';
    if (mx != null && my != null) style += ';--neo-news-focus-m:' + mx + '% ' + my + '%';
    return style;
  }

  function newsCardHtml(item, flags, linkOpts) {
    if (!isRecord(item)) return '';
    var handle = item.urlHandle || (item.id != null ? String(item.id) : '');
    var href = newsHref(handle, linkOpts);
    var title = item.title ? String(item.title) : '';
    // `badge` is what a LIVE row resolved through window.aicoNewsBadgeLabel
    // (the theme's localised tag/category chip); an authored snapshot only has
    // the raw category name, so it still falls back to that.
    var category = item.badge ? String(item.badge) : (item.categoryName ? String(item.categoryName) : '');
    var inner = '';
    if (flags.image) {
      var image = newsCardImage(item, flags);
      var focusStyle = newsFocusStyle(item);
      inner += '<span class="neo-news__media">' +
        (image ? '<img class="neo-news__img" src="' + escapeAttr(image) + '" alt="" loading="lazy"' +
          (focusStyle ? ' style="' + focusStyle + '"' : '') + '>' : '') +
        (flags.category && category ? '<span class="neo-news__badge">' + escapeHtml(category) + '</span>' : '') +
        '</span>';
    }
    inner += '<span class="neo-news__body">';
    var date = flags.date ? newsDateLabel(item.publishDate) : '';
    if (date || (flags.category && !flags.image && category)) {
      inner += '<span class="neo-news__meta">';
      if (date) {
        inner += '<time class="neo-news__date" datetime="' + escapeAttr(String(item.publishDate).slice(0, 10)) + '">' +
          escapeHtml(date) + '</time>';
      }
      if (flags.category && !flags.image && category) inner += '<span class="neo-news__category">' + escapeHtml(category) + '</span>';
      inner += '</span>';
    }
    inner += '<h3 class="neo-news__title">' + escapeHtml(title || lsT('news.untitled', 'Sans titre')) + '</h3>';
    if (flags.excerpt && item.excerpt) inner += '<p class="neo-news__excerpt">' + escapeHtml(stripTags(item.excerpt)) + '</p>';
    inner += '</span>';
    return '<article class="neo-news__card"' +
      (item.id != null ? ' data-neo-news-id="' + escapeAttr(String(item.id)) + '"' : '') + '>' +
      (href ? '<a class="neo-news__link" href="' + escapeAttr(href) + '">' : '<span class="neo-news__link">') +
      inner + (href ? '</a>' : '</span>') + '</article>';
  }

  function newsFlagsOf(c) {
    return {
      image: c.showImage !== false,
      date: c.showDate !== false,
      excerpt: c.showExcerpt !== false,
      category: c.showCategory === true
    };
  }

  /** The link shape an element carries. Both fields are optional. */
  function newsLinkOptsOf(c) {
    return {
      template: c && c.linkTemplate ? String(c.linkTemplate) : '',
      linkBase: c && c.linkBase ? String(c.linkBase) : ''
    };
  }

  function intListOf(raw) {
    if (!Array.isArray(raw)) return [];
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var n = toPositiveInt(raw[i]);
      if (n) out.push(n);
    }
    return out;
  }

  function stringListOf(raw) {
    if (!Array.isArray(raw)) return [];
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var v = raw[i] == null ? '' : String(raw[i]).trim();
      if (v) out.push(v);
    }
    return out;
  }

  /**
   * A live source, when the element asks for one instead of carrying only
   * snapshots. Two shapes are accepted on purpose:
   *
   *   the contract shape, `source: 'latest'` with a `query` object, which is
   *   what the builder writes now;
   *
   *   the older flat one (`categoryId` / `limit`) that documents already on
   *   this shop were saved with. Dropping it would blank those blocks.
   *
   * `tagIds` is deliberately ignored here. Tags are stored twice, by id for
   * the admin list and by name for the storefront, and neither side converts;
   * filtering by an id the public API does not match would silently return
   * everything.
   */
  function newsSourceOf(c) {
    var query = isRecord(c.query) ? c.query : {};
    var wantsLatest = c.source === 'latest' || c.mode === 'latest' || c.source === 'dynamic';

    var categoryIds = intListOf(query.categoryIds);
    var legacyCategory = toPositiveInt(c.categoryId) || toPositiveInt(c.newsCategoryId) || toPositiveInt(c.category);
    if (!categoryIds.length && legacyCategory) categoryIds = [legacyCategory];

    if (!wantsLatest && !legacyCategory) return null;

    return {
      // Whether the element ASKED for the contract's standing query, as opposed
      // to merely carrying an old flat categoryId. The two must not be treated
      // alike: see the call site.
      explicit: wantsLatest,
      // A HOMEPAGE rule, not a general one: the hero slider shows the TOP
      // article, so every list below it excludes top news or the same story is
      // on screen twice (snippets/news-collage-js.liquid and news-stories-js
      // both send `filter[isTopNews]=0`). Off by default so an ordinary
      // news-list asks exactly what it asked before; the collage turns it on
      // at the call site, and a document can set it explicitly.
      excludeTopNews: query.excludeTopNews === true,
      categoryIds: categoryIds,
      tagNames: stringListOf(query.tagNames),
      limit: clampInt(query.limit != null ? query.limit : (c.limit || c.maxItems || c.count), 1, 24, 3),
      offset: clampInt(query.offset, 0, 60, 0),
      sort: query.sort === 'oldest' ? 'oldest' : 'newest'
    };
  }

  /** Columns collapse like upstream's responsiveColumns: 3 on tablet, 2 on a phone. */
  function newsColumnsStyleTag(el, columns) {
    var id = safeId(el && el.id);
    if (!id) return '';
    var css = '';
    if (columns > 3) {
      css += '@media screen and (min-width: 750px) and (max-width: 989px){[data-neo-el="' + id +
        '"]{grid-template-columns:repeat(3, minmax(0, 1fr)) !important}}';
    }
    if (columns > 2) {
      css += '@media screen and (max-width: 749px){[data-neo-el="' + id +
        '"]{grid-template-columns:repeat(2, minmax(0, 1fr)) !important}}';
    }
    return css ? '<style>' + css + '</style>' : '';
  }

  Neo.registerType('news-list', function (el) {
    var c = el.content || {};
    var items = itemsOf(c, 'newsItems');
    var flags = newsFlagsOf(c);
    var layout = newsCardLayoutOf(c);
    var homeCard = isHomeNewsCard(el);
    // Collage cards run up to 1350px wide, so they take the full-size variant
    // (see newsCardImage), and so do overlay/image cards, which are all photo.
    // Every other news-list keeps aiconeo's list variant.
    if (homeCard || layout === 'overlay' || layout === 'image') flags.large = true;
    var linkOpts = newsLinkOptsOf(c);
    // With `source: 'latest'` the snapshots are the PINNED articles, not the
    // whole list: they render first and the query fills in behind them.
    //
    // An element carrying only an old flat `categoryId` is NOT that. Picking a
    // category to browse and then picking articles out of it is the ordinary
    // authoring flow, so a great many stored blocks hold both, and treating
    // that as "pinned plus fill" makes a hand-curated block quietly grow
    // articles the editor never chose. A flat source keeps its original job:
    // it fills a block that has nothing in it.
    var source = newsSourceOf(c);
    if (source && !source.explicit && items.length) source = null;
    // The collage sits under the hero slider, which shows the top article.
    // (A native document says so itself, through query.excludeTopNews.)
    if (source && homeCard) source.excludeTopNews = true;
    if (!items.length && !source) return '';

    var columns = clampInt(c.columns, 1, 6, 3);
    // Deviation from upstream: never more tracks than cards. The builder keeps
    // the authored count, so one article in a "3 columns" list (the default)
    // drawn inside a grid cell was a third as wide as the cell, with two empty
    // tracks beside it. A source knows how many cards are coming, so it counts
    // those rather than the pinned ones it has on screen right now.
    var expected = source ? Math.max(items.length, source.limit) : items.length;
    if (expected) columns = Math.min(columns, expected);

    var styles = pickStyles(el, 'desktop');
    var merged = {};
    for (var k in styles) {
      if (Object.prototype.hasOwnProperty.call(styles, k)) merged[k] = styles[k];
    }
    merged.display = 'grid';
    merged.gridTemplateColumns = 'repeat(' + columns + ', minmax(0, 1fr))';

    // p56 `cardLayout` / `overlayColor`. The title's font-size follows the
    // element's own style bag when the bag sets one (see the CSS), otherwise
    // the layout's default clamp.
    var listClass = 'content-type-neo-news-list';
    if (layout !== 'stacked') {
      listClass += ' neo-news--' + layout;
      if (pickStyles(el, 'desktop').fontSize) listClass += ' neo-news--bag-fs';
      else if (pickStyles(el, 'mobile').fontSize) listClass += ' neo-news--bag-fs-mobile';
      if (layout === 'overlay') merged['--neo-news-scrim-rgb'] = imageOverlayRgb(gridHexOrEmpty(c.overlayColor) || '#000000');
    }

    var attrs = '';
    if (source) {
      attrs += ' data-neo-news-source="1"' +
        ' data-neo-news-query="' + escapeAttr(JSON.stringify(source)) + '"' +
        ' data-neo-news-flags="' + escapeAttr(JSON.stringify(flags)) + '"' +
        ' data-neo-news-link="' + escapeAttr(JSON.stringify(linkOpts)) + '"' +
        ' data-neo-news-pinned="' + items.length + '"' +
        ' data-neo-news-pinned-ids="' + escapeAttr(JSON.stringify(newsPinnedIds(items))) + '"';
    }

    var html = viewportStyleTags(el) + newsColumnsStyleTag(el, columns) +
      '<div class="' + listClass + '"' + dataElAttr(el) + styleAttrOf(merged) + attrs + '>';
    for (var i = 0; i < items.length; i++) html += newsCardHtml(items[i], flags, linkOpts);
    // Placeholders for the cards the query still owes, so the block occupies
    // its final height from the first paint and nothing below it jumps when
    // the articles land. hydrateNewsLists removes them on success AND on
    // failure: a skeleton that never resolves reads as a hung page.
    for (var p = items.length; source && p < source.limit; p++) html += newsPlaceholderHtml(flags);
    return html + '</div>';
  });

  /** news-list `cardLayout`: 'stacked' (default) | 'overlay' | 'image'. */
  function newsCardLayoutOf(c) {
    var v = c && c.cardLayout;
    return (v === 'overlay' || v === 'image') ? v : 'stacked';
  }

  /** The ids a query has to skip, so a pinned article never appears twice. */
  function newsPinnedIds(items) {
    var ids = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i] && items[i].id != null) ids.push(String(items[i].id));
    }
    return ids;
  }

  /** A card-shaped skeleton: same box, same media ratio, same text rows. */
  function newsPlaceholderHtml(flags) {
    var inner = '';
    if (flags.image) inner += '<span class="neo-news__media neo-news__media--ph"></span>';
    inner += '<span class="neo-news__body">';
    if (flags.date) inner += '<span class="neo-news__meta"><span class="neo-news__ph neo-news__ph--date"></span></span>';
    inner += '<span class="neo-news__ph neo-news__ph--title"></span>';
    if (flags.excerpt) inner += '<span class="neo-news__ph neo-news__ph--text"></span>';
    inner += '</span>';
    return '<article class="neo-news__card neo-news__card--placeholder" aria-hidden="true">' +
      '<span class="neo-news__link">' + inner + '</span></article>';
  }

  /** aico JSON:API news row -> the snapshot shape newsCardHtml reads. */
  function newsSnapshotFromApi(item) {
    var attrs = (item && item.attributes) || {};
    var translations = Array.isArray(attrs.translations) ? attrs.translations : [];
    var locale = typeof currentLang !== 'undefined' ? String(currentLang) : 'fr_CH';
    var lang = locale.split('_')[0];
    var tr = null;
    for (var i = 0; i < translations.length && !tr; i++) if (translations[i] && translations[i].locale === locale) tr = translations[i];
    for (var j = 0; j < translations.length && !tr; j++) if (translations[j] && String(translations[j].locale || '').split('_')[0] === lang) tr = translations[j];
    tr = tr || translations[0] || attrs;
    var category = attrs.newsCategory && (attrs.newsCategory.name || attrs.newsCategory.webName);
    return {
      id: item && item.id,
      title: tr.name || attrs.name || '',
      excerpt: tr.lead || attrs.lead || tr.smallDescription || '',
      imageUrl: tr.optimizedListImage || attrs.optimizedListImage || tr.optimizedImage || tr.image || attrs.optimizedImage || attrs.image || '',
      // The theme collage's own order, for cards wide enough to want it.
      imageUrlLarge: tr.optimizedImage || tr.image || attrs.optimizedImage || attrs.image ||
        tr.optimizedListImage || attrs.optimizedListImage || '',
      publishDate: attrs.publishDate || '',
      // Focal point of the 16:9 image, desktop + phone pair (newsFocusStyle).
      focusX: newsFocusPct(attrs.focusXPercentage),
      focusY: newsFocusPct(attrs.focusYPercentage),
      mobileFocusX: newsFocusPct(attrs.mobileFocusXPercentage),
      mobileFocusY: newsFocusPct(attrs.mobileFocusYPercentage),
      categoryName: category || '',
      // The chip is the article's CATEGORY (the field staff always set), in the
      // theme's localised wording. Tags are not used: most articles carry none.
      // The light list rows the homepage reads have no category at all, so
      // runNewsQuery fills the chip in afterwards (newsCategoryLookup).
      badge: newsCategoryBadge(attrs.newsCategory),
      // Only for an article that turns out to have NO category.
      tagBadge: typeof window.aicoNewsBadgeLabel === 'function' && Array.isArray(attrs.newsTags) && attrs.newsTags.length
        ? (window.aicoNewsBadgeLabel({ newsTags: attrs.newsTags }) || '') : '',
      newsCategoryId: toPositiveInt(attrs.newsCategoryId) ||
        (attrs.newsCategory && toPositiveInt(attrs.newsCategory.id)) || 0,
      urlHandle: tr.urlHandle || attrs.urlHandle || ''
    };
  }

  /**
   * A category -> the chip text. The theme's own labels first (ls.team.* /
   * ls.news.badge_* through window.aicoNewsBadgeLabel, matched by NAME), so the
   * chip reads the same as every other news surface and is translated on /en;
   * then the category's own name in the page language. A bare `{id}` (a block
   * that asks for one category knows its id but not its name) goes through the
   * theme's id table instead.
   */
  function newsCategoryBadge(cat) {
    if (!isRecord(cat)) return '';
    var hasName = !!(cat.name || cat.webName || (Array.isArray(cat.translations) && cat.translations.length));
    if (typeof window.aicoNewsBadgeLabel === 'function') {
      var probe = hasName ? { name: cat.name, webName: cat.webName, translations: cat.translations } : { id: cat.id };
      var label = window.aicoNewsBadgeLabel({ newsCategory: probe }) || '';
      if (label) return label;
    }
    if (!hasName) return '';
    var list = Array.isArray(cat.translations) ? cat.translations : [];
    var locale = typeof currentLang !== 'undefined' ? String(currentLang) : 'fr_CH';
    var lang = locale.split('_')[0];
    var tr = null;
    for (var i = 0; i < list.length && !tr; i++) if (list[i] && list[i].locale === locale) tr = list[i];
    for (var j = 0; j < list.length && !tr; j++) if (list[j] && String(list[j].locale || '').split('_')[0] === lang) tr = list[j];
    return String((tr && (tr.webName || tr.name)) || cat.webName || cat.name || '');
  }

  /**
   * Article id -> its category, for the cards a light list row left without
   * one. Ids asked for within the same moment go out as ONE request
   * (`filter[id][]`, full rows: the only shape that carries the category), and
   * the answer is kept per session so a reload or a filter click costs nothing.
   * Only the category travels into storage, never the article.
   */
  var NEWS_CATEGORY_STORE = 'ls:neo-news-cat:v1';
  var newsCategoryById = null;
  var newsCategoryQueue = null;

  function newsCategoryCache() {
    if (newsCategoryById) return newsCategoryById;
    newsCategoryById = {};
    try {
      var saved = JSON.parse(sessionStorage.getItem(NEWS_CATEGORY_STORE));
      if (isRecord(saved)) newsCategoryById = saved;
    } catch (err) {}
    return newsCategoryById;
  }

  function newsCategoryLookup(ids) {
    var cache = newsCategoryCache();
    var wanted = [];
    for (var i = 0; i < ids.length; i++) {
      var id = String(ids[i]);
      if (!/^\d+$/.test(id) || Object.prototype.hasOwnProperty.call(cache, id)) continue;
      wanted.push(id);
    }
    if (!wanted.length) return Promise.resolve(cache);
    if (!newsCategoryQueue) {
      var queue = { ids: {}, promise: null };
      newsCategoryQueue = queue;
      queue.promise = new Promise(function (resolve) { setTimeout(resolve, 30); }).then(function () {
        newsCategoryQueue = null;
        var list = Object.keys(queue.ids).sort();
        var url = API_URL + 'news?filter[isActive]=1&page[number]=1&page[size]=' + list.length;
        for (var k = 0; k < list.length; k++) url += '&filter[id][]=' + encodeURIComponent(list[k]);
        return fetch(url, typeof API_Header_Options !== 'undefined' ? API_Header_Options : undefined)
          .then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.json();
          })
          .then(function (payload) {
            var rows = (payload && payload.data) || [];
            for (var r = 0; r < rows.length; r++) {
              var cat = rows[r] && rows[r].attributes && rows[r].attributes.newsCategory;
              cache[String(rows[r].id)] = isRecord(cat)
                ? { id: cat.id, name: cat.name || '', webName: cat.webName || '', translations: (cat.translations || []).map(function (t) {
                    return { locale: t && t.locale, name: t && t.name, webName: t && t.webName };
                  }) }
                : null;
            }
            for (var m = 0; m < list.length; m++) if (!Object.prototype.hasOwnProperty.call(cache, list[m])) cache[list[m]] = null;
            try { sessionStorage.setItem(NEWS_CATEGORY_STORE, JSON.stringify(cache)); } catch (err) {}
            return cache;
          })
          .catch(function (err) {
            warnOnce('news-category', 'news-list category lookup failed (' + (err && err.message ? err.message : err) + ')');
            return cache;
          });
      });
    }
    for (var w = 0; w < wanted.length; w++) newsCategoryQueue.ids[wanted[w]] = true;
    return newsCategoryQueue.promise;
  }

  /** Put a late chip on a card rendered without one (same markup as newsCardHtml). */
  function newsApplyBadge(card, label) {
    if (!card || !label || card.querySelector('.neo-news__badge, .neo-news__category')) return;
    var media = card.querySelector('.neo-news__media');
    if (media) {
      var badge = document.createElement('span');
      badge.className = 'neo-news__badge';
      badge.textContent = label;
      media.appendChild(badge);
      return;
    }
    var body = card.querySelector('.neo-news__body');
    if (!body) return;
    var meta = body.querySelector('.neo-news__meta');
    if (!meta) {
      meta = document.createElement('span');
      meta.className = 'neo-news__meta';
      body.insertBefore(meta, body.firstChild);
    }
    var chip = document.createElement('span');
    chip.className = 'neo-news__category';
    chip.textContent = label;
    meta.appendChild(chip);
  }

  function readJsonAttr(host, name, fallback) {
    try {
      var raw = host.getAttribute(name);
      if (!raw) return fallback;
      var parsed = JSON.parse(raw);
      return parsed == null ? fallback : parsed;
    } catch (err) {
      return fallback;
    }
  }

  /**
   * The request the contract specifies, with the parameter names the theme's
   * own news listings already use (see snippets/news-collage-js.liquid).
   *
   * The API pages, it does not take an offset, so the window is asked for to
   * its end (`offset + limit + pinned`) and sliced here. Categories travel as
   * one comma list: the API matches an article's main category OR any of its
   * additional ones, which the light rows do not carry, so the server decides.
   * Tags go by NAME, repeated.
   */
  /**
   * Page sizes are rounded UP to one of these, so blocks of the same shape ask
   * the SAME url and share one response. The homepage collage is six one-article
   * lists reading successive offsets of one answer: without the bucket they ask
   * for sizes 1..6 and the page makes six near-identical CDN round trips, on
   * load and again on every filter click. Extra rows are free — runNewsQuery
   * slices its own window out of whatever came back.
   */
  var NEWS_PAGE_BUCKETS = [6, 12, 24, 60];

  function newsBucketSize(size) {
    for (var i = 0; i < NEWS_PAGE_BUCKETS.length; i++) {
      if (size <= NEWS_PAGE_BUCKETS[i]) return NEWS_PAGE_BUCKETS[i];
    }
    return NEWS_PAGE_BUCKETS[NEWS_PAGE_BUCKETS.length - 1];
  }

  function newsQueryString(source, pinnedCount) {
    var size = newsBucketSize(clampInt(source.offset + source.limit + (pinnedCount || 0), 1, 60, source.limit));
    var query = 'news?page[number]=1&page[size]=' + size +
      '&sort=' + (source.sort === 'oldest' ? 'publishDate' : '-publishDate') +
      '&filter[isActive]=1&filter[isLightVersion]=true';
    if (source.excludeTopNews) query += '&filter[isTopNews]=0';
    if (source.categoryIds.length) {
      query += '&filter[newsCategoryId]=' + encodeURIComponent(source.categoryIds.join(','));
    }
    for (var i = 0; i < source.tagNames.length; i++) {
      query += '&filter[tag][]=' + encodeURIComponent(source.tagNames[i]);
    }
    return query;
  }

  /**
   * One request per url per page, and a locale-scoped sessionStorage copy so a
   * filter click that comes back to a category is instant. Locale-scoped
   * because what a caller builds out of the payload is rendered text — titles,
   * handles and badge labels all follow the request locale, and both locales
   * share this origin's storage (the same rule as news-collage-js).
   *
   * The payload is READ-ONLY to its callers: several blocks hold the same
   * object and each one slices its own window out of it.
   */
  var newsInFlight = {};
  var NEWS_STORE_TTL_MS = 5 * 60 * 1000;

  function newsStoreKey(url) {
    var locale = typeof currentLangIsocode !== 'undefined' ? currentLangIsocode : 'fr';
    return 'neoNews-' + locale + '-' + url;
  }

  function newsFetchJson(url) {
    if (newsInFlight[url]) return newsInFlight[url];
    var stored = null;
    try {
      var entry = JSON.parse(sessionStorage.getItem(newsStoreKey(url)));
      if (entry && entry.ts && Date.now() - entry.ts < NEWS_STORE_TTL_MS) stored = entry.payload;
    } catch (err) {
      stored = null; // storage blocked (private mode / cookies off)
    }
    if (stored) {
      newsInFlight[url] = Promise.resolve(stored);
      return newsInFlight[url];
    }
    newsInFlight[url] = fetch(url, typeof API_Header_Options !== 'undefined' ? API_Header_Options : undefined)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        try {
          sessionStorage.setItem(newsStoreKey(url), JSON.stringify({ ts: Date.now(), payload: payload }));
        } catch (err) {}
        return payload;
      })
      .catch(function (err) {
        // A failure must not be remembered: the next click has to try again.
        delete newsInFlight[url];
        throw err;
      });
    return newsInFlight[url];
  }

  function clearNewsPlaceholders(host) {
    var ghosts = host.querySelectorAll('.neo-news__card--placeholder');
    for (var i = 0; i < ghosts.length; i++) ghosts[i].parentNode.removeChild(ghosts[i]);
  }

  /**
   * Run (or re-run) one news block's query and put the answer behind whatever
   * is pinned. Pinned cards are never taken back out, which is what the filter
   * row relies on: choosing a category replaces the articles the query added
   * and leaves the editor's own picks standing.
   */
  function runNewsQuery(host, override) {
    var flags = readJsonAttr(host, 'data-neo-news-flags', newsFlagsOf({}));
    var linkOpts = readJsonAttr(host, 'data-neo-news-link', {});
    var stored = readJsonAttr(host, 'data-neo-news-query', null);
    if (!stored) return;
    // hydrateNewsLists checks this before its first pass, but the filter row
    // calls straight in from a click handler, which runs outside the dispatch
    // block's try/catch. On a template that never rendered the API globals the
    // first click would throw a ReferenceError instead of doing nothing.
    if (typeof API_URL === 'undefined' || !API_URL) {
      clearNewsPlaceholders(host);
      warnOnce('news-api', 'news-list live source needs the API_URL global; nothing fetched');
      return;
    }

    var source = {
      categoryIds: Array.isArray(stored.categoryIds) ? stored.categoryIds : [],
      tagNames: Array.isArray(stored.tagNames) ? stored.tagNames : [],
      limit: clampInt(stored.limit, 1, 24, 3),
      offset: clampInt(stored.offset, 0, 60, 0),
      sort: stored.sort === 'oldest' ? 'oldest' : 'newest',
      excludeTopNews: stored.excludeTopNews === true
    };
    // The filter row's choice REPLACES the block's own narrowing rather than
    // adding to it: an option for one category must not be intersected with a
    // different category the block was authored with, which matches nothing.
    if (override) {
      source.categoryIds = override.categoryId ? [override.categoryId] : [];
      source.tagNames = Array.isArray(override.tagNames) ? override.tagNames : [];
    }

    var pinned = host.querySelectorAll('.neo-news__card:not(.neo-news__card--placeholder)');
    var pinnedCount = clampInt(host.getAttribute('data-neo-news-pinned'), 0, 24, 0);
    var pinnedIds = {};
    var storedPinned = readJsonAttr(host, 'data-neo-news-pinned-ids', []);
    for (var pi = 0; pi < storedPinned.length; pi++) pinnedIds[String(storedPinned[pi])] = true;

    // Anything the previous run added goes; the pinned prefix stays put.
    for (var d = pinned.length - 1; d >= pinnedCount; d--) pinned[d].parentNode.removeChild(pinned[d]);

    var token = String((Number(host.getAttribute('data-neo-news-run')) || 0) + 1);
    host.setAttribute('data-neo-news-run', token);

    newsFetchJson(API_URL + newsQueryString(source, pinnedCount))
      .then(function (payload) {
        // A slower earlier run must not overwrite a newer choice.
        if (host.getAttribute('data-neo-news-run') !== token) return;
        var rows = (payload && payload.data) || [];
        var kept = [];
        for (var k = 0; k < rows.length; k++) {
          var snap = newsSnapshotFromApi(rows[k]);
          if (!snap.title) continue;
          if (pinnedIds[String(snap.id)]) continue;
          kept.push(snap);
        }
        var slice = kept.slice(source.offset, source.offset + Math.max(0, source.limit - pinnedCount));
        // The chip is the category (newsCategoryBadge). A block that asks for
        // ONE category already knows it; any other card waits for the lookup.
        var askedCategory = source.categoryIds.length === 1 ? newsCategoryBadge({ id: source.categoryIds[0] }) : '';
        var unlabelled = [];
        var tagFallback = {};
        for (var u = 0; u < slice.length; u++) {
          if (!slice[u].badge && askedCategory) slice[u].badge = askedCategory;
          if (!slice[u].badge && slice[u].id != null) {
            unlabelled.push(String(slice[u].id));
            tagFallback[String(slice[u].id)] = slice[u].tagBadge || '';
          }
        }
        var html = '';
        for (var s = 0; s < slice.length; s++) html += newsCardHtml(slice[s], flags, linkOpts);
        clearNewsPlaceholders(host);
        host.insertAdjacentHTML('beforeend', html);
        newsSyncEmptiness(host);
        if (flags.category && unlabelled.length) {
          newsCategoryLookup(unlabelled).then(function (cache) {
            if (host.getAttribute('data-neo-news-run') !== token) return;
            for (var q = 0; q < unlabelled.length; q++) {
              var label = newsCategoryBadge(cache[unlabelled[q]]) || tagFallback[unlabelled[q]];
              if (!label) continue;
              newsApplyBadge(host.querySelector('.neo-news__card[data-neo-news-id="' + unlabelled[q] + '"]'), label);
            }
          });
        }
      })
      .catch(function (err) {
        if (host.getAttribute('data-neo-news-run') !== token) return;
        clearNewsPlaceholders(host);
        newsSyncEmptiness(host);
        warnOnce('news-fetch', 'news-list live fetch failed (' + (err && err.message ? err.message : err) + ')');
      });
  }

  function hydrateNewsLists(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var hosts = scope.querySelectorAll('[data-neo-news-source="1"]');
    if (!hosts.length) return;
    if (typeof API_URL === 'undefined' || !API_URL) {
      warnOnce('news-api', 'news-list with a live source needs the API_URL global; nothing rendered');
      for (var g = 0; g < hosts.length; g++) clearNewsPlaceholders(hosts[g]);
      return;
    }
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].getAttribute('data-neo-news-hydrated')) continue;
      hosts[i].setAttribute('data-neo-news-hydrated', '1');
      runNewsQuery(hosts[i], null);
    }
  }

  // ---- 22c. LS TV video card ---------------------------------
  //
  // A `youtube` element inside the homepage collage is NOT an embedded player:
  // the design (Figma "News - Layout 6") puts a poster card with the LSTV chip,
  // a centred play circle and the video title on a scrim in that cell, linking
  // to the theme's own /pages/video detail — an iframe there would autoload the
  // YouTube player on every homepage view and break the collage's ratios.
  //
  // Opt-in, so every other youtube element on every other page keeps the
  // engine's iframe: the element carries the `hm-news__video` className, or it
  // is a child of a grid whose className contains `hm-news`.
  //
  // Empty videoId/src = AUTO: the newest LS TV video, taken from the SAME feed
  // snippets/ls-tv-js.liquid caches (`lsTvFeed-*` in sessionStorage) and from
  // its `feed-loaded` publish when an LS TV section is on the page, so the
  // homepage still makes one request for both. A non-empty videoId is the
  // manual override and is rendered straight away.
  var LSTV_CACHE_PREFIX = 'lsTvFeed-';
  var LSTV_CACHE_TTL_MS = 10 * 60 * 1000;
  var LSTV_WAIT_MS = 6000;

  var LSTV_PLAY_SVG =
    '<svg viewBox="0 0 102 102" fill="none" aria-hidden="true" focusable="false">' +
    '<circle cx="51" cy="51" r="43.5" stroke="#ffffff" stroke-width="3"/>' +
    '<path d="M43 34.5 71 51 43 67.5Z" fill="#ffffff"/></svg>';

  /**
   * The innermost skin-relevant grid being rendered: 'hm' inside a legacy
   * `.hm-news` collage (its children opt in), 'native' inside a grid carrying
   * the p56 native fields (every className skin OFF, see gridIsNativeLayout),
   * '' elsewhere. A plain grid does not change it.
   */
  var gridSkinContext = '';

  function isHomeNewsGrid(el) {
    return /(^|\s)hm-news(\s|$)/.test(String((el && el.className) || ''));
  }

  function isHomeNewsVideo(el) {
    if (gridSkinContext === 'native') return false;
    return gridSkinContext === 'hm' ||
      /(^|\s)hm-news__video(\s|$)/.test(String((el && el.className) || ''));
  }

  /** A news-list that is one of the collage's overlay cards. */
  function isHomeNewsCard(el) {
    if (gridSkinContext === 'native') return false;
    return gridSkinContext === 'hm' ||
      /(^|\s)hm-nc(\s|$)/.test(String((el && el.className) || ''));
  }

  function youtubeIdOf(url) {
    if (!url) return '';
    var m = String(url).match(/(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    return m ? m[1] : '';
  }

  /**
   * The poster card. `opts.skin` 'hm' is the legacy collage markup (always the
   * LSTV chip); anything else is the generic p56 `display: 'poster'` card,
   * whose chip is the element's own `badge` and is left out when that is empty.
   */
  function lsTvCardHtml(video, opts) {
    var target = video.external ? ' target="_blank" rel="noopener noreferrer"' : '';
    var o = opts || { skin: 'hm' };
    var b = o.skin === 'hm' ? 'hm-video' : 'neo-yt-poster';
    var badge = o.skin === 'hm' ? 'LSTV' : String(o.badge || '').trim();
    // The generic poster's scrim colour: the element's `overlayColor` (hex,
    // default black). The legacy collage card keeps its navy stylesheet scrim.
    var scrim = o.skin === 'hm' ? ''
      : ' style="--neo-yt-scrim-rgb:' + imageOverlayRgb(gridHexOrEmpty(o.overlayColor) || '#000000') + '"';
    return '<article class="' + b + '"' + scrim + '>' +
      '<a class="' + b + '__media" href="' + escapeAttr(video.href) + '"' + target +
      (o.skin === 'hm' ? '' : ' aria-label="' + escapeAttr(video.title || 'YouTube') + '"') + '>' +
      '<img class="' + b + '__img" src="' + escapeAttr(video.img) + '" alt="" loading="lazy">' +
      (badge ? '<span class="' + b + '__badge">' + escapeHtml(badge) + '</span>' : '') +
      '<span class="' + b + '__scrim" aria-hidden="true"></span>' +
      '<span class="' + b + '__play" aria-hidden="true">' + LSTV_PLAY_SVG + '</span>' +
      (o.skin === 'hm' || video.title
        ? '<div class="' + b + '__overlay"><h3 class="' + b + '__title">' + escapeHtml(video.title) + '</h3></div>'
        : '') +
      '</a></article>';
  }

  /** The engine's own player markup, for a `source: 'latest'` player. */
  function youtubePlayerHtml(id, content) {
    var aspect = String((content && content.aspectRatio) || '16/9').replace(':', '/');
    if (!/^\d+(\.\d+)?\/\d+(\.\d+)?$/.test(aspect)) aspect = '16/9';
    return '<div class="neo-video__frame" style="aspect-ratio:' + aspect + ';">' +
      '<iframe title="' + escapeAttr((content && content.videoTitle) || 'YouTube video') +
      '" src="https://www.youtube.com/embed/' + escapeAttr(id) +
      '" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>';
  }

  (function overrideYoutubeForCollage() {
    var original = Neo._renderers.youtube;
    Neo.registerType('youtube', function (el) {
      var content = (el && el.content) || {};
      var legacy = isHomeNewsVideo(el);
      var poster = legacy || content.display === 'poster';
      var latest = legacy || content.source === 'latest';
      var id = content.videoId || youtubeIdOf(content.src || '');
      // p56: a player with an explicit id, or with no `source: 'latest'` to
      // fill it, is the engine's iframe exactly as before.
      if (!poster && (id || !latest)) return typeof original === 'function' ? original(el) : '';
      if (!id && !latest) return '';

      var skin = legacy ? 'hm' : 'native';
      var cls = 'content-type-neo-youtube' + (legacy ? '' : (poster ? ' neo-yt--poster' : ' neo-yt--latest-player'));
      // The poster title's size follows the element's style bag when it sets one.
      if (!legacy && poster) {
        if (pickStyles(el, 'desktop').fontSize) cls += ' neo-yt--bag-fs';
        else if (pickStyles(el, 'mobile').fontSize) cls += ' neo-yt--bag-fs-mobile';
      }
      var open = viewportStyleTags(el) + '<div class="' + cls + '"' + dataElAttr(el);
      var badge = typeof content.badge === 'string' ? content.badge : '';
      var overlayColor = gridHexOrEmpty(content.overlayColor);
      if (id) {
        var video = {
          href: 'https://www.youtube.com/watch?v=' + encodeURIComponent(id),
          external: true,
          img: safeHref(content.videoThumbnail) || ('https://i.ytimg.com/vi/' + encodeURIComponent(id) + '/hqdefault.jpg'),
          title: content.videoTitle || ''
        };
        return open + inlineStyleAttr(el) + '>' +
          lsTvCardHtml(video, { skin: skin, badge: badge, overlayColor: overlayColor }) + '</div>';
      }
      // AUTO / `source: 'latest'` — filled at hydrate from the LS TV feed.
      // base.css hides div:empty; the stylesheet re-shows the skeleton div.
      return open + ' data-neo-lstv="1"' +
        (legacy ? '' : ' data-neo-lstv-skin="' + (poster ? 'native' : 'player') + '"' +
          ' data-neo-lstv-badge="' + escapeAttr(badge) + '"' +
          (overlayColor ? ' data-neo-lstv-scrim="' + escapeAttr(overlayColor) + '"' : '') +
          (poster ? '' : ' data-neo-lstv-aspect="' + escapeAttr(String(content.aspectRatio || '')) + '"')) +
        inlineStyleAttr(el) + '>' +
        '<div class="' + (legacy ? 'hm-video__skel' : 'neo-yt-poster__skel') + '" aria-hidden="true"></div></div>';
    });
  })();

  /** The freshest `lsTvFeed-*` entry, whatever page size wrote it. */
  function lsTvSharedFeed() {
    var newest = null;
    try {
      for (var i = 0; i < sessionStorage.length; i++) {
        var key = sessionStorage.key(i);
        if (!key || key.indexOf(LSTV_CACHE_PREFIX) !== 0) continue;
        var entry = JSON.parse(sessionStorage.getItem(key));
        if (!entry || !entry.ts || Date.now() - entry.ts >= LSTV_CACHE_TTL_MS) continue;
        if (!entry.data || !((entry.data.data || []).length)) continue;
        if (!newest || entry.ts > newest.ts) newest = entry;
      }
    } catch (e) {
      return null; // storage blocked (private mode / cookies off)
    }
    return newest && newest.data;
  }

  function lsTvWriteFeed(res) {
    try {
      sessionStorage.setItem(LSTV_CACHE_PREFIX + '1', JSON.stringify({ ts: Date.now(), data: res }));
    } catch (e) {}
  }

  /** One retry on non-ok — the Bunny edge returns transient 401s (CLAUDE.md). */
  function lsTvFetchFeed(retried) {
    var url = API_URL +
      'social-media-posts?sort=-publishedAt&page[number]=1&page[size]=1&filter[source]=YOUTUBE';
    return fetch(url, typeof API_Header_Options !== 'undefined' ? API_Header_Options : undefined)
      .then(function (res) {
        if (!res.ok) {
          if (!retried) return lsTvFetchFeed(true);
          throw new Error('LS TV feed HTTP ' + res.status);
        }
        return res.json();
      });
  }

  function lsTvVideoFromFeed(res) {
    var post = (res && res.data && res.data[0]) || null;
    if (!post) return null;
    var attrs = (post.attributes && post.attributes[0]) || {};
    // The post's own YouTube id, when any of its link fields carries one (for
    // a `source: 'latest'` PLAYER; the poster links to the theme's video page).
    var youtubeId = '';
    for (var key in attrs) {
      if (!youtubeId && Object.prototype.hasOwnProperty.call(attrs, key) && typeof attrs[key] === 'string') {
        youtubeId = youtubeIdOf(attrs[key]);
      }
    }
    var title = attrs.title || '';
    if (title && typeof unicodeToNormal === 'function') title = unicodeToNormal(title);
    var img = attrs.webThumbnailUrl || attrs.imageUrl || attrs.mobileThumbnailUrl || '';
    var base = (typeof videoPageURL !== 'undefined' && videoPageURL) || '/pages/video';
    if (!title || !img) return null;
    return {
      href: String(base).replace(/\/+$/, '') + '/' + post.id + (window.videoViewParam || ''),
      external: false,
      img: img,
      title: title,
      youtubeId: youtubeId
    };
  }

  /** Nothing to show: the cell collapses (the stylesheet widens its neighbour). */
  function lsTvHide(wrapper) {
    var cell = wrapper.closest ? wrapper.closest('.neo-grid__cell') : null;
    if (!cell) { wrapper.hidden = true; return; }
    cell.setAttribute('data-neo-cell-empty', '1');
    // The stylesheet lets the article beside it take the whole strip.
    var grid = cell.closest('.content-type-neo-grid');
    if (grid) grid.setAttribute('data-neo-lstv-empty', '1');
  }

  function lsTvFill(wrapper, res) {
    var video = lsTvVideoFromFeed(res);
    if (!video) return lsTvHide(wrapper);
    var skin = wrapper.getAttribute('data-neo-lstv-skin') || 'hm';
    if (skin === 'player' && video.youtubeId) {
      wrapper.innerHTML = youtubePlayerHtml(video.youtubeId, {
        aspectRatio: wrapper.getAttribute('data-neo-lstv-aspect') || '', videoTitle: video.title
      });
      return;
    }
    wrapper.innerHTML = lsTvCardHtml(video, {
      skin: skin === 'hm' ? 'hm' : 'native',
      badge: wrapper.getAttribute('data-neo-lstv-badge') || '',
      overlayColor: wrapper.getAttribute('data-neo-lstv-scrim') || ''
    });
  }

  function initLsTvCard(wrapper) {
    var cached = lsTvSharedFeed();
    if (cached) return lsTvFill(wrapper, cached);

    var done = false;
    var unsubscribe = null;
    function finish(res) {
      if (done) return;
      done = true;
      if (unsubscribe) unsubscribe();
      lsTvFill(wrapper, res);
    }
    function fetchOwn() {
      lsTvFetchFeed(false)
        .then(function (res) {
          if (res && res.data && res.data.length) lsTvWriteFeed(res);
          finish(res);
        })
        .catch(function (err) {
          warnOnce('lstv-feed', 'LS TV feed unavailable (' + (err && err.message ? err.message : err) + ')');
          finish(null);
        });
    }

    // An LS TV section on the same page is already fetching this feed.
    var lsTvOnPage = !!document.querySelector('[id^="ls-tv-"]');
    if (lsTvOnPage && typeof subscribe === 'function') {
      unsubscribe = subscribe('feed-loaded', function (event) {
        if (done || !event || event.area !== 'lsTv') return;
        var feed = lsTvSharedFeed();
        if (feed) finish(feed);
      });
    }
    if (!lsTvOnPage) return fetchOwn();
    window.setTimeout(function () {
      if (done) return;
      var feed = lsTvSharedFeed();
      if (feed) return finish(feed);
      fetchOwn();
    }, LSTV_WAIT_MS);
  }

  function hydrateLsTvCards(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var wrappers = scope.querySelectorAll('[data-neo-lstv="1"]');
    if (!wrappers.length) return;
    if (typeof API_URL === 'undefined' || !API_URL) {
      warnOnce('lstv-api', 'the LS TV card needs the API_URL global; nothing rendered');
      return;
    }
    for (var i = 0; i < wrappers.length; i++) {
      if (wrappers[i].getAttribute('data-neo-lstv-ready')) continue;
      wrappers[i].setAttribute('data-neo-lstv-ready', '1');
      initLsTvCard(wrappers[i]);
    }
  }

  // ---- Shop helpers (shop-the-look, product-grid) ---------------
  // The shop the page renders in, live product data from the Ajax API, the
  // theme's money format and price markup, and the cart add.
  /** window.Shopify.shop, trimmed and lower-cased; '' outside a storefront. */
  function shopDomain() {
    var shop = window.Shopify && window.Shopify.shop;
    return typeof shop === 'string' ? shop.trim().toLowerCase() : '';
  }

  /** Locale-aware root ('/', '/en/'), always with a trailing slash. */
  function shopRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    root = (typeof root === 'string' && root) ? root : '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  /** The entry of a `shopify[]` ref list that belongs to THIS shop, matched by
   *  domain only (never a lone entry, never by shopId), or null. */
  function resolveShopifyRef(refs, domainKey) {
    var wanted = shopDomain();
    if (!wanted || !Array.isArray(refs)) return null;
    for (var i = 0; i < refs.length; i++) {
      var ref = refs[i];
      if (!ref || typeof ref !== 'object') continue;
      var domain = ref[domainKey || 'domain'];
      if (typeof domain === 'string' && domain.trim().toLowerCase() === wanted) return ref;
    }
    return null;
  }

  /** Locale-rooted product page URL from an Ajax API product. The Ajax API
   *  already prefixes `url` with the locale ('/en/products/…'), so the root is
   *  only added when it is missing. */
  function productPageUrl(product) {
    var root = shopRoot().replace(/\/$/, '');
    var path = (product && typeof product.url === 'string' && product.url) ||
      ('/products/' + encodeURIComponent((product && product.handle) || ''));
    return root && path.indexOf(root + '/') === 0 ? path : root + path;
  }

  /** Shopify CDN image with a width hint; '' when the product has no image. */
  function productImageUrl(product, width) {
    var src = product && (product.featured_image || (Array.isArray(product.images) && product.images[0]));
    if (!src || typeof src !== 'string') return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + (width || 800);
  }

  /**
   * Live product data by handle, from `{root}products/{handle}.js` (title,
   * price, images, variants, availability). One request per handle per page,
   * shared by every pin and card that names it; a failure is logged once and
   * the caller skips the pin or card.
   */
  var productCache = {};
  /**
   * Same rule as the Liquid listings (snippets/price.liquid): a variant at 0 is
   * a missing price from the PIM sync, never a real price. Drop those variants
   * and recompute the price range; a product without any priced variant is
   * skipped like a product that failed to load, so a card never shows CHF 0.00.
   */
  function withoutUnpricedVariants(product) {
    var variants = Array.isArray(product.variants) ? product.variants : [];
    var priced = variants.filter(function (v) { return Number(v.price) > 0; });
    if (!variants.length) return product;
    if (!priced.length) throw new Error('no priced variant');
    if (priced.length === variants.length) return product;
    var cheapest = priced.reduce(function (a, v) { return Number(v.price) < Number(a.price) ? v : a; });
    var max = priced.reduce(function (m, v) { return Math.max(m, Number(v.price)); }, 0);
    return Object.assign({}, product, {
      variants: priced,
      price: cheapest.price,
      price_min: cheapest.price,
      price_max: max,
      price_varies: Number(cheapest.price) !== max,
      compare_at_price: cheapest.compare_at_price,
      available: priced.some(function (v) { return v.available; })
    });
  }

  function loadProduct(handle) {
    var key = String(handle == null ? '' : handle).trim();
    if (!key) return Promise.reject(new Error('empty product handle'));
    if (!productCache[key]) {
      productCache[key] = fetch(shopRoot() + 'products/' + encodeURIComponent(key) + '.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function (product) {
          if (!product || typeof product !== 'object' || !product.handle) throw new Error('not a product payload');
          return withoutUnpricedVariants(product);
        })
        .catch(function (error) {
          delete productCache[key];
          warnOnce('product:' + key, 'product "' + key + '" could not be loaded from this shop (' +
            (error && error.message ? error.message : error) + '); its pin/card is skipped');
          throw error;
        });
    }
    return productCache[key];
  }

  /**
   * Shopify's money filter, for prices the Ajax API returns in cents. The
   * format string is `window.moneyFormat` (snippets/global-variables.liquid,
   * following the currency_code_enabled switch of snippets/price.liquid);
   * the CHF fallback keeps prices readable if that global ever goes missing.
   */
  function formatMoney(cents) {
    var format = (typeof window.moneyFormat === 'string' && window.moneyFormat) || 'CHF {{amount}}';
    var value = Number(cents);
    if (!isFinite(value)) value = 0;
    var match = /\{\{\s*(\w+)\s*\}\}/.exec(format);
    var kind = match ? match[1] : 'amount';

    function number(precision, thousands, decimal) {
      var parts = (Math.abs(value) / 100).toFixed(precision).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return (value < 0 ? '-' : '') + whole + (parts[1] ? decimal + parts[1] : '');
    }

    var out;
    switch (kind) {
      case 'amount_no_decimals': out = number(0, ',', '.'); break;
      case 'amount_with_comma_separator': out = number(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = number(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = number(2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': out = number(0, ' ', '.'); break;
      case 'amount_with_space_separator': out = number(2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': out = number(2, ' ', '.'); break;
      default: out = number(2, ',', '.');
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, function () { return out; });
  }

  /** The theme's price markup (snippets/price.liquid + component-price.css). */
  function priceHtml(product) {
    var price = Number(product.price);
    var compare = Number(product.compare_at_price);
    var onSale = isFinite(compare) && compare > price;
    var soldOut = product.available === false;
    var varies = product.price_varies === true && Number(product.price_min) !== Number(product.price_max);
    var regular = formatMoney(varies ? product.price_min : price);
    if (varies) {
      regular = lsT('shop.from_price', 'Dès __PRICE__').replace('__PRICE__', function () { return regular; });
    }
    var html = '<div class="price' + (onSale ? ' price--on-sale' : '') + (soldOut ? ' price--sold-out' : '') +
      '"><div class="price__container">';
    if (onSale) {
      html += '<div class="price__sale">' +
        '<s class="price-item price-item--regular">' + escapeHtml(formatMoney(compare)) + '</s> ' +
        '<span class="price-item price-item--sale price-item--last">' + escapeHtml(regular) + '</span></div>';
    } else {
      html += '<div class="price__regular"><span class="price-item price-item--regular">' +
        escapeHtml(regular) + '</span></div>';
    }
    return html + '</div></div>';
  }

  /** The one variant that can go straight to the cart, or null (options to pick, sold out). */
  function quickAddVariant(product) {
    var variants = Array.isArray(product && product.variants) ? product.variants : [];
    if (variants.length !== 1) return null;
    var variant = variants[0];
    return (variant && variant.available && variant.id) ? variant : null;
  }

  // assets/icon-cart-ls.svg with currentColor, so the button colour drives it.
  var CART_ICON =
    '<svg viewBox="0 0 19 21" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M9.33301 0.5C12.2241 0.5 13.5771 2.87681 13.8828 4.17969L13.8877 4.2002L13.8906 4.20996C13.8907 4.21226 13.8915 4.21582 13.8916 4.2207V7.04785H17.0859C17.2321 7.04785 17.3585 7.16158 17.3701 7.31934V7.32715L17.3711 7.3418L18.166 20.1689V20.1699C18.1707 20.2661 18.1382 20.3489 18.0889 20.4072C18.0225 20.4727 17.9536 20.4999 17.8818 20.5H0.785156C0.700126 20.5 0.626938 20.4649 0.582031 20.415C0.5306 20.3579 0.496956 20.2738 0.5 20.1768L1.29492 7.33887C1.30906 7.1605 1.44962 7.04801 1.58008 7.04785H4.77539V4.25879C4.77542 4.25083 4.7761 4.23827 4.77832 4.2207C4.78012 4.20651 4.78197 4.19105 4.78516 4.17383C5.09422 2.87887 6.44798 0.500179 9.33301 0.5ZM1.81055 8.12305L1.11426 19.3516L1.08203 19.8828H17.585L17.5518 19.3516L16.8564 8.12305L16.8271 7.6543H13.8916V9.34961C13.8916 9.53218 13.7476 9.6582 13.6074 9.6582C13.4672 9.6582 13.3232 9.53218 13.3232 9.34961V7.6543H5.34375V9.34961C5.34375 9.53205 5.1997 9.65801 5.05957 9.6582C4.91934 9.6582 4.77539 9.53218 4.77539 9.34961V7.6543H1.83887L1.81055 8.12305ZM13.3232 4.30469L13.3047 4.23926C13.2269 3.95871 12.9636 3.19403 12.3613 2.49023C11.7467 1.77197 10.7757 1.11719 9.33301 1.11719C7.89017 1.11729 6.92274 1.77282 6.30957 2.49121C5.70816 3.19585 5.44515 3.96001 5.36426 4.22949L5.34375 4.2998V7.04785H13.3232V4.30469Z" fill="currentColor" stroke="currentColor"/>' +
    '</svg>';

  // snippets/loading-spinner.liquid, shown by the cart request (base.css .loading__spinner).
  var CART_SPINNER =
    '<span class="loading__spinner hidden" aria-hidden="true">' +
    '<svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">' +
    '<circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg></span>';

  /**
   * The cart control of a product card: an add-to-cart button when exactly
   * one variant exists and it is available (the theme's own rule, see
   * sections/shop-the-look.liquid and sections/nouveautes-slider.liquid),
   * otherwise a link to the product page where options are picked.
   * `label` is the visible text ('' for an icon-only square).
   */
  function cartControlHtml(product, cls, label, extraAttrs) {
    var title = product.title || '';
    var variant = quickAddVariant(product);
    var inner = CART_ICON + (label ? '<span class="' + cls + '-label">' + escapeHtml(label) + '</span>'
      : '<span class="visually-hidden">' + escapeHtml(lsT('shop.add_to_cart', 'Ajouter au panier')) + '</span>');
    if (variant) {
      return '<button type="button" class="' + cls + '" data-neo-cart-variant="' + escapeAttr(String(variant.id)) + '"' +
        ' aria-label="' + escapeAttr(lsT('shop.add_named_to_cart', 'Ajouter __TITLE__ au panier').replace('__TITLE__', function () { return title; })) + '"' +
        (extraAttrs || '') + '>' + inner + CART_SPINNER + '</button>';
    }
    var view = lsT('shop.view_product', 'Voir le produit');
    return '<a class="' + cls + '" href="' + escapeAttr(productPageUrl(product)) + '"' +
      ' aria-label="' + escapeAttr(lsT('shop.view_named', 'Voir __TITLE__').replace('__TITLE__', function () { return title; })) + '"' +
      (extraAttrs || '') + '>' + CART_ICON +
      (label ? '<span class="' + cls + '-label">' + escapeHtml(view) + '</span>' : '<span class="visually-hidden">' + escapeHtml(view) + '</span>') +
      '</a>';
  }

  /**
   * Add one variant to the cart the way assets/product-form.js does, minus
   * the <product-form> element (its script is not global and expects the
   * jersey configurator markup): POST the theme's cart_add_url with the cart
   * drawer's section ids, publish the cartUpdate event for the other
   * listeners, then let the drawer/notification repaint and open itself.
   * Without a cart component on the page the visitor lands on /cart, as
   * product-form.js does.
   */
  function cartHost() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function addVariantToCart(variantId, trigger) {
    var routes = window.routes || {};
    var url = routes.cart_add_url || (shopRoot() + 'cart/add.js');
    var cart = cartHost();
    var body = { items: [{ id: variantId, quantity: 1 }] };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map(function (section) { return section.id; });
      body.sections_url = window.location.pathname;
    }
    if (cart && typeof cart.setActiveElement === 'function') cart.setActiveElement(trigger || document.activeElement);

    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    })
      .then(function (response) { return response.json(); })
      .then(function (response) {
        // Shopify answers an error as {status, message, description} with a 4xx.
        if (response && response.status) {
          throw new Error(response.description || response.message || 'cart error');
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: variantId, cartData: response });
        }
        if (cart && typeof cart.renderContents === 'function') {
          cart.renderContents(response);
        } else {
          window.location = routes.cart_url || (shopRoot() + 'cart');
        }
        return response;
      });
  }

  /** One delegated listener for every [data-neo-cart-variant] button on the page. */
  var cartButtonsBound = false;
  function bindCartButtons() {
    if (cartButtonsBound) return;
    cartButtonsBound = true;
    document.addEventListener('click', function (event) {
      var button = event.target && event.target.closest ? event.target.closest('[data-neo-cart-variant]') : null;
      if (!button || button.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      var spinner = button.querySelector('.loading__spinner');
      var error = button.parentNode ? button.parentNode.querySelector('.neo-cart-error') : null;
      if (error) error.hidden = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
      addVariantToCart(button.getAttribute('data-neo-cart-variant'), button)
        .catch(function (err) {
          warnOnce('cart:' + button.getAttribute('data-neo-cart-variant'), 'add to cart failed: ' + (err && err.message ? err.message : err));
          if (!error && button.parentNode) {
            error = document.createElement('p');
            error.className = 'neo-cart-error';
            error.setAttribute('role', 'alert');
            button.parentNode.appendChild(error);
          }
          if (error) {
            error.textContent = lsT('shop.error_add_to_cart', 'Ajout au panier impossible.');
            error.hidden = false;
          }
        })
        .then(function () {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        });
    });
  }


  // ---- 24. shop-the-look -------------------------------------
  // Contract: docs/contentbuilder-json.md "shop-the-look" + shopTheLookModel.ts.
  // Normalised defensively (every rule of normalizeShopTheLookContent), then:
  //   image in a box shaped by aspectMode (auto = natural ratio, presets as
  //   aspect-ratio, custom = customHeight px), object-position cropX/cropY;
  //   overlay in overlayColor at overlayOpacity/100; title + button bottom-
  //   left (button hidden when the label is empty; href = this shop's
  //   buttonCollection ref as {root}collections/{handle}, else the safe
  //   buttonUrl, else no button); one <button> pin per hotspot whose
  //   shopify[] has an entry for window.Shopify.shop, at x%/y% of the box.
  // The product behind a pin is read live at hydrate (loadProduct); a pin
  // whose product fails to load is removed and logged once. Card behaviour
  // mirrors sections/shop-the-look.liquid and ShopTheLookRenderer.tsx:
  // click toggles (one open per element), hover opens on (hover: hover)
  // devices with a 150ms close delay, Esc closes and returns focus to the
  // pin, an outside press closes; an element narrower than 560px shows the
  // card as a panel under the image instead of floating beside the pin.
  // layout (p63): 'banner' | anything else = 'overlay' (the look above).
  // Banner is the Figma display banner (the homepage collections): the root
  // gets `neo-stl--banner`, which the stylesheet's banner skin (shared with
  // the legacy `.hm-stl` className) turns into a top-left page-width title
  // and a full-width bar BELOW the photo; an auto/absent aspectMode becomes
  // aspect-ratio 1691 / 1024 (min-heights live in the CSS). buttonSize is
  // ignored there (bar sizes are CSS); the bar background defaults to
  // #e3b562, text/background colours stay inline, and a bar in that gold
  // (unset or authored) hovers to the theme's #d3a24a.
  var STL_ASPECT_RATIOS = { wide: '16 / 9', landscape: '3 / 2', square: '1 / 1', portrait: '2 / 3' };
  var STL_ASPECT_MODES = ['auto', 'wide', 'landscape', 'square', 'portrait', 'custom'];
  var STL_BANNER_RATIO = '1691 / 1024';
  var STL_BANNER_BUTTON_BG = '#e3b562';
  var STL_BUTTON_SIZES = {
    sm: { padding: '8px 16px', fontSize: '13px', lineHeight: '18px' },
    md: { padding: '10px 20px', fontSize: '14px', lineHeight: '20px' },
    lg: { padding: '14px 28px', fontSize: '16px', lineHeight: '24px' }
  };
  var STL_MAX_HOTSPOTS = 12;
  var STL_NARROW_WIDTH = 560;
  var STL_HOVER_CLOSE_DELAY_MS = 150;
  var STL_CARD_WIDTH = 240;
  var STL_CARD_GAP = 24;
  var STL_CARD_INSET = 8;
  var STL_HOTSPOT_ID = /^[A-Za-z0-9_-]{1,64}$/;

  function clampPct(value) {
    if (typeof value !== 'number' || !isFinite(value)) return 50;
    return Math.round(Math.min(100, Math.max(0, value)) * 100) / 100;
  }

  function normalizeShopifyRefs(raw) {
    if (!Array.isArray(raw)) return [];
    var refs = [];
    var seen = {};
    for (var i = 0; i < raw.length; i++) {
      var entry = raw[i];
      if (!isRecord(entry)) continue;
      var shopId = toPositiveInt(entry.shopId);
      var domain = typeof entry.domain === 'string' ? entry.domain.trim() : '';
      var handle = typeof entry.handle === 'string' ? entry.handle.trim() : '';
      if (shopId === null || !domain || !handle || seen[shopId]) continue;
      seen[shopId] = true;
      refs.push({ shopId: shopId, domain: domain, handle: handle });
    }
    return refs;
  }

  function normalizeHotspot(raw) {
    if (!isRecord(raw)) return null;
    var x = toFiniteNumber(raw.x);
    var y = toFiniteNumber(raw.y);
    if (x === null || y === null) return null;
    var productId = toPositiveInt(raw.productId);
    return {
      id: typeof raw.id === 'string' ? raw.id : '',
      x: clampPct(x),
      y: clampPct(y),
      label: typeof raw.label === 'string' ? raw.label : '',
      cardSide: (raw.cardSide === 'left' || raw.cardSide === 'right') ? raw.cardSide : 'auto',
      productId: productId,
      shopify: productId === null ? [] : normalizeShopifyRefs(raw.shopify)
    };
  }

  function normalizeShopTheLookContent(raw) {
    var src = isRecord(raw) ? raw : {};
    var cropX = toFiniteNumber(src.cropX);
    var cropY = toFiniteNumber(src.cropY);
    var overlay = toFiniteNumber(src.overlayOpacity);
    var customHeight = toFiniteNumber(src.customHeight);
    var hotspots = [];
    var seen = {};
    var rawHotspots = Array.isArray(src.hotspots) ? src.hotspots : [];
    for (var i = 0; i < rawHotspots.length && hotspots.length < STL_MAX_HOTSPOTS; i++) {
      var hotspot = normalizeHotspot(rawHotspots[i]);
      if (!hotspot) continue;
      var id = hotspot.id;
      if (!STL_HOTSPOT_ID.test(id) || seen[id]) {
        var base = 'hs_idx' + hotspots.length;
        id = base;
        for (var suffix = 1; seen[id]; suffix += 1) id = base + '_' + suffix;
      }
      seen[id] = true;
      hotspot.id = id;
      hotspots.push(hotspot);
    }
    var collection = null;
    if (isRecord(src.buttonCollection) && toPositiveInt(src.buttonCollection.id) !== null) {
      collection = { id: toPositiveInt(src.buttonCollection.id), shopify: normalizeShopifyRefs(src.buttonCollection.shopify) };
    }
    return {
      src: typeof src.src === 'string' ? src.src.trim() : '',
      alt: typeof src.alt === 'string' ? src.alt : '',
      cropX: cropX === null ? 50 : clampPct(cropX),
      cropY: cropY === null ? 50 : clampPct(cropY),
      aspectMode: STL_ASPECT_MODES.indexOf(src.aspectMode) !== -1 ? src.aspectMode : 'auto',
      customHeight: customHeight !== null && customHeight >= 1 ? Math.round(customHeight) : null,
      overlayOpacity: overlay === null ? 0 : Math.round(Math.min(100, Math.max(0, overlay))),
      overlayColor: cssColour(src.overlayColor) || '#000000',
      title: typeof src.title === 'string' ? src.title : '',
      titleColor: cssColour(src.titleColor) || '#ffffff',
      buttonLabel: typeof src.buttonLabel === 'string' ? src.buttonLabel : '',
      buttonUrl: typeof src.buttonUrl === 'string' ? src.buttonUrl.trim() : '',
      buttonCollection: collection,
      buttonBackgroundColor: cssColour(src.buttonBackgroundColor),
      buttonTextColor: cssColour(src.buttonTextColor),
      buttonSize: STL_BUTTON_SIZES[src.buttonSize] ? src.buttonSize : 'md',
      layout: src.layout === 'banner' ? 'banner' : 'overlay',
      hotspots: hotspots
    };
  }

  /** This shop's collection ref as a locale-rooted URL, else the safe buttonUrl, else ''. */
  function shopTheLookButtonHref(content) {
    var ref = content.buttonCollection ? resolveShopifyRef(content.buttonCollection.shopify) : null;
    if (ref) return shopRoot() + 'collections/' + encodeURIComponent(ref.handle);
    return safeHref(content.buttonUrl);
  }

  function resolveCardSide(hotspot) {
    if (hotspot.cardSide === 'left' || hotspot.cardSide === 'right') return hotspot.cardSide;
    return hotspot.x > 60 ? 'left' : 'right';
  }

  Neo.registerType('shop-the-look', function (el) {
    var c = normalizeShopTheLookContent(el.content);
    if (!c.src) return '';
    var elId = safeId(el.id);

    var banner = c.layout === 'banner';
    var box = {};
    if (c.aspectMode === 'custom' && c.customHeight) box.height = c.customHeight + 'px';
    else if (STL_ASPECT_RATIOS[c.aspectMode]) box.aspectRatio = STL_ASPECT_RATIOS[c.aspectMode];
    else if (banner) box.aspectRatio = STL_BANNER_RATIO;
    var fixed = !!(box.height || box.aspectRatio);

    var html = viewportStyleTags(el) +
      '<div class="content-type-neo-shop-the-look' + (banner ? ' neo-stl--banner' : '') + '" data-neo-stl="' + escapeAttr(elId) + '"' + dataElAttr(el) + inlineStyleAttr(el) + '>' +
      '<div class="neo-stl__box' + (fixed ? ' neo-stl__box--fixed' : '') + '"' + styleAttrOf(box) + '>' +
      '<div class="neo-stl__media">' +
      '<img class="neo-stl__img" src="' + escapeAttr(c.src) + '" alt="' + escapeAttr(c.alt) + '" loading="lazy"' +
      styleAttrOf({ objectPosition: c.cropX + '% ' + c.cropY + '%' }) + '>';
    if (c.overlayOpacity > 0) {
      html += '<span class="neo-stl__overlay" aria-hidden="true"' +
        styleAttrOf({ backgroundColor: c.overlayColor, opacity: c.overlayOpacity / 100 }) + '></span>';
    }
    html += '</div>';

    var buttonHref = shopTheLookButtonHref(c);
    var showButton = c.buttonLabel.trim() !== '' && !!buttonHref;
    if (c.title || showButton) {
      html += '<div class="neo-stl__caption">';
      // `--stl-title-chars` is the fit-to-width cap of the theme's Display-2
      // recipe (assets/shop-the-look.css): the widest uppercase advance this
      // family averages is 0.665em/char, so `100cqw / (chars * 0.68)` never
      // overflows the box. Stamped here rather than measured in JS, so the
      // title is already the right size in the first paint.
      if (c.title) {
        html += '<h2 class="neo-stl__title"' +
          styleAttrOf({ color: c.titleColor, '--stl-title-chars': Math.max(1, c.title.length) }) +
          '>' + escapeHtml(c.title) + '</h2>';
      }
      if (showButton) {
        // Banner: the bar's padding/font come from the CSS skin, so only the
        // colours go inline (the default bar is the theme gold).
        // A banner bar authored in the default gold counts as the default one,
        // so it gets the theme's #d3a24a hover rather than the generic darken.
        var size = banner ? {} : STL_BUTTON_SIZES[c.buttonSize];
        var defaultButton = c.buttonBackgroundColor === null ||
          (banner && String(c.buttonBackgroundColor).toLowerCase() === STL_BANNER_BUTTON_BG);
        html += '<a class="neo-stl__button' + (defaultButton ? ' neo-stl__button--default' : '') +
          '" href="' + escapeAttr(buttonHref) + '"' +
          styleAttrOf({
            padding: size.padding,
            fontSize: size.fontSize,
            lineHeight: size.lineHeight,
            backgroundColor: c.buttonBackgroundColor || (banner ? STL_BANNER_BUTTON_BG : undefined),
            color: c.buttonTextColor || undefined
          }) + '>' + escapeHtml(c.buttonLabel) + '</a>';
      }
      html += '</div>';
    }

    var skipped = 0;
    for (var i = 0; i < c.hotspots.length; i++) {
      var h = c.hotspots[i];
      if (h.productId === null) continue;
      var ref = resolveShopifyRef(h.shopify);
      if (!ref) { skipped++; continue; }
      var label = h.label.trim();
      html += '<button type="button" class="neo-stl__pin"' +
        ' data-neo-stl-pin="' + escapeAttr(h.id) + '"' +
        ' data-neo-stl-handle="' + escapeAttr(ref.handle) + '"' +
        ' data-neo-stl-side="' + resolveCardSide(h) + '"' +
        ' data-neo-stl-x="' + h.x + '" data-neo-stl-y="' + h.y + '"' +
        (label ? ' data-neo-stl-label="' + escapeAttr(label) + '"' : '') +
        ' aria-label="' + escapeAttr(label || lsT('shop.view_product', 'Voir le produit')) + '"' +
        ' aria-expanded="false"' +
        styleAttrOf({ left: h.x + '%', top: h.y + '%' }) + '>' +
        '<span class="neo-stl__pin-core" aria-hidden="true"></span></button>';
    }
    if (skipped) {
      warnOnce('stl-unmapped:' + elId, 'shop-the-look ' + elId + ': ' + skipped + ' pin(s) have no product mapped for ' +
        (shopDomain() || '(no window.Shopify.shop)') + ' and are skipped');
    }

    html += '</div>'; // .neo-stl__box
    html += '<div class="neo-stl__panel" hidden></div>';
    return html + '</div>';
  });

  function shopTheLookCardHtml(product, name, variant) {
    var image = productImageUrl(product, 480);
    var url = productPageUrl(product);
    return '<div class="neo-stl__card-media">' +
      (image ? '<img class="neo-stl__card-img" src="' + escapeAttr(image) + '" alt="" loading="lazy">' : '') +
      cartControlHtml(product, 'neo-stl__cart', '') +
      '</div>' +
      '<div class="neo-stl__card-meta">' +
      '<h3 class="neo-stl__card-title"><a href="' + escapeAttr(url) + '">' + escapeHtml(name) + '</a></h3>' +
      priceHtml(product) +
      '</div>' +
      (variant === 'panel'
        ? '<button type="button" class="neo-stl__close" aria-label="' + escapeAttr(lsT('common.close', 'Fermer')) + '">' +
          '<span aria-hidden="true">&times;</span></button>'
        : '');
  }

  function initShopTheLook(el) {
    var box = el.querySelector('.neo-stl__box');
    var panel = el.querySelector('.neo-stl__panel');
    if (!box || !panel) return;
    var cardId = 'neo-stl-card-' + (el.getAttribute('data-neo-stl') || Math.random().toString(36).slice(2));
    var products = {};
    var open = null;
    var closeTimer = null;
    var hoverCapable = !!(window.matchMedia && window.matchMedia('(hover: hover)').matches);

    toArray(el.querySelectorAll('.neo-stl__pin')).forEach(function (pin) {
      var id = pin.getAttribute('data-neo-stl-pin');
      loadProduct(pin.getAttribute('data-neo-stl-handle'))
        .then(function (product) {
          products[id] = product;
          if (!pin.getAttribute('data-neo-stl-label') && product.title) {
            pin.setAttribute('aria-label', lsT('shop.view_named', 'Voir __TITLE__').replace('__TITLE__', function () { return product.title; }));
          }
        })
        .catch(function () {
          if (open && open.pin === pin) closeCard(false);
          if (pin.parentNode) pin.parentNode.removeChild(pin);
        });
    });

    /** A CSS custom property so a skin can align the switch with its own
     *  mobile breakpoint (the banner skin, `.neo-stl--banner` / `.hm-stl`,
     *  does: its card layout changes at 750px). */
    function cssNumber(name, fallback) {
      if (!window.getComputedStyle) return fallback;
      var raw = parseFloat(window.getComputedStyle(el).getPropertyValue(name));
      return isFinite(raw) && raw > 0 ? raw : fallback;
    }

    function isNarrow() {
      return el.offsetWidth > 0 && el.offsetWidth < cssNumber('--neo-stl-narrow', STL_NARROW_WIDTH);
    }

    function cancelClose() {
      if (closeTimer !== null) window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    function scheduleClose() {
      cancelClose();
      closeTimer = window.setTimeout(function () {
        closeTimer = null;
        closeCard(false);
      }, STL_HOVER_CLOSE_DELAY_MS);
    }

    function placeFloating(card, pin) {
      var boxWidth = box.offsetWidth;
      var boxHeight = box.offsetHeight;
      var x = Number(pin.getAttribute('data-neo-stl-x')) || 0;
      var y = Number(pin.getAttribute('data-neo-stl-y')) || 0;
      var side = pin.getAttribute('data-neo-stl-side') === 'left' ? 'left' : 'right';
      // The STYLESHEET owns the card width — the generic 240px, or a skin's own
      // `--neo-stl-card-width` (the banner skin reproduces the theme section's
      // clamp(230px, 19.5vw, 329px)). Measured, not read: an unregistered
      // custom property computes to its token stream, so `clamp(…)` would come
      // back as the literal text. Clearing the inline width first also keeps a
      // resize responsive instead of freezing the first measurement.
      card.style.width = '';
      var cardWidth = card.offsetWidth || STL_CARD_WIDTH;
      var cardHeight = card.offsetHeight;
      if (!boxWidth || !boxHeight || !cardHeight) {
        card.style.left = side === 'right' ? 'calc(' + x + '% + ' + STL_CARD_GAP + 'px)' : 'calc(' + x + '% - ' + (STL_CARD_GAP + cardWidth) + 'px)';
        card.style.top = y + '%';
        card.style.transform = 'translateY(-50%)';
        return;
      }
      var pinLeft = (x / 100) * boxWidth;
      var pinTop = (y / 100) * boxHeight;
      var rawLeft = side === 'right' ? pinLeft + STL_CARD_GAP : pinLeft - STL_CARD_GAP - cardWidth;
      var maxLeft = Math.max(STL_CARD_INSET, boxWidth - cardWidth - STL_CARD_INSET);
      var maxTop = Math.max(STL_CARD_INSET, boxHeight - cardHeight - STL_CARD_INSET);
      card.style.transform = '';
      card.style.left = Math.round(Math.min(maxLeft, Math.max(STL_CARD_INSET, rawLeft))) + 'px';
      card.style.top = Math.round(Math.min(maxTop, Math.max(STL_CARD_INSET, pinTop - cardHeight / 2))) + 'px';
    }

    function openCard(pin, focusCard) {
      var id = pin.getAttribute('data-neo-stl-pin');
      var product = products[id];
      if (!product) return; // still loading, or failed (the pin is being removed)
      if (open && open.pin !== pin) closeCard(false);
      if (open) return;

      var narrow = isNarrow();
      var name = pin.getAttribute('data-neo-stl-label') || product.title || lsT('shop.view_product', 'Voir le produit');
      var card = document.createElement('div');
      card.className = 'neo-stl__card neo-stl__card--' + (narrow ? 'panel' : 'floating');
      card.id = cardId;
      card.setAttribute('role', 'dialog');
      card.setAttribute('aria-label', name);
      card.setAttribute('tabindex', '-1');
      card.innerHTML = shopTheLookCardHtml(product, name, narrow ? 'panel' : 'floating');

      if (narrow) {
        panel.innerHTML = '';
        panel.appendChild(card);
        panel.hidden = false;
      } else {
        box.appendChild(card);
        placeFloating(card, pin);
      }
      pin.setAttribute('aria-expanded', 'true');
      pin.setAttribute('aria-controls', cardId);
      el.classList.add('is-open');
      open = { pin: pin, card: card, narrow: narrow };
      // The narrow panel sits after the whole box in the DOM, so a click that
      // opened it moves focus into it; the floating card follows its pin.
      if (focusCard && narrow) card.focus();
    }

    function closeCard(returnFocus) {
      cancelClose();
      if (!open) return;
      var current = open;
      open = null;
      if (current.card.parentNode) current.card.parentNode.removeChild(current.card);
      panel.hidden = true;
      current.pin.setAttribute('aria-expanded', 'false');
      current.pin.removeAttribute('aria-controls');
      el.classList.remove('is-open');
      if (returnFocus) current.pin.focus();
    }

    el.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var pin = target.closest('.neo-stl__pin');
      if (pin && el.contains(pin)) {
        cancelClose();
        if (open && open.pin === pin) closeCard(false);
        else openCard(pin, true);
        return;
      }
      if (target.closest('.neo-stl__close')) closeCard(true);
    });

    document.addEventListener('keydown', function (event) {
      if (open && event.key === 'Escape') closeCard(true);
    });

    document.addEventListener('pointerdown', function (event) {
      if (!open) return;
      var target = event.target;
      if (!(target instanceof Node)) return;
      if (open.card.contains(target) || open.pin.contains(target)) return;
      closeCard(false);
    });

    if (hoverCapable) {
      el.addEventListener('mouseover', function (event) {
        var target = event.target && event.target.closest ? event.target.closest('.neo-stl__pin, .neo-stl__card') : null;
        if (!target) return;
        cancelClose();
        if (target.classList.contains('neo-stl__pin')) openCard(target, false);
      });
      el.addEventListener('mouseout', function (event) {
        var from = event.target && event.target.closest ? event.target.closest('.neo-stl__pin, .neo-stl__card') : null;
        if (!from) return;
        if (event.relatedTarget && from.contains(event.relatedTarget)) return;
        scheduleClose();
      });
    }

    // A resize can flip the narrow mode or move the pin under a floating card.
    var resizeFrame = 0;
    window.addEventListener('resize', function () {
      if (!open || resizeFrame) return;
      resizeFrame = window.requestAnimationFrame(function () {
        resizeFrame = 0;
        if (!open) return;
        if (open.narrow !== isNarrow()) {
          var pin = open.pin;
          closeCard(false);
          openCard(pin, false);
        } else if (!open.narrow) {
          placeFloating(open.card, open.pin);
        }
      });
    });
  }

  function hydrateShopTheLooks(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var hosts = scope.querySelectorAll('[data-neo-stl]');
    if (!hosts.length) return;
    bindCartButtons();
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].getAttribute('data-neo-stl-ready')) continue;
      hosts[i].setAttribute('data-neo-stl-ready', '1');
      initShopTheLook(hosts[i]);
    }
  }

  // ---- 25. product-grid --------------------------------------
  // content (published shape, PageBuilder.tsx serializeElementsForHeadless):
  // products[] (EmbeddedProduct snapshots, each with productHandles[] =
  // [{shopifyDomain, productHandle}]; or, on a category-driven block the
  // aico sync filled in, {title, handle, image, price, url} for this shop),
  // productHandles[] (the same list flattened at element level, the only
  // thing left when there is no snapshot), items[] (theme link-list form),
  // columns, layoutVariant (grid-small/medium/large, bento, list,
  // carousel, single-card) with the legacy displayMode precedence of
  // productLayout.ts, showPrice / showDescription / showButton, buttonText,
  // buttonMode (cart|details), cardStyle (card|plain), cardBackgroundColor,
  // maxProducts, carouselGap, slidesToShow, bentoPreset, bentoColumnSizes /
  // bentoRowSizes, productSizes, buttonColor / buttonTextColor, showSaleBadge
  // (default false: a "Promo!" tag on the media when compare_at_price > price;
  // the sold-out tag keeps priority).
  // Each product resolves to the handle whose shopifyDomain is this shop and
  // is read live (title, image, price, availability, variants); the snapshot
  // only lends its localised short description. The element paints skeleton
  // slots at once and swaps each one for its card as the product arrives; a
  // product without a handle here or that fails to load drops out (logged
  // once). Not ported: filters, size chips, entry animations, spotlight and
  // showcase variants (they render as the grid).
  var BENTO_PRESETS = {
    hero: { areas: [[2, 2], [1, 1], [1, 1]], minH: [360, 175, 175] },
    magazine: { areas: [[2, 1], [2, 1], [1, 1], [1, 1], [2, 1]], minH: [220, 220, 200, 200, 200] },
    banner: { areas: [[2, 1], [1, 1], [1, 1]], minH: [280, 220, 220] },
    mosaic: { areas: [[1, 2], [1, 1], [1, 1], [2, 1]], minH: [360, 175, 175, 200] },
    staggered: { areas: [[1, 1], [1, 1], [1, 1], [1, 1]], minH: [220, 220, 220, 220] }
  };
  var LEGACY_BENTO_SIZES = { small: [1, 1], medium: [2, 1], large: [2, 2], full: [3, 1], 'full-tall': [3, 2] };
  var PRODUCT_LAYOUTS = ['grid', 'grid-small', 'grid-medium', 'grid-large', 'bento', 'list', 'carousel', 'single-card'];

  /** productLayout.ts resolveProductLayout: legacy displayMode first. */
  function resolveProductLayout(c) {
    var legacy = { carousel: 'carousel', card: 'single-card', bento: 'bento' }[String(c.displayMode || '')];
    if (legacy) return legacy;
    var variant = typeof c.layoutVariant === 'string' ? c.layoutVariant : '';
    return PRODUCT_LAYOUTS.indexOf(variant) !== -1 ? variant : 'grid-medium';
  }

  function parseBentoSize(token) {
    if (!token) return null;
    if (LEGACY_BENTO_SIZES[token]) return LEGACY_BENTO_SIZES[token];
    var match = /^(\d+)x(\d+)$/.exec(String(token));
    return match ? [Math.max(1, parseInt(match[1], 10)), Math.max(1, parseInt(match[2], 10))] : null;
  }

  /** The snapshot translation for the request locale (currentLang, e.g. fr_CH). */
  function productTranslation(snapshot) {
    var translations = isRecord(snapshot && snapshot.translations) ? snapshot.translations : {};
    var keys = Object.keys(translations);
    if (!keys.length) return {};
    var locale = typeof currentLang !== 'undefined' ? String(currentLang) : 'fr_CH';
    if (translations[locale]) return translations[locale];
    var lang = locale.split('_')[0];
    for (var i = 0; i < keys.length; i++) if (keys[i].split('_')[0] === lang) return translations[keys[i]];
    return translations[keys[0]] || {};
  }

  /**
   * The products of a block as [{handle, ...card hints}] for THIS shop, in
   * authored order. Three shapes reach the storefront:
   *   - aiconeo publish: `products[]` EmbeddedProduct snapshots, each with
   *     `productHandles[] = [{shopifyDomain, productHandle}]`; the entry whose
   *     domain is this shop wins, a product without one is skipped;
   *   - aico sync: a category-driven block ships `products[]` as
   *     `{title, handle, image, price, url}` written FOR this shop
   *     (ProductMetafieldConverterService::embedNeoProductSnapshots), so a bare
   *     `handle` is taken as is;
   *   - theme link-list form (utils/themeLinkItems.ts): no catalog picks at all,
   *     `items[] = [{handle, href, label}]`.
   * With no snapshot the element-level `productHandles[]` (domain-matched) is
   * the last resort.
   */
  function productEntry(handle, snapshot, tr) {
    var s = isRecord(snapshot) ? snapshot : {};
    var t = isRecord(tr) ? tr : {};
    return {
      handle: handle,
      id: s.id != null ? String(s.id) : '',
      description: (typeof s.descriptionOverride === 'string' && s.descriptionOverride.trim()) ||
        (typeof t.smallDescription === 'string' ? t.smallDescription : ''),
      detailUrl: safeHref(s.detailUrl),
      cardBgColor: cssColour(s.cardBgColor),
      focalX: s.focalX !== undefined ? clampInt(s.focalX, 0, 100, 50) : 50,
      focalY: s.focalY !== undefined ? clampInt(s.focalY, 0, 100, 50) : 50,
      imageFit: s.imageFit === 'contain' ? 'contain' : ''
    };
  }

  function productEntries(c) {
    var entries = [];
    var i;
    var products = Array.isArray(c.products) ? c.products : [];
    if (products.length) {
      for (i = 0; i < products.length; i++) {
        var snapshot = products[i];
        if (!isRecord(snapshot)) continue;
        if (Array.isArray(snapshot.productHandles)) {
          var ref = resolveShopifyRef(snapshot.productHandles, 'shopifyDomain');
          if (!ref || typeof ref.productHandle !== 'string' || !ref.productHandle.trim()) continue;
          entries.push(productEntry(ref.productHandle.trim(), snapshot, productTranslation(snapshot)));
        } else if (typeof snapshot.handle === 'string' && snapshot.handle.trim()) {
          entries.push(productEntry(snapshot.handle.trim(), snapshot, null));
        }
      }
      return entries;
    }
    var items = Array.isArray(c.items) ? c.items : [];
    var hasCatalogPicks = (Array.isArray(c.productIds) && c.productIds.length > 0) || !!c.categoryId;
    if (items.length && !hasCatalogPicks) {
      for (i = 0; i < items.length; i++) {
        if (isRecord(items[i]) && typeof items[i].handle === 'string' && items[i].handle.trim()) {
          entries.push(productEntry(items[i].handle.trim(), null, null));
        }
      }
      return entries;
    }
    var handles = Array.isArray(c.productHandles) ? c.productHandles : [];
    var wanted = shopDomain();
    for (i = 0; i < handles.length; i++) {
      var entry = handles[i];
      if (!isRecord(entry) || typeof entry.productHandle !== 'string' || !entry.productHandle.trim()) continue;
      if (typeof entry.shopifyDomain !== 'string' || entry.shopifyDomain.trim().toLowerCase() !== wanted) continue;
      entries.push(productEntry(entry.productHandle.trim(), null, null));
    }
    return entries;
  }

  Neo.registerType('product-grid', function (el) {
    var c = el.content || {};
    var elId = safeId(el.id);
    var entries = productEntries(c);
    if (!entries.length) {
      warnOnce('products-unmapped:' + elId, 'product-grid ' + elId + ': no product is mapped for ' +
        (shopDomain() || '(no window.Shopify.shop)') + '; nothing rendered');
      return '';
    }
    var layout = resolveProductLayout(c);
    entries = entries.slice(0, clampInt(c.maxProducts, 1, 60, 12));
    var columns = layout === 'single-card' ? 1 : clampInt(c.columns, 1, 6, 3);
    var opts = {
      layout: layout,
      showPrice: c.showPrice !== false,
      showDescription: c.showDescription !== false,
      showButton: c.showButton !== false,
      // `undefined` (dropped by JSON.stringify) rather than false, so a grid
      // saved before the field existed keeps its exact data-neo-products-opts.
      showSaleBadge: c.showSaleBadge === true ? true : undefined,
      buttonText: productButtonText(c.buttonText),
      buttonMode: c.buttonMode === 'details' ? 'details' : 'cart',
      plain: c.cardStyle === 'plain',
      cardBg: cssColour(c.cardBackgroundColor),
      buttonBg: cssColour(c.buttonColor),
      buttonFg: cssColour(c.buttonTextColor),
      newTab: c.openLinksInNewTab === true,
      imageFit: c.imageFit === 'contain' ? 'contain' : 'cover',
      large: layout === 'grid-large'
    };

    // The wrapper: authored styles (display/gap/padding) plus the layout's own tracks.
    var styles = pickStyles(el, 'desktop');
    var wrap = {};
    for (var k in styles) {
      if (Object.prototype.hasOwnProperty.call(styles, k)) wrap[k] = styles[k];
    }
    var slotStyles = [];
    if (layout === 'bento') {
      var preset = BENTO_PRESETS[c.bentoPreset] || BENTO_PRESETS.hero;
      var numCols = clampInt(c.columns, 2, 12, 3);
      if (!(Number(c.columns) >= 2)) numCols = 3;
      var colSizes = Array.isArray(c.bentoColumnSizes) && c.bentoColumnSizes.length === numCols ? c.bentoColumnSizes.map(clampFr) : null;
      var rowSizes = Array.isArray(c.bentoRowSizes) && c.bentoRowSizes.length ? c.bentoRowSizes.map(clampFr) : null;
      var sizes = isRecord(c.productSizes) ? c.productSizes : {};
      var hasCustom = Object.keys(sizes).length > 0;
      wrap.display = 'grid';
      wrap.gridTemplateColumns = colSizes
        ? colSizes.map(function (s) { return s + 'fr'; }).join(' ')
        : 'repeat(' + numCols + ', minmax(0, 1fr))';
      if (rowSizes) wrap.gridTemplateRows = rowSizes.map(function (s) { return 'minmax(' + Math.round(s * 180) + 'px, auto)'; }).join(' ');
      for (var b = 0; b < entries.length; b++) {
        var custom = parseBentoSize(sizes[entries[b].id]);
        var span = custom || ((!hasCustom && preset.areas[b]) ? preset.areas[b] : [1, 1]);
        var minH = custom ? span[1] * 160 : ((!hasCustom && preset.minH[b]) ? preset.minH[b] : 220);
        slotStyles.push({
          gridColumn: span[0] > 1 ? 'span ' + Math.min(span[0], numCols) : undefined,
          gridRow: span[1] > 1 ? 'span ' + span[1] : undefined,
          minHeight: rowSizes ? undefined : minH + 'px'
        });
        entries[b].large = span[0] >= 2 && span[1] >= 2;
      }
    } else if (layout === 'carousel') {
      wrap.display = 'block';
      wrap['--neo-slides'] = String(clampInt(c.slidesToShow, 1, 8, 4));
      wrap['--neo-gap'] = clampInt(c.carouselGap, 0, 48, 16) + 'px';
    } else if (layout === 'list') {
      wrap.display = 'grid';
      wrap.gridTemplateColumns = 'minmax(0, 1fr)';
    } else {
      wrap.display = 'grid';
      wrap.gridTemplateColumns = 'repeat(' + columns + ', minmax(0, 1fr))';
    }

    var html = viewportStyleTags(el) +
      '<div class="content-type-neo-product-grid neo-products neo-products--' + layout + (opts.plain ? ' neo-products--plain' : '') + '"' +
      ' data-neo-products="' + escapeAttr(elId) + '"' +
      ' data-neo-products-opts="' + escapeAttr(JSON.stringify(opts)) + '"' +
      dataElAttr(el) + styleAttrOf(wrap) + '>';
    var slots = '';
    for (var i = 0; i < entries.length; i++) {
      // The visually-hidden text keeps the empty-column collapse (§14) from
      // reading a still-loading grid as an empty column.
      slots += '<div class="neo-products__skeleton" data-neo-products-slot="' + i + '"' +
        ' data-neo-products-entry="' + escapeAttr(JSON.stringify(entries[i])) + '"' +
        styleAttrOf(slotStyles[i] || {}) + '>' +
        '<span class="visually-hidden">' + escapeHtml(lsT('common.loading', 'Chargement…')) + '</span></div>';
    }
    html += layout === 'carousel' ? '<div class="neo-products__track">' + slots + '</div>' : slots;
    return html + '</div>';
  });

  function productDescription(entry, product) {
    if (entry.description) return stripTags(entry.description);
    var live = stripTags(product && product.description);
    return live.length > 120 ? live.slice(0, 117) + '…' : live;
  }

  // aiconeo seeds every new products block with its own German default label
  // (elements.ts: buttonText 'In den Warenkorb'), so an untouched block showed
  // German on the French and English site (140065). That default means "no
  // custom label": fall through to the localised theme string instead.
  function productButtonText(value) {
    var text = typeof value === 'string' ? value.trim() : '';
    return text.toLowerCase() === 'in den warenkorb' ? '' : text;
  }

  function productButtonHtml(entry, product, opts) {
    if (!opts.showButton) return '';
    var style = styleAttrOf({ backgroundColor: opts.buttonBg || undefined, color: opts.buttonFg || undefined });
    var text;
    if (opts.buttonMode === 'details') {
      var href = entry.detailUrl || productPageUrl(product);
      text = opts.buttonText || lsT('shop.view_product', 'Voir le produit');
      return '<a class="neo-products__button" href="' + escapeAttr(href) + '"' +
        (opts.newTab ? ' target="_blank" rel="noopener noreferrer"' : '') + style + '>' + escapeHtml(text) + '</a>';
    }
    text = opts.buttonText || lsT('shop.add_to_cart', 'Ajouter au panier');
    return cartControlHtml(product, 'neo-products__button', text, style);
  }

  function productCardHtml(entry, product, opts) {
    var name = product.title || '';
    var url = productPageUrl(product);
    var image = productImageUrl(product, 800);
    var surface = opts.plain ? null : (entry.cardBgColor || opts.cardBg);
    var soldOut = product.available === false;
    var fit = entry.imageFit || opts.imageFit;
    var imgStyle = styleAttrOf({ objectFit: fit, objectPosition: entry.focalX + '% ' + entry.focalY + '%' });
    // One tag at most. Sold out wins: a discount on something that cannot be
    // bought is not an offer. `showSaleBadge` (content, default false) adds the
    // sale tag on the same test priceHtml uses to strike the compare-at price,
    // so the badge and the struck price can never disagree. The media link is
    // aria-hidden, which is fine: the struck price in the meta already tells
    // assistive tech the item is reduced.
    var tag = '';
    if (soldOut) {
      tag = '<span class="neo-products__tag">' + escapeHtml(lsT('shop.sold_out', 'Épuisé')) + '</span>';
    } else if (opts.showSaleBadge && isFinite(Number(product.compare_at_price)) &&
        Number(product.compare_at_price) > Number(product.price)) {
      tag = '<span class="neo-products__tag neo-products__tag--sale">' + escapeHtml(lsT('shop.sale_badge', 'Promo!')) + '</span>';
    }
    var media = '<a class="neo-products__media' + (opts.large ? ' neo-products__media--wide' : '') + '" href="' + escapeAttr(url) + '" tabindex="-1" aria-hidden="true">' +
      (image ? '<img class="neo-products__img" src="' + escapeAttr(image) + '" alt="" loading="lazy"' + imgStyle + '>' : '') +
      tag +
      '</a>';
    var meta = '<div class="neo-products__meta">' +
      '<h3 class="neo-products__title"><a href="' + escapeAttr(url) + '">' + escapeHtml(name) + '</a></h3>' +
      (opts.showDescription && productDescription(entry, product) ? '<p class="neo-products__desc">' + escapeHtml(productDescription(entry, product)) + '</p>' : '') +
      (opts.showPrice ? priceHtml(product) : '') +
      productButtonHtml(entry, product, opts) +
      '</div>';

    if (opts.layout === 'bento') {
      var bentoAttrs = ' class="neo-products__bento' + (entry.large ? ' neo-products__bento--large' : '') + '"';
      return '<div' + bentoAttrs + '>' +
        (image ? '<img class="neo-products__bento-img" src="' + escapeAttr(image) + '" alt="" loading="lazy"' + imgStyle + '>' : '') +
        '<span class="neo-products__bento-scrim" aria-hidden="true"></span>' +
        '<div class="neo-products__bento-body">' +
        '<h3 class="neo-products__bento-title"><a href="' + escapeAttr(url) + '">' + escapeHtml(name) + '</a></h3>' +
        '<div class="neo-products__bento-foot">' +
        (opts.showDescription && productDescription(entry, product) ? '<p class="neo-products__desc">' + escapeHtml(productDescription(entry, product)) + '</p>' : '') +
        (opts.showPrice ? priceHtml(product) : '') +
        productButtonHtml(entry, product, opts) +
        '</div></div></div>';
    }
    return '<article class="neo-products__card' + (opts.layout === 'list' ? ' neo-products__card--row' : '') + '"' +
      (surface ? styleAttrOf({ backgroundColor: surface }) : '') + '>' + media + meta + '</article>';
  }

  function initProductGrid(host) {
    var opts;
    try { opts = JSON.parse(host.getAttribute('data-neo-products-opts') || '{}'); } catch (e) { opts = {}; }
    var slots = toArray(host.querySelectorAll('[data-neo-products-slot]'));
    var pending = slots.length;
    var track = host.querySelector('.neo-products__track');

    function settled() {
      pending -= 1;
      if (pending > 0) return;
      host.setAttribute('data-neo-products-loaded', '1');
      if (track && track.children.length > 1) {
        var nav = buildRailNav(lsT('shop.previous_products', 'Produits précédents'), lsT('shop.next_products', 'Produits suivants'));
        nav.classList.add('ls-rail-nav--story');
        track.insertAdjacentElement('beforebegin', nav);
        bindRailNav(nav, track);
      }
    }

    slots.forEach(function (slot) {
      var entry;
      try { entry = JSON.parse(slot.getAttribute('data-neo-products-entry') || '{}'); } catch (e) { entry = {}; }
      loadProduct(entry.handle)
        .then(function (product) {
          var card = document.createElement('div');
          card.innerHTML = productCardHtml(entry, product, opts);
          var node = card.firstElementChild;
          if (!node) throw new Error('empty card');
          // The slot's grid placement (bento spans, min-height) moves onto the card.
          if (slot.getAttribute('style')) node.setAttribute('style', (node.getAttribute('style') ? node.getAttribute('style') + ';' : '') + slot.getAttribute('style'));
          node.setAttribute('data-neo-products-slot', slot.getAttribute('data-neo-products-slot'));
          slot.parentNode.replaceChild(node, slot);
        })
        .catch(function () {
          if (slot.parentNode) slot.parentNode.removeChild(slot);
        })
        .then(settled);
    });
  }

  function hydrateProductGrids(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var hosts = scope.querySelectorAll('[data-neo-products]');
    if (!hosts.length) return;
    bindCartButtons();
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].getAttribute('data-neo-products-ready')) continue;
      hosts[i].setAttribute('data-neo-products-ready', '1');
      initProductGrid(hosts[i]);
    }
  }

  // ---- 29. table ---------------------------------------------
  //
  // The builder's structured table. Unregistered until now, which is why the
  // club's own authors stopped using it: an unknown type renders '' (see the
  // aggregated warning in §10), so the element they dropped on the canvas was
  // simply not on the page. The workaround is visible in the stored documents
  // as hand-written `<style>` + `<table>` markup inside `html` elements, one
  // page carrying 18 of them with 165 rows between them, every one of which
  // has to be re-authored by hand when a price or a date changes.
  //
  // THE CONTENT MODEL (aiconeo tableModel.ts, the header comment at :14)
  //   rows: TableCell[][]   required. A cell is {text, colSpan?, rowSpan?,
  //                         align?}; `text` is LIMITED INLINE HTML.
  //   headerRow: boolean    required. While true, rows[0] IS the header row.
  //   columns?: [{width?, align?}]      caption?: string
  //   preset? / headerStyle? / rowStyle? / border? / typography? / mobile?
  //
  // Only the first four are written by the aico converter, so every
  // presentational key has to resolve to a default or an IMPORTED table would
  // render unstyled. resolveTableStyle (tableModel.ts:882) falls back to the
  // `simple` preset, and so does the port below.
  //
  // LEGACY SHAPES, STILL IN STORED DOCUMENTS: `rows: string[][]` and a
  // `columnWidths: string[]` beside `columns` (tableModel.ts:29). Both are
  // migrated on read here exactly as upstream migrates them, and neither is
  // ever written back.
  //
  // WHAT IS MIRRORED, AND FROM WHERE
  //   - geometry: buildTableGrid (tableModel.ts:233) is ported verbatim,
  //     including both span clamps. It is not optional bookkeeping: with one
  //     rowSpan above it, `rows[2][0]` is NOT grid column 0, and every border,
  //     alignment and stacked-mobile label below is addressed by GRID column.
  //   - the split box: element styles on the outer div, scrolling on an inner
  //     one (TableBlockRenderer.tsx, note 1). Upstream measured what happens
  //     when the two are merged: the mobile tier stamps `word-break:break-word`
  //     into the element's own bag, it is inherited, every cell's min-content
  //     collapses to one character, the table then always fits its wrapper, so
  //     `overflow-x` never has anything to scroll and the header renders
  //     `Da/tu/m`. Hence the same reset on the scroller here.
  //   - borders are `separate` with zero spacing and a cell draws only its
  //     right and bottom edge, never on the table's outer edge; for the `all`
  //     style the SCROLLER carries the outer border, because a box border
  //     follows its own radius while a collapsed table's straight border gets
  //     clipped at a rounded corner.
  //
  // DELIBERATE DEVIATIONS
  //   - Tailwind's `md` (768px) is upstream's stack/scroll threshold; this file
  //     and content-builder-neo.css use 749px for the mobile tier everywhere
  //     (mobileStyleTag, newsColumnsStyleTag, the row-grid hardening block).
  //     Following upstream's 768 would leave a 19px band where a `card` table
  //     is stacked but still carries its DESKTOP padding, because the element
  //     style tier had not switched yet. The tier wins.
  //   - an empty table renders '' rather than upstream's dashed "Leere Tabelle"
  //     box: that box is `isEditing`-only (ElementRenderer.tsx:4271) and the
  //     storefront is never editing.
  // ============================================================

  var TABLE_MAX_SPAN = 64;
  var TABLE_ALIGNS = { left: 1, center: 1, right: 1 };

  /** tableModel.ts asSpan: a whole number >= 2, capped. 1 and junk mean "no span". */
  function tableSpan(raw) {
    var n = typeof raw === 'number' ? raw : (typeof raw === 'string' ? Number(raw) : NaN);
    if (!isFinite(n)) return 0;
    var whole = Math.floor(n);
    return whole <= 1 ? 0 : Math.min(whole, TABLE_MAX_SPAN);
  }

  function tableAlign(raw) {
    return (typeof raw === 'string' && TABLE_ALIGNS[raw] === 1) ? raw : '';
  }

  /** One cell, from the object shape, a legacy plain string, or junk. */
  function normalizeTableCell(raw) {
    if (raw == null) return { text: '' };
    if (typeof raw === 'string') return { text: raw };
    if (typeof raw === 'number' || typeof raw === 'boolean') return { text: String(raw) };
    if (typeof raw !== 'object' || Array.isArray(raw)) return { text: '' };
    var cell = { text: typeof raw.text === 'string' ? raw.text : (raw.text == null ? '' : String(raw.text)) };
    var colSpan = tableSpan(raw.colSpan);
    if (colSpan) cell.colSpan = colSpan;
    var rowSpan = tableSpan(raw.rowSpan);
    if (rowSpan) cell.rowSpan = rowSpan;
    var align = tableAlign(raw.align);
    if (align) cell.align = align;
    return cell;
  }

  /** A row that is not an array at all (the converter has emitted a bare string) reads as empty. */
  function normalizeTableRows(raw) {
    if (!Array.isArray(raw)) return [];
    var out = [];
    for (var r = 0; r < raw.length; r++) {
      var row = [];
      if (Array.isArray(raw[r])) {
        for (var i = 0; i < raw[r].length; i++) row.push(normalizeTableCell(raw[r][i]));
      }
      out.push(row);
    }
    return out;
  }

  /**
   * A bare number is the HTML `width="120"` attribute's meaning, pixels
   * (tableModel.ts:147, which cites the Shopify body renderer's own
   * `/^\d+(\.\d+)?$/` -> px rule). Anything else (`40%`, `12rem`) passes
   * through, after the length guard below rejects what is not a length.
   */
  function normalizeColumnWidth(raw) {
    if (raw == null) return '';
    var value = String(raw).trim();
    if (!value) return '';
    return /^\d+(\.\d+)?$/.test(value) ? value + 'px' : value;
  }

  /** `content.columns`, falling back to the legacy `content.columnWidths`. */
  function normalizeTableColumns(content) {
    var c = isRecord(content) ? content : {};
    var out = [];
    var i;
    if (Array.isArray(c.columns)) {
      for (i = 0; i < c.columns.length; i++) {
        var entry = c.columns[i];
        // Tolerate `columns: string[]` - a half-migrated document, or a hand-written import.
        if (typeof entry === 'string') {
          out.push({ width: normalizeColumnWidth(entry), align: '' });
          continue;
        }
        var src = isRecord(entry) ? entry : {};
        out.push({ width: normalizeColumnWidth(src.width), align: tableAlign(src.align) });
      }
      return out;
    }
    if (Array.isArray(c.columnWidths)) {
      for (i = 0; i < c.columnWidths.length; i++) {
        out.push({ width: normalizeColumnWidth(c.columnWidths[i]), align: '' });
      }
    }
    return out;
  }

  /**
   * Lay the rows out the way a browser does: a port of tableModel.ts
   * buildTableGrid (:233). Walk each row left to right, skipping every slot a
   * previous row's `rowSpan` (or this row's own `colSpan`) already claimed.
   *
   * Both clamps are upstream's and both matter: `rowSpan` is clamped to the rows
   * that exist, so a malformed import cannot address a row past the end, and
   * `colSpan` is clamped to the run of FREE slots to its right, because a
   * colSpan running into a slot the row above already owns makes the browser
   * draw two cells on top of each other. Nothing here throws.
   */
  function buildTableGrid(rows) {
    var rowCount = rows.length;
    var placed = [];
    var claimed = [];
    var columnCount = 0;
    var r, c;
    for (r = 0; r < rowCount; r++) { placed.push([]); claimed.push([]); }
    for (r = 0; r < rowCount; r++) {
      var column = 0;
      for (var index = 0; index < rows[r].length; index++) {
        var cell = rows[r][index];
        while (claimed[r][column]) column++;
        var wanted = Math.min(Math.max(1, Math.floor(cell.colSpan || 1)), TABLE_MAX_SPAN);
        var colSpan = 1;
        while (colSpan < wanted && !claimed[r][column + colSpan]) colSpan++;
        var rowSpan = Math.min(Math.max(1, Math.floor(cell.rowSpan || 1)), TABLE_MAX_SPAN, rowCount - r);
        var entry = { cell: cell, row: r, index: index, colStart: column, colSpan: colSpan, rowSpan: rowSpan };
        placed[r].push(entry);
        for (var dr = 0; dr < rowSpan; dr++) {
          for (var dc = 0; dc < colSpan; dc++) claimed[r + dr][column + dc] = entry;
        }
        column += colSpan;
        if (column > columnCount) columnCount = column;
      }
    }
    var occupancy = [];
    for (r = 0; r < rowCount; r++) {
      var slots = [];
      for (c = 0; c < columnCount; c++) slots.push(claimed[r][c] || null);
      occupancy.push(slots);
    }
    return { placed: placed, occupancy: occupancy, rowCount: rowCount, columnCount: columnCount };
  }

  // ---- cell HTML: the security-critical part ------------------
  //
  // A cell's `text` is limited inline HTML - <a href target rel>, <strong>,
  // <em>, <br> and nothing else (tableModel.ts:709) - and upstream sanitises it
  // on the RENDER path, not on write, because content already in the database
  // was written before that rule existed and a converter run can put anything
  // in there. Same here, with two differences forced by this file:
  //
  //   - no DOMPurify on the storefront. The sanitiser below is a single scan
  //     that escapes every run of text and rebuilds each allowed tag FROM
  //     SCRATCH: an attribute is never passed through, it is re-emitted from a
  //     value that went through safeHref/escapeAttr. Nothing that is not one of
  //     the four tags can survive as markup, so the escape-by-default direction
  //     is the one DOMPurify gives.
  //   - upstream's no-DOM fallback (tableModel.ts:740) strips the tags to plain
  //     text. That is the wrong trade on a page: an imported price table's
  //     <strong> totals and its links are exactly what the author is looking at
  //     in the builder, and the theme has no DOM at render time either (the
  //     renderer builds a string, the mount sets innerHTML once).
  //
  // Unknown tags are UNWRAPPED, keeping their text, which is DOMPurify's
  // KEEP_CONTENT: true (tableModel.ts:751): a converted `<p>` or `<span>`
  // wrapper must not become visible angle brackets. `<script>` and `<style>`
  // go with their contents, which is what DOMPurify does with those two.
  var TABLE_CELL_DROP = /<(script|style)\b[\s\S]*?<\/\1\s*>/gi;
  var TABLE_CELL_TAG = /<(\/?)([a-zA-Z][\w-]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/g;
  var TABLE_CELL_HREF = /href\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;

  /** `<` and `>` only: `&` is left alone so an authored `&amp;` / `&nbsp;` is not double-escaped. */
  function escapeAngles(s) {
    return String(s == null ? '' : s).replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function tableCellHtml(raw) {
    var source = raw == null ? '' : String(raw);
    if (!source) return '';
    var body = source.replace(TABLE_CELL_DROP, '');
    var out = '';
    var depth = 0;
    var last = 0;
    var m;
    TABLE_CELL_TAG.lastIndex = 0;
    while ((m = TABLE_CELL_TAG.exec(body)) !== null) {
      out += escapeAngles(body.slice(last, m.index));
      last = TABLE_CELL_TAG.lastIndex;
      var closing = m[1] === '/';
      var tag = m[2].toLowerCase();
      if (tag === 'br') {
        out += '<br>';
      } else if (tag === 'strong' || tag === 'em') {
        out += closing ? '</' + tag + '>' : '<' + tag + '>';
      } else if (tag === 'a') {
        if (closing) {
          // An opener that was refused leaves no closer either, so tags stay balanced.
          if (depth > 0) { depth--; out += '</a>'; }
        } else {
          var href = TABLE_CELL_HREF.exec(m[3] || '');
          // `&amp;` in an attribute IS an ampersand: decode before the URL is
          // judged, then escapeAttr puts it back. A query string would otherwise
          // pick up a literal `&amp;` on every hop through this renderer.
          var url = href ? safeHref(String(href[1] || href[2] || href[3] || '').replace(/&amp;/gi, '&')) : '';
          // DOMPurify drops a javascript: href but KEEPS the <a>; an anchor with
          // no href is not a link, and upstream unwraps it (tableModel.ts:761).
          if (url) {
            var blank = /target\s*=\s*["']?_blank/i.test(m[3] || '');
            // Without the rel, the opened page can reach back through window.opener.
            out += '<a href="' + escapeAttr(url) + '"' +
              (blank ? ' target="_blank" rel="noopener noreferrer"' : '') + '>';
            depth++;
          }
        }
      }
      // Anything else: the tag is dropped, its text keeps coming.
    }
    out += escapeAngles(body.slice(last));
    while (depth-- > 0) out += '</a>';
    return out;
  }

  /**
   * A cell's text with the markup taken back out, for the stacked-mobile label.
   * Runs on the SANITISED html, so what comes back is already escaped and safe
   * to emit as-is; escaping it a second time would show `&amp;` to the reader.
   */
  function tableCellLabel(raw) {
    return stripTags(tableCellHtml(raw));
  }

  // ---- presets and style resolution ---------------------------
  //
  // tablePresetPatch (tableModel.ts:937) writes a preset out as the WHOLE
  // bundle of granular keys, not as `preset: <id>`, so a table whose author ever
  // clicked a preset button carries literal `hsl(var(--muted))` strings in its
  // stored content. Those are shadcn design tokens from the builder's own theme;
  // this theme defines none of them, and an unresolvable var() makes the whole
  // declaration invalid - including, for `border: 1px solid <invalid>`, the
  // border itself. So the five tokens the presets use are translated to the
  // theme's colour variables (the same `rgba(var(--color-foreground, 18, 18,
  // 18), a)` form the rest of content-builder-neo.css uses), and a colour the
  // author picked by hand passes through this file's own CSS_COLOUR rule.
  var TABLE_MUTED = 'rgba(var(--color-foreground, 18, 18, 18), 0.06)';
  var TABLE_ZEBRA = 'rgba(var(--color-foreground, 18, 18, 18), 0.04)';
  var TABLE_FOREGROUND = 'rgb(var(--color-foreground, 18, 18, 18))';
  var TABLE_BACKGROUND = 'rgb(var(--color-background, 255, 255, 255))';
  var TABLE_BORDER = 'rgba(var(--color-foreground, 18, 18, 18), 0.15)';

  var TABLE_TOKEN_COLOURS = {
    'hsl(var(--muted))': TABLE_MUTED,
    'hsl(var(--muted) / 0.5)': TABLE_ZEBRA,
    'hsl(var(--foreground))': TABLE_FOREGROUND,
    'hsl(var(--background))': TABLE_BACKGROUND,
    'hsl(var(--border))': TABLE_BORDER
  };

  function tableColour(raw, fallback) {
    var value = typeof raw === 'string' ? raw.trim() : '';
    if (!value) return fallback;
    if (TABLE_TOKEN_COLOURS[value]) return TABLE_TOKEN_COLOURS[value];
    // Any OTHER var() is a token name this theme does not define. Emitting it
    // would not fail loudly, it would silently drop the declaration.
    if (value.indexOf('var(--') !== -1) return fallback;
    return cssColour(value) || fallback;
  }

  /**
   * A length the author typed into the Stil tab (border width / radius, cell
   * padding, column width, font size). It is interpolated into a style
   * attribute, so it may not carry `;` or `(`: escapeAttr keeps it from breaking
   * OUT of the attribute, but not from adding declarations inside it.
   */
  function tableLength(raw, fallback) {
    var value = typeof raw === 'string' ? raw.trim() : (typeof raw === 'number' ? String(raw) : '');
    return (value && /^[0-9a-z.%\s]+$/i.test(value)) ? value : (fallback || '');
  }

  var TABLE_DENSITY_PADDING = { compact: '4px 8px', normal: '8px 12px', roomy: '14px 18px' };

  // TABLE_PRESETS, flattened (tableModel.ts:812). Flat rather than nested
  // because every reader below wants one value, not a group, and ES5 has no
  // spread to rebuild groups with.
  var TABLE_PRESETS = {
    simple: {
      headerBackground: TABLE_MUTED, headerColor: TABLE_FOREGROUND, headerBold: true, headerSticky: false,
      zebra: false, hover: false, density: 'normal',
      borderStyle: 'horizontal', borderRadius: '0px', fontSize: '14px'
    },
    zebra: {
      headerBackground: TABLE_MUTED, headerColor: TABLE_FOREGROUND, headerBold: true, headerSticky: false,
      zebra: true, hover: true, density: 'normal',
      borderStyle: 'horizontal', borderRadius: '0px', fontSize: '14px'
    },
    datasheet: {
      headerBackground: TABLE_FOREGROUND, headerColor: TABLE_BACKGROUND, headerBold: true, headerSticky: true,
      zebra: false, hover: true, density: 'compact',
      borderStyle: 'all', borderRadius: '0px', fontSize: '13px'
    },
    // No stripes, on purpose upstream: with them `compact` was `zebra` at 13px.
    compact: {
      headerBackground: TABLE_MUTED, headerColor: TABLE_FOREGROUND, headerBold: true, headerSticky: false,
      zebra: false, hover: false, density: 'compact',
      borderStyle: 'horizontal', borderRadius: '0px', fontSize: '13px'
    },
    bordered: {
      headerBackground: TABLE_MUTED, headerColor: TABLE_FOREGROUND, headerBold: true, headerSticky: false,
      zebra: false, hover: false, density: 'normal',
      borderStyle: 'all', borderRadius: '0px', fontSize: '14px'
    },
    // The one preset with an opinion about mobile: its stacked layout is the point of it.
    card: {
      headerBackground: TABLE_MUTED, headerColor: TABLE_FOREGROUND, headerBold: true, headerSticky: false,
      zebra: false, hover: true, density: 'roomy',
      borderStyle: 'all', borderRadius: '12px', fontSize: '15px', mobileBehaviour: 'stack'
    }
  };

  /**
   * The preset's bundle with every explicitly-set granular key laid over it, key
   * by key (resolveTableStyle, tableModel.ts:882). That layering is what makes
   * "pick a preset, then tweak" work in the builder - a tweak is stored as its
   * own key and always wins - and it is also what makes an imported table, which
   * carries no presentational keys at all, look like `simple`.
   */
  function resolveTableStyle(content) {
    var c = isRecord(content) ? content : {};
    var preset = (typeof c.preset === 'string' && TABLE_PRESETS[c.preset]) ? c.preset : 'simple';
    var base = TABLE_PRESETS[preset];
    var headerStyle = isRecord(c.headerStyle) ? c.headerStyle : {};
    var rowStyle = isRecord(c.rowStyle) ? c.rowStyle : {};
    var border = isRecord(c.border) ? c.border : {};
    var typography = isRecord(c.typography) ? c.typography : {};
    var mobile = isRecord(c.mobile) ? c.mobile : {};
    var density = TABLE_DENSITY_PADDING[rowStyle.density] ? rowStyle.density : base.density;
    var borderStyle = (border.style === 'all' || border.style === 'horizontal' || border.style === 'none')
      ? border.style : base.borderStyle;
    return {
      headerBackground: tableColour(headerStyle.background, base.headerBackground),
      headerColor: tableColour(headerStyle.color, base.headerColor),
      headerBold: typeof headerStyle.bold === 'boolean' ? headerStyle.bold : base.headerBold,
      headerSticky: typeof headerStyle.sticky === 'boolean' ? headerStyle.sticky : base.headerSticky,
      zebra: typeof rowStyle.zebra === 'boolean' ? rowStyle.zebra : base.zebra,
      zebraColor: tableColour(rowStyle.zebraColor, TABLE_ZEBRA),
      hover: typeof rowStyle.hover === 'boolean' ? rowStyle.hover : base.hover,
      borderStyle: borderStyle,
      borderColor: tableColour(border.color, TABLE_BORDER),
      borderWidth: tableLength(border.width, '1px'),
      borderRadius: tableLength(border.radius, base.borderRadius),
      fontSize: tableLength(typography.fontSize, base.fontSize),
      cellPadding: tableLength(typography.cellPadding, TABLE_DENSITY_PADDING[density]),
      behaviour: mobile.behaviour === 'stack' ? 'stack'
        : (mobile.behaviour === 'scroll' ? 'scroll' : (base.mobileBehaviour || 'scroll')),
      stickyFirstColumn: mobile.stickyFirstColumn === true
    };
  }

  /**
   * The edges a cell draws itself: right and bottom only, and never on the
   * table's outer edge for `all`, where the scroller's own border is the outer
   * edge. `horizontal` KEEPS the rule under the last row - it is the table's
   * closing rule and there is no box to close it otherwise. Read off the placed
   * spans, so a merged cell counts as reaching the edge it reaches.
   */
  function tableCellBorder(st, entry, grid) {
    if (st.borderStyle === 'none') return {};
    var line = st.borderWidth + ' solid ' + st.borderColor;
    if (st.borderStyle === 'horizontal') return { borderBottom: line };
    var out = {};
    if (entry.colStart + entry.colSpan !== grid.columnCount) out.borderRight = line;
    if (entry.row + entry.rowSpan !== grid.rowCount) out.borderBottom = line;
    return out;
  }

  Neo.registerType('table', function (el) {
    var c = el.content || {};
    var rows = normalizeTableRows(c.rows);
    var grid = buildTableGrid(rows);
    if (!grid.rowCount || !grid.columnCount) return '';

    var columns = normalizeTableColumns(c);
    var st = resolveTableStyle(c);
    var hasHeader = c.headerRow === true;
    var caption = typeof c.caption === 'string' ? c.caption.trim() : '';
    var stack = st.behaviour === 'stack';
    var boxed = st.borderStyle === 'all';
    var radius = (st.borderRadius && st.borderRadius !== '0px') ? st.borderRadius : '';
    var i, j;

    // The label a stacked cell shows beside its value on a phone: the header
    // text for its GRID column, so a header cell with a colSpan labels every
    // column it covers (upstream reads the occupancy map for the same reason).
    var labels = [];
    if (hasHeader && stack) {
      for (i = 0; i < grid.columnCount; i++) {
        var head = grid.occupancy[0][i];
        labels.push(head ? tableCellLabel(head.cell.text) : '');
      }
    }

    function cellHtml(entry, isHeader, zebra) {
      var align = entry.cell.align || (columns[entry.colStart] && columns[entry.colStart].align) || '';
      var frozen = st.stickyFirstColumn && entry.colStart === 0;
      var style = tableCellBorder(st, entry, grid);
      style.padding = st.cellPadding;
      style.verticalAlign = 'top';
      if (align) style.textAlign = align;
      if (isHeader) {
        style.backgroundColor = st.headerBackground;
        style.color = st.headerColor;
        style.fontWeight = st.headerBold ? '600' : '400';
        if (st.headerSticky) { style.position = 'sticky'; style.top = '0'; }
      } else if (frozen) {
        // A frozen cell scrolls OVER the ones behind it, so it has to be opaque;
        // the zebra tint is translucent, so it is layered on top as a gradient
        // rather than used instead of the opaque colour.
        style.backgroundColor = TABLE_BACKGROUND;
        if (zebra) style.backgroundImage = 'linear-gradient(' + st.zebraColor + ', ' + st.zebraColor + ')';
      }
      if (frozen) { style.position = 'sticky'; style.left = '0'; }
      if (isHeader && st.headerSticky && frozen) style.zIndex = 3;
      else if (isHeader && st.headerSticky) style.zIndex = 2;
      else if (frozen) style.zIndex = 1;

      var tag = isHeader ? 'th' : 'td';
      var html = '<' + tag + ' class="neo-table__cell' + (isHeader ? ' neo-table__cell--head' : '') +
        (frozen ? ' neo-table__cell--frozen' : '') + '"' +
        (isHeader ? ' scope="col"' : '') +
        (entry.colSpan > 1 ? ' colspan="' + entry.colSpan + '"' : '') +
        (entry.rowSpan > 1 ? ' rowspan="' + entry.rowSpan + '"' : '') +
        styleAttrOf(style) + '>';
      var label = labels[entry.colStart];
      if (!isHeader && label) html += '<span class="neo-table__label">' + label + '</span>';
      return html + '<span class="neo-table__value">' + tableCellHtml(entry.cell.text) + '</span></' + tag + '>';
    }

    var scroller = { wordBreak: 'normal', overflowWrap: 'break-word' };
    if (boxed) {
      scroller.border = st.borderWidth + ' solid ' + st.borderColor;
      // Stacked below 749px every row is a block and the outer border moves onto
      // each row block, one card per row. An inline style cannot be responsive;
      // a class reading an inherited custom property can.
      scroller['--tbl-bw'] = st.borderWidth;
      scroller['--tbl-bc'] = st.borderColor;
      scroller['--tbl-r'] = radius || '0px';
    }
    if (radius) scroller.borderRadius = radius;

    var tableClass = 'neo-table neo-table--' + (stack ? 'stack' : 'scroll') +
      (st.hover ? ' neo-table--hover' : '') + (boxed ? ' neo-table--boxed' : '');

    var html = viewportStyleTags(el) +
      // The element's own box: padding, margin, background, width, everything
      // the style bag resolved. Nothing here scrolls. The radius is mirrored
      // onto it so an authored background follows the same curve.
      wrapperOpen(el, 'content-type-neo-table', radius ? { borderRadius: radius } : null) +
      // The stack modifier is repeated on the scroller because the outer border
      // moves onto the row blocks below 749px, and the rule that has to switch
      // it off is on THIS node, whose inline border it must beat.
      '<div class="neo-table__scroller' + (stack ? ' neo-table__scroller--stack' : '') + '"' + styleAttrOf(scroller) +
      (st.headerSticky ? ' data-neo-table-sticky="1"' : '') + '>' +
      '<table class="' + tableClass + '"' + styleAttrOf({ fontSize: st.fontSize }) + '>';

    if (caption) html += '<caption class="neo-table__caption">' + escapeHtml(caption) + '</caption>';

    var widths = [];
    var anyWidth = false;
    for (i = 0; i < grid.columnCount; i++) {
      var w = columns[i] ? tableLength(columns[i].width, '') : '';
      if (w) anyWidth = true;
      widths.push(w);
    }
    if (anyWidth) {
      html += '<colgroup>';
      for (i = 0; i < grid.columnCount; i++) html += '<col' + styleAttrOf({ width: widths[i] || undefined }) + '>';
      html += '</colgroup>';
    }

    if (hasHeader) {
      html += '<thead><tr class="neo-table__row neo-table__row--head">';
      for (i = 0; i < grid.placed[0].length; i++) html += cellHtml(grid.placed[0][i], true, false);
      html += '</tr></thead>';
    }

    html += '<tbody>';
    for (i = hasHeader ? 1 : 0; i < grid.rowCount; i++) {
      // Striping counts BODY rows, so the first body row is never tinted whether
      // or not the table has a header (upstream slices, then tests i % 2).
      var zebra = st.zebra && ((i - (hasHeader ? 1 : 0)) % 2 === 1);
      html += '<tr class="neo-table__row"' +
        (zebra ? styleAttrOf({ backgroundColor: st.zebraColor }) : '') + '>';
      for (j = 0; j < grid.placed[i].length; j++) html += cellHtml(grid.placed[i][j], false, zebra);
      html += '</tr>';
    }
    return html + '</tbody></table></div></div>';
  });

  /**
   * `headerStyle.sticky` (the `datasheet` preset turns it on) pins the header
   * against the nearest SCROLLPORT, and a wrapper with `overflow-x: auto` is a
   * scroll container on both axes - per CSS Overflow 3, `visible` alongside a
   * scrolling value computes to `auto`. So with the scroller in place the header
   * pins to a box that never scrolls vertically and the toggle is inert. No CSS
   * gives both on one element.
   *
   * `overflow-x: clip` clips without creating a scroll container, so while the
   * table FITS there is nothing to scroll and the header can pin to the page
   * instead. Measure and flip, exactly as upstream's useTableFits does
   * (TableBlockRenderer.tsx:112), comparing USED widths rather than scrollWidth
   * so the answer does not depend on which mode the wrapper is in at the time.
   * A table wider than its wrapper keeps `auto`: on a phone, scrolling wins.
   * Idempotent via data-neo-table-armed, like hydrateCountdowns.
   */
  function hydrateTableScrollers(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-neo-table-sticky]');
    if (!nodes.length || !('ResizeObserver' in window)) return;
    for (var i = 0; i < nodes.length; i++) {
      (function (box) {
        if (box.getAttribute('data-neo-table-armed')) return;
        box.setAttribute('data-neo-table-armed', '1');
        var table = box.querySelector('table');
        if (!table) return;
        function measure() {
          box.style.overflowX = table.offsetWidth <= box.clientWidth ? 'clip' : 'auto';
        }
        var observer = new ResizeObserver(measure);
        observer.observe(box);
        observer.observe(table);
        measure();
      })(nodes[i]);
    }
  }

  // ============================================================
  // 3. nested columns (section-level `columns` stays with the engine)
  // ============================================================

  function renderNestedColumns(el) {
    var content = (el && el.content) || {};
    var count = parseInt(content.columnCount, 10);
    if (isNaN(count) || count < 1) count = 2;
    if (count > 6) count = 6; // total-columns-1..6 in content-builder-neo.css

    var children = Array.isArray(el.children) ? el.children : [];
    var buckets = [];
    for (var c = 0; c < count; c++) buckets.push([]);

    var fallbackIdx = 0;
    for (var i = 0; i < children.length; i++) {
      var child = children[i];
      var ci = (child && child.content && typeof child.content.columnIndex === 'number')
        ? child.content.columnIndex
        : fallbackIdx++;
      if (ci < 0) ci = 0;
      if (ci >= count) ci = count - 1;
      buckets[ci].push(child);
    }

    var columnsHtml = '';
    for (var col = 0; col < count; col++) {
      var inner = '';
      // §35 (h): the canvas is the COLUMN, not the row. A child placed at 50%
      // of a half-width column is at 50% of that column in both renderers.
      var columnPlaced = false;
      for (var k = 0; k < buckets[col].length; k++) {
        var colChild = renderFreeChild(buckets[col][k]);
        inner += colChild.html;
        if (colChild.placed) columnPlaced = true;
      }
      columnsHtml += '<div class="content-column column__' + (col + 1) + '"' +
        (columnPlaced ? freeformCanvasMark(el) : '') + '>' + inner + '</div>';
    }

    // The ratio rule belongs here too, not only on section-level rows: 59 of the
    // 63 `columns` elements in this club's landing pages are nested inside a
    // container, and every one of the measured builder/shop mismatches was one
    // of those. decorateElementHtml keeps a leading <style> outside its wrapper,
    // so emitting it in front is safe. See columnRatioTemplate.
    return viewportStyleTags(el) +
      columnRatioStyleTag(el, columnRatioTemplate(content.columnRatio, count)) +
      '<div class="content-row total-columns-' + count + '"' + dataElAttr(el) + inlineStyleAttr(el) + '>' +
      columnsHtml +
      '</div>';
  }

  // ============================================================
  // 29. `cards` and `teaser`: the TYPED STOREFRONT pair
  //
  // WHY: both types exist in the builder and neither was drawable here, so an
  // author who picked them got an empty page. What they picked instead was the
  // `html` element: the photo-card-with-overlaid-title tile is hand-written as
  // raw <style> + markup on 7 of the 23 live landing-page documents (measured
  // over the stored documents: pages 4, 5, 8, 9, 13, 14 and 23 each carry a
  // rule matching /::after[^}]*linear-gradient/ next to a /scale\(1\.0\d\)/
  // hover). Every copy re-derives the same four things: an image forced to
  // `position:absolute;inset:0;object-fit:cover`, a bottom-up gradient scrim so
  // white text stays legible, a body pinned to the bottom, and a hover zoom.
  // That is a renderer's job, not an author's.
  //
  // CONTENT MODEL (the builder is the contract; see elements.ts `teaser` /
  // `cards`, ElementRenderer.tsx cases 'teaser' / 'cards', the two editors in
  // properties/ThemeSectionContentEditors.tsx and the shape doc in
  // utils/typedStorefrontContent.ts):
  //
  //   cards   {variant:'cards'|'posts', items:[{
  //              image:{src,alt}|null, heading:{tag,html}|null,
  //              date:{tag,html}|null, texts:[html…],
  //              link:{href,label}|null, linkWrapsCard:boolean}]}
  //   teaser  {images:[{src,alt}], badge:{tag,html}|null,
  //            heading:{tag,html}|null, text:'…html…', button:{label,href}|null}
  //
  // Three traps in that shape, all of them from typedStorefrontContent.ts:
  //   - a `{tag, html}` field holds HTML SOURCE (`<strong>`, `<br>`, `&shy;`),
  //     so it is emitted verbatim exactly as the engine's own `heading` / `text`
  //     renderers do (content-builder-neo.js:220-233). Escaping it would print
  //     the tags on the page; only the TAG NAME is validated here.
  //   - an absent field is `null`, never `{}`: the writers return null on
  //     purpose so the theme's `{%- if h -%}` test still means "removed". A
  //     truthiness test on the object is therefore not enough: `{tag:'h4'}`
  //     with no html must draw nothing rather than an empty heading.
  //   - `texts` is an ARRAY, one entry per paragraph, and a bare string is a
  //     legal legacy value (readNeoTexts).
  //
  // WHY NOT gridCardHtml: it is the same picture and none of the same data.
  // It escapes its title and description (§23 above: `escapeHtml(cell.title)`)
  // where a card heading is HTML source; it hard-codes `<h3>` where a card
  // carries its own tag (the editor seeds 'h4', a converted page can hold
  // anything from h1 to h5); it takes ONE `description` string where a card has
  // `texts[]`; it has no `date`; and it reuses the title as the image alt where
  // a card authors a real one. Widening its signature would edit the homepage
  // collage's card path for no gain, so the two stay separate renderers that
  // share a look rather than an implementation.
  //
  // DEVIATIONS from the builder, called out per the FIDELITY NOTE above:
  //   - `linkWrapsCard:false` is documented upstream as "renders as a slider
  //     item" (ElementRenderer.tsx, the linkWrapsCard comment). This theme
  //     ships no carousel hydrator (`testimonial`'s slider mode makes the same
  //     call), so a non-wrapping card renders as a stacked tile in the same
  //     grid, with the link as a trailing text link. Nothing is lost but the
  //     motion.
  //   - a teaser with several images is a slider upstream. Same reason, so it
  //     is a CSS scroll-snap track instead (the pattern `grid` already uses for
  //     `mobileLayout:'carousel'`), which needs no JS and still reaches every
  //     image by swipe, trackpad or keyboard.
  //   - the image SIDE is a SECTION setting upstream (`sp-teaser--left|--right`,
  //     ElementRenderer.tsx case 'teaser'). A section in this theme's document
  //     model carries only `layout`, `styles` and the visibility window
  //     (wrapSection above), so there is no channel to read it from; the
  //     element-level `content.side` the palette seeds is the authority here,
  //     exactly as it is in the builder's own preview.
  // ============================================================

  /** HTML SOURCE of a `{tag, html}` field (typedStorefrontContent readNeoHtml).
   *  A bare string reads as itself, so a pre-conversion value still draws. */
  function neoHtmlOf(value) {
    if (typeof value === 'string') return value;
    if (isRecord(value) && typeof value.html === 'string') return value.html;
    return '';
  }

  /** The tag of a `{tag, html}` field, restricted to the block-level names the
   *  editors can produce. An unknown tag falls back rather than reaching the
   *  DOM: `tag` is authored data, and data must never be able to open an
   *  element of its own choosing. */
  var NEO_TEXT_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'div'];
  function neoTagOf(value, fallback) {
    var tag = isRecord(value) && typeof value.tag === 'string' ? value.tag.trim().toLowerCase() : '';
    return NEO_TEXT_TAGS.indexOf(tag) === -1 ? fallback : tag;
  }

  /** `<tag class="…">html</tag>`, or '' when the field is absent or empty.
   *  The emptiness test is on the HTML, not on the object: an absent field is
   *  spelled null, but `{tag:'h4', html:''}` is what an emptied input leaves
   *  behind, and it must not draw an empty heading. */
  function neoHtmlTag(value, fallbackTag, cls) {
    var html = neoHtmlOf(value);
    if (!html || !html.trim()) return '';
    var tag = neoTagOf(value, fallbackTag);
    return '<' + tag + ' class="' + cls + '">' + html + '</' + tag + '>';
  }

  /** `{src, alt}`, with a bare string reading as the src (readNeoImage). */
  function neoImageOf(value) {
    if (typeof value === 'string') return { src: value, alt: '' };
    if (!isRecord(value)) return { src: '', alt: '' };
    return {
      src: typeof value.src === 'string' ? value.src : '',
      alt: typeof value.alt === 'string' ? value.alt : ''
    };
  }

  /** `texts[]`, one entry per paragraph; a bare string is a legal legacy value. */
  function neoTextsOf(value) {
    if (typeof value === 'string') return value.trim() ? [value] : [];
    if (!Array.isArray(value)) return [];
    var out = [];
    for (var i = 0; i < value.length; i++) {
      if (typeof value[i] === 'string' && value[i].trim()) out.push(value[i]);
    }
    return out;
  }

  /** The photo box every card here is built on: the image is absolutely
   *  positioned inside an aspect-ratio box, which is the one arrangement that
   *  keeps a row of cards the same height whatever the source images are. The
   *  empty case still paints the box, because a tile whose text is white needs
   *  something dark behind it even when the photo is missing. */
  function neoCardMediaHtml(image, cls, imgCls) {
    if (!image.src) {
      return '<span class="' + cls + ' ' + cls + '--empty" aria-hidden="true"></span>';
    }
    return '<span class="' + cls + '">' +
      '<img class="' + imgCls + '" src="' + escapeAttr(image.src) + '" alt="' + escapeAttr(image.alt) +
      '" loading="lazy">' +
      '</span>';
  }

  function cardsItemHtml(item, isPosts) {
    var image = neoImageOf(item.image);
    var link = isRecord(item.link) ? item.link : {};
    var href = safeHref(link.href);
    var label = typeof link.label === 'string' ? link.label.trim() : '';
    // The DESIGN switch, not a link test: upstream calls the two forms "a bento
    // tile" and "a slider item" and they read the same fields (the
    // linkWrapsCard control in ThemeSectionContentEditors.tsx is a Switch for
    // exactly that reason). A tile with no usable href therefore stays a tile:
    // it only stops being clickable.
    var overlay = item.linkWrapsCard === true;
    var tag = overlay && href ? 'a' : 'div';
    var attrs = overlay && href ? ' href="' + escapeAttr(href) + '"' : '';

    var html = '<li class="neo-cards__item">' +
      '<' + tag + ' class="neo-cards__card neo-cards__card--' + (overlay ? 'tile' : 'stack') + '"' + attrs + '>';
    html += neoCardMediaHtml(image, 'neo-cards__media', 'neo-cards__img');
    // The scrim belongs to the overlaid form only. On a stacked card the text
    // sits below the photo on the page background, so darkening the picture
    // there would cost contrast and buy nothing.
    if (overlay) html += '<span class="neo-cards__scrim" aria-hidden="true"></span>';

    var body = '';
    // Upstream shows the date in the `posts` variant only (ElementRenderer.tsx
    // `isPostsVariant && cardDate`). The field stays authored either way, so
    // switching the variant back shows it again.
    if (isPosts) body += neoHtmlTag(item.date, 'p', 'neo-cards__date');
    body += neoHtmlTag(item.heading, 'h4', 'neo-cards__title');
    var texts = neoTextsOf(item.texts);
    for (var i = 0; i < texts.length; i++) {
      body += '<p class="neo-cards__text">' + texts[i] + '</p>';
    }
    if (label) {
      // Inside an overlaid tile the whole card is already the anchor, so the
      // label is a span: an <a> inside an <a> is invalid markup that browsers
      // silently unnest, which breaks the card's own click target (the same
      // reasoning as the grid banner's button, §23).
      body += (overlay || !href)
        ? '<span class="neo-cards__cta">' + escapeHtml(label) + '</span>'
        : '<a class="neo-cards__cta" href="' + escapeAttr(href) + '">' + escapeHtml(label) + '</a>';
    }
    if (body) html += '<span class="neo-cards__body">' + body + '</span>';

    return html + '</' + tag + '></li>';
  }

  Neo.registerType('cards', function (el) {
    var c = el.content || {};
    var raw = Array.isArray(c.items) ? c.items : [];
    var items = [];
    for (var i = 0; i < raw.length; i++) if (isRecord(raw[i])) items.push(raw[i]);
    // Upstream draws its "add a card" hint under `isEditing` only and renders
    // null otherwise, so an empty element is nothing on the storefront too.
    if (!items.length) return '';
    var isPosts = c.variant === 'posts';

    var html = viewportStyleTags(el) +
      wrapperOpen(el, 'content-type-neo-cards', null,
        ' data-neo-cards-variant="' + (isPosts ? 'posts' : 'cards') + '"') +
      '<ul class="neo-cards__list">';
    for (var k = 0; k < items.length; k++) html += cardsItemHtml(items[k], isPosts);
    return html + '</ul></div>';
  });

  Neo.registerType('teaser', function (el) {
    var c = el.content || {};
    var rawImages = Array.isArray(c.images) ? c.images : [];
    var images = [];
    for (var i = 0; i < rawImages.length; i++) {
      var img = neoImageOf(rawImages[i]);
      // An image row with no src is what "Add image" leaves behind until a URL
      // is pasted; drawing it would put an empty slide in the track.
      if (img.src) images.push(img);
    }
    var button = isRecord(c.button) ? c.button : {};
    var buttonHref = safeHref(button.href);
    var buttonLabel = typeof button.label === 'string' ? button.label.trim() : '';

    var body = neoHtmlTag(c.badge, 'span', 'neo-teaser__badge') +
      neoHtmlTag(c.heading, 'h2', 'neo-teaser__title');
    // `text` is a plain string in the schema (its editor is a Textarea), but the
    // builder still renders it through its sanitiser, so it is HTML source like
    // every other copy field here.
    var text = typeof c.text === 'string' ? c.text : neoHtmlOf(c.text);
    if (text && text.trim()) body += '<div class="neo-teaser__text">' + text + '</div>';
    if (buttonLabel) {
      body += buttonHref
        ? '<a class="neo-teaser__button" href="' + escapeAttr(buttonHref) + '">' + escapeHtml(buttonLabel) + '</a>'
        : '<span class="neo-teaser__button">' + escapeHtml(buttonLabel) + '</span>';
    }
    if (!images.length && !body) return '';

    var side = c.side === 'right' ? 'right' : 'left';
    var html = viewportStyleTags(el) +
      wrapperOpen(el, 'content-type-neo-teaser', null, ' data-neo-teaser-side="' + side + '"');

    if (images.length) {
      // One image is one box; several become a scroll-snap track of the same
      // box. `tabindex="0"` is what makes a scroll container reachable by
      // keyboard: without it the second image cannot be reached without a
      // mouse. The label goes through lsT with an inline fallback, since the key is
      // not in snippets/ls-i18n.liquid (that file is outside this change) and
      // the word is spelled the same in both storefront locales, so the
      // fallback is the right string either way.
      html += '<div class="neo-teaser__media' + (images.length > 1 ? ' neo-teaser__media--multi' : '') + '"' +
        (images.length > 1
          ? ' tabindex="0" role="group" aria-label="' + escapeAttr(lsT('common.images', 'Images')) + '"'
          : '') + '>';
      for (var m = 0; m < images.length; m++) {
        html += '<span class="neo-teaser__slide">' +
          '<img class="neo-teaser__img" src="' + escapeAttr(images[m].src) + '" alt="' +
          escapeAttr(images[m].alt) + '" loading="lazy">' +
          '</span>';
      }
      html += '</div>';
    }
    if (body) html += '<div class="neo-teaser__body">' + body + '</div>';
    return html + '</div>';
  });

  // ============================================================
  // 31. team / contact-info / crm-list: the person row
  //
  // An audit of the 23 live landing pages found the SAME person row rebuilt by
  // hand inside `html` elements on five of them (documents 5, 8, 9, 10 and 22):
  // a circular avatar carrying the person's initial, a gold hairline, a
  // translucent fill, then a name, a role and a tel/mail link. All three
  // builder types that draw that row already existed and none of them was
  // registered here, so picking the real type drew NOTHING and the authors had
  // no other option. §30 records the same story for `cards`.
  //
  // CONTENT MODELS (the builder is the contract: elements.ts, the two cases in
  // ElementRenderer.tsx plus renderers/CrmListRenderer.tsx, and the three
  // editors, UnifiedContentEditor.tsx `team`, ThemeSectionContentEditors.tsx
  // `contact-info`, properties/CrmListProperties.tsx):
  //
  //   team          {members:[{name, role, bio, avatar}], columns:4,
  //                  showAvatar, showRole, showBio,
  //                  teamNameColor, teamRoleColor, teamBioColor}
  //   contact-info  {blocks:[{heading:{tag,html}|null, body:[
  //                    {type:'text', html:{tag,html}|string}
  //                  | {type:'list', items:[string…]}
  //                  | {type:'cta',  label, href, style:'primary'}]}]}
  //   crm-list      {source:'aico-crm', entityType:'b2c'|'b2b',
  //                  crmGroupId:int|null, crmTagId:int|null, columns:4,
  //                  showAvatar, showPosition,
  //                  crmNameColor, crmPositionColor}
  //
  // `team` also carries {source:'aico-staff', maxItems, layout, layoutVariant}
  // from its defaultContent and layoutVariants.ts, and its renderer reads NONE
  // of them. The members are always the authored array and the layout is
  // always the grid. Reading them here would draw something the author cannot
  // see in the builder, so they are ignored on purpose.
  //
  // THE SHOW FLAGS ARE NOT SPELLED THE SAME WAY, and that is upstream, not a
  // slip: `team` tests them for TRUTH (`element.content.showAvatar &&` in
  // ElementRenderer.tsx), so a document saved before a flag existed draws
  // WITHOUT it, while `crm-list` tests `content.showAvatar !== false`
  // (CrmListRenderer.tsx), so the same absent flag draws WITH it. Both are
  // mirrored exactly as written: a document round-trips through the builder,
  // and a theme that quietly "corrected" one of them would disagree with the
  // preview the author is looking at while they author it.
  //
  // WHY ONE CELL HELPER FOR team AND crm-list, when §30 argues the opposite for
  // gridCardHtml: there the picture matched and the DATA did not (escaped text
  // against HTML source, one `description` against `texts[]`, no date). Here
  // both types read exactly the same triple (a name, a role label and an image
  // URL) and upstream's two renderers are line-for-line the same JSX, down to
  // `member.name.charAt(0)` as the fallback initial. Sharing the cell is the
  // honest description of that; only the field NAMES differ (role/avatar
  // against position/imageUrl) and they are normalised before the call.
  //
  // THE SKIN is the pasted CSS, de-duplicated and measured off the documents
  // rather than re-invented. `.conf-person__initial` (doc 10),
  // `.pap-person__initial` (8) and `.onz-person__initial` (9) agree to the
  // pixel: 72px on a phone, 88px from 750px up, `border: 1px solid` the club
  // gold over a 10% cream wash, the initial set in the gold. Upstream's own
  // circle is `w-24 h-24` (96px) on `bg-muted`, which is the builder canvas's
  // neutral and not a colour this theme owns. The FIDELITY NOTE above is the
  // rule being applied: field names and DOM shape are mirrored, the skin is the
  // theme's.
  //
  // What is NOT taken from those pasted rows is their ARRANGEMENT. They lay the
  // avatar beside the text (`display:flex;align-items:center;gap:16px`) because
  // each one sits inside a prose column; upstream stacks it above, centred
  // (`text-center space-y-3`, `mx-auto`), and that is what the author sees in
  // the builder while placing the element. The preview wins over the pastes on
  // layout, the pastes win on colour.
  //
  // DEVIATIONS, per the FIDELITY NOTE:
  //   - an empty `team` renders '' where upstream renders the empty grid div.
  //     Its default style bag is `padding: 40px 24px`, so upstream's version of
  //     "no members yet" is a blank 80px band, the exact bug §14 exists to
  //     remove from /pages/enzonet-lounge, and re-introducing it deliberately
  //     would be perverse. `contact-info` and `crm-list` already render null
  //     upstream when they have nothing, so those two need no deviation.
  //   - `crm-list` gets the SAME responsive column ladder as `team`. Upstream
  //     does not: `team` runs its count through responsiveColumns()
  //     (mobile ≤2, tablet ≤3) and crm-list interpolates the raw number, which
  //     is an inconsistency between two renderers that are otherwise identical
  //     (CrmListRenderer.tsx vs ElementRenderer.tsx case 'team'). Honouring it
  //     would put six cells on a 390px phone and scroll the page sideways,
  //     which nothing in this file is allowed to do.
  //   - an empty role/bio line is skipped. `crm-list` already guards
  //     (`showPosition && member.position`); `team` does not, so a member with
  //     no role gets an empty <p> upstream. In a centred grid cell that is a
  //     blank line pushing the next one down, so the guard is applied to both.
  //   - `team` children are DROPPED. The builder lets a `team` accept children
  //     (validatePageTree.ts lists it as a container) but its renderer never
  //     draws them, so drawing them here would show something that is invisible
  //     in the builder. Before this section a `team` fell through the
  //     unknown-with-children degradation (§2) and its children DID draw; no
  //     live document uses the type, so nothing on the storefront changes.
  //   - a `contact-info` heading keeps its authored TAG. Upstream's preview
  //     flattens every one to `<span class="block font-semibold">`, because
  //     readNeoHtml() returns the html and drops the tag. But the editor
  //     deliberately writes one (`writeNeoHtml(block.heading, value, 'h2')`,
  //     ThemeSectionContentEditors.tsx) and on a real page the heading levels
  //     are the document's outline, not decoration.
  //   - a `contact-info` CTA is a real <a>. Upstream draws a pill <span> and
  //     ignores `href`. But its own comment says why, and says the theme is
  //     the other half of the contract: "the theme reads the first paragraph as
  //     the description and the `a.sp-btn` as the CTA". A CTA cannot be
  //     clickable inside the editor canvas; the storefront is the side that has
  //     to be. `style:'primary'` travels as a modifier class and is given no
  //     look of its own, because upstream gives it none either.
  // ============================================================

  /** Upstream's fallback initial, `member.name.charAt(0)`, in both renderers.
   *  Guarded because a member row with no name is authorable (the editor seeds
   *  one and the field can be emptied) and `.charAt` on undefined would throw
   *  the whole element away through renderChild's catch. Not upper-cased:
   *  upstream does not, and the CSS below does not either. */
  function neoPersonInitial(name) {
    return String(name == null ? '' : name).trim().charAt(0);
  }

  /**
   * One centred person cell: avatar (photo, else initial), name, role, bio.
   * `opts` carries the already-resolved show flags and colours, so the caller
   * owns the truthy-vs-`!== false` difference described above.
   */
  function neoPersonCellHtml(person, opts) {
    var name = person.name == null ? '' : String(person.name);
    var html = '<div class="neo-person">';
    if (opts.showAvatar) {
      html += '<div class="neo-person__avatar">' +
        (person.image
          // The alt is the person's name, as upstream (`alt={member.name}`):
          // this image IS the person, so it is not decorative.
          ? '<img class="neo-person__photo" src="' + escapeAttr(person.image) +
            '" alt="' + escapeAttr(name) + '" loading="lazy">'
          : '<span class="neo-person__initial" aria-hidden="true">' +
            escapeHtml(neoPersonInitial(name)) + '</span>') +
        '</div>';
    }
    html += '<h3 class="neo-person__name"' + styleAttrOf({ color: opts.nameColor || undefined }) + '>' +
      escapeHtml(name) + '</h3>';
    if (opts.showRole && person.role) {
      html += '<p class="neo-person__role"' + styleAttrOf({ color: opts.roleColor || undefined }) + '>' +
        escapeHtml(person.role) + '</p>';
    }
    if (opts.showBio && person.bio) {
      html += '<p class="neo-person__bio"' + styleAttrOf({ color: opts.bioColor || undefined }) + '>' +
        escapeHtml(person.bio) + '</p>';
    }
    return html + '</div>';
  }

  // ---- team ---------------------------------------------------
  Neo.registerType('team', function (el) {
    var c = el.content || {};
    var raw = itemsOf(c, 'members');
    var people = [];
    for (var m = 0; m < raw.length; m++) if (isRecord(raw[m])) people.push(raw[m]);
    if (!people.length) return '';

    // `columns || 4` is upstream's own default; the 1..12 bound is the grid
    // element's column range (§23), so a hand-edited 200 cannot make 200 tracks.
    var columns = clampInt(c.columns || 4, 1, 12, 4);
    var opts = {
      showAvatar: !!c.showAvatar,
      showRole: !!c.showRole,
      showBio: !!c.showBio,
      nameColor: cssColour(c.teamNameColor),
      roleColor: cssColour(c.teamRoleColor),
      bioColor: cssColour(c.teamBioColor)
    };

    // newsColumnsStyleTag is NOT news-specific: it emits exactly upstream's
    // responsiveColumns() ladder (min(count,3) on a tablet, min(count,2) on a
    // phone) as @media overrides of the inline track list. Shared verbatim
    // rather than copied, so the two stay one rule.
    var html = viewportStyleTags(el) + newsColumnsStyleTag(el, columns) +
      wrapperOpen(el, 'content-type-neo-team neo-people', {
        display: 'grid',
        gridTemplateColumns: 'repeat(' + columns + ', minmax(0, 1fr))'
      });
    for (var i = 0; i < people.length; i++) {
      html += neoPersonCellHtml({
        name: people[i].name,
        role: people[i].role,
        bio: people[i].bio,
        image: people[i].avatar
      }, opts);
    }
    return html + '</div>';
  });

  // ---- contact-info -------------------------------------------
  //
  // The body is an ORDERED list of typed parts and the order IS the schema (the
  // renderer says so), so nothing is regrouped here: parts are emitted in the
  // sequence they were authored in.
  //
  // Lucide is unavailable in this theme, but these two glyphs are NOT authored
  // icon names (the renderer hard-codes `Clock` on every list line and
  // `ArrowRight` on the CTA), so they are inlined as their lucide paths rather
  // than dropped the way an authored `icon` name is (see the FIDELITY NOTE).
  var NEO_CONTACT_CLOCK_SVG =
    '<svg class="neo-contact__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
    ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<circle cx="12" cy="12" r="10"></circle><path d="M12 6v6l4 2"></path></svg>';
  var NEO_CONTACT_ARROW_SVG =
    '<svg class="neo-contact__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
    ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>';

  function contactBodyPartHtml(part) {
    if (!isRecord(part)) return '';
    var type = String(part.type || '');

    if (type === 'list') {
      // The entries go through sanitizeRichText + dangerouslySetInnerHTML
      // upstream, so they are HTML SOURCE like every other copy field here
      // (§30's first trap), not text to escape.
      var items = Array.isArray(part.items) ? part.items : [];
      var lines = '';
      for (var i = 0; i < items.length; i++) {
        if (typeof items[i] !== 'string' || !items[i].trim()) continue;
        lines += '<li class="neo-contact__line">' + NEO_CONTACT_CLOCK_SVG +
          '<span class="neo-contact__line-text">' + items[i] + '</span></li>';
      }
      return lines ? '<ul class="neo-contact__list">' + lines + '</ul>' : '';
    }

    if (type === 'cta') {
      // The label is a plain string in the editor (an Input, not a rich-text
      // field), so unlike the heading and the text parts it IS escaped.
      var label = typeof part.label === 'string' ? part.label.trim() : '';
      if (!label) return '';
      var href = safeHref(part.href);
      var cls = 'neo-contact__cta' +
        (part.style === 'primary' ? ' neo-contact__cta--primary' : '');
      return href
        ? '<a class="' + cls + '" href="' + escapeAttr(href) + '">' +
          '<span class="neo-contact__cta-label">' + escapeHtml(label) + '</span>' +
          NEO_CONTACT_ARROW_SVG + '</a>'
        : '<span class="' + cls + '">' +
          '<span class="neo-contact__cta-label">' + escapeHtml(label) + '</span>' +
          NEO_CONTACT_ARROW_SVG + '</span>';
    }

    // Upstream's DEFAULT branch, which catches `text` and any unknown or
    // missing type. Never a dropped part.
    var html = neoHtmlOf(part.html);
    return html && html.trim() ? '<div class="neo-contact__text">' + html + '</div>' : '';
  }

  Neo.registerType('contact-info', function (el) {
    var c = el.content || {};
    var raw = Array.isArray(c.blocks) ? c.blocks : [];
    var blocks = '';
    for (var b = 0; b < raw.length; b++) {
      if (!isRecord(raw[b])) continue;
      var heading = neoHtmlTag(raw[b].heading, 'h2', 'neo-contact__heading');
      var body = '';
      var parts = Array.isArray(raw[b].body) ? raw[b].body : [];
      for (var p = 0; p < parts.length; p++) body += contactBodyPartHtml(parts[p]);
      // A block emptied down to `{heading:null, body:[]}` is what "add a block"
      // leaves behind until something is typed into it; drawing it would put a
      // bordered empty box in the row.
      if (!heading && !body) continue;
      blocks += '<div class="neo-contact__block">' + heading + body + '</div>';
    }
    // Upstream draws its "add a block" hint under `isEditing` only and renders
    // null otherwise, so an empty element is nothing on the storefront too.
    if (!blocks) return '';
    return viewportStyleTags(el) +
      wrapperOpen(el, 'content-type-neo-contact-info') +
      '<div class="neo-contact__grid">' + blocks + '</div>' +
      '</div>';
  });

  // ---- crm-list -----------------------------------------------
  //
  // This is the one type of the three whose PEOPLE ARE NOT IN THE DOCUMENT. The
  // element carries only the (entityType, crmGroupId, crmTagId) triple plus the
  // display flags, and elements.ts says so in as many words: "the element
  // carries the triple and resolves the ordered roster live from the crm-lists
  // API (no baked snapshot)".
  //
  // aiconeo resolves it through `/api/aiconeo/crm-lists/entries`, which
  // hooks/queries/useCrmListApi.ts opens with a workspace key and a Bearer JWT
  // and which throws "Missing authentication" without them. A storefront has
  // neither, so THAT endpoint is unreachable from here: the element would draw
  // a permanent skeleton if it were the only route.
  //
  // What IS reachable is the same CRM behind the theme's own `API_URL` (the
  // Bunny CDN in front of the aico API, which injects the Authorization header
  // at the edge; docs/backend-api-routes.md, and it is why these routes are
  // listed there as "require Bearer token" yet work from the storefront). This
  // theme already reads exactly this data through it, keyed by exactly the pair
  // the element carries:
  //   b2c → `crm-sorts?filter[customerGroupId]=<crmGroupId>
  //          &filter[crmTagId][]=<crmTagId>&sort=sortNumber`
  //         snippets/player-list-js.liquid builds that query verbatim for a
  //         squad roster, and a CRM "sort" IS a CRM list: the same
  //         (customer group, tag) pair the builder's two comboboxes write.
  //   b2b → `b2bs?filter[crmSort][]=<crmGroupId>,<crmTagId>`
  //         snippets/sponsores.liquid, same pair, comma-joined.
  //
  // Both ids are validated positive integers before they reach the query, which
  // is also why nothing here is percent-encoded: the theme's own CRM calls send
  // these bare, and a `%2C` in filter[crmSort][] is not the same filter.
  //
  // Ordering is the SERVER's (`sort=sortNumber`, the order staff drag into the
  // CRM list), so nothing is re-sorted here. One page of 100 is asked for:
  // aiconeo's own endpoint answers a whole list at once and the builder offers
  // no limit or paging control, so walking pages would be inventing a behaviour
  // the author cannot see. A longer list is truncated and says so in the
  // console instead of silently.
  var CRM_PAGE_SIZE = 100;

  function crmEntriesUrl(q) {
    if (q.entityType === 'b2b') {
      return API_URL + 'b2bs?filter[crmSort][]=' + q.groupId + ',' + q.tagId +
        '&page[number]=1&page[size]=' + CRM_PAGE_SIZE;
    }
    return API_URL + 'crm-sorts?page[number]=1&page[size]=' + CRM_PAGE_SIZE +
      '&filter[customerGroupId]=' + q.groupId +
      '&filter[crmTagId][]=' + q.tagId +
      '&filter[isLightVersion]=true&sort=sortNumber';
  }

  /** A `crm-sorts` row -> the {name, role, image} triple the cell reads. Shape
   *  from snippets/player-list-js.liquid load_players(): the person sits under
   *  `attributes.entity`, and the ROW's own `position` (the label for this list)
   *  outranks the entity's own. */
  function crmPersonFromSort(row) {
    var attrs = (row && row.attributes) || {};
    var entity = isRecord(attrs.entity) ? attrs.entity : {};
    // player-list skips `isActive != 1`. Kept, but only when the flag is
    // actually present: the light-version payload is free to omit it, and a
    // loose `!= 1` against undefined would empty the whole list.
    if ('isActive' in entity && !entity.isActive) return null;
    var parts = [];
    if (entity.firstName) parts.push(String(entity.firstName).trim());
    if (entity.lastName) parts.push(String(entity.lastName).trim());
    var name = parts.join(' ').trim() ||
      (entity.name ? String(entity.name).trim() : '');
    if (!name) return null;
    return {
      name: name,
      role: attrs.position || entity.position || '',
      image: entity.image || ''
    };
  }

  /** A `b2bs` row -> the same triple. A company carries no position label, and
   *  upstream's crm-list draws no link at all, so `attributes.website` is read
   *  by nothing here. See the report note rather than inventing an anchor the
   *  builder preview does not show. */
  function crmPersonFromCompany(row) {
    var attrs = (row && row.attributes) || {};
    var name = attrs.company ? String(attrs.company).trim() : '';
    if (!name) return null;
    return { name: name, role: '', image: attrs.image || '' };
  }

  /** A cell-shaped skeleton: same circle, same two text rows. */
  function crmPlaceholderHtml(q) {
    return '<div class="neo-person neo-person--placeholder" aria-hidden="true">' +
      (q.showAvatar ? '<span class="neo-person__avatar neo-person__avatar--ph"></span>' : '') +
      '<span class="neo-person__ph neo-person__ph--name"></span>' +
      (q.showPosition ? '<span class="neo-person__ph neo-person__ph--role"></span>' : '') +
      '</div>';
  }

  Neo.registerType('crm-list', function (el) {
    var c = el.content || {};
    var groupId = toPositiveInt(c.crmGroupId);
    var tagId = toPositiveInt(c.crmTagId);
    // Upstream: `scope = crmGroupId && crmTagId ? {…} : null`, and a null scope
    // renders null on a published page (the dashed "pick a list" box is
    // `isEditing` only).
    if (!groupId || !tagId) return '';

    var columns = clampInt(c.columns || 4, 1, 12, 4);
    var query = {
      entityType: c.entityType === 'b2b' ? 'b2b' : 'b2c',
      groupId: groupId,
      tagId: tagId,
      showAvatar: c.showAvatar !== false,
      showPosition: c.showPosition !== false,
      nameColor: cssColour(c.crmNameColor) || '',
      positionColor: cssColour(c.crmPositionColor) || ''
    };

    var html = viewportStyleTags(el) + newsColumnsStyleTag(el, columns) +
      wrapperOpen(el, 'content-type-neo-crm-list neo-people', {
        display: 'grid',
        gridTemplateColumns: 'repeat(' + columns + ', minmax(0, 1fr))'
      }, ' data-neo-crm="1" data-neo-crm-query="' + escapeAttr(JSON.stringify(query)) + '"');
    // Upstream's loading state is exactly `columns` pulsing cards; the same
    // count here, so the block owns a sensible height from the first paint and
    // nothing below it jumps when the roster lands.
    for (var i = 0; i < columns; i++) html += crmPlaceholderHtml(query);
    return html + '</div>';
  });

  function clearCrmPlaceholders(host) {
    var phs = host.querySelectorAll('.neo-person--placeholder');
    for (var i = phs.length - 1; i >= 0; i--) phs[i].parentNode.removeChild(phs[i]);
  }

  function runCrmQuery(host) {
    var q = readJsonAttr(host, 'data-neo-crm-query', null);
    if (!q) {
      clearCrmPlaceholders(host);
      return;
    }
    fetch(crmEntriesUrl(q), typeof API_Header_Options !== 'undefined' ? API_Header_Options : undefined)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (payload) {
        var rows = (payload && payload.data) || [];
        var html = '';
        var drawn = 0;
        for (var i = 0; i < rows.length; i++) {
          var person = q.entityType === 'b2b'
            ? crmPersonFromCompany(rows[i])
            : crmPersonFromSort(rows[i]);
          if (!person) continue;
          drawn++;
          html += neoPersonCellHtml(person, {
            showAvatar: q.showAvatar,
            showRole: q.showPosition,
            showBio: false,
            nameColor: q.nameColor,
            roleColor: q.positionColor
          });
        }
        clearCrmPlaceholders(host);
        host.insertAdjacentHTML('beforeend', html);
        // Loaded but empty renders null upstream; here the wrapper already
        // exists with its authored padding, so it is hidden rather than left
        // as a blank band (the §14 reasoning again).
        if (!drawn) host.setAttribute('data-neo-crm-empty', '1');
        var total = payload && payload.meta && payload.meta.page && payload.meta.page.total;
        if (total && total > CRM_PAGE_SIZE) {
          warnOnce('crm-truncated', 'crm-list shows the first ' + CRM_PAGE_SIZE +
            ' of ' + total + ' entries');
        }
      })
      .catch(function (err) {
        // Placeholders go on failure as well as on success: a skeleton that
        // never resolves reads as a hung page (the news-list rule, §24).
        clearCrmPlaceholders(host);
        host.setAttribute('data-neo-crm-empty', '1');
        warnOnce('crm-fetch', 'crm-list fetch failed (' +
          (err && err.message ? err.message : err) + ')');
      });
  }

  function hydrateCrmLists(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var hosts = scope.querySelectorAll('[data-neo-crm="1"]');
    if (!hosts.length) return;
    if (typeof API_URL === 'undefined' || !API_URL) {
      warnOnce('crm-api', 'crm-list needs the API_URL global; nothing fetched');
      for (var g = 0; g < hosts.length; g++) {
        clearCrmPlaceholders(hosts[g]);
        hosts[g].setAttribute('data-neo-crm-empty', '1');
      }
      return;
    }
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].getAttribute('data-neo-crm-hydrated')) continue;
      hosts[i].setAttribute('data-neo-crm-hydrated', '1');
      runCrmQuery(hosts[i]);
    }
  }

  // ============================================================
  // 31. tabs + pricing: the last two palette types that drew NOTHING
  //
  // WHY THEY ARE DONE TOGETHER: one landing page (document 14, /pages/ls-is-more)
  // rebuilt both of them by hand, side by side, inside two `html` elements,
  // because picking the real element types rendered an empty page. Measured on
  // that document: element ...c0 is 10'026 bytes of markup holding 8 hidden
  // radio inputs (6 in group `lsim-tz-t` for a six-zone selector, 2 in group
  // `lsim-tz-p` for a monthly/yearly price switch) with 8 `<label for>` acting
  // as tabs and a 3-column price card list under them; its sibling style sheet,
  // element ...03, is 62'632 bytes and carries 48 `:checked` sibling rules whose
  // only job is to drive those 8 radios. Every one of those bytes is a
  // workaround for the two renderers below.
  //
  // INTERACTION, and the deliberate deviation from upstream: aiconeo's tabs are
  // the Radix Tabs primitive (ElementRenderer.tsx:5947-5962), a JS-driven
  // role="tablist" tree. The engine paints through innerHTML and this theme
  // runs no authored page scripts, which is exactly why faq/accordion here are
  // native <details> rather than Radix Accordion (see disclosureList above).
  // The same route is taken here: ONE native radio group per element. It is the
  // only zero-JS control that both switches a panel AND has its selection
  // announced: a screen reader reads "<title>, radio button, selected, 2 of 3",
  // and it brings native arrow-key movement inside the group for free, so the
  // control is keyboard-operable without a line of script.
  //
  // role="tab" / aria-selected are deliberately NOT emitted. aria-selected
  // cannot be updated without JS, so a statically stamped one would keep
  // claiming the FIRST tab was selected after the reader moved to another, i.e.
  // announcing the wrong answer, which is worse than not claiming the ARIA tabs
  // pattern at all. The hand-built selector on document 14 reached for the very
  // same control (`<input type="radio" class="lsim-tz__radio">` + `<label for>`)
  // and spelled no ARIA whatsoever.
  //
  // The 48 sibling rules are NOT reproduced. They exist because that document
  // keeps its tab strip and its panels in two separate wrappers, so every
  // pairing needs its own `:nth-of-type(n):checked ~ ... :nth-child(n)` rule,
  // 6 tabs x 2 price modes x the elements each drives. Interleaving
  // radio -> label -> panel instead makes the whole switch TWO rules
  // (`:checked + label` and `:checked + label + panel`) whatever the tab count,
  // with a wrapping flex row and `order` dropping the panels below the strip.
  // See the §31 block in assets/content-builder-neo.css.
  // ============================================================

  /**
   * A stable suffix for the radio group `name` and the per-tab ids.
   *
   * disclosureList can live with a shared group name when `el.id` is missing:
   * two id-less accordions merely merge into one exclusive group and every
   * panel is still reachable. A shared radio name cannot: checking a tab in
   * one element would UNCHECK the other element's only checked radio, and that
   * element would then show its labels with no panel at all, which is the same
   * blank output this whole file exists to stop.
   *
   * Derived from the content rather than from a counter so the same document
   * always renders the same bytes; the offline render check compares two
   * branches byte for byte, and a counter would make every run differ.
   */
  function neoGroupKey(el, titles) {
    var id = safeId(el && el.id);
    if (id) return id;
    var seed = String(el && el.type) + '|' + titles.join('|');
    var h = 5381;
    for (var i = 0; i < seed.length; i++) h = ((h * 33) ^ seed.charCodeAt(i)) >>> 0;
    return 'x' + h.toString(36);
  }

  // ---- tabs, panel mode (p69) ---------------------------------
  //
  // CONTRACT (shared with the aiconeo builder's TabsRenderer — plans/p69):
  //   children       panel i = children[i], any element (normally a container),
  //                  rendered with the normal element renderer, nested tabs OK
  //   content.tabs[i].title   label of tab i ('Onglet N' when missing)
  //   content.defaultTab      0-based active tab on load (clamped)
  //   look           tabStyle 'underline'|'pill', tabAlign 'start'|'center'|
  //                  'justify', tabBarBg, tabColor, tabActiveColor, tabActiveBg,
  //                  tabIndicatorColor, tabFontSize (px), tabFontWeight,
  //                  tabUppercase, panelGap (px)
  // Values travel as custom properties on `.neo-tabpanels`; the fixed geometry
  // lives in content-builder-neo.css. No motion here (theme motion rule).
  var TAB_WEIGHTS = { '400': 1, '500': 1, '600': 1, '700': 1 };

  function tabPx(raw, fallback, min, max) {
    var n = Number(raw);
    if (!isFinite(n) || raw === '' || raw == null) return fallback;
    return Math.min(max, Math.max(min, Math.round(n)));
  }

  function renderTabPanels(el, c, kids) {
    var en = typeof window.currentLangIsocode === 'string' && window.currentLangIsocode === 'en';
    var rawTabs = Array.isArray(c.tabs) ? c.tabs : [];
    var titles = [];
    for (var i = 0; i < kids.length; i++) {
      var t = isRecord(rawTabs[i]) && typeof rawTabs[i].title === 'string' ? rawTabs[i].title.trim() : '';
      titles.push(t || ((en ? 'Tab ' : 'Onglet ') + (i + 1)));
    }
    var active = tabPx(c.defaultTab, 0, 0, kids.length - 1);
    var style = c.tabStyle === 'pill' ? 'pill' : 'underline';
    var align = c.tabAlign === 'center' || c.tabAlign === 'justify' ? c.tabAlign : 'start';
    var barBg = cssColour(c.tabBarBg) || '';
    var tabColor = cssColour(c.tabColor) || '';
    var activeColor = cssColour(c.tabActiveColor) || '';
    var vars = {
      '--neo-tab-size': tabPx(c.tabFontSize, 16, 8, 64) + 'px',
      '--neo-tab-weight': TAB_WEIGHTS[String(c.tabFontWeight)] ? String(c.tabFontWeight) : '700',
      '--neo-panel-gap': tabPx(c.panelGap, 24, 0, 240) + 'px'
    };
    if (barBg) vars['--neo-tab-bar-bg'] = barBg;
    if (tabColor) vars['--neo-tab-color'] = tabColor;
    if (activeColor) vars['--neo-tab-active'] = activeColor;
    if (cssColour(c.tabActiveBg)) vars['--neo-tab-active-bg'] = cssColour(c.tabActiveBg);
    var indicator = cssColour(c.tabIndicatorColor) || activeColor;
    if (indicator) vars['--neo-tab-indicator'] = indicator;
    var cls = 'neo-tabpanels neo-tabpanels--' + style + ' neo-tabpanels--align-' + align +
      (barBg ? ' neo-tabpanels--bar' : '') +
      (tabColor ? '' : ' neo-tabpanels--dim') +
      (c.tabUppercase === true ? ' neo-tabpanels--upper' : '');
    var key = neoGroupKey(el, titles);
    var style_ = '';
    for (var v in vars) if (Object.prototype.hasOwnProperty.call(vars, v)) style_ += v + ':' + vars[v] + ';';

    var html = viewportStyleTags(el) +
      wrapperOpen(el, 'content-type-neo-tabs content-type-neo-tabs--panels') +
      '<div class="' + cls + '" data-neo-tabs style="' + escapeAttr(style_) + '">' +
      '<div class="neo-tabpanels__bar" role="tablist">';
    for (var b = 0; b < kids.length; b++) {
      var on = b === active;
      html += '<button type="button" role="tab" class="neo-tabpanels__tab' + (on ? ' is-active' : '') + '"' +
        ' id="neo-tp-' + key + '-t' + b + '" aria-controls="neo-tp-' + key + '-p' + b + '"' +
        ' aria-selected="' + (on ? 'true' : 'false') + '" tabindex="' + (on ? '0' : '-1') + '"' +
        ' data-neo-tab="' + b + '">' + escapeHtml(titles[b]) + '</button>';
    }
    html += '</div>';
    for (var p = 0; p < kids.length; p++) {
      html += '<div class="neo-tabpanels__panel" role="tabpanel" id="neo-tp-' + key + '-p' + p + '"' +
        ' aria-labelledby="neo-tp-' + key + '-t' + p + '"' + (p === active ? '' : ' hidden') + '>' +
        (renderChild(kids[p]) || '') + '</div>';
    }
    return html + '</div></div>';
  }

  /** Own bar/panels only — a nested tabs element keeps its own state. */
  function tabPanelParts(root) {
    var bar = null, panels = [];
    for (var i = 0; i < root.children.length; i++) {
      var ch = root.children[i];
      if (ch.classList.contains('neo-tabpanels__bar')) bar = ch;
      else if (ch.classList.contains('neo-tabpanels__panel')) panels.push(ch);
    }
    var tabs = bar ? Array.prototype.slice.call(bar.children).filter(function (x) {
      return x.classList.contains('neo-tabpanels__tab');
    }) : [];
    return { tabs: tabs, panels: panels };
  }

  function hydrateTabPanel(root) {
    if (root.getAttribute('data-neo-tabs-ready') === '1') return;
    root.setAttribute('data-neo-tabs-ready', '1');
    var parts = tabPanelParts(root);
    function select(idx, focus) {
      for (var i = 0; i < parts.tabs.length; i++) {
        var on = i === idx;
        parts.tabs[i].classList.toggle('is-active', on);
        parts.tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
        parts.tabs[i].setAttribute('tabindex', on ? '0' : '-1');
        if (parts.panels[i]) {
          if (on) parts.panels[i].removeAttribute('hidden');
          else parts.panels[i].setAttribute('hidden', '');
        }
      }
      if (focus && parts.tabs[idx]) parts.tabs[idx].focus();
    }
    parts.tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(i, false); });
      tab.addEventListener('keydown', function (e) {
        var n = parts.tabs.length;
        var to = e.key === 'ArrowRight' ? (i + 1) % n : e.key === 'ArrowLeft' ? (i - 1 + n) % n :
          e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
        if (to < 0) return;
        e.preventDefault();
        select(to, true);
      });
    });
  }

  function hydrateTabPanels(root) {
    var nodes = (root || document).querySelectorAll('[data-neo-tabs]');
    for (var i = 0; i < nodes.length; i++) {
      try {
        hydrateTabPanel(nodes[i]);
      } catch (err) {
        if (window.console && console.warn) console.warn('content-builder-neo-ext: tabs hydration failed', err);
      }
    }
  }

  // ---- tabs ---------------------------------------------------
  // content: tabs [{title, content}]. Both fields are PLAIN text: the editor
  // gives them a text input and a textarea (UnifiedContentEditor.tsx:775-786)
  // and upstream passes both to React as children (ElementRenderer.tsx:5952 and
  // :5956), which escapes them. So both are escaped here, unlike accordion's
  // `content`, which goes through InlineEditableField and IS rich text.
  Neo.registerType('tabs', function (el) {
    var c = el.content || {};
    // Panel mode (p69): children are the panels. Plain mode below is untouched.
    var panelKids = [];
    var kidsRaw = Array.isArray(el.children) ? el.children : [];
    for (var pk = 0; pk < kidsRaw.length; pk++) if (isRecord(kidsRaw[pk])) panelKids.push(kidsRaw[pk]);
    if (panelKids.length) return renderTabPanels(el, c, panelKids);
    var raw = Array.isArray(c.tabs) ? c.tabs : [];
    var items = [];
    for (var i = 0; i < raw.length; i++) if (isRecord(raw[i])) items.push(raw[i]);
    // Upstream renders an empty <Tabs> shell for an empty list; nothing is what
    // an empty element should be on the storefront (same call as `cards`, §30).
    if (!items.length) return '';

    var titles = [];
    for (var t = 0; t < items.length; t++) {
      titles.push(typeof items[t].title === 'string' ? items[t].title : '');
    }
    var key = neoGroupKey(el, titles);
    var group = 'neo-tabs-' + key;

    // The group needs a name of its own: each radio is named by its <label for>,
    // but nothing would name the control as a whole. The key is not in
    // snippets/ls-i18n.liquid (that file is outside this change), so it carries
    // an inline fallback, exactly as the teaser's scroll track does above.
    var html = viewportStyleTags(el) +
      wrapperOpen(el, 'content-type-neo-tabs') +
      '<div class="neo-tabs" role="group" aria-label="' +
      escapeAttr(lsT('common.tabs', 'Onglets')) + '">';

    for (var k = 0; k < items.length; k++) {
      var inputId = 'neo-tab-' + key + '-' + k;
      var panelId = 'neo-tabpanel-' + key + '-' + k;
      var body = typeof items[k].content === 'string' ? items[k].content : '';
      // DOM order MUST be radio -> label -> panel and nothing may come between
      // them: the entire switch is two adjacent-sibling rules, and an adjacent
      // sibling is the very next element.
      html += '<input class="neo-tabs__radio" type="radio" name="' + escapeAttr(group) +
        '" id="' + inputId + '" aria-controls="' + panelId + '"' +
        (k === 0 ? ' checked' : '') + '>' +
        '<label class="neo-tabs__tab" for="' + inputId + '">' + escapeHtml(titles[k]) + '</label>' +
        '<div class="neo-tabs__panel" id="' + panelId + '">' +
        '<p class="neo-tabs__text">' + escapeHtml(body) + '</p></div>';
    }
    return html + '</div></div>';
  });

  // ---- pricing ------------------------------------------------
  // content: plans [{name, price, period, features[], highlighted}] plus the
  // four colour overrides pricingNameColor / pricingPriceColor /
  // pricingPeriodColor / pricingFeatureColor (elements.ts:366-385,
  // UnifiedContentEditor.tsx:554-578, ElementRenderer.tsx:4769-4801).
  //
  // `CHF` and the `/` before the period are upstream's own literals, not
  // authored text (ElementRenderer.tsx:4788-4789): the plan model has no
  // currency field, so they are hard-coded there and hard-coded here.
  //
  // The colours run through cssColour rather than straight into the style
  // attribute. The builder's picker emits hex (PageBuilderColorPicker.tsx:192
  // and its swatch buttons), so nothing an author can pick is lost, and the
  // same reasoning the table block spells out applies: a value this theme
  // cannot resolve should fall back to a colour that works, not silently kill
  // the declaration it sits in.

  /**
   * The TABLET cap only, and deliberately not newsColumnsStyleTag.
   *
   * Upstream's responsiveColumns (utils/responsiveGrid.ts:24) collapses to 3 on
   * a tablet and 2 on a phone, and news-list mirrors both. A plan card is not a
   * news card: it stacks a name, a 44px-plus price, a feature list and a button
   * in one column, and the hand-built price cards on document 14 answer the
   * phone tier for themselves with `.lsim-tz__cards{grid-template-columns:1fr}`
   * i.e. ONE column, not two. That is the authors' own measurement of their own
   * content, so the phone tier goes to one column, as a plain rule in
   * assets/content-builder-neo.css next to the identical collapse already
   * written there for features / multicolumn / testimonial.
   *
   * It has to be a stylesheet rule rather than part of this tag for two
   * reasons: it needs no element id to work, and an authored `styles.mobile`
   * override must still win, because mobileStyleTag's block is injected into the body
   * after the stylesheet, so with equal `!important` weight the author's rule
   * comes last and takes it. A mobile rule emitted HERE would outrank them.
   */
  function pricingColumnsStyleTag(el, columns) {
    var id = safeId(el && el.id);
    if (!id || columns <= 3) return '';
    return '<style>@media screen and (min-width: 750px) and (max-width: 989px){[data-neo-el="' +
      id + '"]{grid-template-columns:repeat(3, minmax(0, 1fr)) !important}}</style>';
  }

  Neo.registerType('pricing', function (el) {
    var c = el.content || {};
    var raw = Array.isArray(c.plans) ? c.plans : [];
    var plans = [];
    for (var i = 0; i < raw.length; i++) if (isRecord(raw[i])) plans.push(raw[i]);
    if (!plans.length) return '';

    var nameColour = cssColour(c.pricingNameColor) || undefined;
    var priceColour = cssColour(c.pricingPriceColor) || undefined;
    var periodColour = cssColour(c.pricingPeriodColor) || undefined;
    var featureColour = cssColour(c.pricingFeatureColor) || undefined;
    // The storefront fallback is the word the club's own hand-built price cards
    // use on their CTA (document 14, `<a class="lsim-tz__cta">Choisir</a>`),
    // not upstream's German "Auswählen", which is the builder UI's language.
    var label = lsT('common.select', 'Choisir');

    // The grid goes on the element WRAPPER, because that is where upstream puts
    // it (ElementRenderer.tsx:4775 writes gridTemplateColumns onto cssStyles)
    // and the palette ships `display:grid` in the element's own default styles
    // (elements.ts:379-383).
    var html = viewportStyleTags(el) +
      pricingColumnsStyleTag(el, plans.length) +
      wrapperOpen(el, 'content-type-neo-pricing', {
        gridTemplateColumns: 'repeat(' + plans.length + ', minmax(0, 1fr))'
      });

    for (var k = 0; k < plans.length; k++) {
      var plan = plans[k];
      var price = plan.price == null ? '' : String(plan.price).trim();
      var period = plan.period == null ? '' : String(plan.period).trim();
      html += '<div class="neo-pricing__plan' +
        (plan.highlighted === true ? ' neo-pricing__plan--highlighted' : '') + '">' +
        '<h3 class="neo-pricing__name"' + styleAttrOf({ color: nameColour }) + '>' +
        escapeHtml(plan.name) + '</h3>';
      // An empty price would leave a bare "CHF" standing on its own line, and an
      // empty period a bare "/". Upstream draws both, because in the builder the
      // author is looking at the field they just emptied; on the storefront they
      // are only debris.
      if (price) {
        html += '<p class="neo-pricing__price"' + styleAttrOf({ color: priceColour }) +
          '>CHF ' + escapeHtml(price) + '</p>';
      }
      if (period) {
        html += '<p class="neo-pricing__period"' + styleAttrOf({ color: periodColour }) +
          '>/ ' + escapeHtml(period) + '</p>';
      }
      var features = Array.isArray(plan.features) ? plan.features : [];
      var list = '';
      for (var f = 0; f < features.length; f++) {
        if (typeof features[f] !== 'string' || !features[f].trim()) continue;
        list += '<li class="neo-pricing__feature"' + styleAttrOf({ color: featureColour }) + '>' +
          escapeHtml(features[f]) + '</li>';
      }
      if (list) html += '<ul class="neo-pricing__features">' + list + '</ul>';
      // A <span>, not a <button>. Upstream renders a real Button, but the plan
      // model carries no href or action of any kind (the list editor offers
      // name / price / period / highlighted only, UnifiedContentEditor.tsx:
      // 570-575), so that button does nothing on a published page either. A
      // <button> here would be focusable and announced as an actionable control
      // that cannot act; the same call `cardsItemHtml` makes for a card label
      // with no usable href.
      html += '<span class="neo-pricing__button neo-pricing__button--' +
        (plan.highlighted === true ? 'primary' : 'outline') + '">' +
        escapeHtml(label) + '</span></div>';
    }
    return html + '</div>';
  });

  // ============================================================
  // 31-34. carousel / quote / job-listings / announcement-bar
  //
  // The last four types the club's authors were rebuilding by hand. Same
  // reading as §29/§30: picking the real type in the builder drew NOTHING on
  // the storefront, so the page shipped a raw `html` element carrying its own
  // <style> block and its own markup instead.
  //
  // TWO CONSTRAINTS THAT EVERY RAIL BELOW OBEYS, both learned the hard way on
  // the hand-written pages this replaces:
  //
  //   1. NO AUTHORED JAVASCRIPT RUNS. The engine paints with innerHTML and the
  //      builder canvas is React, so a <script> in an `html` element never
  //      executes in either place. A carousel here is therefore CSS scroll-snap
  //      with a focusable track, never a scripted slider, the same answer
  //      `slideshow` and the grid's mobileLayout:'carousel' already give. The
  //      track carries `tabindex="0"`, which is what makes a scroll container
  //      reachable by keyboard: without it the second card cannot be reached
  //      without a mouse (the note on neo-teaser__media--multi, §30).
  //
  //   2. `vw` IS THE WRONG UNIT INSIDE THE BUILDER. The canvas simulates a
  //      device by RESIZING ITSELF, while `vw` keeps resolving against the
  //      browser window, so a full-bleed rail written the theme's usual way
  //      (`margin-right: calc(50% - 50vw)`) overflowed a phone canvas by about
  //      533px on one page, and that page now carries a hand-written
  //      `.ez-scroller{margin-right:-40px}` precisely to avoid the unit. There
  //      is no `vw` anywhere below, and no negative bleed at all: a rail is
  //      exactly as wide as its own box and scrolls INSIDE it.
  //
  // Neither element draws arrows or dots. Upstream's CarouselScroller wires
  // both to React state, which is state this renderer does not have; a chevron
  // that cannot scroll anything is worse than no chevron, and swipe, trackpad,
  // shift-wheel and the arrow keys all reach every card without one. The
  // authored `showArrows` / `showDots` flags are therefore read and ignored,
  // deliberately, not overlooked.
  // ============================================================

  // ---- 31. carousel ------------------------------------------
  // content: items[] of {image, title, description}, cardWidth ('300'),
  // cardGap ('16'), cardBorderRadius ('16'), imageHeight ('220'),
  // contentLayout ('overlay'|'bottom'|'minimal'|'split', default 'overlay'),
  // showTitle / showDescription (default true), showArrows / showDots (read,
  // see above). Every numeric field is authored as a STRING and upstream reads
  // it with parseInt(…, 10) || default, mirrored exactly, so '300px' and a
  // stray '' land on the same value here as they do in the canvas.
  //
  // `showOverlay` sits in the palette's defaultContent but no upstream code
  // path reads it; left unread here too rather than inventing a meaning for it.

  var CAROUSEL_LAYOUTS = { overlay: 1, bottom: 1, minimal: 1, split: 1 };

  /** parseInt with upstream's fallback: a non-numeric or absent value is the default. */
  function carouselInt(raw, fallback) {
    var n = parseInt(raw == null ? '' : String(raw), 10);
    return isFinite(n) ? n : fallback;
  }

  /**
   * The photo box. Upstream paints the picture as a `background-image` on a
   * fixed-height div; this draws an <img> absolutely positioned in a box of the
   * same height with `object-fit: cover`, which crops identically. Two reasons
   * for the deviation, both of them this file's existing rules:
   *   - the src is AUTHORED DATA and a background-image concatenates it into a
   *     `url(…)` inside a style attribute, where `a.jpg)"` closes the function
   *     and opens a declaration list. Every other picture in this file goes
   *     through an <img> src for exactly that reason (neoCardMediaHtml, §30).
   *   - an <img> can be `loading="lazy"`; a CSS background cannot.
   * `alt=""` because the photo is decorative in all four layouts: the title
   * beside or over it is the real label, and upstream gives the background no
   * accessible name at all.
   */
  function carouselMediaHtml(src, height, radius, extraClass) {
    var style = { height: height + 'px', borderRadius: radius };
    var cls = 'neo-carousel__media' + (extraClass ? ' ' + extraClass : '');
    if (!src) {
      return '<span class="' + cls + ' neo-carousel__media--empty"' + styleAttrOf(style) +
        ' aria-hidden="true"></span>';
    }
    return '<span class="' + cls + '"' + styleAttrOf(style) + '>' +
      '<img class="neo-carousel__img" src="' + escapeAttr(src) + '" alt="" loading="lazy">' +
      '</span>';
  }

  /** One card's inner markup. The four layouts are upstream's `renderCardContent` switch. */
  function carouselCardHtml(item, layout, radius, imageHeight, showTitle, showDescription) {
    var src = typeof item.image === 'string' ? item.image : '';
    var title = typeof item.title === 'string' ? item.title : '';
    var description = typeof item.description === 'string' ? item.description : '';
    var r = radius + 'px';
    var html;

    if (layout === 'bottom') {
      // The only layout where the text sits BELOW the photo, so the photo keeps
      // the radius on its top corners only and the card body supplies the rest.
      html = carouselMediaHtml(src, imageHeight, r + ' ' + r + ' 0 0');
      var body = '';
      if (showTitle && title) body += '<span class="neo-carousel__title">' + escapeHtml(title) + '</span>';
      if (showDescription && description) {
        body += '<span class="neo-carousel__text">' + escapeHtml(description) + '</span>';
      }
      return html + '<span class="neo-carousel__body">' + body + '</span>';
    }

    html = carouselMediaHtml(src, imageHeight, r);

    if (layout === 'minimal') {
      // Upstream's `minimal` draws the title regardless of showTitle (its test
      // is `item.title &&` alone) and never draws the description. Mirrored,
      // including the asymmetry: an author who turns titles off and still sees
      // them on a minimal card is seeing what the canvas shows.
      if (!title) return html;
      return html + '<span class="neo-carousel__overlay neo-carousel__overlay--minimal"' +
        styleAttrOf({ borderRadius: r }) + '>' +
        '<span class="neo-carousel__title neo-carousel__title--minimal">' + escapeHtml(title) + '</span>' +
        '</span>';
    }

    if (layout === 'split') {
      var splitBody = '';
      if (showTitle && title) {
        splitBody += '<span class="neo-carousel__title neo-carousel__title--split">' + escapeHtml(title) + '</span>';
      }
      // The 32x2 rule is drawn unconditionally upstream, between the two slots,
      // so it survives even when both are empty. It is what makes `split` look
      // different from `overlay` at a glance.
      splitBody += '<span class="neo-carousel__rule" aria-hidden="true"></span>';
      if (showDescription && description) {
        splitBody += '<span class="neo-carousel__text neo-carousel__text--split">' + escapeHtml(description) + '</span>';
      }
      return html + '<span class="neo-carousel__overlay neo-carousel__overlay--split"' +
        styleAttrOf({ borderRadius: r }) + '>' + splitBody + '</span>';
    }

    // overlay (upstream's `default`): the scrim is drawn only when there is
    // something to put on it, otherwise a card with no copy would be darkened
    // for nothing.
    if (!(showTitle && title) && !(showDescription && description)) return html;
    var overlayBody = '';
    if (showTitle && title) {
      overlayBody += '<span class="neo-carousel__title neo-carousel__title--overlay">' + escapeHtml(title) + '</span>';
    }
    if (showDescription && description) {
      overlayBody += '<span class="neo-carousel__text neo-carousel__text--overlay">' + escapeHtml(description) + '</span>';
    }
    return html + '<span class="neo-carousel__overlay neo-carousel__overlay--overlay"' +
      styleAttrOf({ borderRadius: r }) + '>' + overlayBody + '</span>';
  }

  Neo.registerType('carousel', function (el) {
    var c = el.content || {};
    var raw = Array.isArray(c.items) ? c.items : [];
    var items = [];
    for (var i = 0; i < raw.length; i++) if (isRecord(raw[i])) items.push(raw[i]);
    // Upstream renders the scroller with an empty track rather than nothing,
    // but an empty track is an invisible 8px strip on a page; a type with no
    // items is treated the way `cards` treats it (§30) and draws nothing.
    if (!items.length) return '';

    var cardWidth = carouselInt(c.cardWidth, 300);
    var cardGap = carouselInt(c.cardGap, 16);
    var radius = carouselInt(c.cardBorderRadius, 16);
    var imageHeight = carouselInt(c.imageHeight, 220);
    var layout = Object.prototype.hasOwnProperty.call(CAROUSEL_LAYOUTS, c.contentLayout)
      ? c.contentLayout : 'overlay';
    var showTitle = c.showTitle !== false;
    var showDescription = c.showDescription !== false;

    // `min(<n>px, 100%)` rather than upstream's bare `<n>px`: the same rule
    // compileWidth already applies to a fixed authored width on the mobile tier
    // (see the style-bag contract above). A 600px card on a 390px phone would
    // otherwise be permanently half-visible with its right edge unreachable:
    // the track scrolls, but a card wider than the track can never be framed.
    // At any width where the card fits, min() resolves to the authored number,
    // so the desktop design is untouched.
    var itemStyle = { flex: '0 0 min(' + cardWidth + 'px, 100%)', borderRadius: radius + 'px' };

    var html = viewportStyleTags(el) +
      wrapperOpen(el, 'content-type-neo-carousel') +
      // The gap is the authored one; the track's own padding-bottom is
      // upstream's 8px, which is the room the scrollbar sits in.
      '<div class="neo-carousel__track"' + styleAttrOf({ gap: cardGap + 'px' }) +
      ' tabindex="0" role="group" aria-label="' + escapeAttr(lsT('common.carousel', 'Carousel')) + '">';
    for (var k = 0; k < items.length; k++) {
      html += '<div class="neo-carousel__item"' + styleAttrOf(itemStyle) + '>' +
        carouselCardHtml(items[k], layout, radius, imageHeight, showTitle, showDescription) +
        '</div>';
    }
    return html + '</div></div>';
  });

  // ---- 32. quote ---------------------------------------------
  // content: text (LIMITED INLINE HTML), author (plain text), and the optional
  // look keys variant / align / size / accentColor / quoteMark.
  //
  // The model is upstream's `quoteModel.ts`, which exists precisely so that the
  // three renderers agree: "the PHP body renderer and the theme's
  // content-builder-neo.js both CONCATENATE the value into a style string,
  // where `red;background:url(…)` is a CSS injection. One rule in all three,
  // identical." That rule is this file's existing `cssColour`, the SAME regex,
  // already here for shop-the-look, so the accent goes through it and nothing
  // else reaches a style attribute.
  //
  // WHY THE ELEMENT EXISTS AT ALL: InlineTextEditor configures TipTap with
  // `blockquote: false`, so a <blockquote> pasted into a `text` element is
  // dropped the next time an author edits it. A pull-quote cannot survive
  // inside rich text, which is why one is currently hand-written here.
  //
  // MARKUP IS `figure > blockquote + figcaption`, not `blockquote > footer >
  // cite`: the attribution is not part of what was said, and the spec puts it
  // outside the quotation. A screen reader then announces the quotation and
  // then its source, instead of reading the name as the quote's last sentence.
  //
  // The dash before the name is PUNCTUATION THIS RENDERER OWNS, not data:
  // upstream's normalizeQuoteAuthor strips a leading em/en/hyphen dash because
  // 319 of 935 stored attributions carry one and 616 do not, and the two must
  // not render differently. `- ` is the exact glyph upstream draws.

  var QUOTE_VARIANTS = { plain: 1, bordered: 1, card: 1, large: 1 };
  var QUOTE_ALIGNS = { left: 1, center: 1 };
  var QUOTE_SIZES = { normal: 1, large: 1 };

  /** Upstream resolveQuoteStyle, key for key. Every look field is optional. */
  function resolveQuoteStyle(c) {
    var content = c || {};
    var variant = Object.prototype.hasOwnProperty.call(QUOTE_VARIANTS, content.variant)
      ? content.variant : 'bordered';
    return {
      variant: variant,
      align: Object.prototype.hasOwnProperty.call(QUOTE_ALIGNS, content.align) ? content.align : 'left',
      // `large` is a variant AND a size in the legacy markup, so the variant
      // implies the size unless the author set one explicitly.
      size: Object.prototype.hasOwnProperty.call(QUOTE_SIZES, content.size)
        ? content.size : (variant === 'large' ? 'large' : 'normal'),
      accentColor: cssColour(content.accentColor) || '',
      // The decorative glyph is off by default: it lands on top of the first
      // words at some font sizes, and none of the imported quotes carried one.
      quoteMark: content.quoteMark === true
    };
  }

  /**
   * The quotation's HTML. Upstream's allow-list is <a href target rel>,
   * <strong>, <em>, <br>, the SAME four tags the table cell allows, so this
   * reuses `tableCellHtml`, the escape-by-default single-scan sanitiser
   * documented above, rather than growing a second copy of it. The two
   * allow-lists being identical is a fact about today's data, not a promise:
   * upstream keeps them as two separate constants "free to diverge (a table
   * cell is a label, a quote is prose)", so if QUOTE_ALLOWED_TAGS ever gains a
   * tag this has to fork rather than follow.
   */
  function quoteHtml(raw) {
    return tableCellHtml(raw);
  }

  /** Upstream normalizeQuoteAuthor: tags out, entities and runs collapsed, leading dash stripped. */
  function quoteAuthor(raw) {
    if (typeof raw !== 'string') return '';
    return raw
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .replace(/^\s+|\s+$/g, '')
      // \u2014 em dash, \u2013 en dash, then the plain hyphen: the three
      // shapes the stored attributions actually use.
      .replace(/^[\u2014\u2013-]\s*/, '')
      .replace(/^\s+|\s+$/g, '');
  }

  Neo.registerType('quote', function (el) {
    var c = el.content || {};
    var st = resolveQuoteStyle(c);
    var text = typeof c.text === 'string' ? c.text.replace(/^\s+|\s+$/g, '') : '';
    var author = quoteAuthor(c.author);
    var body = quoteHtml(text);
    // Upstream's empty state is an editor-only dashed hint and `null` on the
    // published page, so an empty quote is nothing here.
    if (!body && !author) return '';

    var centered = st.align === 'center';
    // The accent rule is a leading border on the figure for `bordered` and the
    // card's leading edge for `card`; `plain` and `large` draw no rule at all:
    // `large` says it with type size. Logical properties, not left/right, so a
    // future RTL locale flips with the text instead of against it.
    var figure = {};
    if (st.variant === 'bordered') {
      if (centered) {
        figure.borderTopWidth = '3px';
        figure.borderTopStyle = 'solid';
        figure.paddingTop = '1rem';
      } else {
        figure.borderInlineStartWidth = '3px';
        figure.borderInlineStartStyle = 'solid';
        figure.paddingInlineStart = '1.25rem';
      }
      if (st.accentColor) figure.borderColor = st.accentColor;
    } else if (st.variant === 'card') {
      figure.borderInlineStartWidth = '4px';
      figure.borderInlineStartStyle = 'solid';
      if (st.accentColor) figure.borderInlineStartColor = st.accentColor;
    }

    var html = viewportStyleTags(el) + wrapperOpen(el, 'content-type-neo-quote') +
      '<figure class="neo-quote neo-quote--' + st.variant +
      ' neo-quote--' + st.size + (centered ? ' neo-quote--center' : '') +
      (st.accentColor ? ' neo-quote--accent' : '') + '"' + styleAttrOf(figure) + '>';

    // `aria-label` carries the plain text so the decorative quote mark is never
    // read out as part of the quotation. stripTags is this file's own helper.
    var plain = stripTags(body);
    html += '<blockquote class="neo-quote__text"' +
      (plain ? ' aria-label="' + escapeAttr(plain) + '"' : '') + '>';
    if (st.quoteMark) {
      html += '<span class="neo-quote__mark" aria-hidden="true"' +
        styleAttrOf(st.accentColor ? { color: st.accentColor } : null) + '>&#8220;</span>';
    }
    html += '<span class="neo-quote__body">' + body + '</span></blockquote>';

    if (author) {
      html += '<figcaption class="neo-quote__caption">' +
        '<span aria-hidden="true">- </span>' +
        '<cite class="neo-quote__cite">' + escapeHtml(author) + '</cite>' +
        '</figcaption>';
    }
    return html + '</figure></div>';
  });

  // ---- 33. job-listings --------------------------------------
  // content: headline, jobs[] of {title, location, type, description,
  // buttonText, buttonLink, featured}, showLocation / showType /
  // showDescription, and an element-level buttonText fallback.
  //
  // `source` and `maxItems` sit in the palette's defaultContent and are read by
  // NOTHING upstream: not the renderer, not UnifiedContentEditor's schema for
  // this type. The whole `jobs` array is drawn, in order, and that is mirrored:
  // silently clipping a list to 5 because of a key no editor exposes would hide
  // vacancies an author can see in the canvas.
  //
  // `buttonLink` is the one place this deliberately does MORE than upstream.
  // The editor collects it (`{key:'buttonLink', type:'url'}`) but upstream
  // renders a bare <Button> with no href, so in the canvas it goes nowhere. A
  // vacancy whose Apply button is not a link is the element failing at its only
  // job on a real storefront, so a safe href becomes an <a> and anything else
  // stays the non-interactive span the canvas draws.
  //
  // Upstream draws a lucide MapPin beside the location. The icon set does not
  // exist in this theme (the FIDELITY NOTE above), so the location is its text
  // alone rather than a missing-glyph box.

  function jobListingHtml(job, fallbackButton, showLocation, showType, showDescription) {
    var title = typeof job.title === 'string' ? job.title : '';
    var location = typeof job.location === 'string' ? job.location : '';
    var type = typeof job.type === 'string' ? job.type : '';
    var description = typeof job.description === 'string' ? job.description : '';
    var label = (typeof job.buttonText === 'string' && job.buttonText) ? job.buttonText : fallbackButton;
    var href = safeHref(job.buttonLink);
    var featured = job.featured === true;

    var html = '<li class="neo-jobs__item' + (featured ? ' neo-jobs__item--featured' : '') + '">';
    if (featured) {
      html += '<span class="neo-jobs__flag">' + escapeHtml(lsT('common.featured', 'Featured')) + '</span>';
    }
    html += '<div class="neo-jobs__main">';
    if (title) html += '<h3 class="neo-jobs__title">' + escapeHtml(title) + '</h3>';
    var meta = '';
    if (showLocation && location) meta += '<span class="neo-jobs__place">' + escapeHtml(location) + '</span>';
    if (showType && type) meta += '<span class="neo-jobs__type">' + escapeHtml(type) + '</span>';
    if (meta) html += '<div class="neo-jobs__meta">' + meta + '</div>';
    if (showDescription && description) {
      html += '<p class="neo-jobs__text">' + escapeHtml(description) + '</p>';
    }
    html += '</div>';
    if (label) {
      html += href
        ? '<a class="neo-jobs__cta" href="' + escapeAttr(href) + '">' + escapeHtml(label) + '</a>'
        : '<span class="neo-jobs__cta">' + escapeHtml(label) + '</span>';
    }
    return html + '</li>';
  }

  Neo.registerType('job-listings', function (el) {
    var c = el.content || {};
    var raw = Array.isArray(c.jobs) ? c.jobs : [];
    var jobs = [];
    for (var i = 0; i < raw.length; i++) if (isRecord(raw[i])) jobs.push(raw[i]);
    var headline = typeof c.headline === 'string' ? c.headline.replace(/^\s+|\s+$/g, '') : '';
    if (!jobs.length && !headline) return '';

    // Upstream's own default for the shared button label, so a vacancy with no
    // per-job text and no element-level text still says something.
    var fallbackButton = (typeof c.buttonText === 'string' && c.buttonText)
      ? c.buttonText : lsT('common.apply', 'Apply');
    var showLocation = c.showLocation !== false;
    var showType = c.showType !== false;
    var showDescription = c.showDescription !== false;

    var html = viewportStyleTags(el) + wrapperOpen(el, 'content-type-neo-jobs');
    if (headline) html += '<h2 class="neo-jobs__headline">' + escapeHtml(headline) + '</h2>';
    if (jobs.length) {
      html += '<ul class="neo-jobs__list">';
      for (var k = 0; k < jobs.length; k++) {
        html += jobListingHtml(jobs[k], fallbackButton, showLocation, showType, showDescription);
      }
      html += '</ul>';
    }
    return html + '</div>';
  });

  // ---- 34. announcement-bar ----------------------------------
  // content: text, linkText, linkUrl, dismissible, style.
  //
  // POSITION: FIXED IS NOT USED, AND THAT IS THE DECISION, NOT AN OMISSION.
  //
  // Upstream's renderer draws an in-flow band, a centred flex row with a
  // radius, and the element's editor exposes no position control at all, so
  // "fixed" is not part of this element's contract in the first place. It is
  // also not portable: the one page that DOES pin a promo bar had to scope the
  // rule to the storefront mount and says why in its own stylesheet:
  //
  //     /* Theme renderer ONLY (#aico-content mount): in the aico SPA
  //        position:fixed breaks inside the transformed canvas (the bar lands
  //        mid-hero) */
  //
  // A transformed ancestor makes a fixed descendant position against the
  // TRANSFORM, not the viewport, and the builder canvas is transformed. So
  // `fixed` is theme-only-safe: correct on the storefront, visibly wrong in the
  // canvas, which is the one place an author checks their work. A renderer
  // shared by both cannot take that trade, and the clean degradation is simply
  // to stay in flow: identical in both renderers, no bleed, no overlap, and
  // nothing for a phone to get wrong. An author who genuinely needs a pinned,
  // scroll-driven bar still has the `html` element and its own scoped CSS,
  // which is what that page uses today.
  //
  // The style vocabulary is wider in the editor than in the canvas: the editor
  // offers primary/secondary/accent/warning while ElementRenderer's switch only
  // branches on 'primary' and 'dark' and sends everything else to `muted`. Both
  // sets are honoured here (that drift is upstream's, and an author picking
  // "warning" should not get the muted band by accident), with an unknown value
  // falling back to `primary`, the palette's own default.
  //
  // The styles land on the INNER band, not the wrapper, because that is where
  // upstream puts `cssStyles`, so `data-neo-el` goes there too, or the
  // styles.mobile @media block would be aimed at the wrong node (same shape as
  // `spacer`).

  var BAR_STYLES = { primary: 1, secondary: 1, accent: 1, warning: 1, dark: 1, muted: 1 };

  Neo.registerType('announcement-bar', function (el) {
    var c = el.content || {};
    var text = typeof c.text === 'string' ? c.text : '';
    var linkText = typeof c.linkText === 'string' ? c.linkText.replace(/^\s+|\s+$/g, '') : '';
    var href = safeHref(c.linkUrl);
    var tone = Object.prototype.hasOwnProperty.call(BAR_STYLES, c.style) ? c.style : 'primary';
    if (!text && !linkText) return '';

    var html = viewportStyleTags(el) +
      '<div class="content-type-neo-bar">' +
      '<div class="neo-bar neo-bar--' + tone + '"' + dataElAttr(el) +
      styleAttrOf(pickStyles(el, 'desktop')) + '>';
    if (text) html += '<span class="neo-bar__text">' + escapeHtml(text) + '</span>';
    if (linkText) {
      // Upstream renders the anchor whether or not the href resolves; a link
      // that goes nowhere is a dead affordance, so an unsafe or empty url
      // leaves the label as plain emphasis instead.
      html += href
        ? '<a class="neo-bar__link" href="' + escapeAttr(href) + '">' + escapeHtml(linkText) + '</a>'
        : '<span class="neo-bar__link">' + escapeHtml(linkText) + '</span>';
    }
    if (c.dismissible) {
      // The glyph is a real character, not a lucide <X>. The button is wired in
      // a hydrate pass (hydrateBarDismiss) for the same reason the countdown
      // ticks in one: the engine paints via innerHTML, so nothing in this
      // string can carry behaviour of its own.
      html += '<button type="button" class="neo-bar__close" data-neo-bar-close="1"' +
        ' aria-label="' + escapeAttr(lsT('common.close', 'Close')) + '">&#215;</button>';
    }
    return html + '</div></div>';
  });

  /**
   * Make the announcement bar's dismiss button do something. Without this the
   * X is a button that visibly does nothing, which is worse than not drawing
   * one, and the authored page cannot supply the handler itself, because no
   * authored script ever runs (see the note at the top of this block).
   *
   * Idempotent via `data-neo-bar-bound`: hydrate runs again on the secondary
   * (site chrome) mount and after a locale switch repaints.
   */
  function hydrateBarDismiss(root) {
    var scope = root || document;
    var buttons = scope.querySelectorAll('[data-neo-bar-close="1"]');
    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      if (button.getAttribute('data-neo-bar-bound') === '1') continue;
      button.setAttribute('data-neo-bar-bound', '1');
      button.addEventListener('click', function (event) {
        event.preventDefault();
        var bar = this.closest ? this.closest('.neo-bar') : null;
        // The band is removed rather than hidden so it stops taking part in
        // layout: a `display:none` flex row still leaves the wrapper's own
        // authored padding behind as a coloured strip.
        if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
      });
    }
  }


  // ---- 35b. popup ------------------------------------------
  //
  // The page builder's `popup` element (aiconeo popupModel.ts, docs/contentbuilder-json.md):
  // a dialog over the page holding ordinary child elements. The renderer only paints it,
  // hidden; hydratePopups decides whether and when it opens:
  //   - never before the visitor answered the cookie banner (Cookiebot), never while another
  //     popup is open, never outside startDate..endDate (visitor's local date), never on a
  //     phone (< 750px) when showOnMobile is false;
  //   - how often: frequency session (sessionStorage) / days (localStorage, every
  //     frequencyDays days) / always, keyed by the element id;
  //   - when: delay (delaySeconds after hydrate), scroll (scrollPercent of the page), exit
  //     (pointer leaves the window at the top; on touch devices the scroll rule instead).
  // When it opens it is moved to <body> (an ancestor with a transform would otherwise trap the
  // fixed position) and removed again when closed. Escape and the close button always close it;
  // the dimmed page only with closeOnOverlay. A centred popup is modal: focus stays inside it
  // and the page behind does not scroll; the corner box leaves the page usable.

  var POPUP_TRIGGERS = ['delay', 'scroll', 'exit'];
  var POPUP_FREQUENCIES = ['session', 'days', 'always'];
  var POPUP_SIZES = ['sm', 'md', 'lg'];

  function popupInteger(value, min, max, fallback) {
    var parsed = typeof value === 'number' ? value : parseInt(String(value == null ? '' : value), 10);
    if (!isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, Math.round(parsed)));
  }

  function popupDate(value) {
    var text = typeof value === 'string' ? value.trim() : '';
    return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : '';
  }

  /** Same reading as aiconeo normalizePopupContent: unknown values fall back to the defaults. */
  function popupSettings(content) {
    var c = content && typeof content === 'object' ? content : {};
    return {
      trigger: POPUP_TRIGGERS.indexOf(c.trigger) >= 0 ? c.trigger : 'delay',
      delaySeconds: popupInteger(c.delaySeconds, 0, 120, 5),
      scrollPercent: popupInteger(c.scrollPercent, 10, 90, 50),
      frequency: POPUP_FREQUENCIES.indexOf(c.frequency) >= 0 ? c.frequency : 'days',
      frequencyDays: popupInteger(c.frequencyDays, 1, 365, 7),
      startDate: popupDate(c.startDate),
      endDate: popupDate(c.endDate),
      position: c.position === 'bottom-right' ? 'bottom-right' : 'center',
      size: POPUP_SIZES.indexOf(c.size) >= 0 ? c.size : 'md',
      showOnMobile: typeof c.showOnMobile === 'boolean' ? c.showOnMobile : true,
      closeOnOverlay: typeof c.closeOnOverlay === 'boolean' ? c.closeOnOverlay : true
    };
  }

  Neo.registerType('popup', function (el) {
    var s = popupSettings(el && el.content);
    var key = safeId(el && el.id);
    if (!key) return '';
    var isCenter = s.position === 'center';
    var inner = renderChildren(el);
    return viewportStyleTags(el) +
      '<div class="neo-popup neo-popup--' + s.position + '" hidden data-neo-popup="1"' +
      ' data-popup-key="' + key + '"' +
      ' data-popup-trigger="' + s.trigger + '"' +
      ' data-popup-delay="' + s.delaySeconds + '"' +
      ' data-popup-scroll="' + s.scrollPercent + '"' +
      ' data-popup-frequency="' + s.frequency + '"' +
      ' data-popup-days="' + s.frequencyDays + '"' +
      ' data-popup-start="' + s.startDate + '"' +
      ' data-popup-end="' + s.endDate + '"' +
      ' data-popup-mobile="' + (s.showOnMobile ? '1' : '0') + '"' +
      ' data-popup-overlay-close="' + (isCenter && s.closeOnOverlay ? '1' : '0') + '">' +
      (isCenter ? '<div class="neo-popup__overlay" data-neo-popup-overlay="1"></div>' : '') +
      '<div class="neo-popup__box neo-popup__box--' + s.size + '" role="dialog"' +
      (isCenter ? ' aria-modal="true"' : '') + ' tabindex="-1"' + dataElAttr(el) + inlineStyleAttr(el) + '>' +
      '<button type="button" class="neo-popup__close" data-neo-popup-close="1"' +
      ' aria-label="' + escapeAttr(lsT('common.close', 'Fermer')) + '">&#215;</button>' +
      '<div class="neo-popup__body">' + inner + '</div>' +
      '</div></div>';
  });

  var openPopup = null;

  function popupStore(frequency) {
    try {
      return frequency === 'session' ? window.sessionStorage : window.localStorage;
    } catch (e) {
      return null;
    }
  }

  function popupWasShown(node) {
    var frequency = node.getAttribute('data-popup-frequency');
    if (frequency === 'always') return false;
    var store = popupStore(frequency);
    if (!store) return false;
    var raw = null;
    try { raw = store.getItem('neo-popup:' + node.getAttribute('data-popup-key')); } catch (e) { return false; }
    if (!raw) return false;
    if (frequency === 'session') return true;
    var days = parseInt(node.getAttribute('data-popup-days'), 10) || 7;
    var shownAt = parseInt(raw, 10);
    return isFinite(shownAt) && (Date.now() - shownAt) < days * 86400000;
  }

  function popupRememberShown(node) {
    var frequency = node.getAttribute('data-popup-frequency');
    if (frequency === 'always') return;
    var store = popupStore(frequency);
    try { if (store) store.setItem('neo-popup:' + node.getAttribute('data-popup-key'), String(Date.now())); } catch (e) { /* private mode: it may show again */ }
  }

  function popupToday() {
    var now = new Date();
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    return now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate());
  }

  function popupIsEligible(node) {
    var today = popupToday();
    var start = node.getAttribute('data-popup-start');
    var end = node.getAttribute('data-popup-end');
    if (start && today < start) return false;
    if (end && today > end) return false;
    if (node.getAttribute('data-popup-mobile') === '0' && window.matchMedia && window.matchMedia('(max-width: 749px)').matches) return false;
    return !popupWasShown(node);
  }

  /** Run `callback` once the visitor answered the cookie banner, or at once when there is none. */
  function whenConsentAnswered(callback) {
    var done = false;
    function run() {
      if (done) return;
      done = true;
      window.removeEventListener('CookiebotOnAccept', run);
      window.removeEventListener('CookiebotOnDecline', run);
      callback();
    }
    var cookiebot = window.Cookiebot;
    var hasBanner = !!(cookiebot || document.getElementById('Cookiebot'));
    if (!hasBanner || (cookiebot && cookiebot.hasResponse)) {
      run();
      return;
    }
    // Cookiebot fires these on the visitor's answer and, for an earlier answer, when it loads.
    window.addEventListener('CookiebotOnAccept', run);
    window.addEventListener('CookiebotOnDecline', run);
  }

  function popupFocusables(box) {
    return Array.prototype.filter.call(
      box.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (item) { return item.offsetWidth > 0 || item.offsetHeight > 0 || item === document.activeElement; }
    );
  }

  function closePopup() {
    var state = openPopup;
    if (!state) return;
    openPopup = null;
    document.removeEventListener('keydown', state.onKey, true);
    document.documentElement.classList.remove('neo-popup-lock');
    if (state.node.parentNode) state.node.parentNode.removeChild(state.node);
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
      try { state.lastFocus.focus({ preventScroll: true }); } catch (e) { /* element gone */ }
    }
  }

  function showPopup(node) {
    if (openPopup || !node || !document.documentElement.contains(node) || node.getAttribute('data-popup-opened') === '1') return;
    node.setAttribute('data-popup-opened', '1');
    popupRememberShown(node);
    var box = node.querySelector('.neo-popup__box');
    if (!box) return;
    var heading = box.querySelector('h1, h2, h3, h4, h5, h6');
    if (heading) {
      if (!heading.id) heading.id = 'neo-popup-title-' + node.getAttribute('data-popup-key');
      box.setAttribute('aria-labelledby', heading.id);
    }
    var isModal = box.getAttribute('aria-modal') === 'true';
    var state = { node: node, lastFocus: document.activeElement, onKey: null };
    state.onKey = function (event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        closePopup();
        return;
      }
      if (event.key !== 'Tab' || !isModal) return;
      var items = popupFocusables(box);
      if (!items.length) { event.preventDefault(); box.focus(); return; }
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === box)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    openPopup = state;
    document.body.appendChild(node);
    node.hidden = false;
    if (isModal) document.documentElement.classList.add('neo-popup-lock');
    document.addEventListener('keydown', state.onKey, true);
    var closeButton = node.querySelector('[data-neo-popup-close="1"]');
    if (closeButton) closeButton.addEventListener('click', function (event) { event.preventDefault(); closePopup(); });
    var overlay = node.querySelector('[data-neo-popup-overlay="1"]');
    if (overlay && node.getAttribute('data-popup-overlay-close') === '1') overlay.addEventListener('click', closePopup);
    // Focus the dialog itself (not the close button), so a screen reader announces it first.
    try { box.focus({ preventScroll: true }); } catch (e) { box.focus(); }
    window.requestAnimationFrame(function () { node.classList.add('is-open'); });
  }

  function armPopupTrigger(node) {
    var trigger = node.getAttribute('data-popup-trigger');
    var isTouch = !!(window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches);
    if (trigger === 'exit' && !isTouch) {
      var onLeave = function (event) {
        if (event.relatedTarget || event.clientY > 0) return;
        document.removeEventListener('mouseout', onLeave);
        showPopup(node);
      };
      document.addEventListener('mouseout', onLeave);
      return;
    }
    if (trigger === 'scroll' || trigger === 'exit') {
      var share = (parseInt(node.getAttribute('data-popup-scroll'), 10) || 50) / 100;
      var onScroll = function () {
        var doc = document.documentElement;
        var room = Math.max(doc.scrollHeight, document.body.scrollHeight) - window.innerHeight;
        if (room > 0 && (window.pageYOffset || doc.scrollTop) / room < share) return;
        window.removeEventListener('scroll', onScroll);
        showPopup(node);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      return;
    }
    var delay = parseInt(node.getAttribute('data-popup-delay'), 10);
    window.setTimeout(function () { showPopup(node); }, (isFinite(delay) ? delay : 5) * 1000);
  }

  function hydratePopups(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-neo-popup="1"]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.getAttribute('data-neo-popup-bound') === '1') continue;
      node.setAttribute('data-neo-popup-bound', '1');
      if (!popupIsEligible(node)) continue;
      (function (popupNode) {
        whenConsentAnswered(function () { armPopupTrigger(popupNode); });
      })(node);
    }
  }


  // ---- 36. kicker / stats ------------------------------------
  //
  // The last two patterns the 23 live landing pages carry that had NO element
  // behind them at all. Unlike §29-§33, these were not renderers missing for a
  // type the palette already offered: the builder had no `kicker` and no
  // `stats`, so the only way to draw either was to hand-build it out of `text`
  // and `container` elements and paste the CSS that makes them look right into
  // a sibling `html` element, where the page's author can neither see it nor
  // change it. The builder now owns the values; this file owns the look.
  //
  // KICKER - a short uppercase label with a coloured rule beside it, above a
  // heading. 132 of them across 22 of the 23 pages, 1 to 18 per page, every one
  // written as:
  //     .ls-kicker{display:flex;align-items:center;gap:12px}
  //     .ls-kicker::after{content:'';display:block;flex:0 0 40px;width:40px;
  //                       height:2px;background:#e3b562}
  //     .ls-kicker--rule-first{flex-direction:row-reverse;justify-content:flex-end}
  // plus the 28px in-card variant (`.lsk-kick::after{flex:0 0 28px;width:28px}`
  // on 12-kids and 17-familles, `.ls-kicker--28` on 15-acces and
  // 16-infos-pratiques). One page's own comment calls it "native kicker
  // (lib.mjs kicker())", so it was already a named component in their kit with
  // nothing behind it.
  //
  // The rule is a pseudo-element in all 132 because it HAD to be: the label was
  // a rich-text element and a decorative <span> typed into one does not survive
  // the next edit. It is a real <span> here, which is the whole point of the
  // element existing.
  //
  // STATS - a large numeral with a small caption under it, several per row.
  // Twice in the corpus, in two shapes:
  //   24-palmares, a 3-cell band:
  //     .pal-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(210px,100%),1fr));
  //                gap:0;border-top:1px solid rgba(254,252,237,.14)}
  //     .pal-stat{…;border-bottom:1px solid …;border-right:1px solid …}
  //     .pal-stat:last-child{border-right:none}
  //     .pal-stat__num{font-size:clamp(46px,7.6cqi,84px);line-height:.92}
  //     .pal-stat__num sup{font-size:.42em;vertical-align:super;color:#e3b562;margin-left:2px}
  //   14-ls-is-more, a wrapping hero chip row:
  //     .lsim-stats{display:flex;flex-wrap:wrap;gap:16px;justify-content:center}
  //     .lsim-stat{flex:0 1 auto;min-width:150px;padding:20px 32px;…}
  //
  // FIDELITY: the four normalisers below are ports of aiconeo's kickerModel.ts
  // and statsModel.ts, deliberately line for line. The colour rule is this
  // file's own `cssColour`, which is character for character the regex the
  // builder's model uses, because this file CONCATENATES the value into a
  // style="…" string where `red;background:url(…)` would be a CSS injection.
  //
  // DEVIATION FROM UPSTREAM, both types: an EMPTY element renders '' here
  // rather than an empty box. Upstream's empty state is an editor-only dashed
  // hint plus a wrapper that stays selectable on the canvas; a storefront has
  // nothing to select, and a kicker with no label would otherwise put a
  // 40x2 gold rule on the page on its own.

  var KICKER_DEFAULT_RULE_LENGTH = 40;
  var KICKER_MIN_RULE_LENGTH = 4;
  var KICKER_MAX_RULE_LENGTH = 240;

  /**
   * Port of kickerModel.ts normalizeKickerText. Tags are STRIPPED, not escaped:
   * the 132 kickers in the corpus are `text` elements whose content is
   * `<p>Bientot</p>`, so a converted one has to land on `Bientot`.
   */
  function kickerText(raw) {
    if (typeof raw !== 'string') return '';
    return raw
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .replace(/^\s+|\s+$/g, '');
  }

  /** Port of kickerModel.ts resolveKicker. Every key optional. */
  function resolveKicker(content) {
    var source = content || {};
    var rawLength = Number(source.ruleLength);
    return {
      text: kickerText(source.text),
      rulePosition: source.rulePosition === 'before' ? 'before' : 'after',
      ruleLength: (isFinite(rawLength) && rawLength > 0)
        ? Math.min(KICKER_MAX_RULE_LENGTH, Math.max(KICKER_MIN_RULE_LENGTH, Math.round(rawLength)))
        : KICKER_DEFAULT_RULE_LENGTH,
      accentColor: cssColour(source.accentColor) || ''
    };
  }

  Neo.registerType('kicker', function (el) {
    var kicker = resolveKicker(el.content);
    if (!kicker.text) return '';

    // No inline background when the author picked no colour: the stylesheet
    // then supplies `var(--aico-neo-accent, …)`, and `--aico-neo-accent` is the
    // document's own `settings.accentColor`, stamped on `.aico-neo-page` by
    // wrapPage below. That is how a kicker is correct with nothing authored.
    var ruleStyle = { width: kicker.ruleLength + 'px' };
    if (kicker.accentColor) ruleStyle.background = kicker.accentColor;

    // The label stays FIRST in the DOM whichever side the rule is drawn on, so
    // a screen reader and a text-only crawler never read an empty span before
    // it. `--rule-first` flips it with flex-direction, exactly as the corpus
    // does, rather than by reordering the two nodes.
    return viewportStyleTags(el) + wrapperOpen(el, 'content-type-neo-kicker') +
      '<span class="neo-kicker' +
      (kicker.rulePosition === 'before' ? ' neo-kicker--rule-first' : '') + '">' +
      '<span class="neo-kicker__label">' + escapeHtml(kicker.text) + '</span>' +
      '<span class="neo-kicker__rule" aria-hidden="true"' + styleAttrOf(ruleStyle) + '></span>' +
      '</span></div>';
  });

  var STATS_MIN_COLUMNS = 1;
  var STATS_MAX_COLUMNS = 6;
  /** The live hero row's own floor: `.lsim-stat{min-width:150px}` on 14-ls-is-more. */
  var STATS_MIN_CELL_PX = 150;
  var STATS_GAP_PX = 24;

  /** Port of statsModel.ts toPlainText, used for all three cell fields. */
  function statText(raw) {
    if (typeof raw === 'number' && isFinite(raw)) return String(raw);
    if (typeof raw !== 'string') return '';
    return raw
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .replace(/^\s+|\s+$/g, '');
  }

  /** Port of statsModel.ts resolveStats. A cell with neither value nor label is dropped. */
  function resolveStats(content) {
    var source = content || {};
    var raw = Array.isArray(source.items) ? source.items : [];
    var items = [];
    for (var i = 0; i < raw.length; i++) {
      var cell = (raw[i] && typeof raw[i] === 'object') ? raw[i] : {};
      var item = {
        value: statText(cell.value),
        label: statText(cell.label),
        suffix: statText(cell.suffix)
      };
      if (item.value !== '' || item.label !== '') items.push(item);
    }
    var rawColumns = Number(source.columns);
    var columns = (isFinite(rawColumns) && rawColumns > 0)
      ? Math.round(rawColumns)
      : (items.length || 3);
    return {
      items: items,
      columns: Math.min(STATS_MAX_COLUMNS, Math.max(STATS_MIN_COLUMNS, columns)),
      hasDividers: source.hasDividers === true
    };
  }

  /**
   * Port of statsModel.ts buildStatsTemplate. The string has to be IDENTICAL to
   * the builder's: this is the one piece of geometry both renderers compute, and
   * a row that reflows in the canvas and overflows on the storefront would be
   * the same failure as having no element at all.
   *
   *   repeat(auto-fit, minmax(max(calc((100% - (N-1)*Gpx) / N), 150px), 1fr))
   *
   * Wide, the exact 1/N track beats the 150px floor and auto-fit fits exactly N.
   * Narrow, the floor takes over and auto-fit fits as many 150px tracks as fit.
   * Subtracting the gaps from the percentage is what stops the wide case
   * overflowing by (N-1)*gap. No media query is involved, which is deliberate:
   * a @media rule cannot be right inside the builder's transformed canvas, and
   * a track list is right at every width by construction.
   */
  function buildStatsTemplate(columns, gapPx) {
    var track = columns > 1
      ? 'calc((100% - ' + ((columns - 1) * gapPx) + 'px) / ' + columns + ')'
      : '100%';
    return 'repeat(auto-fit, minmax(max(' + track + ', ' + STATS_MIN_CELL_PX + 'px), 1fr))';
  }

  Neo.registerType('stats', function (el) {
    var stats = resolveStats(el.content);
    if (stats.items.length === 0) return '';

    // Dividers need the cells to touch: with a gap there is nothing for a
    // hairline BETWEEN two cells to sit on, so the hairline is the gap.
    var gap = stats.hasDividers ? 0 : STATS_GAP_PX;
    var gridStyle = {
      gridTemplateColumns: buildStatsTemplate(stats.columns, gap),
      gap: gap + 'px'
    };

    // <dl>, not the corpus's <ul>: each cell is one value described by one
    // caption, and <dt> before <dd> makes the reading order match the visual
    // one, so a screen reader says "7, Titres de Champion de Suisse" instead of
    // two unrelated lines. The <div> wrapper is the pair grouping the spec
    // allows inside a <dl>, and it is also the grid cell.
    var html = viewportStyleTags(el) + wrapperOpen(el, 'content-type-neo-stats') +
      '<dl class="neo-stats' + (stats.hasDividers ? ' neo-stats--divided' : '') + '"' +
      styleAttrOf(gridStyle) + '>';

    for (var i = 0; i < stats.items.length; i++) {
      var item = stats.items[i];
      html += '<div class="neo-stats__cell">' +
        '<dt class="neo-stats__value">' + escapeHtml(item.value);
      // The suffix is a FIELD, not markup inside the value, so it cannot be
      // eaten by a sanitiser or a converter on the way here, and it is the one
      // fragment of a numeral that is accent-coloured.
      if (item.suffix) {
        html += '<sup class="neo-stats__suffix">' + escapeHtml(item.suffix) + '</sup>';
      }
      html += '</dt>' +
        '<dd class="neo-stats__label">' + escapeHtml(item.label) + '</dd>' +
        '</div>';
    }

    return html + '</dl></div>';
  });

  // ============================================================
  // 37. `content-slider`: one slide at a time, any element as a slide
  //
  // CONTRACT (shared with the aiconeo builder element of the same name, do not
  // diverge on one side only):
  //   type     `content-slider`
  //   children every CHILD element is one slide, in document order. Any type
  //            can be a slide; the usual ones are a `container`, or an `image`
  //            with children (photo + scrim + content layer).
  //   content  every key optional, malformed values fall back to the default:
  //              autoplay          boolean  true   advance automatically
  //              autoplayInterval  int ms   6000   per slide, clamped to >= 2000
  //              loop              boolean  true   last -> first, first -> last
  //              showDots          boolean  true   one dot per slide
  //              showArrows        boolean  true   prev / next buttons
  //              showPause         boolean  false  pause / play toggle (only
  //                                                with autoplay and 2+ slides)
  //   size     the element's own native `styles` (minHeight per tier, width).
  //            Every slide fills the element: full width, stretched to the
  //            slider's height (the tallest slide sets it, so nothing jumps).
  //
  // There are NO motion fields on purpose: the horizontal slide, its duration
  // and easing, and the look of the dots and arrows belong to the theme
  // (content-builder-neo.css §37), the "motion lives in the theme" rule.
  //
  // Guards:
  //   * 0 renderable slides -> '' ; 1 slide -> no controls, no autoplay.
  //   * Without JS (or before hydrate) slide 1 shows: the viewport clips and
  //     the track is a flex row of 100%-wide slides.
  //   * Hydration is idempotent (`data-neo-slider-ready`) and a slider that
  //     leaves the DOM (a repaint) stops its own timer.
  //   * `prefers-reduced-motion: reduce` -> no autoplay, instant switch.
  //   * Autoplay never runs in the Shopify theme editor (Shopify.designMode)
  //     and pauses on hover, on keyboard focus inside, while the slider is off
  //     screen (IntersectionObserver) and while the tab is hidden.
  //   * showPause: the toggle is the visitor's own stop (WCAG 2.2.2 for a
  //     carousel that moves by itself). It is one more reason in the `paused`
  //     map, `user`, which only the toggle writes: hover-out, focus-out, coming
  //     back on screen or to the tab clear THEIR reasons and never this one, so
  //     a pause holds until the same button is pressed again. It is an
  //     aria-pressed toggle with ONE fixed name ("pause the carousel", pressed =
  //     paused), the ARIA toggle-button pattern: a name that flips as well as
  //     the pressed state would announce the opposite of what is true. The
  //     icon flips (pause bars / play triangle). It renders `hidden` and is
  //     shown by hydrate only when autoplay can actually run (not in the theme
  //     editor); under `prefers-reduced-motion: reduce` there is no autoplay
  //     to stop, so the stylesheet hides it (§37 CSS).
  //   * Loop wraps by rewinding the track; there are no cloned slides, so
  //     element ids, form fields and theme slots inside a slide stay unique.
  //   * Swipe is pointer events with a horizontal-intent threshold. The
  //     viewport is `touch-action: pan-y`, so a vertical drag always scrolls
  //     the page and the slider only claims a clearly sideways gesture.
  // ============================================================

  var SLIDER_DEFAULT_INTERVAL = 6000;
  var SLIDER_MIN_INTERVAL = 2000;
  var SLIDER_SWIPE_INTENT_PX = 10;

  function sliderFlag(value, fallback) {
    if (value === true || value === false) return value;
    if (value === 'true' || value === 1 || value === '1') return true;
    if (value === 'false' || value === 0 || value === '0') return false;
    return fallback;
  }

  function sliderInterval(value) {
    var n = Number(value);
    if (value === null || value === '' || typeof value === 'boolean' || !isFinite(n) || n <= 0) {
      return SLIDER_DEFAULT_INTERVAL;
    }
    return Math.max(SLIDER_MIN_INTERVAL, Math.round(n));
  }

  function sliderIsEnglish() {
    return typeof window.currentLangIsocode === 'string' && window.currentLangIsocode === 'en';
  }

  var SLIDER_CHEVRON_PREV = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">' +
    '<path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var SLIDER_CHEVRON_NEXT = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">' +
    '<path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  // Both glyphs ship in the button; the stylesheet shows one per aria-pressed.
  var SLIDER_PAUSE_ICONS = '<svg class="neo-slider__pause-icon neo-slider__pause-icon--pause" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">' +
    '<path d="M8 5v14M16 5v14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>' +
    '<svg class="neo-slider__pause-icon neo-slider__pause-icon--play" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">' +
    '<path d="M8 5.5v13a.5.5 0 0 0 .77.42l10.2-6.5a.5.5 0 0 0 0-.84L8.77 5.08A.5.5 0 0 0 8 5.5z" fill="currentColor"/></svg>';

  Neo.registerType('content-slider', function (el) {
    var c = isRecord(el && el.content) ? el.content : {};
    var children = (el && Array.isArray(el.children)) ? el.children : [];

    // A child that draws nothing (an image without a src, an unknown leaf)
    // would otherwise be a blank slide with a dot of its own.
    var slides = [];
    for (var i = 0; i < children.length; i++) {
      if (!children[i] || typeof children[i] !== 'object') continue;
      var inner = renderChild(children[i]);
      if (inner && /\S/.test(inner)) slides.push(inner);
    }
    if (!slides.length) return '';

    if (c.layout === 'rail') return renderContentRail(el, c, slides);

    var en = sliderIsEnglish();
    var count = slides.length;
    var multi = count > 1;
    var autoplay = multi && sliderFlag(c.autoplay, true);
    var loop = sliderFlag(c.loop, true);
    var showDots = multi && sliderFlag(c.showDots, true);
    var showArrows = multi && sliderFlag(c.showArrows, true);
    var showPause = autoplay && sliderFlag(c.showPause, false);

    var html = viewportStyleTags(el) +
      '<div class="neo-slider' + (multi ? '' : ' neo-slider--single') + '" data-neo-slider' +
      ' data-autoplay="' + (autoplay ? '1' : '0') + '"' +
      ' data-interval="' + sliderInterval(c.autoplayInterval) + '"' +
      ' data-loop="' + (loop ? '1' : '0') + '"' +
      ' role="region" aria-roledescription="' + (en ? 'carousel' : 'carrousel') + '"' +
      ' aria-label="' + (en ? 'Carousel' : 'Carrousel') + '"' +
      // Nothing else in the slider is focusable when both controls are off,
      // so the region itself takes focus to keep the arrow keys reachable.
      (multi && !showDots && !showArrows ? ' tabindex="0"' : '') +
      dataElAttr(el) + styleAttrOf(pickStyles(el, 'desktop')) + '>';

    html += '<div class="neo-slider__viewport"><div class="neo-slider__track">';
    for (var s = 0; s < count; s++) {
      // Slides 2..n start inert so, before hydrate, nobody tabs into a slide
      // that is clipped out of view.
      html += '<div class="neo-slider__slide' + (s === 0 ? ' is-active' : '') + '"' +
        ' role="group" aria-roledescription="' + (en ? 'slide' : 'diapositive') + '"' +
        ' aria-label="' + (s + 1) + ' / ' + count + '"' +
        (s === 0 ? '' : ' aria-hidden="true" inert') + '>' +
        slides[s] + '</div>';
    }
    html += '</div></div>';

    if (showArrows) {
      html += '<button type="button" class="neo-slider__arrow neo-slider__arrow--prev" data-neo-slider-prev' +
        ' aria-label="' + (en ? 'Previous slide' : 'Diapositive précédente') + '">' + SLIDER_CHEVRON_PREV + '</button>' +
        '<button type="button" class="neo-slider__arrow neo-slider__arrow--next" data-neo-slider-next' +
        ' aria-label="' + (en ? 'Next slide' : 'Diapositive suivante') + '">' + SLIDER_CHEVRON_NEXT + '</button>';
    }
    if (showDots) {
      html += '<div class="neo-slider__dots">';
      for (var d = 0; d < count; d++) {
        html += '<button type="button" class="neo-slider__dot' + (d === 0 ? ' is-active' : '') + '"' +
          ' data-neo-slider-dot="' + d + '"' +
          ' aria-label="' + (en ? 'Slide ' : 'Diapositive ') + (d + 1) + '"' +
          (d === 0 ? ' aria-current="true"' : '') + '></button>';
      }
      html += '</div>';
    }
    if (showPause) {
      html += '<button type="button" class="neo-slider__pause" data-neo-slider-pause aria-pressed="false"' +
        ' aria-label="' + (en ? 'Pause carousel' : 'Mettre le carrousel en pause') + '" hidden>' +
        SLIDER_PAUSE_ICONS + '</button>';
    }
    return html + '</div>';
  });

  // ---- 37b. rail layout (p59 T2) ------------------------------------------
  //
  // CONTRACT (shared with the aiconeo builder A2):
  //   content.layout   'slides' | 'rail'   absent = slides (everything above)
  //   rail-only keys, malformed values fall back to the default:
  //     slideWidth        px   360  slide width, capped at the rail's width
  //     slideWidthMobile  %    80   of the rail's width, below 750px
  //     slideGap          px   20
  //     slideGapMobile    px   12   below 750px
  //     bleed             bool false  the track runs on to the viewport's right
  //                                   edge; its last 48px fade out (mask-image,
  //                                   any background) over a 48px end padding
  //     showArrows        bool true   prev / next, one slide + gap per press
  //     showDots          ignored (no dots on a rail)
  //     autoplay / loop   ignored
  //
  // A rail is native horizontal scroll, not a transform: every slide is
  // visible and reachable, so nothing is inert. The scroller is the focusable
  // `role=region` (arrow keys scroll it natively), snaps `proximity` to slide
  // starts on coarse pointers only (a wheel/trackpad snap pulls the scroll
  // back between events, p52: `none` under pointer:fine), and draws a thin
  // scrollbar that takes the accent colour on hover. Widths are container
  // units of the scroller's content box less the bleed's 48px end padding,
  // so the mobile % means "of the track" exactly as in the builder (#3050).
  // Dots never render; arrows only while the track overflows, never on phones.
  //
  // DOM:
  //   div.neo-slider.neo-slider--rail[.neo-slider--bleed][data-neo-slider-layout=rail]
  //     div.neo-slider__rail-nav            (showArrows && 2+ slides)
  //       button.neo-slider__rail-btn--prev / --next
  //     div.neo-slider__rail-frame
  //       div.neo-slider__viewport[role=region][tabindex=0]   the scroller
  //         div.neo-slider__track
  //           div.neo-slider__slide[role=group]  x n
  // ------------------------------------------------------------------------

  var RAIL_DEFAULTS = { slideWidth: 360, slideWidthMobile: 80, slideGap: 20, slideGapMobile: 12 };

  function railNumber(value, fallback, max) {
    if (value === null || value === '' || typeof value === 'boolean') return fallback;
    var n = Number(value);
    if (!isFinite(n) || n < 0) return fallback;
    return max ? Math.min(max, n) : n;
  }

  function renderContentRail(el, c, slides) {
    var en = sliderIsEnglish();
    var count = slides.length;
    var bleed = sliderFlag(c.bleed, false);
    var showArrows = count > 1 && sliderFlag(c.showArrows, true);
    var railId = 'neo-rail-' + safeId(el && el.id);

    var rootStyles = pickStyles(el, 'desktop');
    var styles = {};
    for (var k in rootStyles) {
      if (Object.prototype.hasOwnProperty.call(rootStyles, k)) styles[k] = rootStyles[k];
    }
    styles['--neo-rail-w'] = railNumber(c.slideWidth, RAIL_DEFAULTS.slideWidth) + 'px';
    styles['--neo-rail-w-m'] = String(railNumber(c.slideWidthMobile, RAIL_DEFAULTS.slideWidthMobile, 100));
    styles['--neo-rail-gap'] = railNumber(c.slideGap, RAIL_DEFAULTS.slideGap) + 'px';
    styles['--neo-rail-gap-m'] = railNumber(c.slideGapMobile, RAIL_DEFAULTS.slideGapMobile) + 'px';

    var label = en ? 'Carousel' : 'Carrousel';
    var html = viewportStyleTags(el) +
      '<div class="neo-slider neo-slider--rail' + (bleed ? ' neo-slider--bleed' : '') + '"' +
      ' data-neo-slider data-neo-slider-layout="rail"' +
      dataElAttr(el) + styleAttrOf(styles) + '>';

    if (showArrows) {
      html += '<div class="neo-slider__rail-nav" hidden>' +
        '<button type="button" class="neo-slider__rail-btn neo-slider__rail-btn--prev" data-neo-slider-prev' +
        ' aria-controls="' + railId + '" aria-disabled="true"' +
        ' aria-label="' + (en ? 'Scroll back' : 'Défiler en arrière') + '">' + SLIDER_CHEVRON_PREV + '</button>' +
        '<button type="button" class="neo-slider__rail-btn neo-slider__rail-btn--next" data-neo-slider-next' +
        ' aria-controls="' + railId + '"' +
        ' aria-label="' + (en ? 'Scroll forward' : 'Défiler en avant') + '">' + SLIDER_CHEVRON_NEXT + '</button>' +
        '</div>';
    }

    html += '<div class="neo-slider__rail-frame">' +
      '<div class="neo-slider__viewport" id="' + railId + '" role="region" tabindex="0"' +
      ' aria-roledescription="' + (en ? 'carousel' : 'carrousel') + '"' +
      ' aria-label="' + label + '">' +
      '<div class="neo-slider__track">';
    for (var s = 0; s < count; s++) {
      html += '<div class="neo-slider__slide" role="group"' +
        ' aria-roledescription="' + (en ? 'slide' : 'diapositive') + '"' +
        ' aria-label="' + (s + 1) + ' / ' + count + '">' + slides[s] + '</div>';
    }
    return html + '</div></div></div></div>';
  }

  /** Layout right edge in document coordinates, ignoring transforms (a
   *  revealing ancestor is translated/scaled while it animates in). */
  function railLayoutRight(node) {
    var x = 0;
    var n = node;
    while (n) {
      x += n.offsetLeft || 0;
      if (n !== node) x += n.clientLeft || 0;
      n = n.offsetParent;
    }
    return x + node.offsetWidth;
  }

  function hydrateContentRail(root) {
    var frame = sliderChild(root, 'neo-slider__rail-frame');
    var viewport = sliderChild(frame, 'neo-slider__viewport');
    var track = sliderChild(viewport, 'neo-slider__track');
    if (!viewport || !track) return;
    var nav = sliderChild(root, 'neo-slider__rail-nav');
    var prev = nav && nav.querySelector('[data-neo-slider-prev]');
    var next = nav && nav.querySelector('[data-neo-slider-next]');
    var bleed = root.classList.contains('neo-slider--bleed');
    var frameQueued = false;

    function measureBleed() {
      if (!bleed) return;
      var edge = document.documentElement.clientWidth;
      var gap = Math.max(0, Math.round(edge - railLayoutRight(root)));
      root.style.setProperty('--neo-rail-bleed', gap + 'px');
    }

    function sync() {
      frameQueued = false;
      if (!root.isConnected) return;
      var max = viewport.scrollWidth - viewport.clientWidth;
      var atStart = viewport.scrollLeft <= 1;
      var atEnd = viewport.scrollLeft >= max - 1;
      if (nav) {
        nav.hidden = max <= 1;
        if (prev) prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
        if (next) next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      }
    }

    function queueSync() {
      if (frameQueued) return;
      frameQueued = true;
      (window.requestAnimationFrame || window.setTimeout)(sync);
    }

    function step(dir) {
      var first = null;
      for (var i = 0; i < track.children.length; i++) {
        if (track.children[i].classList.contains('neo-slider__slide')) { first = track.children[i]; break; }
      }
      if (!first) return;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      var by = dir * (first.offsetWidth + gap);
      if (typeof viewport.scrollBy === 'function') {
        viewport.scrollBy({ left: by, behavior: sliderPrefersReducedMotion() ? 'auto' : 'smooth' });
      } else {
        viewport.scrollLeft += by;
      }
    }

    if (prev) prev.addEventListener('click', function () { if (prev.getAttribute('aria-disabled') !== 'true') step(-1); });
    if (next) next.addEventListener('click', function () { if (next.getAttribute('aria-disabled') !== 'true') step(1); });
    viewport.addEventListener('scroll', queueSync, { passive: true });

    function refresh() {
      if (!root.isConnected) return;
      measureBleed();
      queueSync();
    }
    refresh();
    // The rail's size AND its position decide the bleed: observe the one,
    // re-measure on window resizes for the other.
    if (typeof ResizeObserver === 'function') new ResizeObserver(refresh).observe(root);
    window.addEventListener('resize', refresh);
    // Late slide images change scrollWidth without resizing the rail.
    var imgs = track.querySelectorAll('img');
    for (var m = 0; m < imgs.length; m++) {
      if (!imgs[m].complete) imgs[m].addEventListener('load', queueSync, { once: true });
    }
  }

  /** Direct children only: a slider inside a slide must not be driven by its host. */
  function sliderChild(parent, cls) {
    if (!parent) return null;
    for (var i = 0; i < parent.children.length; i++) {
      if (parent.children[i].classList.contains(cls)) return parent.children[i];
    }
    return null;
  }

  function sliderChildren(parent, cls) {
    var out = [];
    if (!parent) return out;
    for (var i = 0; i < parent.children.length; i++) {
      if (parent.children[i].classList.contains(cls)) out.push(parent.children[i]);
    }
    return out;
  }

  function sliderPrefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function hydrateContentSlider(root) {
    if (root.getAttribute('data-neo-slider-ready') === '1') return;
    root.setAttribute('data-neo-slider-ready', '1');
    if (root.getAttribute('data-neo-slider-layout') === 'rail') {
      hydrateContentRail(root);
      return;
    }

    var viewport = sliderChild(root, 'neo-slider__viewport');
    var track = sliderChild(viewport, 'neo-slider__track');
    var slides = sliderChildren(track, 'neo-slider__slide');
    var count = slides.length;
    if (count < 2) return;

    var loop = root.getAttribute('data-loop') !== '0';
    var interval = sliderInterval(root.getAttribute('data-interval'));
    var wantsAutoplay = root.getAttribute('data-autoplay') === '1' &&
      !(window.Shopify && window.Shopify.designMode);
    var prev = sliderChild(root, 'neo-slider__arrow--prev');
    var next = sliderChild(root, 'neo-slider__arrow--next');
    var dotsWrap = sliderChild(root, 'neo-slider__dots');
    var dots = sliderChildren(dotsWrap, 'neo-slider__dot');
    var pauseButton = sliderChild(root, 'neo-slider__pause');

    var index = 0;
    var timer = null;
    // `user` is the showPause toggle's and is written by nothing else.
    var paused = { hover: false, focus: false, offscreen: false, hidden: !!document.hidden, drag: false, user: false, scroll: false };
    var observer = null;

    function isPaused() {
      for (var k in paused) {
        if (Object.prototype.hasOwnProperty.call(paused, k) && paused[k]) return true;
      }
      return false;
    }

    function autoplayOn() {
      return wantsAutoplay && !sliderPrefersReducedMotion();
    }

    function setTrack(offsetPx) {
      var base = -index * 100;
      track.style.transform = offsetPx
        ? 'translateX(calc(' + base + '% + ' + Math.round(offsetPx) + 'px))'
        : 'translateX(' + base + '%)';
    }

    function paint() {
      setTrack(0);
      for (var i = 0; i < count; i++) {
        var active = i === index;
        slides[i].classList.toggle('is-active', active);
        if (active) {
          slides[i].removeAttribute('aria-hidden');
          slides[i].removeAttribute('inert');
          slides[i].inert = false;
        } else {
          slides[i].setAttribute('aria-hidden', 'true');
          slides[i].setAttribute('inert', '');
        }
      }
      for (var d = 0; d < dots.length; d++) {
        dots[d].classList.toggle('is-active', d === index);
        if (d === index) dots[d].setAttribute('aria-current', 'true');
        else dots[d].removeAttribute('aria-current');
      }
      if (!loop) {
        if (prev) prev.setAttribute('aria-disabled', index === 0 ? 'true' : 'false');
        if (next) next.setAttribute('aria-disabled', index === count - 1 ? 'true' : 'false');
      }
      // Announce manual changes only: a rotating carousel that speaks every
      // six seconds drowns out the rest of the page.
      viewport.setAttribute('aria-live', autoplayOn() && !isPaused() ? 'off' : 'polite');
    }

    function goTo(target, instant) {
      var n = target;
      if (loop) n = ((n % count) + count) % count;
      else n = Math.max(0, Math.min(count - 1, n));
      if (instant) root.classList.add('is-instant');
      index = n;
      paint();
      if (instant) {
        // Commit the jump before the transition comes back.
        void track.offsetWidth;
        root.classList.remove('is-instant');
      }
      schedule();
    }

    function stop() {
      if (timer) window.clearTimeout(timer);
      timer = null;
    }

    function teardown() {
      stop();
      if (observer) observer.disconnect();
      observer = null;
      document.removeEventListener('visibilitychange', onVisibility);
    }

    function schedule() {
      stop();
      viewport.setAttribute('aria-live', autoplayOn() && !isPaused() ? 'off' : 'polite');
      if (!autoplayOn() || isPaused()) return;
      if (!loop && index >= count - 1) return;
      timer = window.setTimeout(function () {
        timer = null;
        // A repaint replaced this slider: stop for good.
        if (!root.isConnected) { teardown(); return; }
        goTo(index + 1);
      }, interval);
    }

    function setPaused(reason, value) {
      if (paused[reason] === value) return;
      paused[reason] = value;
      schedule();
    }

    function onVisibility() {
      if (!root.isConnected) { teardown(); return; }
      setPaused('hidden', !!document.hidden);
    }

    // ---- controls ----------------------------------------------------
    if (prev) {
      prev.addEventListener('click', function () {
        if (prev.getAttribute('aria-disabled') === 'true') return;
        goTo(index - 1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        if (next.getAttribute('aria-disabled') === 'true') return;
        goTo(index + 1);
      });
    }
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var n = parseInt(dot.getAttribute('data-neo-slider-dot'), 10);
        if (!isNaN(n)) goTo(n);
      });
    });
    if (pauseButton) {
      if (wantsAutoplay) {
        pauseButton.hidden = false;
        pauseButton.addEventListener('click', function () {
          var nowPaused = !paused.user;
          pauseButton.setAttribute('aria-pressed', nowPaused ? 'true' : 'false');
          setPaused('user', nowPaused);
        });
      } else if (pauseButton.parentNode) {
        // Theme editor: nothing moves, so there is nothing to stop.
        pauseButton.parentNode.removeChild(pauseButton);
        pauseButton = null;
      }
    }

    // Driver hook for a page motion profile (§21b `scrollSlider`): the scroll
    // picks the slide and autoplay stays off while it drives. Runtime only.
    root.__neoSliderDrive = {
      count: count,
      goTo: function (n) { if (n !== index) goTo(n); },
      hold: function (on) { setPaused('scroll', !!on); }
    };

    root.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      var t = e.target;
      if (t && t.closest && t.closest('[data-neo-slider]') !== root) return;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      goTo(index + (e.key === 'ArrowRight' ? 1 : -1), sliderPrefersReducedMotion());
    });

    // ---- pauses ------------------------------------------------------
    // Hover is a pointer-with-hover thing; a touch "enter" would never leave.
    root.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'touch') setPaused('hover', true);
    });
    root.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'touch') setPaused('hover', false);
    });
    // Keyboard focus only: a mouse click on a dot also focuses the button,
    // and that must not stop the rotation for good.
    root.addEventListener('focusin', function (e) {
      var t = e.target;
      var visible = true;
      try { visible = t.matches(':focus-visible'); } catch (err) { visible = true; }
      if (visible) setPaused('focus', true);
    });
    root.addEventListener('focusout', function (e) {
      if (!e.relatedTarget || !root.contains(e.relatedTarget)) setPaused('focus', false);
    });
    document.addEventListener('visibilitychange', onVisibility);
    if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) setPaused('offscreen', !entries[i].isIntersecting);
      });
      observer.observe(root);
    }

    // ---- swipe -------------------------------------------------------
    var drag = null;
    var suppressClick = false;

    viewport.addEventListener('pointerdown', function (e) {
      if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return;
      var host = e.target && e.target.closest ? e.target.closest('[data-neo-slider]') : root;
      if (host !== root) return;
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, dx: 0, horizontal: false, width: viewport.clientWidth || 1 };
      suppressClick = false;
    });

    viewport.addEventListener('pointermove', function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.x;
      var dy = e.clientY - drag.y;
      if (!drag.horizontal) {
        if (Math.abs(dy) > SLIDER_SWIPE_INTENT_PX && Math.abs(dy) >= Math.abs(dx)) {
          drag = null; // a vertical gesture belongs to the page
          return;
        }
        if (Math.abs(dx) <= SLIDER_SWIPE_INTENT_PX || Math.abs(dx) <= Math.abs(dy)) return;
        drag.horizontal = true;
        root.classList.add('is-dragging');
        setPaused('drag', true);
        try { viewport.setPointerCapture(e.pointerId); } catch (err) { /* capture is best-effort */ }
      }
      e.preventDefault();
      var offset = dx;
      // Past either end without loop: rubber-band instead of revealing nothing.
      if (!loop && ((index === 0 && dx > 0) || (index === count - 1 && dx < 0))) offset = dx / 3;
      drag.dx = dx;
      setTrack(offset);
    });

    function endDrag(e, cancelled) {
      if (!drag || e.pointerId !== drag.id) return;
      var d = drag;
      drag = null;
      if (!d.horizontal) return;
      root.classList.remove('is-dragging');
      try { viewport.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ }
      // Touch swipes often fire no click at all; a stale flag must not eat the
      // next real activation (a keyboard Enter on a link has no pointerdown).
      suppressClick = true;
      window.setTimeout(function () { suppressClick = false; }, 400);
      var threshold = Math.min(80, d.width * 0.15);
      paused.drag = false;
      if (!cancelled && Math.abs(d.dx) > threshold) goTo(index + (d.dx < 0 ? 1 : -1));
      else goTo(index);
    }

    viewport.addEventListener('pointerup', function (e) { endDrag(e, false); });
    viewport.addEventListener('pointercancel', function (e) { endDrag(e, true); });
    // A drag that ends on a link inside a slide is not a click on it.
    viewport.addEventListener('click', function (e) {
      if (!suppressClick) return;
      suppressClick = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);
    viewport.addEventListener('dragstart', function (e) {
      // A linked photo (image content.linkUrl) is an <a>: the browser's native
      // link drag would cancel the pointer stream mid-swipe, as an image drag does.
      var t = e.target;
      if (t && (t.tagName === 'IMG' || (t.closest && t.closest('a')))) e.preventDefault();
    });

    root.classList.add('is-instant');
    paint();
    void track.offsetWidth;
    root.classList.remove('is-instant');
    schedule();
  }

  function hydrateContentSliders(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-neo-slider]');
    for (var i = 0; i < nodes.length; i++) {
      try {
        hydrateContentSlider(nodes[i]);
      } catch (err) {
        if (window.console && console.warn) {
          console.warn('content-builder-neo-ext: content-slider hydration failed', err);
        }
      }
    }
  }

  // ============================================================
  // 4. linked-form
  //
  // The element carries only {formId, formName, submitEndpoint, showTitle,
  // showDescription} — NOT the fields. aiconeo's own renderer reads them from
  // an authenticated admin endpoint, which a storefront cannot use, so the
  // field list is fetched at hydrate time from the PUBLIC, CORS-enabled read
  // endpoint (Modules/AicoForms/Routes/api.php → `aiconeo/public/aico-forms/{id}`,
  // throttle:api-read, allow-listed output).
  //
  // Submission uses the LEGACY public endpoint baked into the element as
  // `submitEndpoint`: a NATIVE multipart form POST (no fetch), because it
  // answers 302 back to `redirect_to` with ?isSuccessful=true / ?isError=true —
  // a top-level form navigation follows that redirect without needing CORS.
  // Contract: docs/aico-landing-pages.md.
  // ============================================================

  Neo.registerType('linked-form', function (el) {
    var content = (el && el.content) || {};
    var formId = content.formId || content.linkedFormId;
    if (!formId) {
      warnUnknownOnce('linked-form(no formId)');
      return '';
    }
    var endpoint = content.submitEndpoint || FALLBACK_SUBMIT_ENDPOINT;
    // element.className is added by the registry decorator (section 7), not here.
    var classes = ['content-type-neo-linked-form', 'aico-custom-form-wrap'];

    return viewportStyleTags(el) +
      '<div class="' + escapeAttr(classes.join(' ')) + '"' + dataElAttr(el) + inlineStyleAttr(el) +
      ' data-neo-form="1"' +
      ' data-neo-form-id="' + escapeAttr(formId) + '"' +
      ' data-neo-form-endpoint="' + escapeAttr(endpoint) + '"' +
      ' data-neo-form-title="' + escapeAttr(content.showTitle === true ? '1' : '') + '"' +
      '>' +
      '<div class="aico-form-banner aico-form-banner--success d-none" role="status"></div>' +
      '<div class="aico-form-banner aico-form-banner--error d-none" role="alert"></div>' +
      '<div class="aico-form-slot"></div>' +
      '</div>';
  });

  /** Derive the public form-definition URL from the authored submit endpoint,
   *  so the aico host + tenant prefix are never hardcoded here.
   *  …/<tenant>/api/v1/news/form-submissions → …/<tenant>/api/aiconeo/public/aico-forms/<id> */
  function formReadUrl(submitEndpoint, formId) {
    var base = String(submitEndpoint || FALLBACK_SUBMIT_ENDPOINT)
      .replace(/\/api\/v1\/news\/form-submissions\/?$/, '')
      .replace(/\/+$/, '');
    return base + '/api/aiconeo/public/aico-forms/' + encodeURIComponent(formId);
  }

  /** Map an aico form element type to an HTML input type / control kind. */
  function controlFor(type) {
    switch (String(type || '').toLowerCase()) {
      case 'textarea':
      case 'text_area': return { kind: 'textarea' };
      case 'dropdown': return { kind: 'select' };
      case 'radio': return { kind: 'radio' };
      case 'checkbox': return { kind: 'checkbox' };
      case 'file_upload':
      case 'multiple_file_upload': return { kind: 'input', inputType: 'file' };
      case 'email': return { kind: 'input', inputType: 'email' };
      case 'phone':
      case 'phone_number':
      case 'tel': return { kind: 'input', inputType: 'tel' };
      case 'number': return { kind: 'input', inputType: 'number' };
      case 'date': return { kind: 'input', inputType: 'date' };
      default: return { kind: 'input', inputType: 'text' };
    }
  }

  /** Browser autofill hint from the field key / label (FR, DE, EN), #141269. aico
   *  forms carry no autocomplete setting, so the common contact fields are
   *  recognised by name; anything else gets none. */
  function autocompleteFor(field) {
    var s = (String(field.key || '') + ' ' + String(field.label || '')).toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    var rules = [
      [/(pre|first|vor)\s*-?\s*nom|prenom|first.?name|vorname|given/, 'given-name'],
      [/nom de famille|last.?name|nachname|family|surname|\bnom\b|\bname\b/, 'family-name'],
      [/e-?mail|courriel/, 'email'],
      [/tel|phone|natel|mobile|portable|handy/, 'tel'],
      [/\bnpa\b|\bplz\b|postal|zip|code postal/, 'postal-code'],
      [/ville|city|\bort\b|localite|wohnort/, 'address-level2'],
      [/adresse|address|strasse|rue\b|street/, 'street-address'],
      [/pays|country|\bland\b/, 'country-name'],
      [/societe|entreprise|company|firma|organisation|organization/, 'organization']
    ];
    for (var i = 0; i < rules.length; i++) if (rules[i][0].test(s)) return rules[i][1];
    return '';
  }

  function fieldHtml(field, formId) {
    var control = controlFor(field.type);
    var auto = (control.kind === 'input' && control.inputType !== 'file') || control.kind === 'textarea' ? autocompleteFor(field) : '';
    if (auto === 'tel' && control.inputType === 'text') control = { kind: 'input', inputType: 'tel' };
    var autoAttr = auto ? ' autocomplete="' + auto + '"' : '';
    var name = escapeAttr(field.key);
    var id = 'aico-form-' + escapeAttr(formId) + '-' + escapeAttr(field.key);
    var label = escapeHtml(field.label || field.key);
    var required = field.isRequired ? ' required' : '';
    var requiredMark = field.isRequired ? ' <span class="aico_label__required" aria-hidden="true">*</span>' : '';
    var half = String(field.columnSize || '').toUpperCase() === 'HALF_ROW';
    var options = (field.config && field.config.possibleValues) || [];

    var body;
    if (control.kind === 'textarea') {
      body = '<textarea class="aico_input" id="' + id + '" name="' + name + '" rows="5"' + (auto === 'street-address' ? autoAttr : '') + required + '></textarea>';
    } else if (control.kind === 'select') {
      body = '<select class="aico_input" id="' + id + '" name="' + name + '"' + required + '>' +
        '<option value=""></option>';
      for (var i = 0; i < options.length; i++) {
        var v = escapeAttr(options[i].value);
        body += '<option value="' + v + '">' + escapeHtml(options[i].label || options[i].value) + '</option>';
      }
      body += '</select>';
    } else if (control.kind === 'radio') {
      body = '<div class="aico_options">';
      for (var r = 0; r < options.length; r++) {
        var rid = id + '-' + r;
        body += '<label class="aico_option" for="' + rid + '">' +
          '<input type="radio" id="' + rid + '" name="' + name + '" value="' + escapeAttr(options[r].value) + '"' + required + '>' +
          '<span>' + escapeHtml(options[r].label || options[r].value) + '</span></label>';
      }
      body += '</div>';
    } else if (control.kind === 'checkbox') {
      // Single opt-in checkbox: the label sits next to the box, not above it.
      return '<div class="form-item form-item--checkbox' + (half ? ' form-item--half' : '') + '">' +
        '<label class="aico_label aico_label--inline" for="' + id + '">' +
        '<input type="checkbox" id="' + id + '" name="' + name + '" value="1"' + required + '>' +
        '<span>' + label + requiredMark + '</span></label>' +
        '</div>';
    } else {
      body = '<input class="aico_input" type="' + control.inputType + '" id="' + id + '" name="' + name + '"' + autoAttr + required + '>';
    }

    return '<div class="form-item' + (half ? ' form-item--half' : '') + '">' +
      '<label class="aico_label" for="' + id + '">' + label + requiredMark + '</label>' +
      body +
      '</div>';
  }

  /** Current URL without the isSuccessful/isError params, so a resubmit after a
   *  redirect doesn't stack query flags. */
  function redirectTarget() {
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('isSuccessful');
      url.searchParams.delete('isError');
      return url.toString();
    } catch (e) {
      return window.location.href;
    }
  }

  // i18n via window.LS_I18N (snippets/ls-i18n.liquid), FR fallback inline
  function lsT(key, fallback) { return (window.LS_I18N && window.LS_I18N[key]) || fallback; }

  function buildForm(host, form) {
    var formId = host.getAttribute('data-neo-form-id');
    var endpoint = host.getAttribute('data-neo-form-endpoint');
    var elements = (form && Array.isArray(form.elements)) ? form.elements : [];

    var fields = elements
      .filter(function (f) { return f && !f.isHidden && f.type !== 'title' && f.type !== 'html' && f.key; })
      .sort(function (a, b) { return (a.position || 0) - (b.position || 0); });

    if (fields.length === 0) {
      warnUnknownOnce('linked-form(' + formId + ') has no visible fields');
      return;
    }

    // No `novalidate` — native browser validation is the only client-side check.
    var html = '<form class="aico-custom-form" method="post" enctype="multipart/form-data" action="' +
      escapeAttr(endpoint) + '">' +
      '<input type="hidden" name="form_id" value="' + escapeAttr(formId) + '">' +
      '<input type="hidden" name="redirect_to" value="' + escapeAttr(redirectTarget()) + '">';

    if (host.getAttribute('data-neo-form-title') === '1' && form.name) {
      html += '<h3 class="aico-custom-form__title">' + escapeHtml(form.name) + '</h3>';
    }

    html += '<div class="aico-custom-form__fields">';
    for (var i = 0; i < fields.length; i++) html += fieldHtml(fields[i], formId);
    html += '</div>';

    html += '<button type="submit" class="aico-custom-form__submit">' +
      escapeHtml(form.submissionText || lsT('common.send', 'Envoyer')) + '</button>';
    html += '</form>';

    var slot = host.querySelector('.aico-form-slot');
    if (slot) slot.innerHTML = html;
  }

  /**
   * The legacy submit answers 302 back to `redirect_to` with ?isSuccessful=true
   * or ?isError=true, so the outcome is read off the query string on load.
   * Copy comes from window.LS_I18N (snippets/ls-i18n.liquid) with the French
   * literal as fallback.
   */
  function showBanners(host) {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    var success = host.querySelector('.aico-form-banner--success');
    var error = host.querySelector('.aico-form-banner--error');

    if (params.get('isSuccessful') === 'true' && success) {
      success.textContent = lsT('js.form_success', 'Merci ! Votre demande a bien été envoyée.');
      success.classList.remove('d-none');
    } else if (params.get('isError') === 'true' && error) {
      error.textContent = lsT('js.form_error', "Une erreur s'est produite. Merci de réessayer.");
      error.classList.remove('d-none');
    }
  }

  function hydrateForms(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var hosts = scope.querySelectorAll('[data-neo-form="1"]');
    for (var i = 0; i < hosts.length; i++) {
      (function (host) {
        if (host.getAttribute('data-neo-form-hydrated') === '1') return;
        host.setAttribute('data-neo-form-hydrated', '1');

        var formId = host.getAttribute('data-neo-form-id');
        var url = formReadUrl(host.getAttribute('data-neo-form-endpoint'), formId);

        fetch(url, { credentials: 'omit', headers: { Accept: 'application/json' } })
          .then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.json();
          })
          .then(function (payload) {
            var form = (payload && payload.data) ? payload.data : payload;
            buildForm(host, form);
            showBanners(host);
          })
          .catch(function (error) {
            if (window.console && console.warn) {
              console.warn('content-builder-neo-ext: could not load aico form ' + formId, error);
            }
            host.setAttribute('data-neo-form-hydrated', '0');
          });
      })(hosts[i]);
    }
  }

  // ============================================================
  // 2. Unknown-with-children degradation
  //
  // renderUnknown is internal to the engine, so instead of patching it the
  // document is normalised on the way into render(): an unregistered type that
  // carries children becomes a `container`, keeping its styles and subtree.
  // ============================================================

  /**
   * Tally of element types the registry could not render, for the aggregated
   * warning at the end of each render. Reset per Neo.render call.
   */
  var unrenderedTally = null;

  function tallyUnrendered(type) {
    if (!unrenderedTally) unrenderedTally = {};
    unrenderedTally[type] = (unrenderedTally[type] || 0) + 1;
  }

  /**
   * Placeholder for an unrenderable LEAF. aiconeo defines 58 element types and
   * this renderer implements a subset, so the rest would otherwise vanish with
   * nothing in the DOM to explain the hole. Emitting a comment makes "why is
   * this block missing" a view-source away instead of a bisect.
   */
  Neo.registerType('neo-unrendered', function (el) {
    return '<!-- neo: unrendered type "' + String(el.__neoOriginalType || 'unknown').replace(/--+/g, '-') +
      '" id="' + safeId(el.id) + '" -->';
  });

  function normalizeUnknown(node) {
    if (!node || typeof node !== 'object') return node;

    var children = Array.isArray(node.children) ? node.children
      : Array.isArray(node.elements) ? node.elements
        : null;
    if (children) {
      for (var i = 0; i < children.length; i++) normalizeUnknown(children[i]);
    }

    var type = node.type;
    if (typeof type !== 'string' ||
      type === 'columns' ||
      typeof Neo._renderers[type] === 'function') {
      return node;
    }

    tallyUnrendered(type);

    if (children && children.length > 0) {
      // Wrapper-ish: degrade to a container so the subtree survives.
      node.type = 'container';
      node.children = children;
    } else {
      // Leaf: nothing to fall back to — leave a marker in place of the element.
      node.__neoOriginalType = type;
      node.type = 'neo-unrendered';
    }
    return node;
  }

  // ============================================================
  // 39. `hidden` elements
  //
  // aiconeo ElementRenderer: `if (element.hidden && !isEditing) return null`,
  // i.e. a hidden element renders nothing, children included. The flag reaches
  // the shop untouched (neither the snapshot nor LandingPageMetafieldConverter
  // prunes it), so without this pass an element the author switched off still
  // painted. Pruning the tree up front covers every path at once: the engine
  // pass, the §35 freeform pass and the ext renderers that recurse on their
  // own. In place, like normalizeUnknown — a repaint of the same data is a
  // no-op. Removing a theme-slot marker this way is how an author switches a
  // theme section off (see §15d).
  // ============================================================

  function pruneHidden(node) {
    if (!node || typeof node !== 'object') return;
    var keys = ['children', 'elements'];
    for (var k = 0; k < keys.length; k++) {
      var list = node[keys[k]];
      if (!Array.isArray(list)) continue;
      for (var i = list.length - 1; i >= 0; i--) {
        if (list[i] && list[i].hidden === true) list.splice(i, 1);
        else pruneHidden(list[i]);
      }
    }
  }

  /** One warning per render listing every type that could not be rendered. */
  function reportUnrendered() {
    if (!unrenderedTally) return;
    var types = Object.keys(unrenderedTally);
    if (types.length === 0) return;
    if (window.console && console.warn) {
      var summary = types.sort().map(function (t) {
        return t + ' ×' + unrenderedTally[t];
      }).join(', ');
      console.warn('content-builder-neo-ext: ' + types.length +
        ' unrendered Neo element type(s) on this page: ' + summary +
        '. Search the DOM for "neo: unrendered type" to locate them.');
    }
    unrenderedTally = null;
  }

  // ============================================================
  // 5. Section pass — styles, layout mode, page settings, scheduling
  //
  // The engine has NO section wrapper: renderSection() emits bare `.content-row`
  // divs and render() concatenates them, so `section.styles` (backgrounds,
  // padding) and `section.layout` were dropped entirely — which is why two of
  // the three Club des 130 sections looked blank (cream text on cream, the
  // authored navy never applied).
  //
  // Rather than parse the engine's output, render() is re-run PER SECTION and
  // each result is wrapped here. The engine stays the authority on element
  // markup; only the missing outer shell is added.
  //
  // Structure mirrors aiconeo's PublicPageRenderer.renderRegularSection:
  //   <div section styles>            ← background + padding, full width
  //     <div max-width: outerMaxWidth; margin: 0 auto>
  //       <div max-width: contentMaxWidth; margin: 0 auto; padding-top: …>
  //
  // Layout modes follow utils/sectionLayout.ts::resolveSectionLayout exactly:
  //   full      → outer 100%, optional innerContainer constrains the content
  //   custom    → outer = layout.maxWidth || page maxWidth || 1200px
  //   contained → outer = page maxWidth || DEFAULT_PAGE_MAX_WIDTH
  //               (layout.maxWidth is NOT read in this mode upstream, so it is
  //               not read here either — the builder preview is the contract)
  // The mobile clamps upstream (`min(100%, X)`) only exist because the builder
  // simulates viewports inside a fixed-width canvas; in a real browser a
  // max-width can never exceed the viewport, so they are no-ops here.
  // ============================================================

  // The page width every `contained` section falls back to.
  //
  // 1350px, not aiconeo's generic 1200px, because that is this club's content
  // width: the authors build every section from the standard LS block, which
  // wraps its content in `max-width:1430px; padding:0 40px` — 1430 - 2x40 =
  // 1350px of content. A `contained` section left on the default has to resolve
  // to the same 1350 or it does not share a left edge with the blocks around
  // it.
  //
  // Measured on landing page 2 (Club des 130): left edge of the hero heading vs
  // left edge of the frame in the section below it, both authored from that
  // block.
  //
  //   screen   1200px (was)   1500px   1350px (this)
  //   1200         0px          0px         0px
  //   1400         0px          0px         0px
  //   1500       -75px        -35px         0px
  //   1600       -75px        -75px         0px
  //   1713       -75px        -75px         0px
  //   1920       -75px        -75px         0px
  //   2200       -75px        -75px         0px
  //
  // 1350 is the only value that holds at every width: past 1350 plus gutters
  // only the side margin grows, so the frames never shift against each other.
  // 1200 and 1500 both split by 75px, in opposite directions — 1200 was simply
  // never this club's width (`layout/theme.liquid` fixes `--page-width: 143rem`,
  // so header, footer and every theme section sit on 1430).
  //
  // Why nobody caught it in the builder: its canvas is 1200px wide, and at 1200
  // both containers are clamped to the viewport and land on the same edge. The
  // defect only exists from ~1440 up, which is the width the visitors have and
  // the authors do not.
  //
  // A document that sets `settings.maxWidth` still wins over this: landing
  // pages 14 (LS is more) and 69 are authored at 1200px and keep their width.
  var DEFAULT_PAGE_MAX_WIDTH = '1350px';

  // Page-level settings of the document currently being rendered, read by
  // wrapSection for the default max-width. Set at the top of Neo.render.
  var currentPageSettings = null;

  // Mirrors buildSectionCss's skipKeys: these are not section box styles.
  var SECTION_STYLE_SKIP = {
    borderRadiusEnabled: true,
    borderRadiusScope: true,
    cardBorderRadius: true,
    contentPaddingTop: true
  };

  function sectionBoxStyles(section, breakpoint) {
    var raw = pickStyles(section, breakpoint);
    var out = {};
    for (var k in raw) {
      if (!Object.prototype.hasOwnProperty.call(raw, k)) continue;
      if (SECTION_STYLE_SKIP[k]) continue;
      out[k] = raw[k];
    }
    return out;
  }

  function resolveSectionLayout(section, pageSettings) {
    var pageMaxWidth = (pageSettings && pageSettings.maxWidth) || DEFAULT_PAGE_MAX_WIDTH;
    var layout = (section && section.layout) || {};
    var mode = layout.mode || (section && section.fullWidth ? 'full' : 'contained');

    if (mode === 'full') {
      var inner = layout.innerContainer;
      return {
        outerMaxWidth: '100%',
        innerMaxWidth: (inner && inner.enabled) ? (inner.maxWidth || pageMaxWidth) : null
      };
    }
    if (mode === 'custom') {
      return { outerMaxWidth: layout.maxWidth || pageMaxWidth, innerMaxWidth: null };
    }
    return { outerMaxWidth: pageMaxWidth, innerMaxWidth: null };
  }

  /** Mirrors utils/timeVisibilityCheck.ts::isSectionTimeHidden. */
  function isSectionTimeHidden(section, now) {
    if (!section) return false;
    var from = section.visibleFrom ? Date.parse(section.visibleFrom) : NaN;
    var until = section.visibleUntil ? Date.parse(section.visibleUntil) : NaN;
    if (!isNaN(from) && now < from) return true;
    if (!isNaN(until) && now > until) return true;
    return false;
  }

  // ============================================================
  // 35. `section.layoutMode === 'freeform'` — an element placed anywhere
  //
  // The author drops a heading over a photo in the builder and it has to be in
  // that exact spot on the shop. Three things stood between the builder and
  // this renderer, and each one is answered below.
  //
  // (a) NOTHING IN THE CHAIN IS POSITIONED. wrapSection emits three nested
  //     divs that carry max-width and margin and no `position` at all, and
  //     assets/content-builder-neo.css sets none either on `.aico-neo-section`,
  //     `.aico-neo-section__content`, `.content-row`, `.content-column` or
  //     `.content` (checked; the image renderer's two `position:relative` rules
  //     are scoped to `.content-type-neo-image--scrim/--overlay`). An
  //     absolutely positioned element would therefore have escaped all the way
  //     out to the page chrome and landed over the navbar. So the content box
  //     is made `position: relative` — but ONLY for a freeform section, which
  //     is why the declaration is appended to `contentStyle` inside the
  //     `isFreeformSection` branch and not put in the stylesheet. Every
  //     existing page emits the same bytes it emitted before.
  //
  //     Nothing has to be done about the row/column/content divs in between:
  //     an absolutely positioned box resolves against its nearest POSITIONED
  //     ancestor and skips static ones, so the engine's own wrappers are
  //     transparent to this and keep their markup untouched.
  //
  // (b) THE COORDINATE IS A PERCENTAGE, NEVER A PIXEL. This renderer resolves a
  //     contained section to 1350px (see DEFAULT_PAGE_MAX_WIDTH above for the
  //     measurements) while the builder's own canvas resolves it to 1200px. A
  //     stored pixel offset would have been wrong by 12.5% on every freeform
  //     element on the shop, in a way the author could never see, because the
  //     builder canvas is the narrower of the two. A percentage of the content
  //     box is the one unit that means the same place in both. The builder
  //     converts on write and stores only percentages
  //     (src/components/pagebuilder/utils/freeformPosition.ts); this file
  //     mirrors that file function for function, and when a rule changes on one
  //     side it has to change on the other.
  //
  //     The vertical axis needs a height to be a percentage OF: a box whose
  //     children are all out of flow has no content left to get one from, so
  //     `section.minHeight` is applied as an explicit canvas height. The
  //     default when the author set none is 480px on BOTH sides, for the same
  //     reason the unit is a percentage.
  //
  // (c) BELOW 768px THERE IS NO COORDINATE AT ALL. Every rule here is emitted
  //     inside `min-width: 768px`, which is aiconeo's boundary rather than this
  //     theme's own 749px tier, so that both renderers answer "does a
  //     coordinate apply here" the same way. A coordinate authored against a
  //     1350px canvas is not a smaller version of itself on a 390px screen, and
  //     there is no second coordinate to fall back to: the builder emits no
  //     media queries, and the one mobile tier it does publish
  //     (SHOPIFY_THEME_MOBILE_STYLE_TYPES) does not even carry heading, text or
  //     html. So a phone gets the children stacked in document order, exactly
  //     as a flow section, and the canvas height is not applied either — the
  //     section is as tall as what is in it. `position: relative` on the
  //     content box is the only thing that stays at every width, and it draws
  //     nothing on its own.
  //
  // (d) THE COORDINATE GOES ON A WRAPPER THIS FILE OWNS, NEVER ON THE ELEMENT'S
  //     OWN STYLED NODE. `[data-neo-el]` marks the STYLED node, and for several
  //     types that is an inner one: the image renderer puts it on the <img>,
  //     the button on the <a>, the spacer on the inner div. Positioning that
  //     node moved it inside its own wrapper instead of inside the section (an
  //     image authored at x:20 w:25 drew at 25% and 84px wide, and an image
  //     with a coordinate and no width collapsed to 0x0 and vanished). It is
  //     also the node that carries the element's inline `width`, which 133 of
  //     the 184 top-level elements in this shop's corpus set to a percentage:
  //     positioning it turned `width:100%` into 100% of the section starting at
  //     x%, and a single text element at x:50 pushed document.scrollWidth to
  //     2070 in a 1440 viewport. So each child is wrapped in a bare
  //     `.aico-neo-free` div and THAT is what moves, exactly as aiconeo wraps
  //     each child of a freeform section in a plain positioned div. With no
  //     authored width the wrapper is shrink-to-fit in both renderers; with one
  //     it is that percentage of the section in both.
  //
  //     This is why a freeform section does NOT go through the engine's
  //     row/column machinery at all. Wrapping the engine's output per element
  //     would have meant balancing tags in a string, and a section-level
  //     `columns` element never reaches the registry in the first place (the
  //     known gap in section 9), so an unstyled columns row could not be
  //     stamped at all and stayed at 0%. renderFreeformSection dispatches each
  //     child through renderChild, which is the same live registry the engine
  //     dispatches through and which handles `columns` itself. Document order
  //     decides nothing in this mode but paint order, so the rows the engine
  //     would have built carry no information here.
  //
  // (e) THE CANVAS IS A BOX INSIDE THE PADDING, NOT THE PADDED BOX ITSELF.
  //     `.aico-neo-section__content` carries `padding-top: contentPaddingTop`
  //     (40px unless the author changed it) and `box-sizing: border-box`, so
  //     resolving percentages against it put every element 40px higher than the
  //     builder and made the section 40px shorter. aiconeo resolves against an
  //     inner `relative` div that sits INSIDE that padding
  //     (PublicPageRenderer.tsx:160), so this emits the same inner div.
  //
  // (f) THE SECTION CLIPS. Both builder renderers give the section root
  //     `overflow-hidden`, so an element dragged past the edge is cut off
  //     there; here `.aico-neo-section` had no overflow unless a border radius
  //     set one, and an element at x:100 y:100 drew 600px down over the NEXT
  //     section. The freeform branch sets it.
  //
  // (g) THE CANVAS ISOLATES. This shop's sticky header is `z-index: 3`
  //     (assets/base.css:2395) and nothing between #MainContent and a Neo
  //     section makes a stacking context, so an authored layer of 999 painted a
  //     section child over the header and the cart drawer. The author cannot
  //     see that: the builder never draws the shop's header. `isolation:
  //     isolate` on the canvas makes the layers a private scale that cannot
  //     reach out of the section, whatever number the document carries.
  //
  // WHY THE ELEMENT DECLARATIONS CARRY `!important` AND THE HEIGHT DOES NOT:
  // the wrapper is ours and carries no inline style, but these pages ship
  // roughly 1'475 hand-written `!important` declarations pasted into `html`
  // elements (see section 34), so a plain rule can lose to page CSS that
  // happens to match. Nothing anywhere sets a height on the canvas, so that one
  // rule needs no escalation.
  //
  // (h) EVERY CONTAINER INSIDE THE SECTION IS A CANVAS TOO, AT ANY DEPTH.
  //     Free placement stopped at the section's own children: a child of a
  //     container, of a column of a `columns` row, or of a grid cell carried a
  //     coordinate that nothing read, so it drew in flow while the builder drew
  //     it where the author put it. The rule is now the same at every depth: a
  //     box that takes a positioned child becomes that child's containing
  //     block, and the child's percentages are percentages of THAT box.
  //
  //     Three things make it one mechanism rather than four:
  //       * the wrapper is the same `.aico-neo-free` div, built by the same
  //         freeformWrap, so the aiming reset in content-builder-neo.css and
  //         the arithmetic below cover a nested child with no new bytes;
  //       * the canvas is stamped `data-neo-free-canvas` at the moment its
  //         children are rendered, and ONE rule per section turns every stamp
  //         into `position: relative`. Marked only where a positioned child was
  //         actually taken, so a container that holds nothing free keeps the
  //         exact markup it has today;
  //       * the mark rides on the box the author sees, which is the column and
  //         not the row, the grid CELL and not the grid. Those two have no
  //         `data-neo-el` of their own to hang a rule on, which is why the mark
  //         is an attribute on the tag rather than a selector built from an id.
  //
  //     `position: relative` changes no box's size and gives a positioned child
  //     nothing of the container's own alignment: an absolutely positioned box
  //     is not a flex item and not a grid item, so `align-items`,
  //     `justify-content` and `place-items` reach it only through the static
  //     position, and the static position is never used because left AND top
  //     are always both declared. A child with no coordinate is not wrapped at
  //     all below section level: it flows exactly as it did, which also keeps
  //     a flex or grid container's own children out of a wrapper that would
  //     have become the flex item in their place.
  //
  //     A NESTED CANVAS CARRIES ITS OWN HEIGHT, AND THE AUTHOR WRITES IT.
  //     `element.minHeight` is to a container, a columns row or a grid what
  //     `section.minHeight` is to the section: a box whose children have all
  //     left the flow has no content to take a height from, so a percentage `y`
  //     inside it would resolve against 0 and every child would sit on its top
  //     edge. Same field name, same meaning, same validation on both sides, and
  //     absent means the box keeps exactly the height it has today. It is a
  //     number both renderers read from the document, never one either of them
  //     invents, which is the whole arrangement this section rests on.
  //
  //     Every canvas gets the same declarations, so one box behaves like the
  //     next: `position: relative` so it is the containing block, `isolation:
  //     isolate` so its layers are a private scale (g) and a high layer cannot
  //     climb out of the box the author put it in, `overflow-x: clip` so what
  //     hangs past the left or right edge is cut there and no width can ever
  //     reach the page's own scrollbar, and `overflow-y: visible` because
  //     downward is the one direction that grows instead of cutting (j).
  //
  // (i) A COORDINATE IS DRAWN AS WRITTEN. The percentages used to be clamped
  //     into 0..100 (0..200 for width) on both sides. That made a whole class
  //     of design unreachable: a band bleeding off the left edge, a badge
  //     hanging over a corner, a photo wider than its column. There is nothing
  //     to protect by clamping, because the section already clips (f), so an element
  //     placed past the edge is cut at the edge and can never reach the page
  //     around it. The only guard left is the one that matters in a stylesheet:
  //     a value that is not a finite number becomes 0 rather than text.
  //     zIndex is the exception and stays bounded, for the reason in (g): it is
  //     a layer, not a place, and the shop's header is on the same scale.
  //
  // (j) THE CANVAS IS NEVER RESIZED AT RENDER TIME, IN ANY DIRECTION.
  //     This file grew the section by the pixels its lowest positioned box hung
  //     below the canvas, so that nothing was cut off. That was wrong, and it is
  //     worth writing down why, because it looks like a kindness.
  //
  //     The canvas height is the UNIT the vertical axis is expressed in. `y` is
  //     a percentage of it, so changing it moves every element on that canvas,
  //     not only the one that hung over the edge. Growing it is safe in exactly
  //     one situation: where every `y` on that canvas can be restated in the new
  //     unit at the same moment. That is authoring time, and the builder's drop
  //     already does it. A renderer cannot: it has no way to write the restated
  //     coordinates back, so it would silently move content the author placed,
  //     which is the failure this whole section exists to prevent.
  //
  //     So a coordinate past the bottom hangs or is clipped, exactly as it is on
  //     the builder's published page. Horizontally the canvas clips, which is a
  //     different thing and stays: an element past the left or right edge is cut
  //     at the edge, and a section that grew sideways would be a page-wide
  //     scrollbar on a phone.
  // ============================================================

  /** Canvas height of a freeform section whose author set none. Must match the builder's. */
  var FREEFORM_DEFAULT_CANVAS_HEIGHT = '480px';

  /**
   * A length we are willing to interpolate into a <style> block, where there is
   * no attribute escaping to fall back on. Same reasoning as safeCssColor and
   * safeId: a value carrying `}` or `<` would close the rule or the tag.
   */
  var FREEFORM_LENGTH_RE = /^\d+(?:\.\d+)?(?:px|rem|em|vh|%)$/;

  /** The lowest width at which a coordinate applies. See (c). */
  var FREEFORM_MIN_WIDTH = 768;

  /** How far past a canvas edge a coordinate may reach. The builder's number. */
  var FREEFORM_COORD_LIMIT = 500;

  /** The attribute that turns a box into a containing block for its own children. See (h). */
  var FREEFORM_CANVAS_ATTR = 'data-neo-free-canvas';

  /**
   * Does this section place its children by coordinate?
   *
   * Two fields, and the nearer one wins:
   *
   *   1. `section.layoutMode` is the section's own button. Set, it decides,
   *      whatever the page says. That is what a button on a section means to
   *      the person pressing it.
   *   2. Otherwise `settings.placement`, the page-wide choice.
   *
   * There is no third rule and no opt-out field: one name for one idea, so no
   * document can mean two things at once. A page-wide switch in the builder
   * clears every section's `layoutMode` before it writes `settings.placement`,
   * so choosing Free for the page always reaches every section rather than
   * leaving one stranded on an older answer.
   *
   * This is the single place either field is read; every caller asks this
   * question and no other.
   */
  function isFreeformSection(section) {
    if (!section) return false;
    if (section.layoutMode === 'freeform') return true;
    if (section.layoutMode === 'flow') return false;
    var placement = currentPageSettings && currentPageSettings.placement;
    return placement === 'freeform';
  }

  /**
   * Was this document written under the page-level shape?
   *
   * `settings.placement` is the field the new builder always writes, and a
   * document that carries it was saved by a builder that knows about nested
   * canvases, the authored size and the growing canvas. A document that does
   * not carry it was saved before any of that existed, and is the gate for
   * every behaviour added since (§35 k).
   */
  function pagePlacementDeclared() {
    var placement = currentPageSettings && currentPageSettings.placement;
    return typeof placement === 'string' && !!placement;
  }

  /**
   * The section currently being drawn in free mode, or null.
   *
   * Everything a freeform section needs to know about itself is discovered
   * while its children render (which child took a coordinate, which box
   * became a canvas), and all of it is needed by wrapSection, which runs
   * afterwards (`wrapSection(section, renderFreeformSection(section))`
   * evaluates its argument first). Null everywhere else, which is what keeps
   * every flow page byte for byte what it was.
   */
  var freeformCtx = null;

  /**
   * Set by renderChildren when it wrapped at least one child, read by the very
   * next freeformCanvasAttr(). It lives for the length of one return.
   */
  var freeformCanvasPending = false;

  /**
   * Stamp this box as a canvas and number it, so the section can give it a
   * height of its own.
   *
   * `owner` is the ELEMENT the box belongs to: the container itself, or the
   * `columns` / `grid` element whose column or cell this is. Its `minHeight`
   * is the canvas height, exactly as `PageSection.minHeight` is the section
   * canvas height, with the same validation, because a box whose children have
   * all left the flow has no height to give a percentage `y` any meaning.
   * Absent means the box keeps whatever height it has.
   */
  function freeformCanvasMark(owner) {
    if (!freeformCtx) return '';
    var index = freeformCtx.canvases++;
    var height = freeformHostHeight(owner);
    if (height) {
      freeformCtx.canvasHeights.push({
        selector: '[' + FREEFORM_CANVAS_ATTR + '="' + index + '"]',
        height: height
      });
    }
    return ' ' + FREEFORM_CANVAS_ATTR + '="' + index + '"';
  }

  /** `element.minHeight` when it is a length we are willing to emit, else ''. */
  function freeformHostHeight(owner) {
    var authored = owner && owner.minHeight;
    if (typeof authored !== 'string') return '';
    var trimmed = authored.replace(/^\s+|\s+$/g, '');
    return FREEFORM_LENGTH_RE.test(trimmed) ? trimmed : '';
  }

  /**
   * The host height of a box whose CANVASES ARE ITS TRACKS, not itself: a grid.
   *
   * A grid cell is one track of the grid, and a coordinate inside it is a
   * percentage of that track, which is the box the author can see and drops
   * into. Giving each cell the grid's own height instead made a child of a
   * two-row grid sit at 68px where the builder puts it at 32.8px, made the grid
   * 512px tall against the builder's 340, and, worse, made the same document
   * mean different things at different row counts: every cell would have been
   * as tall as the whole grid. So the height goes on the grid root and the
   * tracks divide it, exactly as the builder's does.
   */
  function freeformTrackHostHeight(el) {
    if (!freeformCtx) return;
    var height = freeformHostHeight(el);
    var elId = safeId(el && el.id);
    if (!height || !elId) return;
    freeformCtx.canvasHeights.push({ selector: '[data-neo-el="' + elId + '"]', height: height });
  }

  /**
   * The canvas stamp for a box whose children were rendered by renderChildren,
   * consumed exactly once. Always called, so a pending mark can never survive
   * into a later tag; returns '' outside a freeform section, where nothing is
   * ever pending.
   */
  function freeformCanvasAttr(owner) {
    if (!freeformCanvasPending) return '';
    freeformCanvasPending = false;
    return freeformCanvasMark(owner);
  }

  /** The coordinate on an element, or null when it has none. Mirrors the builder. */
  function freeformPositionOf(el) {
    var pos = el && el.position;
    // An element in a freeform section that carries no coordinate keeps its
    // place in the stack rather than jumping to the top-left corner, which is
    // what the author sees the moment the switch is flipped.
    if (!pos || pos.type !== 'absolute') return null;
    if (pos.x === undefined && pos.y === undefined) return null;
    return pos;
  }

  /**
   * One child in a wrapper of its own. See (d) for why the coordinate cannot go
   * on the element's own styled node.
   *
   * Leading <style> blocks (styles.mobile, the interaction rules) stay OUTSIDE
   * the wrapper, same reasoning as decorateElementHtml: a <style> paints
   * nothing but it would be the wrapper's first child, and the authored
   * `div:has(> .x)` selectors depend on the element being it.
   */
  function freeformWrap(html, elId) {
    var lead = /^\s*(?:<style>[\s\S]*?<\/style>\s*)*/.exec(html);
    var prefix = lead ? lead[0] : '';
    var body = html.slice(prefix.length);
    return prefix + '<div class="aico-neo-free"' +
      (elId ? ' data-neo-free="' + elId + '"' : '') + '>' + body + '</div>';
  }

  /**
   * Render one child of a CONTAINER (§35 h), wrapping it only if it carries a
   * coordinate. `placed` tells the caller whether its own box has to become a
   * canvas.
   *
   * Outside a freeform section this is renderChild and nothing else, which is
   * why every container in the file can call it unconditionally.
   */
  function renderFreeChild(el) {
    var html = renderChild(el);
    // §35 (k): below section level a coordinate is only read on a document
    // written under the page-level shape. On an older one it is a field no
    // renderer has ever read, and reading it now would move something the
    // author of that page never placed.
    if (!freeformCtx || !freeformCtx.newShape || !html) return { html: html, placed: false };
    var pos = freeformPositionOf(el);
    if (!pos) return { html: html, placed: false };
    // No id, no selector to place it with: leave it in flow rather than move
    // the wrong box. Same condition the section-level rules have always had.
    var elId = safeId(el && el.id);
    if (!elId) return { html: html, placed: false };
    freeformCtx.rules.push(freeformElementRule(elId, pos));
    return { html: freeformWrap(html, elId), placed: true };
  }

  /**
   * The children of a freeform SECTION, each in a wrapper of its own.
   *
   * Replaces the engine's row/column pass for this one mode. See (d) above for
   * why the wrapper cannot be the element's own node and why the rows are not
   * built: document order decides nothing here but paint order.
   *
   * Unlike a nested container (§35 h) every child is wrapped here, coordinate
   * or not: the canvas is `position: relative` in any case, so the wrapper
   * changes nothing for a child that flows, and this is the markup the
   * section's children have shipped with since the mode existed.
   */
  function renderFreeformSection(section) {
    var elements = (section && section.elements) || [];
    var html = '';

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (!el) continue;
      var rendered = renderChild(el);
      if (!rendered) continue;

      var elId = safeId(el.id);
      var pos = freeformPositionOf(el);
      if (pos && elId && freeformCtx && freeformCtx.newShape) {
        freeformCtx.rules.push(freeformElementRule(elId, pos));
      }
      html += freeformWrap(rendered, elId);
    }

    return html;
  }

  function freeformCanvasHeight(section) {
    var authored = section && section.minHeight;
    if (typeof authored === 'string') {
      var trimmed = authored.replace(/^\s+|\s+$/g, '');
      if (FREEFORM_LENGTH_RE.test(trimmed)) return trimmed;
    }
    return FREEFORM_DEFAULT_CANVAS_HEIGHT;
  }

  /**
   * Mirrors roundPercent in the builder's freeformPosition.ts.
   *
   * Rounded to two decimals, which is ~0.3px of travel on a 1350px canvas,
   * below what anyone can aim at, and it keeps the number short in a rule that
   * ships on every request. NOT bounded: see (i). A value that is not a finite
   * number is the one case that becomes 0, because the alternative is text in a
   * stylesheet.
   */
  function freeformPercent(value) {
    var num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num) || !isFinite(num)) return 0;
    return Math.round(num * 100) / 100;
  }

  /**
   * The limit the builder's own writer applies to x and y, mirrored here.
   *
   * Not a limit on where the author may put something: +-500% of a canvas is
   * five canvases away in any direction, further than anyone drags. It is the
   * one number the two renderers must agree on past the edge. The builder
   * clamps on write AND on render, so a document carrying x:-600 draws at -500
   * there; drawing it at -600 here would put a band on the shop that the author
   * cannot see in the builder, which is the exact disagreement this section
   * exists to prevent. Width and height are not clamped here because the
   * builder's writer already bounds them before they are stored.
   */
  function freeformCoord(value) {
    var num = freeformPercent(value);
    if (num < -FREEFORM_COORD_LIMIT) return -FREEFORM_COORD_LIMIT;
    if (num > FREEFORM_COORD_LIMIT) return FREEFORM_COORD_LIMIT;
    return num;
  }

  /** Mirrors clampPercent in the builder as it was BEFORE free placement moved
   * to the page: 0..max, and the only arithmetic a legacy document ever meets. */
  function freeformLegacyPercent(value, max) {
    var num = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(num) || !isFinite(num)) return 0;
    if (num < 0) num = 0;
    if (num > max) num = max;
    return Math.round(num * 100) / 100;
  }

  /**
   * Mirrors clampLayer in the builder. Bounded on both sides because this
   * number goes into a stylesheet rule inside the page's own stacking context:
   * a six-digit layer in a stored document would paint a section child over the
   * shop's fixed navbar and its cart drawer.
   */
  function freeformLayer(value) {
    var num = typeof value === 'number' ? value : parseInt(value, 10);
    if (isNaN(num) || !isFinite(num)) return 0;
    if (num < -999) num = -999;
    if (num > 999) num = 999;
    return Math.round(num);
  }

  /**
   * The declarations that place one child inside its canvas.
   *
   * z-index is declared even when the document carries none, which is the one
   * line that makes the containment in (g) true at depth: `position: absolute`
   * with `z-index: auto` is NOT a stacking context, so without it a layer
   * authored deep inside a container would compete with that container's own
   * siblings and paint over them. With it, every positioned box is a scale of
   * its own and a child can be on top of everything INSIDE its container and
   * nothing outside it, which is what the author is shown. 0 is the same paint
   * order `auto` gave a flat section (both paint in tree order), so the section
   * that has no nesting stacks exactly as it did.
   */
  function freeformElementRule(elId, pos) {
    var declarations = 'position:absolute !important' +
      ';left:' + freeformCoord(pos.x) + '% !important' +
      ';top:' + freeformCoord(pos.y) + '% !important';
    if (pos.width !== undefined) {
      declarations += ';width:' + freeformPercent(pos.width) + '% !important';
    }
    // The authored SIZE, both axes. An author who drags a handle sizes a box,
    // and a box has a height; without this the width was honoured and the
    // height was whatever the content happened to be, which is the same
    // builder-versus-shop gap on the other axis that this section exists to
    // close. A percentage resolves against the containing block's height,
    // which for an absolutely positioned box is definite at every depth: the
    // canvas declares one, and a container has the height it has.
    if (pos.height !== undefined) {
      declarations += ';height:' + freeformPercent(pos.height) + '% !important';
    }
    declarations += ';z-index:' + freeformLayer(pos.zIndex) + ' !important';
    return { id: elId, declarations: declarations };
  }

  /**
   * The rules a document written BEFORE free placement moved to the page gets,
   * unchanged, to the byte. See §35 (k): everything this section learned since
   * is gated on the new shape, and this is the other side of that gate. Walks
   * `section.elements` rather than what was rendered, and clamps into the
   * canvas, because that is what it did.
   */
  function freeformLegacyStyleTag(section, id) {
    var elements = (section && section.elements) || [];
    var rules = '[data-neo-section="' + id + '"] .aico-neo-free__canvas{height:' +
      freeformCanvasHeight(section) + '}';

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      var pos = el && el.position;
      if (!pos || pos.type !== 'absolute') continue;
      if (pos.x === undefined && pos.y === undefined) continue;
      var elId = safeId(el.id);
      if (!elId) continue;

      var declarations = 'position:absolute !important' +
        ';left:' + freeformLegacyPercent(pos.x, 100) + '% !important' +
        ';top:' + freeformLegacyPercent(pos.y, 100) + '% !important';
      if (pos.width !== undefined) {
        declarations += ';width:' + freeformLegacyPercent(pos.width, 200) + '% !important';
      }
      if (pos.zIndex !== undefined) {
        declarations += ';z-index:' + freeformLayer(pos.zIndex) + ' !important';
      }

      rules += '[data-neo-section="' + id + '"] [data-neo-free="' + elId + '"]{' + declarations + '}';
    }

    return '<style>@media screen and (min-width: ' + FREEFORM_MIN_WIDTH + 'px){' + rules + '}</style>';
  }

  function freeformStyleTag(section, id, ctx) {
    if (!id) return '';
    if (!ctx || !ctx.newShape) return freeformLegacyStyleTag(section, id);
    var placed = (ctx && ctx.rules) || [];
    // The canvas declarations, the same set on every canvas at every depth, so
    // that one box behaves like the next: it is the containing block, its
    // layers are a private scale (g), it cuts what hangs past its left or right
    // edge so the page can never gain a sideways scrollbar, and it lets what
    // hangs below it show, because downward the section grows instead (j).
    var canvasOverflow = 'overflow-x:clip;overflow-y:visible';
    var canvasCss = 'position:relative;isolation:isolate;' + canvasOverflow;
    var rules = '[data-neo-section="' + id + '"] .aico-neo-free__canvas{height:' +
      freeformCanvasHeight(section) + ';' + canvasOverflow + '}';

    // §35 (h): one rule for every box that took a positioned child, at any
    // depth. Emitted only when there IS one, so a section whose placement is
    // all at top level ships the bytes it always did.
    if (ctx && ctx.canvases) {
      rules += '[data-neo-section="' + id + '"] [' + FREEFORM_CANVAS_ATTR + ']{' + canvasCss + '}';
    }

    // A canvas the author gave a height of its own. Per box rather than one
    // rule, because a column of a columns row and a grid cell have no id to
    // select on and the number stamped on the tag is what names them.
    var heights = (ctx && ctx.canvasHeights) || [];
    for (var h = 0; h < heights.length; h++) {
      rules += '[data-neo-section="' + id + '"] ' + heights[h].selector +
        '{height:' + heights[h].height + '}';
    }

    for (var i = 0; i < placed.length; i++) {
      rules += '[data-neo-section="' + id + '"] [data-neo-free="' + placed[i].id + '"]{' +
        placed[i].declarations + '}';
    }

    // 768, deliberately NOT the theme's own 749px tier that mobileStyleTag and
    // the `.content-row` collapse use. The two renderers have to agree on the
    // single question "does a coordinate apply at this width", and aiconeo's
    // published page answers it at 768 (useResponsiveViewport.ts: <= 767 is
    // mobile). Between 750 and 767 the shop used to position where the builder
    // stacked. Where they disagree the safe direction is to stack, because a
    // coordinate authored against a 1350px canvas is the thing that does not
    // survive a narrow screen.
    return '<style>@media screen and (min-width: ' + FREEFORM_MIN_WIDTH + 'px){' + rules + '}</style>';
  }

  /** One section tier as a scoped, !important rule on its data-neo-section hook. */
  function sectionTierRule(section, breakpoint, id, maxWidthPx) {
    // No viewport argument, exactly as the mobile rule was written before the
    // tablet tier existed: sectionBoxStyles already folded the width value for
    // this breakpoint, and passing it again here would change bytes this fix
    // has no business changing.
    var box = sectionBoxStyles(section, breakpoint);
    var css = stylesToInline(breakpoint === 'tablet' ? tabletBagWithoutRail(box) : box);
    if (!css) return '';
    // Same filter as the element tier, and for the same reason: a section that
    // owns a tablet bag must not end up on a different rail from the sections
    // beside it that do not. See TABLET_TIER_BLOCKED.
    var declarations = tierDeclarations(css, breakpoint);
    if (!declarations) return '';
    return '<style>@media screen and (max-width: ' + maxWidthPx + 'px){' +
      '[data-neo-section="' + id + '"]{' + declarations + '}}</style>';
  }

  function wrapSection(section, innerHtml) {
    var id = safeId(section && section.id);
    var pageSettings = currentPageSettings;
    var layout = resolveSectionLayout(section, pageSettings);

    var desktop = sectionBoxStyles(section, 'desktop');
    var inlineCss = stylesToInline(desktop);
    // A rounded section must clip its children, same as buildSectionCss.
    if (desktop.borderRadius) inlineCss += (inlineCss ? ';' : '') + 'overflow:hidden';

    // styles.tablet / styles.mobile → scoped @media overrides (the engine only
    // inlines desktop). Same tiers, same order and same reasoning as
    // viewportStyleTags, but keyed on data-neo-section: a section is wrapped
    // here rather than by an element renderer. Tablet first, so mobile wins
    // below 750px on source order.
    var styleTag = '';
    if (id) {
      styleTag = sectionTierRule(section, 'tablet', id, TABLET_MAX_PX) +
        sectionTierRule(section, 'mobile', id, MOBILE_MAX_PX);
    }

    // Historic upstream default: 40px unless the author set contentPaddingTop
    // ('' means explicitly none).
    var contentPaddingTop = pickStyles(section, 'desktop').contentPaddingTop;
    if (contentPaddingTop === undefined) contentPaddingTop = '40px';

    var outerStyle = 'max-width:' + layout.outerMaxWidth + ';margin:0 auto';
    var contentStyle = 'width:100%;margin:0 auto';
    if (layout.innerMaxWidth) contentStyle += ';max-width:' + layout.innerMaxWidth;
    if (contentPaddingTop) contentStyle += ';padding-top:' + contentPaddingTop;

    // §35, all of it inside this branch so a flow section emits what it always
    // did: the canvas is an inner div that sits INSIDE contentPaddingTop (e),
    // it isolates its layers (g), the section clips like the builder's root
    // does (f), and the coordinates arrive as a scoped @media rule.
    var freeformTag = '';
    var content = innerHtml;
    // An EMPTY freeform section emits no canvas at all, so it collapses to the
    // height of its own padding instead of leaving a 520px blank band on the
    // page. This mirrors aiconeo, whose published renderer draws the canvas
    // only inside `section.elements.length > 0` (PublicPageRenderer.tsx) and
    // whose editor does the same; the test is the element count and not the
    // rendered markup, so a section holding one element that renders to nothing
    // keeps its canvas in both renderers rather than collapsing in one.
    var hasFreeformChildren = !!(section && section.elements && section.elements.length > 0);
    if (isFreeformSection(section) && id) {
      // Mirrors the `min-h-[80px]` aiconeo puts on this same box
      // (PublicPageRenderer.tsx), so an empty freeform section is the same
      // height in both renderers instead of 80 there and 40 here. Both boxes
      // are border-box, so the 80 includes contentPaddingTop. It can never
      // affect a section that has children: the canvas below is 480px at its
      // smallest. Emitted for freeform only, because applying it to every
      // section would change the markup of pages that exist today.
      contentStyle += ';min-height:80px';
    }
    if (isFreeformSection(section) && id && hasFreeformChildren) {
      freeformTag = freeformStyleTag(section, id, freeformCtx);
      content = '<div class="aico-neo-free__canvas" style="position:relative;isolation:isolate">' +
        innerHtml + '</div>';
      if (inlineCss.indexOf('overflow:hidden') === -1) {
        inlineCss += (inlineCss ? ';' : '') + 'overflow:hidden';
      }
    }

    return styleTag + freeformTag +
      '<div class="aico-neo-section"' +
      (id ? ' id="section-' + id + '" data-neo-section="' + id + '"' : '') +
      (inlineCss ? ' style="' + escapeAttr(inlineCss) + '"' : '') + '>' +
      '<div class="aico-neo-section__outer" style="' + escapeAttr(outerStyle) + '">' +
      '<div class="aico-neo-section__content" style="' + escapeAttr(contentStyle) + '">' +
      content +
      '</div></div></div>';
  }

  /**
   * 9. SECTION-LEVEL `columns` — closing the registry bypass.
   *
   * renderSection intercepts `el.type === 'columns'` and calls its internal
   * renderColumnsRow, which never touches Neo._renderers — so a section-level
   * columns element is the one thing the registry decorator cannot reach. Worse,
   * renderColumnsRow emits a bare `<div class="content-row …">` with NO
   * inlineStyleAttr at all, so the element's authored styles are dropped too.
   * On club-des-130 that silently lost `display:grid; gap:48px;
   * align-items:center` (falling back to the stylesheet's flex + 1.5rem gap)
   * plus the `le-club` anchor id.
   *
   * Reaching it needs either a core patch or surgery on the engine's output.
   * This is the surgery, kept as narrow and fail-safe as possible: replay the
   * engine's own row-index bookkeeping to compute the exact opening tag the row
   * will have, then match that one literal string. If it is not found — because
   * the engine changed its markup — nothing happens and behaviour is unchanged.
   *
   * Deviation from ElementRenderer: customId goes ON the row div rather than in
   * an extra wrapping div. Locating the row's matching close tag would mean
   * balancing tags in a string; for an anchor target the scroll behaviour is
   * identical, and className still lands on the element's own wrapper so
   * `div:has(> .x)` selectors keep working.
   */
  /**
   * The column ratio the author set in the builder, as a grid template.
   *
   * Mirrors aiconeo's ColumnResizeHandle.parseColumnRatio + ElementRenderer's
   * `activeRatios.map((r) => r + 'fr')`: split on ':', keep the positive
   * numbers, and only when there is exactly one per column normalise them to
   * percentages; anything else means equal columns.
   *
   * The builder writes this ratio LAST (`{ ...cssStyles, gridTemplateColumns }`),
   * so there it beats whatever the element's own styles say. This theme did the
   * opposite: it applied the authored `gridTemplateColumns` and never read the
   * ratio, so a row dragged to 40/60 in the builder rendered 50/50 on the shop,
   * and a row whose styles carried an old template kept it after the author had
   * changed the ratio. Measured on three rows across two landing pages: builder
   * 50/50, shop 42/58.
   *
   * Emitted as a `min-width: 750px` rule rather than inline, because the theme
   * collapses every `.total-columns-N` row to one column under
   * `max-width: 749px`. An inline declaration would outrank that media query
   * and keep the columns side by side on every phone, on every page.
   */
  function columnRatioTemplate(raw, count) {
    var parts = String(raw == null ? '' : raw).split(':').map(function (piece) {
      return parseFloat(piece);
    }).filter(function (num) {
      return !isNaN(num) && num > 0;
    });
    if (parts.length !== count) return '';
    var total = 0;
    for (var i = 0; i < parts.length; i++) total += parts[i];
    if (!total) return '';
    return parts.map(function (num) {
      return (Math.round((num / total) * 10000) / 100) + 'fr';
    }).join(' ');
  }

  function columnRatioStyleTag(node, template) {
    var id = safeId(node && node.id);
    if (!id || !template) return '';
    return '<style>@media screen and (min-width: 750px){' +
      '[data-neo-el="' + id + '"]{grid-template-columns:' + template + ' !important}}</style>';
  }

  function decorateSectionColumns(section, html) {
    var elements = (section && section.elements) || [];
    var isFullWidth = !!(section && section.fullWidth);
    var rowIndex = 1; // a single-section render always restarts at 1
    var pending = 0;  // elements buffered into the current stacking row

    for (var i = 0; i < elements.length; i++) {
      var el = elements[i];
      if (!el) continue;

      if (el.type !== 'columns') {
        pending++;
        continue;
      }

      // Mirrors renderSection: a non-empty buffer is flushed as its own row first.
      if (pending > 0) { rowIndex++; pending = 0; }

      var count = parseInt(el.content && el.content.columnCount, 10);
      if (isNaN(count) || count < 1) count = 2;
      if (count > 4) count = 4;

      var classes = 'content-row row__' + rowIndex + ' total-columns-' + count +
        (isFullWidth ? ' fullwidth' : '');
      var needle = '<div class="' + classes + '">';

      var cls = el.className ? String(el.className).trim() : '';
      var customId = sanitizeCustomId(el.customId);
      var styleAttr = inlineStyleAttr(el);
      var ratioTemplate = columnRatioTemplate(el.content && el.content.columnRatio, count);
      var ratioTag = columnRatioStyleTag(el, ratioTemplate);

      // `ratioTag` joins the condition: without it a row whose only authored
      // instruction IS the ratio never gets its `data-neo-el`, and the rule
      // above would have nothing to select.
      if ((cls || customId || styleAttr || ratioTag) && html.indexOf(needle) !== -1) {
        var replacement = viewportStyleTags(el) + ratioTag +
          '<div class="' + classes + (cls ? ' ' + escapeAttr(cls) : '') + '"' +
          (customId ? ' id="' + escapeAttr(customId) + '"' : '') +
          dataElAttr(el) + styleAttr + '>';
        html = html.replace(needle, replacement);
      }

      rowIndex++;
    }

    return html;
  }

  /**
   * The engine numbers rows `row__1..N` per render() call, so a per-section call
   * restarts at 1. Renumber so the concatenated document keeps the continuous
   * numbering the engine would have produced on its own.
   */
  function renumberRows(html, offset) {
    var max = 0;
    var out = html.replace(/\brow__(\d+)\b/g, function (_m, n) {
      var v = parseInt(n, 10);
      if (v > max) max = v;
      return 'row__' + (v + offset);
    });
    return { html: out, rowCount: max };
  }

  var originalRender = Neo.render;
  Neo.render = function (data) {
    var neo = data && data.contentBuilderNeoJson;
    var sections = (neo && Array.isArray(neo.sections)) ? neo.sections : [];
    currentPageSettings = (neo && neo.settings) || null;
    unrenderedTally = null;

    try {
      for (var s = 0; s < sections.length; s++) {
        pruneHidden(sections[s]);
        var elements = (sections[s] && Array.isArray(sections[s].elements)) ? sections[s].elements : [];
        for (var e = 0; e < elements.length; e++) normalizeUnknown(elements[e]);
      }
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: unknown-type normalisation failed', err);
      }
      return originalRender(data);
    }

    var now = Date.now();
    var html = '';
    var rowOffset = 0;
    var firstSectionSeen = false;

    for (var i = 0; i < sections.length; i++) {
      var section = sections[i];
      if (!section) continue;
      if (isSectionTimeHidden(section, now)) continue;
      if (!firstSectionSeen) {
        firstSectionSeen = true;
        section = fullViewportFirstSection(section);
      }

      // §35: a freeform section places its children itself and builds no rows,
      // so it skips the engine pass entirely and does not advance the row
      // counter. Every other section takes the path it always took.
      if (isFreeformSection(section)) {
        // §35 (h): one context per section, opened before its children render
        // and closed after wrapSection has read what they left in it. Nested
        // sections do not exist, so a plain variable is the whole bookkeeping.
        var outerFreeform = freeformCtx;
        freeformCtx = { rules: [], canvases: 0, canvasHeights: [], newShape: pagePlacementDeclared() };
        try {
          html += wrapSection(section, renderFreeformSection(section));
        } finally {
          freeformCtx = outerFreeform;
          freeformCanvasPending = false;
        }
        continue;
      }

      // One engine pass per section: the engine owns the element markup, this
      // file only supplies the wrapper it never had.
      var rendered = originalRender({ contentBuilderNeoJson: { sections: [section] } });
      rendered = decorateSectionColumns(section, rendered);
      var renumbered = renumberRows(rendered, rowOffset);
      rowOffset += renumbered.rowCount;
      html += wrapSection(section, renumbered.html);
    }

    reportUnrendered();
    return wrapPage(html, currentPageSettings);
  };

  // Every link the sheet authors is locale-less (`/collections/ls-kids`), and
  // the builder has no per-locale URL field, so on `/en` a button, image link or
  // an anchor inside a text element sent the visitor to the French page. One
  // pass over the finished markup covers all element types and the engine's
  // own text/heading HTML alike; the helper leaves `/` (FR) untouched.
  var unlocalizedRender = Neo.render;
  Neo.render = function (data) {
    return localizeStoreHrefs(unlocalizedRender(data));
  };

  // ============================================================
  // 38. Full-viewport hero (p59 T4)
  //
  // The builder's `100vh` min-height preset means "fill the screen". On the
  // storefront the page starts under a FIXED header (body padding-top), so a
  // literal 100vh first section overshoots the fold by the header's height and
  // pages patched it with `min-height:calc(100svh - 98px)!important` in an
  // html element. Contract: an element (at any depth) of the page's FIRST
  // rendered section whose resolved `minHeight` — desktop or mobile tier — is
  // exactly `100vh` / `100svh` renders
  //   calc(var(--ls-neo-fullvh) - var(--ls-neo-header-offset))
  // where --ls-neo-fullvh is 100vh, upgraded to 100svh where supported (the
  // fallback line), and --ls-neo-header-offset is the fixed header (98px
  // desktop, 113px below 1000px), both in content-builder-neo.css. Any other
  // value, and any later section, is left alone. The document is never
  // mutated: the first section is cloned when (and only when) it matches.
  //
  // An `image` with children marked this way also stretches its content layer
  // to the full height (`content-type-neo-image--viewport`), so overlayAlign's
  // vertical centre means the centre of the screen, not of a 300px layer.
  // ============================================================

  var FULL_VIEWPORT_RE = /^\s*100s?vh\s*$/i;
  var FULL_VIEWPORT_VALUE = 'calc(var(--ls-neo-fullvh, 100vh) - var(--ls-neo-header-offset, 98px))';

  function isFullViewportTier(bag) {
    return !!(bag && typeof bag === 'object' && !Array.isArray(bag) &&
      typeof bag.minHeight === 'string' && FULL_VIEWPORT_RE.test(bag.minHeight));
  }

  function hasFullViewportElement(list) {
    if (!Array.isArray(list)) return false;
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (!el || typeof el !== 'object') continue;
      if (el.styles && (isFullViewportTier(el.styles.desktop) || isFullViewportTier(el.styles.mobile))) return true;
      if (hasFullViewportElement(el.children) || hasFullViewportElement(el.elements)) return true;
    }
    return false;
  }

  function markFullViewport(list) {
    if (!Array.isArray(list)) return;
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (!el || typeof el !== 'object') continue;
      if (el.styles) {
        var hit = false;
        if (isFullViewportTier(el.styles.desktop)) { el.styles.desktop.minHeight = FULL_VIEWPORT_VALUE; hit = true; }
        if (isFullViewportTier(el.styles.mobile)) { el.styles.mobile.minHeight = FULL_VIEWPORT_VALUE; hit = true; }
        // Runtime-only flag, set on the CLONE: it is discarded after this
        // render and never reaches the saved document.
        if (hit) el._lsFullViewport = true;
      }
      markFullViewport(el.children);
      markFullViewport(el.elements);
    }
  }

  function fullViewportFirstSection(section) {
    try {
      if (!hasFullViewportElement(section.elements)) return section;
      var clone = JSON.parse(JSON.stringify(section));
      markFullViewport(clone.elements);
      return clone;
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: full-viewport pass failed', err);
      }
      return section;
    }
  }

  /**
   * Page-level shell. `settings.backgroundColor` is applied explicitly rather
   * than relying on it coincidentally matching the theme's own background, and
   * `settings.accentColor` is exposed as a custom property so authored `html`
   * elements can reference it.
   */
  function wrapPage(html, pageSettings) {
    var settings = pageSettings || {};
    var css = '';
    if (settings.backgroundColor) css += 'background-color:' + settings.backgroundColor + ';';
    if (settings.accentColor) css += '--aico-neo-accent:' + settings.accentColor + ';';
    return '<div class="aico-neo-page"' +
      (css ? ' style="' + escapeAttr(css) + '"' : '') + '>' + html + '</div>';
  }

  // ============================================================
  // 12. Collapsed size containers (`container-type: inline-size`)
  //
  // aiconeo authors write their responsive systems as container queries: the
  // element gets `container-type: inline-size` (via its className, see §7) and
  // the page's in-mount <style> carries the @container rules. `container-type`
  // implies `contain: inline-size`, i.e. the element's intrinsic inline size
  // contribution becomes 0 — which is fine in a block/grid/stretch context, and
  // fatal in a shrink-to-fit one. aiconeo containers are inline-styled
  // `display:flex; flex-direction:column; align-items:flex-start`, so a size
  // container inside one is a shrink-to-fit flex item: fit-content resolves to
  // 0px and the whole prose block renders ONE WORD PER LINE
  // (/pages/organisation was 11 724px tall and unreadable; same on
  // nos-engagements, palmares, benevoles — 18 blocks in all).
  //
  // The authored guard `div:has(> .org-w){width:100%!important}` targets the
  // PARENT, not the size container itself, so it never helped.
  //
  // Why this is JS and not a stylesheet rule: CSS cannot select "an element
  // that declares container-type", so a CSS fix has to guess at wrappers by
  // class and would restyle healthy pages (an html block that is a shrink-to-fit
  // item of a flex ROW is supposed to size to its content). Measuring instead
  // keeps the fix exactly as wide as the bug: an element is only touched when it
  // IS a size container AND has already collapsed. Nothing to undo, no class
  // names, no per-page code.
  //
  // Cheap by construction: one batched read pass (rect first, computed style
  // only for the handful of candidates), then one write pass. Repeats while it
  // keeps finding new ones, because fixing an outer container can reveal a
  // collapsed inner one, capped at 3 passes.
  // ============================================================

  var SIZE_GUARD_ATTR = 'data-neo-size-guard';

  function collapsedSizeContainerPass(root) {
    var nodes = root.querySelectorAll('*');
    var collapsed = [];

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.hasAttribute(SIZE_GUARD_ATTR)) continue;

      // A collapsed size container is a TALL, zero-width box. Requiring height
      // skips display:none subtrees and empty nodes (both 0x0), which are
      // zero-width for legitimate reasons.
      var rect = node.getBoundingClientRect();
      if (rect.width >= 4 || rect.height <= 24) continue;

      var cs = window.getComputedStyle(node);
      // `container-type` computes to 'normal' unless the author set it; browsers
      // without container queries report undefined — and cannot have this bug.
      var containerType = cs.containerType;
      if (!containerType || containerType === 'normal') continue;
      // Out-of-flow boxes shrink-to-fit by design; leave them alone.
      if (cs.position === 'absolute' || cs.position === 'fixed') continue;

      collapsed.push(node);
    }

    for (var j = 0; j < collapsed.length; j++) {
      collapsed[j].setAttribute(SIZE_GUARD_ATTR, '1');
      collapsed[j].style.width = '100%';
      collapsed[j].style.minWidth = '0';
    }

    return collapsed.length;
  }

  function guardCollapsedSizeContainers(root) {
    if (!root || !root.querySelectorAll || !window.getComputedStyle) return;
    for (var pass = 0; pass < 3; pass++) {
      if (!collapsedSizeContainerPass(root)) return;
    }
  }

  // ============================================================
  // 14. Empty-column collapse
  //
  // Every renderer that cannot draw its element returns '' — `image` with no
  // `content.src` (the builder shows a dashed "choose an image" placeholder and
  // saves nothing), `youtube`/`video` with no id, `linked-form` with no form,
  // renderUnknown. But wrapColumn()/wrapRow() in the engine run unconditionally
  // and `container` emits its styled div whatever its children rendered to, so
  // the column survives its content and keeps claiming its share of the row:
  // /pages/enzonet-lounge showed an empty dark box where the photo should be,
  // with the text squeezed into the other half.
  //
  // Nothing in the document says "this column is empty" — only the rendered DOM
  // does — so this is a hydrate-time pass, like the size-container guard above,
  // and it is deliberately timid: a wrongly collapsed column is a far worse bug
  // than an empty gap, and this renderer serves ~30 live pages. Three rules do
  // the containment:
  //
  //   a) Only fire when the row has >= 2 columns and EXACTLY ONE of them is
  //      meaningful. With one survivor "collapse" has an unambiguous meaning —
  //      it takes the row. With two survivors of three it would not, and the
  //      authored track placement would start to matter.
  //   b) A `display:none` element counts as MEANINGFUL. That reads backwards
  //      until you look at what these documents do: nos-fans and hospitalites
  //      author real placeholder panels (`.nf-media__ph`, `.hosp-media__panel`)
  //      and hide them from their own CSS, which is authored emptiness — the
  //      author owns that column and may reveal it at another breakpoint. Only
  //      a column the RENDERER emptied (nothing but wrapper divs and the mobile
  //      <style> blocks this file prefixes) is collapsed.
  //   c) Background images, visible borders, thin painted bars (a rule or
  //      divider) and `spacer` elements all count as meaningful. A plain
  //      background-color on a full-size box does not — that is exactly the
  //      enzonet box.
  //
  // Every multi-column row in these documents is authored `display:grid` with an
  // explicit `grid-template-columns` (the stylesheet's flex row is only the
  // unstyled default), and in a grid, hiding a child does NOT free its track —
  // the survivor would just auto-place into the FIRST track and could end up
  // narrower than before. So on grid rows the survivor is additionally spanned
  // across all tracks, and only when the author left it auto-placed.
  // ============================================================

  var EMPTY_COLUMN_ATTR = 'data-neo-empty-column';
  var COLUMN_SPAN_ATTR = 'data-neo-column-span';

  // Never paint anything. <style> is the load-bearing one: every renderer in
  // this file prefixes its markup with a `styles.mobile` <style> block, and its
  // CSS text would otherwise read as "this column has text".
  var NON_VISUAL_TAGS = {
    STYLE: 1, SCRIPT: 1, TEMPLATE: 1, NOSCRIPT: 1, LINK: 1, META: 1
  };

  // Replaced or interactive elements: they show something (or do something)
  // without carrying any text of their own.
  var VISUAL_TAGS = {
    IMG: 1, PICTURE: 1, SVG: 1, VIDEO: 1, AUDIO: 1, IFRAME: 1, CANVAS: 1,
    OBJECT: 1, EMBED: 1, FORM: 1, INPUT: 1, TEXTAREA: 1, SELECT: 1, BUTTON: 1,
    A: 1, HR: 1
  };

  function hasVisibleBorder(cs) {
    var styles = cs.borderTopStyle + ' ' + cs.borderRightStyle + ' ' +
      cs.borderBottomStyle + ' ' + cs.borderLeftStyle;
    if (!/solid|dashed|dotted|double|groove|ridge|inset|outset/.test(styles)) return false;
    return parseFloat(cs.borderTopWidth) > 0 || parseFloat(cs.borderRightWidth) > 0 ||
      parseFloat(cs.borderBottomWidth) > 0 || parseFloat(cs.borderLeftWidth) > 0;
  }

  /** A painted rule/bar: coloured, and thin in one direction. */
  function isPaintedBar(node, cs) {
    var bg = cs.backgroundColor;
    if (!bg || bg === 'transparent' || /rgba\([^)]*,\s*0\s*\)/.test(bg)) return false;
    var rect = node.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return false;
    return rect.height <= 24 || rect.width <= 24;
  }

  function isMeaningfulNode(node) {
    if (!node) return false;
    if (node.nodeType === 3) return /\S/.test(node.nodeValue || '');
    if (node.nodeType !== 1) return false; // comments (incl. the §10 markers)
    if (NON_VISUAL_TAGS[node.tagName]) return false;
    if (VISUAL_TAGS[node.tagName]) return true;
    // An authored spacer is emptiness on purpose.
    if (node.className && String(node.className).indexOf('content-type-neo-spacer') !== -1) {
      return true;
    }

    var cs = window.getComputedStyle(node);
    // See (b): hidden by the document's own CSS = the author owns this column.
    if (cs.display === 'none' || cs.visibility === 'hidden') return true;
    if (cs.backgroundImage && cs.backgroundImage !== 'none') return true;
    if (hasVisibleBorder(cs)) return true;
    if (isPaintedBar(node, cs)) return true;

    var kids = node.childNodes;
    for (var i = 0; i < kids.length; i++) {
      if (isMeaningfulNode(kids[i])) return true;
    }
    return false;
  }

  /**
   * The column WRAPPER is renderer-emitted, never authored, so it is classified
   * by what it contains — not by itself. That distinction matters because Dawn's
   * base.css hides `div:empty`, so a column the renderer emptied completely is
   * already `display:none` and rule (b) would otherwise read it as authored
   * intent and keep the row as it is. (It still owns its grid track: the
   * survivor auto-places into track 1 and can come out NARROWER than the row.)
   */
  function isColumnEmpty(column) {
    var kids = column.childNodes;
    for (var i = 0; i < kids.length; i++) {
      if (isMeaningfulNode(kids[i])) return false;
    }
    return true;
  }

  function collapseEmptyColumns(root) {
    if (!root || !root.querySelectorAll || !window.getComputedStyle) return;

    var rows = root.querySelectorAll('.content-row');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var columns = [];
      var kids = row.children;
      for (var k = 0; k < kids.length; k++) {
        if (kids[k].classList && kids[k].classList.contains('content-column')) {
          columns.push(kids[k]);
        }
      }
      if (columns.length < 2) continue; // a single-column row has nothing to give

      var empties = [];
      var survivors = [];
      for (var c = 0; c < columns.length; c++) {
        // Already collapsed: classify by the marker, not by measuring a
        // display:none node — that keeps repeat passes idempotent.
        if (columns[c].hasAttribute(EMPTY_COLUMN_ATTR)) empties.push(columns[c]);
        else if (isColumnEmpty(columns[c])) empties.push(columns[c]);
        else survivors.push(columns[c]);
      }
      // Rule (a): one unambiguous survivor, or leave the row alone. An
      // all-empty row also falls through here and is left exactly as it was.
      if (!empties.length || survivors.length !== 1) continue;

      for (var e = 0; e < empties.length; e++) {
        empties[e].setAttribute(EMPTY_COLUMN_ATTR, '1');
        empties[e].style.display = 'none';
      }

      var survivor = survivors[0];
      if (survivor.hasAttribute(COLUMN_SPAN_ATTR)) continue;
      var rowStyle = window.getComputedStyle(row);
      if (rowStyle.display !== 'grid' && rowStyle.display !== 'inline-grid') continue;
      var survivorStyle = window.getComputedStyle(survivor);
      // An explicitly placed column belongs to the author; only stretch the
      // auto-placed default across the tracks the hidden columns left behind.
      if (survivorStyle.gridColumnStart !== 'auto' || survivorStyle.gridColumnEnd !== 'auto') {
        continue;
      }
      survivor.setAttribute(COLUMN_SPAN_ATTR, '1');
      survivor.style.gridColumn = '1 / -1';
    }
  }

  /**
   * Both DOM-measuring passes, in dependency order: collapsing a column changes
   * the widths the size-container guard measures.
   */
  function runLayoutGuards(root) {
    try {
      collapseEmptyColumns(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: empty-column collapse failed', err);
      }
    }
    guardCollapsedSizeContainers(root);
  }

  // ============================================================
  // 16. Product-rail chevrons
  //
  // The Shop neo page (landing page 28) draws its "Nouveautés" / product rows
  // as authored HTML: a `.shp-row` horizontal scroller inside `.shp-rowwrap`,
  // with a CSS scroll-timeline progress bar and no script (the renderer runs
  // none from the document). Mouse users without a trackpad therefore had no
  // way to move the rail. This adds the same prev/next chevron pair the theme
  // rails carry (sections/nouveautes-slider.liquid, sections/saison-figma.liquid)
  // next to the band's `.shp-h2` heading, from the theme side, so the page
  // document stays untouched. Idempotent per rail (data-ls-rail-nav marker);
  // styles in assets/content-builder-neo.css (`.ls-rail-*`).
  // ============================================================

  var RAIL_CHEVRON =
    '<svg viewBox="0 0 11 11" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M2.75 4.125L5.5 6.875L8.25 4.125" stroke="currentColor" stroke-width="0.916667" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  function findRailHeading(rowwrap) {
    // Heading wrapper = the closest preceding sibling (walking up through the
    // engine's per-element wrappers) that contains the band's h2.
    var node = rowwrap;
    for (var depth = 0; node && depth < 4; depth++) {
      var sib = node.previousElementSibling;
      while (sib) {
        var h = sib.matches('.shp-h2, h2') ? sib : sib.querySelector('.shp-h2, h2');
        if (h && !sib.querySelector('.shp-row')) return h;
        sib = sib.previousElementSibling;
      }
      node = node.parentElement;
    }
    return null;
  }

  // Shared behaviour for an injected chevron pair: prev/next scroll the track
  // by one card + gap, the pair hides while the track does not overflow, and
  // each button disables at its end of the scroll range.
  function bindRailNav(nav, track) {
    var prev = nav.firstElementChild;
    var next = nav.lastElementChild;

    function step(direction) {
      var card = track.firstElementChild;
      if (!card) return;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }
    function update() {
      var total = track.scrollWidth;
      var visible = track.clientWidth;
      var overflowing = total - visible > 1;
      nav.hidden = !overflowing;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft + visible >= total - 1;
    }
    prev.addEventListener('click', function () { step(-1); });
    next.addEventListener('click', function () { step(1); });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    update();
  }

  function buildRailNav(prevLabel, nextLabel) {
    var nav = document.createElement('div');
    nav.className = 'ls-rail-nav';
    nav.hidden = true;
    nav.innerHTML =
      '<button type="button" class="ls-rail-nav__btn ls-rail-nav__btn--prev" aria-label="' + prevLabel + '" disabled>' + RAIL_CHEVRON + '</button>' +
      '<button type="button" class="ls-rail-nav__btn ls-rail-nav__btn--next" aria-label="' + nextLabel + '">' + RAIL_CHEVRON + '</button>';
    return nav;
  }

  function enhanceProductRail(rowwrap) {
    if (rowwrap.hasAttribute('data-ls-rail-nav')) return;
    var track = rowwrap.querySelector('.shp-row');
    if (!track) return;
    var heading = findRailHeading(rowwrap);
    if (!heading) return;
    rowwrap.setAttribute('data-ls-rail-nav', '');

    var head = heading.parentElement;
    head.classList.add('ls-rail-head');

    var nav = buildRailNav(lsT('shop.previous_products', 'Produits précédents'), lsT('shop.next_products', 'Produits suivants'));
    heading.insertAdjacentElement('afterend', nav);
    bindRailNav(nav, track);
  }

  // The Papet du LS page (landing page 8) carries a heading-less photo rail:
  // `.pap-story-track` inside a `.pap-story-wrap` html element. Same chevron
  // pair, gold on navy, in its own row right-aligned to the track's 40px
  // gutter (`.ls-rail-nav--story`). The marker also switches on the CSS that
  // gives the hover-lifted card (translateY(-6px) scale(1.02)) enough vertical
  // padding inside the scroller — the authored 6px clipped its rounded top.
  function enhanceStoryRail(wrap) {
    if (wrap.hasAttribute('data-ls-rail-nav')) return;
    var track = wrap.querySelector('.pap-story-track');
    if (!track) return;
    wrap.setAttribute('data-ls-rail-nav', '');

    var nav = buildRailNav(lsT('js.rail_prev_photos', 'Photos précédentes'), lsT('js.rail_next_photos', 'Photos suivantes'));
    nav.classList.add('ls-rail-nav--story');
    track.insertAdjacentElement('beforebegin', nav);
    bindRailNav(nav, track);
  }

  // Partner logos: every logo fills the same zone (its tile minus the padding),
  // scaled proportionally: a wide mark fills the width, a tall one the height
  // (club rule, 140748). Many files carry empty space around the mark (a
  // 1007x121 wordmark in a 1080x1350 file), so the mark's own box is measured
  // here and handed to the tile grid in content-builder-neo.css as fractions
  // of the file: --pl-ar file ratio, --pl-iar mark ratio, --pl-fw mark width,
  // --pl-cx / --pl-cy mark centre. Only a transparent or white surround is
  // trimmed; a logo on a coloured box keeps its box. A file whose pixels
  // cannot be read is fitted whole.
  function partnerLogoInkBox(img) {
    var nw = img.naturalWidth, nh = img.naturalHeight;
    var k = 96 / Math.max(nw, nh);
    var w = Math.max(1, Math.round(nw * k)), h = Math.max(1, Math.round(nh * k));
    var data;
    try {
      var canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (e) {
      return null;
    }
    var corners = [0, w - 1, (h - 1) * w, h * w - 1];
    var clear = 0, white = 0;
    for (var c = 0; c < 4; c++) {
      var o = corners[c] * 4;
      if (data[o + 3] < 16) clear++;
      else if (data[o + 3] > 249 && Math.min(data[o], data[o + 1], data[o + 2]) > 243) white++;
    }
    if (clear !== 4 && white !== 4) return null;
    var x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var i = (y * w + x) * 4;
        if (data[i + 3] < 32) continue;
        if (white === 4 && Math.min(data[i], data[i + 1], data[i + 2]) > 235) continue;
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
    if (x1 < x0 || y1 < y0) return null;
    // Half a sample of air on each side: the grid is coarser than the file.
    x0 = Math.max(0, x0 - 0.5); y0 = Math.max(0, y0 - 0.5);
    x1 = Math.min(w, x1 + 1.5); y1 = Math.min(h, y1 + 1.5);
    return { fw: (x1 - x0) / w, fh: (y1 - y0) / h, cx: (x0 + x1) / 2 / w, cy: (y0 + y1) / 2 / h };
  }

  function sizePartnerLogo(img) {
    var nw = img.naturalWidth, nh = img.naturalHeight;
    if (!nw || !nh) return;
    var box = partnerLogoInkBox(img) || { fw: 1, fh: 1, cx: 0.5, cy: 0.5 };
    var style = img.style;
    style.setProperty('--pl-ar', (nw / nh).toFixed(4));
    style.setProperty('--pl-iar', ((nw * box.fw) / (nh * box.fh)).toFixed(4));
    style.setProperty('--pl-fw', box.fw.toFixed(4));
    style.setProperty('--pl-cx', box.cx.toFixed(4));
    style.setProperty('--pl-cy', box.cy.toFixed(4));
    img.classList.add('is-fit');
  }

  function enhancePartnerLogos(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var imgs = scope.querySelectorAll('.neo-partner-logos__img');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.complete) sizePartnerLogo(img);
      else img.addEventListener('load', function () { sizePartnerLogo(this); }, { once: true });
    }
  }

  function enhanceProductRails(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var rails = scope.querySelectorAll('.shp-rowwrap');
    for (var i = 0; i < rails.length; i++) enhanceProductRail(rails[i]);
    var stories = scope.querySelectorAll('.pap-story-wrap');
    for (var j = 0; j < stories.length; j++) enhanceStoryRail(stories[j]);
  }

  // ============================================================
  // 17. Marquee fit — seamless `marquee` bands at any viewport width
  //
  // The `marquee` renderer above emits two identical `.neo-marquee__run`s
  // and the track scrolls by -50%, i.e. by exactly one run. That is seamless
  // only while one run is at least as wide as the band: on a wider screen the
  // track's end scrolls into view and the band shows a blank tail once per
  // cycle. The homepage "Nos partenaires" band (landing page 31, a native
  // marquee element) is the live case. The renderer runs no script from the
  // document, so the fix lives here: clone the first run until each half of
  // the track covers the band's width (k runs per half, 2k in total, so the
  // -50% keyframe still lands on an identical copy) and stretch the duration
  // by k so the authored per-run speed is unchanged. Re-measured on resize
  // and once each logo has loaded (an unloaded `width:auto` image measures
  // 0); clones are aria-hidden like the renderer's own duplicate.
  // ============================================================

  function enhanceMarquee(band) {
    if (band.hasAttribute('data-ls-marquee')) return;
    var track = band.querySelector('.neo-marquee__track');
    var first = track && track.querySelector('.neo-marquee__run');
    if (!first) return;
    band.setAttribute('data-ls-marquee', '');

    // The renderer's inline duration is for one run; it becomes the per-run rate.
    var perRun = parseFloat(track.style.animationDuration) ||
      parseFloat(getComputedStyle(track).animationDuration) || 30;

    function fit() {
      var runW = first.getBoundingClientRect().width;
      var bandW = band.clientWidth;
      if (!runW || !bandW) return;
      var k = Math.max(1, Math.ceil(bandW / runW));
      var want = 2 * k;
      var runs = track.querySelectorAll('.neo-marquee__run');
      var n = runs.length;
      if (n < want) {
        var frag = document.createDocumentFragment();
        for (var i = n; i < want; i++) {
          var clone = first.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          frag.appendChild(clone);
        }
        track.appendChild(frag);
      } else if (n > want) {
        for (var j = n - 1; j >= want; j--) track.removeChild(runs[j]);
      }
      track.style.animationDuration = (perRun * k) + 's';
    }

    fit();
    var imgs = first.querySelectorAll('img');
    for (var m = 0; m < imgs.length; m++) {
      if (!imgs[m].complete) imgs[m].addEventListener('load', fit, { once: true });
    }
    if ('ResizeObserver' in window) {
      new ResizeObserver(fit).observe(band);
    } else {
      window.addEventListener('resize', fit);
    }
  }

  function enhanceMarquees(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var bands = scope.querySelectorAll('.neo-marquee');
    for (var i = 0; i < bands.length; i++) enhanceMarquee(bands[i]);
  }

  // ============================================================
  // 23. Builder marquee — clone the run until it covers the band
  //
  // The `marquee` element (§10) renders two identical `.neo-marquee__run`s
  // inside `.neo-marquee__track` and scrolls them by `translateX(-50%)` —
  // exactly one run — so the loop is seamless only while a single run is at
  // least as wide as the band. The builder tries to guarantee that by
  // repeating a short list inside each run until it holds a dozen items, but
  // a guess made from item *count* cannot know how wide those items render:
  // five or six partner logos come out around 1000px against a ~1400px band,
  // so the tail of every cycle drags ~400px of empty band through the
  // viewport and the marquee reads as having emptied out and stopped. Same
  // cure as the hand-authored band above (§17): measure one run, clone it
  // until each half of the track covers the band — k runs per half, 2k in
  // total, so the -50% keyframe still lands on a pixel-identical copy — and
  // stretch the duration by k so each logo still crosses at the speed the
  // author set. The duration is read once, before the first fit overwrites
  // it, otherwise every later fit would multiply an already-multiplied
  // value. The logos carry `loading="lazy"`, so the first measurement can
  // catch an image at zero width and under-clone the band; the run's images
  // are watched so the fit is redone at their real size. Re-checked on
  // resize, which may also ask for *fewer* runs than are there. The
  // optional logoHeight/gap sizes are inline on each run/logo (mobile ones a
  // scoped <style>), so clones carry them and the fit measures them as-is.
  // ============================================================

  function enhanceNeoMarquee(band) {
    if (band.hasAttribute('data-neo-marquee-fit')) return;
    var track = band.querySelector('.neo-marquee__track');
    var first = track && track.querySelector('.neo-marquee__run');
    if (!first) return;
    band.setAttribute('data-neo-marquee-fit', '');

    // Authored duration is for one run; it becomes the per-run rate.
    var perRun = parseFloat(getComputedStyle(track).animationDuration) || 30;

    function fit() {
      var runW = first.getBoundingClientRect().width;
      var bandW = band.clientWidth;
      if (!runW || !bandW) return;
      var k = Math.max(1, Math.ceil(bandW / runW));
      var want = 2 * k;
      var runs = track.querySelectorAll('.neo-marquee__run');
      var n = runs.length;
      if (n < want) {
        var frag = document.createDocumentFragment();
        for (var i = n; i < want; i++) {
          var clone = first.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          frag.appendChild(clone);
        }
        track.appendChild(frag);
      } else if (n > want) {
        for (var j = n - 1; j >= want; j--) track.removeChild(runs[j]);
      }
      track.style.animationDuration = (perRun * k) + 's';
    }

    fit();

    // A lazy logo only settles on its real width once it has loaded — and a
    // broken one collapses just as late — so re-fit on either outcome rather
    // than trusting a measurement taken while the image was still a blank box.
    var imgs = first.querySelectorAll('img');
    for (var m = 0; m < imgs.length; m++) {
      if (imgs[m].complete) continue;
      imgs[m].addEventListener('load', fit, { once: true });
      imgs[m].addEventListener('error', fit, { once: true });
    }

    if ('ResizeObserver' in window) {
      new ResizeObserver(fit).observe(band);
    } else {
      window.addEventListener('resize', fit);
    }
  }

  function enhanceNeoMarquees(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var bands = scope.querySelectorAll('.neo-marquee');
    for (var i = 0; i < bands.length; i++) enhanceNeoMarquee(bands[i]);
  }

  // ============================================================
  // 21. Scroll reveal (opt-in)
  //
  // Content fades and rises in as it scrolls into view. Two author classes,
  // set through element.className (§7) or on raw markup inside an html
  // element:
  //
  //   ls-reveal        — the element reveals on its own when it enters the
  //                      viewport.
  //   ls-reveal-group  — the element's DIRECT element children reveal one
  //                      after another (REVEAL_STAGGER_MS apart) when the
  //                      group enters. A child that carries either class is
  //                      left to its own observer, so groups can nest.
  //
  // Inert in the aiconeo builder (no theme script there) and inert without
  // JS: the hidden state is a class THIS pass adds (`ls-reveal-pending`), in
  // the same task as the paint, so nothing is ever hidden unless the observer
  // that will show it exists. Whatever is already in view at hydrate reveals
  // at once (the hero rises in on load). prefers-reduced-motion and print are
  // handled by the stylesheet; the JS is identical.
  // ============================================================

  var REVEAL_STAGGER_MS = 90;
  var REVEAL_MAX_DELAY_MS = 540;
  // A target reveals once 8% of it is in view, or REVEAL_MIN_PX of it for a
  // target taller than a viewport: the members list on «Onze d'Or» is one
  // 12,000px block, 8% of which never fits a phone screen, so it stayed at
  // opacity 0 and the page showed 9,000px of nothing. The observer only
  // reports threshold crossings, so the list goes down to 0.5% to make the
  // pixel floor reachable on a block of any height.
  var REVEAL_RATIO = 0.08;
  var REVEAL_MIN_PX = 160;
  var REVEAL_THRESHOLDS = [0, 0.005, 0.01, 0.02, 0.04, REVEAL_RATIO];

  /**
   * Per-group stagger step / cap: a CSS custom property on the group wins over
   * the defaults, so a page motion profile (below) retunes the rhythm from the
   * stylesheet without forking this pass.
   */
  function revealTiming(node) {
    var cs = window.getComputedStyle ? getComputedStyle(node) : null;
    var step = cs ? parseFloat(cs.getPropertyValue('--ls-reveal-stagger')) : NaN;
    var cap = cs ? parseFloat(cs.getPropertyValue('--ls-reveal-max-delay')) : NaN;
    return {
      step: isNaN(step) ? REVEAL_STAGGER_MS : step,
      cap: isNaN(cap) ? REVEAL_MAX_DELAY_MS : cap
    };
  }

  // ------------------------------------------------------------
  // 21b. Page motion profiles (theme-owned choreography)
  //
  // Motion is a theme concern; the CMS only edits content. A profile matches a
  // page by its body class (layout/theme.liquid stamps `page--<handle>`) and
  // marks reveal targets at hydrate time with RUNTIME-ONLY classes, anchored on
  // structure that survives staff edits: every `[data-neo-section]`, rail
  // tracks by their structural class, and fixed section uuids. Nothing is
  // written to the aiconeo document, so duplicating a card, swapping a photo or
  // adding a section keeps animating with no extra step.
  //
  // The stylesheet holds the look (distances, durations, easing, stagger) under
  // `body.<profile body class>`; this pass only decides WHAT moves and HOW
  // (rise / slide in from the right / zoom). Deleting and recreating a zoom
  // section just falls back to the default rise.
  //
  //   body       body class the profile applies to
  //   heroStill  the page's FIRST section, when it holds an image with
  //              children (the native full-viewport hero, §38), gets no
  //              entrance at all, and that image is marked `ls-motion-pinned`:
  //              the stylesheet pins its <img> (≥1000px, no-preference) so the
  //              page scrolls over the photo. No transformed ancestor may sit
  //              above a pinned photo, which is why the section is skipped
  //              whole. LEGACY (remove once lp11 runs on the native doc):
  //              any section holding `.ls-hero-photo` is still treated the
  //              same way, pinned by its own class rule.
  //   rails      selector of horizontal tracks whose children slide in (x)
  //   zoom       section ids whose content block zooms in as one piece
  //   scrollZoom {section id: selector} — that element keeps its frame while
  //              its picture settles from a zoom as it scrolls in (CSS
  //              scroll-driven, no JS)
  //   scrollSlider section ids — the section pins (sticky) and the scroll picks
  //              the slide of its content-slider (JS, ≥1000px, no-preference;
  //              everywhere else the slider keeps its own autoplay)
  //   windowOpen {section id: selector} — that image-with-children's photo and
  //              scrim open from an inset window to full size (CSS scroll-driven)
  //   tabFade    panel-mode tabs fade their panel in on switch (CSS)
  // ------------------------------------------------------------

  var MOTION_PROFILES = [
    {
      // Enzonet Lounge — client prototype (Figma Make, 2026-09-15)
      body: 'page--enzonet-lounge',
      heroStill: true,
      // `.neo-slider--rail` = the native content-slider rail (§37b).
      // LEGACY `.ez-track` = the page-CSS rail of the html-era document; drop
      // it once lp11 is switched to the native doc (p59).
      rails: '.neo-slider--rail .neo-slider__track, .ez-track',
      zoom: ['9b07a5c7-2d4e-4f0b-8c3d-b12bc7000060']
    },
    {
      // Le Corner — the native, page-CSS-free document (landing page 34,
      // /pages/le-corner; landing page 81 /pages/le-corner-sans-css is its test
      // copy). Same feel as Enzonet: still pinned hero, every section rises
      // in, the photo rail slides in from the right.
      body: ['page--le-corner', 'page--le-corner-sans-css'],
      heroStill: true,
      rails: '.neo-slider--rail .neo-slider__track'
    },
    {
      // LS IS MORE — the native, page-CSS-free document (landing page 14,
      // /pages/ls-is-more; landing page 82 /pages/ls-is-more-sans-css is its
      // test copy), after the "Lausanne subscription design"
      // mockup: pinned hero, the campaign video zooms up as it enters, the
      // formule section pins and the scroll walks its slides, the final CTA
      // photo opens from a window to full size. Anchored on section ids
      // (fixed since lp14) + element types, never on authored classes.
      body: ['page--ls-is-more', 'page--ls-is-more-sans-css'],
      heroStill: true,
      scrollZoom: { '9c03a5c7-2d4e-4f0b-8c3d-c12fa30000a0': '.content-type-neo-video' },
      scrollSlider: ['9c03a5c7-2d4e-4f0b-8c3d-c12fa3000010'],
      windowOpen: { '9c03a5c7-2d4e-4f0b-8c3d-c12fa3000060': '.content-type-neo-image--overlay' },
      tabFade: true
    },
    {
      // Le Onze d'Or (lp9), La Confrérie (lp10), Le Comptoir Vaudois (lp35) —
      // their html-era documents opted a few blocks into the site-wide reveal
      // with the author classes; the native, page-CSS-free documents (p71)
      // carry no classes at all, so the profile keeps that reveal alive from
      // the theme: every top-level block rises in with the site-wide timing,
      // a gallery rail slides its photos in from the right. No pinned hero —
      // these pages never had one. The `-sans-css` twins are the p71 test
      // copies; harmless once they are deleted.
      body: [
        'page--onze-d-or', 'page--onze-d-or-sans-css',
        'page--confrerie', 'page--confrerie-sans-css',
        'page--le-comptoir-vaudois', 'page--le-comptoir-vaudois-sans-css'
      ],
      rails: '.neo-slider--rail .neo-slider__track'
    }
  ];

  var REVEAL_CLASSES = ['ls-reveal', 'ls-reveal-group', 'ls-reveal--x', 'ls-reveal--zoom'];

  function stripRevealClasses(node) {
    if (!node || !node.classList) return;
    for (var i = 0; i < REVEAL_CLASSES.length; i++) node.classList.remove(REVEAL_CLASSES[i]);
  }

  function hasRevealInside(node) {
    return !!(node.classList && (node.classList.contains('ls-reveal') || node.classList.contains('ls-reveal-group'))) ||
      !!node.querySelector('.ls-reveal, .ls-reveal-group');
  }

  function matchMotionProfile() {
    var body = document.body;
    if (!body) return null;
    for (var i = 0; i < MOTION_PROFILES.length; i++) {
      // `body` is one body class or a list of them (a page and its test copy)
      var names = [].concat(MOTION_PROFILES[i].body);
      for (var n = 0; n < names.length; n++) {
        if (body.classList.contains(names[n])) return MOTION_PROFILES[i];
      }
    }
    return null;
  }

  // scrollSlider: pin the section and let the scroll pick the slide.
  var SCROLL_SLIDER_MQ = '(min-width: 1000px) and (prefers-reduced-motion: no-preference)';

  function bindScrollSlider(section, slider) {
    if (section.hasAttribute('data-ls-scroll-slider')) return;
    section.setAttribute('data-ls-scroll-slider', '');
    var mq = window.matchMedia ? window.matchMedia(SCROLL_SLIDER_MQ) : null;
    var raf = 0;

    function drive() {
      raf = 0;
      var api = slider.__neoSliderDrive;
      if (!api || !section.isConnected) return;
      var r = section.getBoundingClientRect();
      var span = r.height - window.innerHeight;
      var p = span > 0 ? Math.min(1, Math.max(0, -r.top / span)) : 0;
      api.goTo(Math.min(api.count - 1, Math.floor(p * api.count)));
      section.style.setProperty('--ls-scroll-progress', p.toFixed(3));
    }
    function onScroll() { if (!raf) raf = window.requestAnimationFrame(drive); }
    function sync() {
      var on = !!(mq && mq.matches);
      var api = slider.__neoSliderDrive;
      section.classList.toggle('ls-motion-scrollslider', on);
      if (on) section.style.setProperty('--ls-scroll-steps', String(api ? api.count : 3));
      if (api) api.hold(on);
      if (on) {
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        onScroll();
      } else {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
    }
    // The slider hydrates after this pass; wait for its hook.
    (function waitForSlider(tries) {
      if (slider.__neoSliderDrive || tries > 40) {
        sync();
        if (mq) {
          if (mq.addEventListener) mq.addEventListener('change', sync);
          else if (mq.addListener) mq.addListener(sync);
        }
        return;
      }
      window.setTimeout(function () { waitForSlider(tries + 1); }, 50);
    })(0);
  }

  function applyMotionProfile(root) {
    var profile = matchMotionProfile();
    if (!profile) return;
    if (profile.tabFade && document.body) document.body.classList.add('ls-motion-tabfade');
    var scope = root && root.querySelectorAll ? root : document;
    var sections = scope.querySelectorAll('[data-neo-section]');

    for (var s = 0; s < sections.length; s++) {
      var section = sections[s];
      if (section.hasAttribute('data-ls-motion')) continue; // re-hydrate
      section.setAttribute('data-ls-motion', '');
      var content = section.querySelector('.aico-neo-section__content') || section;
      var id = section.getAttribute('data-neo-section');

      // Hero: still, like the prototype (and a transformed ancestor would
      // break the pinned photo's position:fixed).
      var heroImage = (profile.heroStill && s === 0)
        ? section.querySelector('.content-type-neo-image--overlay')
        : null;
      if (profile.heroStill && (heroImage || section.querySelector('.ls-hero-photo'))) {
        var authored = section.querySelectorAll('.ls-reveal, .ls-reveal-group');
        for (var h = 0; h < authored.length; h++) stripRevealClasses(authored[h]);
        if (heroImage) heroImage.classList.add('ls-motion-pinned');
        continue;
      }

      if (profile.scrollZoom && profile.scrollZoom[id]) {
        var zooms = section.querySelectorAll(profile.scrollZoom[id]);
        for (var sz = 0; sz < zooms.length; sz++) zooms[sz].classList.add('ls-motion-scrollzoom');
      }
      if (profile.windowOpen && profile.windowOpen[id]) {
        var wins = section.querySelectorAll(profile.windowOpen[id]);
        for (var wo = 0; wo < wins.length; wo++) wins[wo].classList.add('ls-motion-window');
      }
      if (profile.scrollSlider && profile.scrollSlider.indexOf(id) !== -1) {
        var driven = section.querySelector('[data-neo-slider]');
        if (driven) bindScrollSlider(section, driven);
        continue; // no rise-in inside a pinned section
      }

      // Zoom section: its content blocks zoom in whole, no inner stagger.
      if (profile.zoom && profile.zoom.indexOf(id) !== -1) {
        var inner = section.querySelectorAll('.ls-reveal, .ls-reveal-group');
        for (var z = 0; z < inner.length; z++) stripRevealClasses(inner[z]);
        for (var zc = 0; zc < content.children.length; zc++) {
          var block = content.children[zc];
          if (block.tagName === 'STYLE' || block.tagName === 'SCRIPT') continue;
          block.classList.add('ls-reveal', 'ls-reveal--zoom');
        }
        continue;
      }

      // Rails: the track reveals its items one after another from the right.
      if (profile.rails) {
        var tracks = section.querySelectorAll(profile.rails);
        for (var t = 0; t < tracks.length; t++) {
          var items = tracks[t].querySelectorAll('.ls-reveal, .ls-reveal-group');
          for (var ti = 0; ti < items.length; ti++) stripRevealClasses(items[ti]);
          tracks[t].classList.remove('ls-reveal');
          tracks[t].classList.add('ls-reveal-group', 'ls-reveal--x');
        }
      }

      // Everything else: any top-level block not already covered rises in. A
      // section staff add in the builder lands here.
      for (var c = 0; c < content.children.length; c++) {
        var top = content.children[c];
        if (top.tagName === 'STYLE' || top.tagName === 'SCRIPT') continue;
        if (hasRevealInside(top)) continue;
        top.classList.add('ls-reveal');
      }
    }
  }

  function revealItemsOf(group) {
    var items = [];
    for (var i = 0; i < group.children.length; i++) {
      var kid = group.children[i];
      if (kid.nodeType !== 1 || kid.tagName === 'STYLE' || kid.tagName === 'SCRIPT') continue;
      if (kid.classList.contains('ls-reveal') || kid.classList.contains('ls-reveal-group')) continue;
      items.push(kid);
    }
    return items;
  }

  function collectRevealTargets(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var targets = [];
    var singles = scope.querySelectorAll('.ls-reveal');
    var groups = scope.querySelectorAll('.ls-reveal-group');
    var i;
    for (i = 0; i < singles.length; i++) {
      if (singles[i].hasAttribute('data-ls-reveal')) continue; // already bound (re-hydrate)
      targets.push({ node: singles[i], items: [singles[i]] });
    }
    for (i = 0; i < groups.length; i++) {
      if (groups[i].hasAttribute('data-ls-reveal')) continue;
      var items = revealItemsOf(groups[i]);
      if (items.length) targets.push({ node: groups[i], items: items });
    }
    return targets;
  }

  /**
   * Reveal items that are scroll-snap targets move their snap areas while
   * they are offset (Chrome snaps to the TRANSFORMED box): a rail whose cards
   * start 120px to the right scrolled itself 120px and drifted back as they
   * slid in. So a horizontal group inside a snapping scroller turns snapping
   * off from the moment its items are hidden until the last item has landed.
   */
  function snapScrollerFor(node) {
    if (!window.getComputedStyle) return null;
    var el = node;
    while (el && el !== document.body) {
      var cs = getComputedStyle(el);
      if ((cs.overflowX === 'auto' || cs.overflowX === 'scroll') &&
          cs.scrollSnapType && cs.scrollSnapType !== 'none') return el;
      el = el.parentElement;
    }
    return null;
  }

  function suspendSnapForReveal(target) {
    if (!target.node.classList.contains('ls-reveal--x')) return;
    var host = snapScrollerFor(target.node);
    if (!host) return;
    target.snapHost = host;
    target.snapInline = host.style.getPropertyValue('scroll-snap-type');
    host.style.setProperty('scroll-snap-type', 'none');
  }

  function resumeSnapAfterReveal(target, lastDelay) {
    var item = target.items[target.items.length - 1];
    var dur = 0;
    if (item && window.getComputedStyle) {
      var list = String(getComputedStyle(item).transitionDuration || '0s').split(',');
      for (var i = 0; i < list.length; i++) {
        var v = parseFloat(list[i]) * (/ms\s*$/.test(list[i]) ? 1 : 1000);
        if (!isNaN(v) && v > dur) dur = v;
      }
    }
    setTimeout(function () {
      if (target.snapInline) target.snapHost.style.setProperty('scroll-snap-type', target.snapInline);
      else target.snapHost.style.removeProperty('scroll-snap-type');
    }, lastDelay + dur + 60);
  }

  function enhanceScrollReveal(root) {
    if (!('IntersectionObserver' in window)) return;
    applyMotionProfile(root);
    var targets = collectRevealTargets(root);
    if (!targets.length) return;

    var observer = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        if (!entries[e].isIntersecting) continue;
        var seen = entries[e].intersectionRect ? entries[e].intersectionRect.height : 0;
        if (entries[e].intersectionRatio < REVEAL_RATIO && seen < REVEAL_MIN_PX) continue;
        var node = entries[e].target;
        observer.unobserve(node);
        var target = targets[+node.getAttribute('data-ls-reveal')];
        if (!target) continue;
        var timing = revealTiming(node);
        var lastDelay = 0;
        for (var k = 0; k < target.items.length; k++) {
          var delay = Math.min(k * timing.step, timing.cap);
          lastDelay = delay;
          target.items[k].style.setProperty('--ls-reveal-delay', delay + 'ms');
          target.items[k].classList.add('ls-reveal-in');
        }
        if (target.snapHost) resumeSnapAfterReveal(target, lastDelay);
      }
    }, { rootMargin: '0px 0px -10% 0px', threshold: REVEAL_THRESHOLDS });

    for (var t = 0; t < targets.length; t++) {
      targets[t].node.setAttribute('data-ls-reveal', String(t));
      // Suspend BEFORE the hidden state is applied, so the scroller never
      // snaps to the offset start position in the first place.
      suspendSnapForReveal(targets[t]);
      for (var k = 0; k < targets[t].items.length; k++) {
        targets[t].items[k].classList.add('ls-reveal-pending');
      }
      observer.observe(targets[t].node);
    }
  }

  // ============================================================
  // 14b. anchor-nav — the tab whose section is in view
  //
  // Upstream underlines the tab of the section currently on screen
  // (AnchorNavRenderer, ElementRenderer.tsx:1090-1121 for the tracking and
  // :1304-1309 for the underline). Nothing marked the active tab here, so on a
  // long page the bar said where you could go but never where you were.
  //
  // Mirrored, not reinvented: ONE IntersectionObserver per bar over the target
  // sections, rootMargin '-20% 0px -70% 0px', threshold 0, and within a batch
  // the last intersecting entry wins -- the same order upstream's for-loop
  // produces. An observer, not a scroll handler: nothing reads layout per
  // frame, and the browser does the work off the main thread.
  //
  // The state lives on `aria-current`, and the stylesheet paints from that same
  // attribute, so what a screen reader announces and what a sighted visitor
  // sees can never drift apart. Without JavaScript (or IntersectionObserver)
  // the bar stays exactly what it is today: plain links, no active tab.
  // ============================================================

  /** Click marks immediately, as upstream's handleTabClick does. */
  function bindAnchorTabClick(tab, setActive) {
    tab.addEventListener('click', function () { setActive(tab); });
  }

  /** Idempotent via data-neo-anchor-bound, like hydrateCountdowns. */
  function bindAnchorNav(bar) {
    if (bar.getAttribute('data-neo-anchor-bound') === '1') return;
    bar.setAttribute('data-neo-anchor-bound', '1');

    var tabs = bar.querySelectorAll('.neo-anchor-nav__tab');
    if (!tabs.length) return;

    var byTargetId = {};
    var targets = [];
    for (var i = 0; i < tabs.length; i++) {
      var href = tabs[i].getAttribute('href') || '';
      if (href.charAt(0) !== '#' || href.length < 2) continue;
      var id = href.slice(1);
      // Two tabs pointing at one section: the first wins, so the observer
      // cannot flip between them while that section is the one in view.
      if (byTargetId[id]) continue;
      var target = document.getElementById(id);
      if (!target) continue;
      byTargetId[id] = tabs[i];
      targets.push(target);
    }

    var active = null;
    function setActive(tab) {
      if (!tab || tab === active) return;
      if (active) active.removeAttribute('aria-current');
      tab.setAttribute('aria-current', 'true');
      active = tab;
    }

    // Upstream seeds `activeTabId` with the FIRST tab, before anything has
    // scrolled, whether or not that tab resolves to a section.
    setActive(tabs[0]);
    for (var c = 0; c < tabs.length; c++) bindAnchorTabClick(tabs[c], setActive);

    if (!targets.length) return;

    var observer = new IntersectionObserver(function (entries) {
      for (var e = 0; e < entries.length; e++) {
        if (!entries[e].isIntersecting) continue;
        setActive(byTargetId[entries[e].target.id]);
      }
    }, { rootMargin: '-20% 0px -70% 0px', threshold: 0 });

    for (var t = 0; t < targets.length; t++) observer.observe(targets[t]);
  }

  function hydrateAnchorNavs(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    if (typeof IntersectionObserver !== 'function') return;
    var bars = root.querySelectorAll('[data-neo-anchor-nav]');
    for (var b = 0; b < bars.length; b++) bindAnchorNav(bars[b]);
  }

  // ============================================================
  // 15. Theme slots
  //
  // Some page furniture cannot be authored in aiconeo at all: the jersey
  // configurator (sections/jersey-configurator.liquid) resolves products,
  // variants, section blocks and metafields in Liquid, so it exists only as a
  // server-rendered Shopify section. /pages/shop is a Neo document, and the
  // content team still wants to decide WHERE inside that document it sits.
  //
  // The contract is deliberately generic, so other sections can reuse it:
  //
  //   theme side — the section root carries `data-ls-slot-source="<name>"`.
  //                assets/content-builder-neo.css hides it until it is placed,
  //                and that stylesheet only loads on pages that HAVE Neo
  //                content, so an ordinary page renders the section as usual.
  //   neo side   — the author drops a Custom HTML element holding
  //                `<div data-ls-slot="<name>">…</div>` (the class form
  //                `.ls-slot-<name>` is accepted as a second marker) wherever
  //                the section belongs. Its inner markup is a placeholder for
  //                the builder canvas only — it is discarded on the storefront.
  //
  // At hydrate the source node is MOVED into the first matching slot. Moving a
  // server-rendered node instead of re-rendering it in JS is the whole point:
  // the renderer produces HTML strings only, and this markup comes from Liquid.
  // jersey-configurator.js binds its listeners to the elements themselves
  // (`init(root)` per `[data-jc]`), so the move is safe before or after init.
  //
  // Emptying the slot first also clears a STALE copy: in the theme editor a
  // re-rendered section reappears inside its `#shopify-section-*` wrapper as a
  // fresh, unplaced node while the previously moved one still sits in the slot.
  //
  // No slot found → the source is revealed where the template put it, i.e.
  // after the Neo content. A missing or mistyped marker must never make a
  // section vanish, so the same reveal also runs on `load` and on a 6s timer —
  // that covers a failed document fetch or a render that never happened.
  //
  // Called from the hydrate wrapper because that is the one place BOTH render
  // paths meet: repaintInlineData() below wipes #aico-content (undoing any
  // earlier placement), and the >64KB URL fallback in
  // snippets/aico-neo-content.liquid renders much later still.
  // ============================================================

  var SLOT_SOURCE_SELECTOR = '[data-ls-slot-source]';
  var SLOT_PLACED_CLASS = 'is-placed';

  /** Slot names reach a CSS selector — keep them to the safe alphabet. */
  var SLOT_NAME_RE = /^[A-Za-z0-9_-]+$/;

  var SLOT_SLOTTED_CLASS = 'is-slotted';

  // 15d. Optional slots
  //
  // The template-position reveal exists so a mistyped marker never makes a
  // section vanish. For a section whose ONLY place is inside the Neo document
  // (the home next-match band, task 139779) that reveal is wrong: the author
  // switching the marker off — eye toggle on it or on the photo it sits on
  // (§39), section «Visible until», deleting it — must switch the section off,
  // not drop it below the Neo content. Such a source carries
  // `data-ls-slot-optional`; a render that finds no marker for it stamps
  // `.is-slot-off`, which keeps it out of the safety net (it is never
  // `.is-placed`, so content-builder-neo.css keeps it display:none). A later
  // render that does find the marker places it as usual. Section scripts that
  // reveal themselves check the class (next-match-js).
  var SLOT_OFF_CLASS = 'is-slot-off';
  var SLOT_OPTIONAL_ATTR = 'data-ls-slot-optional';

  /**
   * The builder's native `theme-slot` element ({slot, label}) — the marker
   * without a Custom HTML element. The label and the dashed chip are canvas
   * furniture; the storefront gets the bare mount point, and a bad name renders
   * nothing (it would otherwise reach a selector).
   */
  Neo.registerType('theme-slot', function (el) {
    var name = String((el && el.content && el.content.slot) || '').trim();
    if (!SLOT_NAME_RE.test(name)) return '';
    return viewportStyleTags(el) +
      '<div class="neo-theme-slot" data-ls-slot="' + escapeAttr(name) + '"' + dataElAttr(el) +
      styleAttrOf(pickStyles(el, 'desktop')) + '></div>';
  });

  /**
   * A slot dropped onto an image (renderImageWithChildren) makes that image the
   * section's cover photo: the photo belongs to the section, so it is shown
   * exactly while the section is. Sections that stay `hidden` until their data
   * lands (next-match: no upcoming fixture → never revealed) would otherwise
   * leave a bare photo on the page. Mirrors the source's `hidden` attribute
   * onto the image and follows later changes.
   */
  function syncSlotPhotoHost(slot, source) {
    var host = slot.closest ? slot.closest('.content-type-neo-image--overlay') : null;
    if (slot.lsHostObserver) {
      slot.lsHostObserver.disconnect();
      slot.lsHostObserver = null;
    }
    if (!host) return;
    var apply = function () {
      if (source.hasAttribute('hidden')) host.setAttribute('hidden', '');
      else host.removeAttribute('hidden');
    };
    apply();
    if (typeof MutationObserver === 'function') {
      slot.lsHostObserver = new MutationObserver(apply);
      slot.lsHostObserver.observe(source, { attributes: true, attributeFilter: ['hidden'] });
    }
  }

  /**
   * Descendants before ancestors (document order otherwise). A band nested in a
   * section root — the match-centre hero band inside `match-center` — is moved
   * into ITS slot before the root moves, so neither move depends on where the
   * other slot sits in the document.
   */
  function sortDeepestFirst(nodeList) {
    var items = [];
    for (var i = 0; i < nodeList.length; i++) {
      var depth = 0;
      for (var n = nodeList[i].parentNode; n; n = n.parentNode) depth++;
      items.push({ node: nodeList[i], depth: depth, index: i });
    }
    items.sort(function (a, b) {
      return b.depth - a.depth || a.index - b.index;
    });
    var out = [];
    for (var j = 0; j < items.length; j++) out.push(items[j].node);
    return out;
  }

  function findThemeSlot(scope, name) {
    if (!scope || !scope.querySelector) return null;
    return (
      scope.querySelector('[data-ls-slot="' + name + '"]') ||
      scope.querySelector('.ls-slot-' + name)
    );
  }

  /**
   * Move every `[data-ls-slot-source]` into its marker inside `root`.
   * Idempotent: a source already sitting in a filled slot is skipped, so
   * repeated hydrates (inline repaint, URL fallback, editor reloads) are free.
   * Logs ONE aggregated line per call.
   */
  function placeThemeSlotSources(root) {
    var scope = root && root.querySelector ? root : document;
    var sources = sortDeepestFirst(document.querySelectorAll(SLOT_SOURCE_SELECTOR));
    if (!sources.length) return;

    var placed = [];
    var unplaced = [];
    var off = [];
    // Moves only: a re-hydrate that finds everything already in place must not
    // make consumers re-init for nothing (see the event at the end).
    var moved = 0;

    for (var i = 0; i < sources.length; i++) {
      var source = sources[i];
      // Skip only sources that already sit in a filled slot. A source merely
      // REVEALED by the safety net (load / 6s timer fired before a slow URL-
      // fallback render) is still eligible to be moved into its slot.
      if (source.parentNode && source.parentNode.classList &&
          source.parentNode.classList.contains('is-filled')) continue;

      var name = (source.getAttribute('data-ls-slot-source') || '').trim();
      var slot = SLOT_NAME_RE.test(name) ? findThemeSlot(scope, name) : null;

      if (!slot && source.hasAttribute(SLOT_OPTIONAL_ATTR)) {
        // Also undoes a safety-net reveal that beat a slow URL-path render.
        source.classList.remove(SLOT_PLACED_CLASS, SLOT_SLOTTED_CLASS);
        source.classList.add(SLOT_OFF_CLASS);
        off.push(name || '(unnamed)');
        continue;
      }
      source.classList.remove(SLOT_OFF_CLASS);

      if (slot && slot !== source && !slot.contains(source)) {
        slot.textContent = '';
        slot.removeAttribute('style');
        slot.classList.add('is-filled');
        slot.appendChild(source);
        source.classList.add(SLOT_SLOTTED_CLASS);
        syncSlotPhotoHost(slot, source);
        placed.push(name);
        moved += 1;
      } else if (slot) {
        // Already sitting in its slot — nothing to move.
        source.classList.add(SLOT_SLOTTED_CLASS);
        placed.push(name);
      } else {
        unplaced.push(name || '(unnamed)');
      }

      source.classList.add(SLOT_PLACED_CLASS);
    }

    if (!placed.length && !unplaced.length && !off.length) return;
    if (window.console && console.info) {
      console.info(
        'content-builder-neo-ext: theme slots — placed: ' +
          (placed.length ? placed.join(', ') : 'none') +
          '; shown in template position: ' +
          (unplaced.length ? unplaced.join(', ') : 'none') +
          (off.length ? '; switched off in the document: ' + off.join(', ') : '')
      );
    }

    // 15a. Late placement signal
    //
    // On the inline-metafield path this runs before DOMContentLoaded, so every
    // consumer measures its section in the final position. The >64KB URL
    // fallback in snippets/aico-neo-content.liquid and theme-editor
    // `shopify:section:load` re-renders both move nodes AFTER those scripts
    // initialised — a section that measured geometry (GSAP pin in ls-tv.js,
    // the players slider track) is then wrong until something re-inits it.
    // Announce the move so those sections can recover; `placed` lets a
    // listener ignore moves it does not care about.
    if (!moved) return;
    try {
      document.dispatchEvent(new CustomEvent('ls:theme-slots-placed', {
        detail: { placed: placed, unplaced: unplaced }
      }));
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: theme slot event failed', err);
      }
    }
  }

  /**
   * Safety net: reveal anything still hidden. Runs on `load` and on a timer so
   * a document that never rendered (fetch error, empty metafield) cannot leave
   * a section invisible.
   */
  function revealThemeSlotSources() {
    var pending = document.querySelectorAll(
      SLOT_SOURCE_SELECTOR + ':not(.' + SLOT_PLACED_CLASS + '):not(.' + SLOT_OFF_CLASS + ')'
    );
    for (var i = 0; i < pending.length; i++) {
      pending[i].classList.add(SLOT_PLACED_CLASS);
    }
    clearNeoLoadingState();
  }

  /**
   * `html.ls-neo-loading` is set by snippets/aico-neo-content.liquid at parse
   * time and hides the footer until the Neo body exists (content-builder-neo.css
   * "Loading state"). Cleared after every hydrate and by every safety net.
   */
  function clearNeoLoadingState() {
    document.documentElement.classList.remove('ls-neo-loading');
  }

  /**
   * A URL-path document is still being fetched (snippets/aico-neo-content.liquid
   * sets the flag at parse time, clears it on render and on failure). Revealing
   * the sources in template position then would flash them there and move them
   * — reloading their iframes — once the document lands.
   */
  function neoRenderPending() {
    return window.lsNeoRenderPending === true;
  }

  window.addEventListener('load', function () {
    if (!neoRenderPending()) revealThemeSlotSources();
  });
  window.setTimeout(function () {
    if (!neoRenderPending()) revealThemeSlotSources();
    // Last resort for theme binds (§15b): no hydrate and no reported failure
    // within 6s. Not on `load` — a slow URL-fallback document may still be on
    // its way then, and would apply its binds after consumers initialised.
    if (!window.lsThemeBindsApplied) announceThemeBinds(false, [], []);
  }, 6000);
  // Absolute last resort, pending render or not: nothing stays hidden past 8s.
  window.setTimeout(revealThemeSlotSources, 8000);

  // Theme editor: a re-rendered section comes back unplaced inside its
  // `#shopify-section-*` wrapper, so place it again against the mounted page.
  document.addEventListener('shopify:section:load', function (event) {
    // §28: a re-rendered section may carry a secondary mount of its own (the
    // footer's aiconeo chrome). Its fresh mount has no `data-ls-neo-done`.
    try {
      renderSecondaryMounts((event && event.target) || document);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: secondary mount re-render failed', err);
      }
    }
    var mount = document.getElementById('aico-content');
    try {
      placeThemeSlotSources(mount || document);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: theme slot placement failed', err);
      }
    }
    // A re-rendered section comes back with the theme's own copy: bind again.
    // (Function declaration below, hoisted.)
    if (mount && mount.firstChild) {
      try {
        applyThemeBinds(mount);
      } catch (err) { /* binds are best-effort in the editor */ }
    }
  });

  // ============================================================
  // 15b. Theme binds — Neo copy and photos into theme-rendered markup
  //
  // Slots (§15) move a theme section INTO the Neo document. Some pages need
  // the opposite: the markup must stay the theme's (Tous au stade — pinned GSAP
  // choreography, word splits), but the club wants to edit its texts and photos
  // in aiconeo. The Neo document then carries a "content sheet":
  //
  //   neo side   — any element whose className holds `ls-bind ls-bind--<key>`
  //                (heading / text / image / button). The sheet usually lives
  //                in a container with className `ls-bind-source`, which
  //                content-builder-neo.css hides on the storefront; the
  //                builder still shows it, so staff edit it like any block.
  //   theme side — `data-ls-bind="<key>"` + `data-ls-bind-kind="<kind>"`:
  //                  text   → textContent (NEVER innerHTML: Neo copy is
  //                           untrusted, see CLAUDE.md security). Line breaks
  //                           in the source (<br>, paragraphs) become <br>
  //                           nodes built with createElement. Default kind.
  //                  src    → <img> src (srcset/sizes dropped, and the
  //                           <source>s of a wrapping <picture>)
  //                  bg     → style.backgroundImage = url("…")
  //                  href   → <a> href, http(s) or relative only
  //                  poster → <video> poster
  //                Several theme nodes may share one key (e.g. an `href` bind
  //                on the <a> and a `text` bind on its label).
  //
  // A key with no source, or an empty source, leaves the theme markup as it
  // was — the theme's own copy is the fallback. Applied nodes get
  // `data-ls-bind-applied`. After every pass the document receives
  // `ls:theme-binds-applied` ({detail: {neo, applied, missing}}) and
  // `window.lsThemeBindsApplied` is set, so a consumer that registers late
  // checks the flag instead of waiting. The paths that never render Neo
  // (snippets/aico-neo-content.liquid reveal()/skip, the inline-data repaint
  // finding no content, the load/6s safety net) dispatch the same event with
  // `neo: false`.
  // ============================================================

  var BIND_KEY_RE = /^[A-Za-z0-9_-]+$/;
  var BIND_CLASS_PREFIX = 'ls-bind--';
  var BIND_BLOCK_TAGS = /^(P|DIV|LI|H[1-6]|BLOCKQUOTE|UL|OL)$/;
  var BIND_CONTROL_CHARS = /[\x00-\x1f\x7f]/;

  function announceThemeBinds(neo, applied, missing) {
    window.lsThemeBindsApplied = true;
    try {
      document.dispatchEvent(new CustomEvent('ls:theme-binds-applied', {
        detail: { neo: !!neo, applied: applied || [], missing: missing || [] }
      }));
    } catch (err) { /* CustomEvent unsupported */ }
  }

  /** key → first source element inside root. */
  function collectBindSources(root) {
    var map = {};
    var nodes = root.querySelectorAll('[class*="' + BIND_CLASS_PREFIX + '"]');
    for (var i = 0; i < nodes.length; i++) {
      var classes = nodes[i].classList;
      for (var c = 0; c < classes.length; c++) {
        var cls = classes[c];
        if (cls.indexOf(BIND_CLASS_PREFIX) !== 0) continue;
        var key = cls.slice(BIND_CLASS_PREFIX.length);
        if (BIND_KEY_RE.test(key) && !map[key]) map[key] = nodes[i];
      }
    }
    return map;
  }

  /** Plain text of a source, one entry per visual line. */
  function bindSourceLines(node) {
    var lines = [''];
    (function visit(n) {
      for (var child = n.firstChild; child; child = child.nextSibling) {
        if (child.nodeType === 3) {
          lines[lines.length - 1] += child.nodeValue;
        } else if (child.nodeType === 1) {
          var tag = child.tagName;
          if (tag === 'STYLE' || tag === 'SCRIPT' || tag === 'TEMPLATE') continue;
          if (tag === 'BR') { lines.push(''); continue; }
          var block = BIND_BLOCK_TAGS.test(tag);
          if (block && lines[lines.length - 1] !== '') lines.push('');
          visit(child);
          if (block && lines[lines.length - 1] !== '') lines.push('');
        }
      }
    })(node);
    var out = [];
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i].replace(/\s+/g, ' ').trim();
      if (line) out.push(line);
    }
    return out;
  }

  /** http(s) absolute or scheme-less relative; everything else → ''. */
  function safeBindUrl(value) {
    var url = String(value || '').trim();
    if (!url || BIND_CONTROL_CHARS.test(url)) return '';
    var scheme = /^([A-Za-z][A-Za-z0-9+.-]*):/.exec(url);
    if (!scheme) return url;
    var proto = scheme[1].toLowerCase();
    return proto === 'http' || proto === 'https' ? url : '';
  }

  function bindSourceImageUrl(node) {
    var img = node.tagName === 'IMG' ? node : node.querySelector('img');
    return img ? safeBindUrl(img.getAttribute('src')) : '';
  }

  function bindSourceHref(node) {
    var a = node.tagName === 'A' ? node : node.querySelector('a[href]');
    return a ? safeBindUrl(a.getAttribute('href')) : '';
  }

  /**
   * Root-relative store paths authored in the Neo sheet are locale-less
   * (`/pages/match-center`); on a translated locale Shopify's routes root is
   * `/en/`, so prefix it — otherwise EN visitors land on the French site.
   * Anchors, absolute URLs and paths already carrying the prefix pass through,
   * and so do Shopify's un-localized routes (files on /cdn/, app proxies).
   */
  var UNLOCALIZED_PATH = /^\/(?:cdn|apps|tools|services|checkouts)(?:[\/?#]|$)/;

  /**
   * An editor who pastes the address bar into a link field authors this very
   * shop as an absolute URL (`https://…myshopify.com/pages/news`), which is
   * just as locale-less as a root-relative path (140266). Same-shop URLs — the
   * current host or the shop's myshopify domain — shed their origin so the
   * prefix rule below applies; every other absolute URL is returned untouched.
   */
  function sameShopPath(href) {
    var m = /^https?:\/\/([^\/?#]+)(.*)$/i.exec(href);
    if (!m) return '';
    var host = m[1].toLowerCase();
    if (host !== window.location.host.toLowerCase() && host !== shopDomain()) return '';
    return m[2] || '/';
  }

  function localizeBindHref(href) {
    if (!href) return href;
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (!root || root === '/') return href;
    href = sameShopPath(href) || href;
    if (href.charAt(0) !== '/' || href.charAt(1) === '/') return href;
    var prefix = root.replace(/\/$/, '');
    if (href === prefix || href.indexOf(prefix + '/') === 0 || href.indexOf(prefix + '?') === 0 ||
        href.indexOf(prefix + '#') === 0) return href;
    if (UNLOCALIZED_PATH.test(href)) return href;
    return prefix + href;
  }

  /** localizeBindHref over every `href="…"` attribute of a rendered HTML string
   *  (escapeAttr output, so always double-quoted; `data-href` is not matched).
   *  Root-relative and absolute hrefs alike; localizeBindHref decides. */
  function localizeStoreHrefs(html) {
    if (typeof html !== 'string' || html.indexOf('href="') === -1) return html;
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (!root || root === '/') return html;
    return html.replace(/(\shref=")((?:https?:\/\/|\/)[^"]*)"/gi, function (m, attr, href) {
      return attr + localizeBindHref(href) + '"';
    });
  }

  function applyBindTarget(target, kind, source) {
    if (kind === 'text') {
      var lines = bindSourceLines(source);
      if (!lines.length) return false;
      while (target.firstChild) target.removeChild(target.firstChild);
      for (var i = 0; i < lines.length; i++) {
        if (i) target.appendChild(document.createElement('br'));
        target.appendChild(document.createTextNode(lines[i]));
      }
      return true;
    }
    if (kind === 'href') {
      var href = localizeBindHref(bindSourceHref(source));
      if (!href) return false;
      target.setAttribute('href', href);
      return true;
    }
    var url = bindSourceImageUrl(source);
    if (!url) return false;
    if (kind === 'src') {
      target.removeAttribute('srcset');
      target.removeAttribute('sizes');
      var picture = target.parentNode;
      if (picture && picture.tagName === 'PICTURE') {
        var alternates = picture.querySelectorAll('source');
        for (var s = 0; s < alternates.length; s++) alternates[s].parentNode.removeChild(alternates[s]);
      }
      target.setAttribute('src', url);
      return true;
    }
    if (kind === 'poster') {
      target.setAttribute('poster', url);
      return true;
    }
    if (kind === 'bg') {
      target.style.backgroundImage = 'url("' + url.replace(/["\\]/g, encodeURIComponent) + '")';
      return true;
    }
    return false;
  }

  // ------------------------------------------------------------
  // 15c. Section feeds — binds by structure
  //
  // Binds (§15b) need a className on every source, so the Neo side ends up a
  // labelled form ("Titre — premier mot") that looks nothing like the page.
  // A feed lets staff edit an ORDINARY native section instead — laid out like
  // a static version of the theme scene — and the theme reads it by position:
  //
  //   theme side — a section root stamped
  //                  data-ls-feed-section="<neo section uuid>"
  //                and, inside it, targets stamped
  //                  data-ls-feed="<type> <n>"  (+ data-ls-bind-kind as §15b)
  //                type = heading | text | button | image, n = 1-based position
  //                of that element type inside the Neo section, in document
  //                order (nested containers, columns, slider slides and the
  //                children of an image all count). `image` is the image
  //                element's own photo, with or without children.
  //   neo side   — nothing: no className, no html, no labels.
  //
  // Values go through the §15b appliers (textContent only, http(s)/relative
  // URLs only, locale prefix on store paths). A feed runs before the binds and
  // a fed target is skipped by them. The Neo section a feed claims gets the
  // runtime class `ls-feed-source` (content-builder-neo.css hides it on the
  // storefront; the builder has no theme script, so staff keep seeing it).
  // A missing section or position leaves the theme's own copy, as a bind does.
  // ------------------------------------------------------------

  var FEED_TYPES = {
    heading: '.content-type-neo-heading',
    text: '.content-type-neo-text',
    button: '.content-type-neo-button',
    image: '.content-type-neo-image'
  };
  var FEED_SECTION_RE = /^[A-Za-z0-9-]+$/;
  var FEED_SPEC_RE = /^(heading|text|button|image)\s+([1-9][0-9]?)$/;

  function applyThemeFeeds(root, applied, missing) {
    var scope = root && root.querySelector ? root : document;
    var hosts = document.querySelectorAll('[data-ls-feed-section]');
    for (var h = 0; h < hosts.length; h++) {
      var id = (hosts[h].getAttribute('data-ls-feed-section') || '').trim();
      if (!FEED_SECTION_RE.test(id)) continue;
      var section = scope.querySelector('[data-neo-section="' + id + '"]');
      if (!section) continue;
      section.classList.add('ls-feed-source');
      var lists = {};
      var targets = hosts[h].querySelectorAll('[data-ls-feed]');
      for (var t = 0; t < targets.length; t++) {
        var target = targets[t];
        var spec = FEED_SPEC_RE.exec((target.getAttribute('data-ls-feed') || '').trim());
        var kind = (target.getAttribute('data-ls-bind-kind') || 'text').trim().toLowerCase();
        var label = 'feed ' + id.slice(0, 8) + ' ' + (spec ? spec[1] + ' ' + spec[2] : '?') + ':' + kind;
        var ok = false;
        if (spec) {
          if (!lists[spec[1]]) lists[spec[1]] = section.querySelectorAll(FEED_TYPES[spec[1]]);
          var source = lists[spec[1]][+spec[2] - 1];
          if (source) {
            try {
              ok = applyBindTarget(target, kind, source);
            } catch (err) {
              ok = false;
            }
          }
        }
        (ok ? applied : missing).push(label);
        if (ok) {
          target.setAttribute('data-ls-bind-applied', '');
          target.setAttribute('data-ls-feed-applied', '');
        }
      }
    }
  }

  function applyThemeBinds(root) {
    var targets = document.querySelectorAll('[data-ls-bind]');
    var applied = [];
    var missing = [];
    applyThemeFeeds(root, applied, missing);
    if (targets.length) {
      var sources = collectBindSources(root && root.querySelector ? root : document);
      for (var i = 0; i < targets.length; i++) {
        var target = targets[i];
        if (target.hasAttribute('data-ls-feed-applied')) continue;
        var key = (target.getAttribute('data-ls-bind') || '').trim();
        var kind = (target.getAttribute('data-ls-bind-kind') || 'text').trim().toLowerCase();
        var source = BIND_KEY_RE.test(key) ? sources[key] : null;
        var ok = false;
        if (source) {
          try {
            ok = applyBindTarget(target, kind, source);
          } catch (err) {
            ok = false;
          }
        }
        (ok ? applied : missing).push(key + ':' + kind);
        if (ok) target.setAttribute('data-ls-bind-applied', '');
      }
      if (window.console && console.info) {
        console.info('content-builder-neo-ext: theme binds — applied: ' +
          (applied.length ? applied.join(', ') : 'none') +
          '; theme fallback: ' + (missing.length ? missing.join(', ') : 'none'));
      }
    }
    announceThemeBinds(true, applied, missing);
  }

  var originalHydrate = Neo.hydrate;

  /**
   * Element hydration for one rendered root.
   *
   * `secondary` (§28) marks a root that is NOT the page document — today the
   * aiconeo site chrome in the footer. Everything that shapes elements runs for
   * both; the PAGE-ONLY steps are skipped, because they are single-mount
   * semantics that belong to `#aico-content` and would misfire from a footer
   * that renders on every page:
   *   - placeThemeSlotSources / applyThemeBinds (§15, §15b): a chrome document
   *     must never move theme sections around or claim to have applied the
   *     page's binds — `announceThemeBinds` is a one-shot page signal.
   *   - clearNeoLoadingState: `html.ls-neo-loading` is the PAGE document's
   *     lock (it hides the whole footer group); clearing it from the footer
   *     would reveal the page mid-paint.
   * The step order is otherwise unchanged from the single-path version.
   */
  function hydrateRoot(root, secondary) {
    originalHydrate(root);
    try {
      hydrateForms(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: form hydration failed', err);
      }
    }
    try {
      runLayoutGuards(root);
      // Web fonts and lazy images can land after hydrate and turn a block that
      // measured empty into a real one; re-check once the page has settled.
      // The data-attribute markers keep both passes idempotent.
      if (document.readyState !== 'complete') {
        window.addEventListener('load', function () {
          try {
            runLayoutGuards(root);
          } catch (e) { /* layout guards are best-effort */ }
        }, { once: true });
      }
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: layout guards failed', err);
      }
    }
    if (!secondary) {
      try {
        placeThemeSlotSources(root);
      } catch (err) {
        if (window.console && console.warn) {
          console.warn('content-builder-neo-ext: theme slot placement failed', err);
        }
      }
      try {
        applyThemeBinds(root);
      } catch (err) {
        if (window.console && console.warn) {
          console.warn('content-builder-neo-ext: theme binds failed', err);
        }
        announceThemeBinds(false, [], []);
      }
    }
    try {
      enhanceProductRails(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: product rail chevrons failed', err);
      }
    }
    try {
      enhancePartnerLogos(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: partner logo sizing failed', err);
      }
    }
    try {
      enhanceMarquees(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: marquee fit failed', err);
      }
    }
    try {
      enhanceNeoMarquees(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: neo marquee fit failed', err);
      }
    }
    try {
      hydrateCountdowns(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: countdown hydration failed', err);
      }
    }
    try {
      hydrateBarDismiss(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: announcement bar hydration failed', err);
      }
    }
    try {
      hydratePopups(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: popup hydration failed', err);
      }
    }
    try {
      hydrateTabPanels(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: tabs hydration failed', err);
      }
    }
    try {
      hydrateContentSliders(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: content-slider hydration failed', err);
      }
    }
    try {
      hydrateTableScrollers(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: table scroller hydration failed', err);
      }
    }
    try {
      hydrateNewsLists(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: news-list hydration failed', err);
      }
    }
    try {
      hydrateCrmLists(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: crm-list hydration failed', err);
      }
    }
    try {
      hydrateLsTvCards(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: LS TV card hydration failed', err);
      }
    }
    try {
      enhanceGridFilters(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: grid filter row failed', err);
      }
    }
    try {
      hydrateShopTheLooks(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: shop-the-look hydration failed', err);
      }
    }
    try {
      hydrateProductGrids(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: product-grid hydration failed', err);
      }
    }
    try {
      enhanceScrollReveal(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: scroll reveal failed', err);
      }
    }
    try {
      hydrateAnchorNavs(root);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: anchor-nav hydration failed', err);
      }
    }
    if (!secondary) clearNeoLoadingState();
  }

  Neo.hydrate = function (root) {
    hydrateRoot(root, false);
  };

  // ============================================================
  // 7. element.className / element.customId
  //
  // aiconeo stamps both onto every element; the engine ignores both. On
  // /pages/ls-is-more that silently killed the page's entire responsive system:
  // the authored CSS is `.lsim-wide, div:has(> .lsim-wide){…}` plus
  // `.lsim-wide{container-type:inline-size}` and @container queries, so with the
  // class dropped no containment context existed, every @container rule was dead
  // and all the pricing grids collapsed to one column. The seven `customId`s are
  // in-page nav anchors (the CSS even ships `[id]{scroll-margin-top:90px}`).
  //
  // Placement matches ElementRenderer exactly:
  //   className → appended to the element's OWN wrapper node, i.e. the same node
  //               that carries its inline styles (`wrapperClasses` there).
  //   customId  → an EXTRA outer div wrapping the whole element
  //               (`node = <div id={customId}>{node}</div>`).
  // That ordering is load-bearing here: the authored `div:has(> .lsim-wide)`
  // twin selector only matches if `.lsim-wide` is a direct child of its wrapper.
  //
  // Applied by decorating the renderer registry itself, so it covers engine
  // types and ext types identically, on both render paths, exactly once —
  // the engine's renderElement and this file's renderChild both dispatch
  // through Neo._renderers.
  //
  // KNOWN GAP: a SECTION-LEVEL `columns` element is intercepted by the engine's
  // renderSection before dispatch, so it never passes through the registry and
  // would not be decorated. Neither pilot document does that (nested columns,
  // which do go through renderChild, are covered). Left alone rather than
  // reaching into the engine.
  // ============================================================

  /** Author-controlled, emitted as an id attribute — allow [A-Za-z0-9_-] only. */
  function sanitizeCustomId(value) {
    if (value == null) return '';
    return String(value).replace(/[^A-Za-z0-9_-]/g, '');
  }

  /**
   * Append `cls` to the class list of the first element tag in `html`.
   * Returns the html untouched when there is no element root to stamp.
   */
  // ============================================================
  // 34. interactions: the values that have NO inline form
  //
  // An audit of 23 of this shop's landing pages counted roughly 1'475
  // hand-written `!important` declarations pasted into page content as raw
  // <style> blocks inside `html` elements, where the page's author can neither
  // see nor change them. Twelve of those blocks were missing renderers and are
  // items 23 to 33 above. What was left is the set that no style panel could
  // ever have produced, because a state and a pseudo-element cannot be written
  // into a `style` attribute at all:
  //
  //     :hover               146 rules across 21 of the 23 pages
  //     ::before / ::after   154 rules across 22 pages (the list markers)
  //     transition            69 rules, each with a reduced-motion twin
  //     :focus / :active      15 rules
  //
  // So the builder writes a VALUE on the element (`element.interactions`) and
  // this file owns the RULE. The compiler is mirrored function for function
  // from the builder's `src/components/pagebuilder/interactionModel.ts`, which
  // carries the same pointer back here; when a rule changes on one side it has
  // to change on the other, or a page looks one way in the builder and another
  // way on the shop. Same arrangement as quoteModel / tableModel.
  //
  // WHY IT HANGS OFF decorateElementHtml AND NOT THE INDIVIDUAL RENDERERS:
  // decorateRegistry (below) already wraps EVERY registered type, the engine's
  // own `text` / `heading` / `image` included. A list lives inside a `text`
  // element, which this file does not render, so per-renderer wiring would
  // have reached neither the markers nor two thirds of the hover targets.
  //
  // WHY EVERY DECLARATION CARRIES `!important`: an inline style attribute
  // outranks every selector, and these renderers write one on every element.
  // The `button` renderer above puts `background-color`, `color`, `border` and
  // `transition: all 0.2s ease` inline (its `computed` bag), so a hover rule
  // without `!important` would lose to it and draw nothing at all.
  // `mobileStyleTag` carries it for exactly the same reason.
  //
  // WHY THE SELECTOR IS `[data-neo-el]`: that attribute already marks THE
  // STYLED NODE here and has shipped on that contract (mobileStyleTag). For a
  // `button` it deliberately sits on the inner <a> rather than the wrapper,
  // because the <a> is the node that carries the paint, so a hover background
  // lands where the author expects it with nothing new to keep in sync.

  var INTERACTION_TRANSITIONED = ['background-color', 'color', 'border-color', 'box-shadow', 'transform'];
  var INTERACTION_EASINGS = ['ease', 'ease-in', 'ease-out', 'ease-in-out', 'linear'];
  var INTERACTION_SHAPES = ['square', 'disc', 'dash', 'none'];

  /**
   * A colour is interpolated into a <style> block, where there is no attribute
   * escaping to fall back on, so it is VALIDATED rather than escaped: a value
   * carrying `}` or `<` would close the rule or the tag. Accepts the forms a
   * colour picker produces and rejects everything else. Same reasoning as
   * `safeId` above, which exists so an id cannot break out of its selector.
   */
  function safeCssColor(value) {
    if (typeof value !== 'string') return undefined;
    var raw = value.replace(/^\s+|\s+$/g, '');
    if (!raw || raw.length > 64) return undefined;
    if (/^#[0-9a-f]{3,8}$/i.test(raw)) return raw;
    if (/^(?:rgb|rgba|hsl|hsla)\(\s*[0-9a-z%.,\s/-]+\s*\)$/i.test(raw)) return raw;
    if (/^var\(--[a-z0-9-]+(?:,\s*[#a-z0-9%.,\s()-]+)?\)$/i.test(raw)) return raw;
    if (/^[a-z]+$/i.test(raw)) return raw;
    return undefined;
  }

  /** A shadow is a free-form list of lengths and colours: allowlist the characters. */
  function safeCssShadow(value) {
    if (typeof value !== 'string') return undefined;
    var raw = value.replace(/^\s+|\s+$/g, '');
    if (!raw || raw.length > 200) return undefined;
    if (/[<>{};\\*]/.test(raw)) return undefined;
    if (!/^[#a-z0-9(),.%\s/-]+$/i.test(raw)) return undefined;
    return raw;
  }

  function safeCssPx(value, fallback, max) {
    if (typeof value !== 'number' || !isFinite(value)) return fallback;
    return Math.min(Math.max(Math.round(value), 0), max);
  }

  function safeCssEasing(value) {
    return INTERACTION_EASINGS.indexOf(value) !== -1 ? value : 'ease';
  }

  function safeMarkerShape(value) {
    return INTERACTION_SHAPES.indexOf(value) !== -1 ? value : undefined;
  }

  function interactionDecl(property, value) {
    return property + ':' + value + ' !important';
  }

  function hoverPaintDeclarations(hover) {
    var out = [];
    var background = safeCssColor(hover.backgroundColor);
    var color = safeCssColor(hover.color);
    var borderColor = safeCssColor(hover.borderColor);
    var shadow = safeCssShadow(hover.boxShadow);
    if (background) out.push(interactionDecl('background-color', background));
    if (color) out.push(interactionDecl('color', color));
    if (borderColor) out.push(interactionDecl('border-color', borderColor));
    if (shadow) out.push(interactionDecl('box-shadow', shadow));
    return out;
  }

  function interactionTransitionValue(motion) {
    var duration = safeCssPx(motion && motion.duration, 200, 5000);
    var easing = safeCssEasing(motion && motion.easing);
    return INTERACTION_TRANSITIONED.map(function (property) {
      return property + ' ' + duration + 'ms ' + easing;
    }).join(',');
  }

  /**
   * `:focus-visible`, never `:focus`: the ring is for keyboard navigation, and
   * a ring that also appears on a mouse click is the thing authors then paste
   * `outline:none` to remove, which is how a page loses its focus ring
   * entirely.
   */
  function focusRingRule(selector, focus) {
    var color = safeCssColor(focus.color);
    if (!color) return null;
    var width = safeCssPx(focus.width, 2, 16) || 2;
    var offset = safeCssPx(focus.offset, 2, 16);
    return selector + ':focus-visible{' +
      interactionDecl('outline', width + 'px solid ' + color) + ';' +
      interactionDecl('outline-offset', offset + 'px') + '}';
  }

  /**
   * The list markers.
   *
   * Scoped to `ul` on purpose: an `ol`'s numbers are content, and replacing
   * them with a box would delete information rather than restyle it.
   *
   * `shape: 'none'` is a real choice, not an absence. It strips the browser
   * bullet and draws nothing, which is what a page wants for a list used as a
   * layout (a row of cards marked up as <ul>).
   */
  function markerRules(selector, marker) {
    var shape = safeMarkerShape(marker.shape);
    if (!shape) return [];
    var rules = [selector + ' ul{' + interactionDecl('list-style', 'none') + ';' +
      interactionDecl('padding-left', '0') + '}'];
    if (shape === 'none') return rules;

    var size = safeCssPx(marker.size, 8, 64) || 8;
    var color = safeCssColor(marker.color) || 'currentColor';
    // A dash reads as a rule, not a dot: full width, hairline height.
    var boxWidth = shape === 'dash' ? size * 2 : size;
    var boxHeight = shape === 'dash' ? Math.max(1, Math.round(size / 4)) : size;
    var gutter = boxWidth + size;
    var radius = shape === 'disc' ? '50%' : '0';

    rules.push(selector + ' ul>li{' + interactionDecl('position', 'relative') + ';' +
      interactionDecl('list-style', 'none') + ';' +
      interactionDecl('padding-left', gutter + 'px') + '}');
    // `top` is in `em` so the marker tracks the TEXT it sits beside rather than
    // the list box: on a two-line item the box must align with the first line.
    // 0.65em is the optical centre of a cap-height line at the default 1.5
    // line-height, measured against the hand-written markers being replaced.
    rules.push(selector + ' ul>li::before{' + [
      interactionDecl('content', "''"),
      interactionDecl('position', 'absolute'),
      interactionDecl('left', '0'),
      interactionDecl('top', 'calc(0.65em - ' + Math.round(boxHeight / 2) + 'px)'),
      interactionDecl('width', boxWidth + 'px'),
      interactionDecl('height', boxHeight + 'px'),
      interactionDecl('background', color),
      interactionDecl('border-radius', radius)
    ].join(';') + '}');
    return rules;
  }

  /**
   * Compile one element's `interactions` into CSS rule text, or '' when there
   * is nothing to draw. '' is the case that has to stay free: an element that
   * sets nothing must render exactly as it did before this existed, with no
   * style tag, no attribute and no extra bytes.
   */
  function buildInteractionCss(el) {
    var interactions = el && el.interactions;
    if (!interactions || typeof interactions !== 'object' || Array.isArray(interactions)) return '';
    var id = safeId(el && el.id);
    if (!id) return '';
    var selector = '[data-neo-el="' + id + '"]';
    var rules = [];

    var hover = interactions.hover;
    var paint = (hover && typeof hover === 'object') ? hoverPaintDeclarations(hover) : [];
    var lift = (hover && typeof hover === 'object') ? safeCssPx(hover.lift, 0, 64) : 0;
    if (paint.length) rules.push(selector + ':hover{' + paint.join(';') + '}');

    // Motion lives inside `no-preference` rather than being emitted and then
    // cancelled under `reduce`. Same end state, one direction fewer to get
    // wrong: a reader who asked for less motion is never sent the declaration,
    // so there is nothing left to forget to switch off. A colour change is not
    // motion and stays outside, above.
    if (paint.length || lift) {
      var motionRules = selector + '{' +
        interactionDecl('transition', interactionTransitionValue(interactions.motion)) + '}';
      if (lift) {
        motionRules += selector + ':hover{' +
          interactionDecl('transform', 'translateY(-' + lift + 'px)') + '}';
      }
      rules.push('@media (prefers-reduced-motion: no-preference){' + motionRules + '}');
    }

    if (interactions.focus && typeof interactions.focus === 'object') {
      var ring = focusRingRule(selector, interactions.focus);
      if (ring) rules.push(ring);
    }

    if (interactions.marker && typeof interactions.marker === 'object') {
      rules = rules.concat(markerRules(selector, interactions.marker));
    }

    return rules.length ? '<style>' + rules.join('') + '</style>' : '';
  }

  /**
   * Give the element's own node a `data-neo-el` when it has none.
   *
   * The renderers in THIS file already stamp it (that is what mobileStyleTag
   * selects on), but the engine's own types -- `text`, `heading`, `image`,
   * `video` -- do not, and a list lives inside a `text`. Without this the rule
   * above would be emitted with nothing to select. Modelled on injectClass.
   */
  function injectDataEl(html, id) {
    // Already marked somewhere in this element's own markup -- on the outer tag,
    // or on a STYLED NODE further in. The button renderer is the case that
    // matters: it draws `<div class="content-type-button"><a data-neo-el=…>`,
    // marking the <a> because that is the node its styles are on. Marking the
    // wrapper too would make `[data-neo-el="<id>"]` select TWO nodes, and every
    // rule keyed on that hook -- the tier @media blocks, the interaction rules
    // -- would then apply to both. Measured before this guard: a Contact CTA
    // with `interactions` and a mobile bag took its 13px 24px mobile padding on
    // the <a> AND on the wrapper, +52px of section height at 390px.
    //
    // The leading space is what separates the ATTRIBUTE form that dataElAttr
    // writes from the SELECTOR form `[data-neo-el="…"]` inside a <style> block,
    // which must not count as a mark.
    if (html.indexOf(' data-neo-el="' + id + '"') !== -1) return html;
    var tag = /^<([a-z][\w-]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/i.exec(html);
    if (!tag) return html;
    if (/\sdata-neo-el\s*=/i.test(tag[2])) return html;
    return '<' + tag[1] + tag[2] + ' data-neo-el="' + escapeAttr(id) + '">' + html.slice(tag[0].length);
  }

  function injectClass(html, cls) {
    var tag = /^<([a-z][\w-]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/i.exec(html);
    if (!tag) return html;

    var attrs = tag[2];
    var existing = /\sclass\s*=\s*"([^"]*)"/i.exec(attrs);
    var newAttrs = existing
      ? attrs.replace(existing[0], ' class="' + existing[1] + ' ' + escapeAttr(cls) + '"')
      : attrs + ' class="' + escapeAttr(cls) + '"';

    return '<' + tag[1] + newAttrs + '>' + html.slice(tag[0].length);
  }

  function decorateElementHtml(el, html) {
    if (!html || !el) return html;

    // Renderers in this file prefix a <style> block for styles.mobile; keep it
    // outside the customId wrapper so that wrapper's first child is the
    // element node itself (the authored `div:has(> .lsim-wide)` depends on it).
    var lead = /^\s*(?:<style>[\s\S]*?<\/style>\s*)*/.exec(html);
    var prefix = lead ? lead[0] : '';
    var body = html.slice(prefix.length);

    var cls = el.className ? String(el.className).trim() : '';
    if (cls) body = injectClass(body, cls);

    // §34: the interaction rules, and the hook they select on. Both or
    // neither: a stamped attribute with no rule is dead weight, and a rule
    // with no attribute draws nothing. '' for every element that authors no
    // `interactions`, which is every element saved before this existed, so
    // those documents keep rendering byte for byte what they rendered before.
    var interactionCss = buildInteractionCss(el);
    if (interactionCss) {
      body = injectDataEl(body, safeId(el.id));
      prefix += interactionCss;
    }

    var customId = sanitizeCustomId(el.customId);
    if (customId) body = '<div id="' + escapeAttr(customId) + '">' + body + '</div>';

    return prefix + body;
  }

  // 22. Builder-only fonts (p48 carrier pages)
  //
  // The cms-* documents (scripts/aico-migrate/lib/cms/kit.mjs fontsCss) carry a
  // tiny `<style>/*ls-builder-fonts*/@font-face{… url(storage.aico.swiss/…)}`
  // so the aiconeo builder previews TWK Lausanne / Pineforest. The storefront
  // already declares the same families from assets/fonts.css (preloaded); a
  // later same-descriptor @font-face would win the match and re-download every
  // face from aico storage (plus a swap reflow). Drop that one style block from
  // the `html` renderer output. Landing page 2's data-URI fonts element carries
  // no marker and is untouched.
  (function stripBuilderFonts() {
    var BUILDER_FONTS_MARK = '/*ls-builder-fonts*/';
    var BUILDER_FONTS_RE = /<style\b[^>]*>\s*\/\*ls-builder-fonts\*\/[\s\S]*?<\/style>/gi;
    var originalHtml = Neo._renderers.html;
    if (typeof originalHtml !== 'function') return;
    Neo._renderers.html = function (el) {
      var markup = el && el.content ? el.content.html : null;
      if (typeof markup === 'string' && markup.indexOf(BUILDER_FONTS_MARK) !== -1) {
        var content = {};
        for (var key in el.content) {
          if (Object.prototype.hasOwnProperty.call(el.content, key)) content[key] = el.content[key];
        }
        // An empty <style> rather than nothing: an emptied wrapper div would be
        // collapsed by base.css `div:empty` and shift margins by ~10px.
        content.html = markup.replace(BUILDER_FONTS_RE, '<style></style>');
        var copy = {};
        for (var prop in el) {
          if (Object.prototype.hasOwnProperty.call(el, prop)) copy[prop] = el[prop];
        }
        copy.content = content;
        return originalHtml(copy);
      }
      return originalHtml(el);
    };
  })();

  // styles.mobile for the ENGINE's heading and text (p56). The engine inlines
  // styles.desktop only and these two never passed through this file, so a
  // heading's mobile size/margin shown by the builder's phone preview was
  // dropped on the storefront. Same fix as every renderer here: a scoped
  // @media block on the STYLED node (the <hN> for a heading, the wrapper div
  // for text). Elements without a mobile bag render byte for byte as before.
  // An element authoring `interactions` (§34) used to be skipped here, because
  // the interaction hook stamped data-neo-el on the wrapper div as well and two
  // marked nodes took the tier styles twice. injectDataEl now refuses to mark a
  // second node, so the skip is gone with it -- a heading that authors a hover
  // no longer loses its tablet and mobile bags to that workaround.
  (function honourEngineMobileStyles() {
    ['heading', 'text'].forEach(function (type) {
      var original = Neo._renderers[type];
      if (typeof original !== 'function') return;
      Neo.registerType(type, function (el) {
        var html = original(el);
        var tag = viewportStyleTags(el);
        var id = safeId(el && el.id);
        if (!html || !tag || !id) return html;
        if (type === 'heading') {
          var marked = html.replace(/<(h[1-6])(?=[\s>])/i, '<$1 data-neo-el="' + id + '"');
          if (marked === html) return html;
          return tag + marked;
        }
        return tag + injectDataEl(html, id);
      });
    });
  })();

  // Native text marks (145751). The builder's inline toolbar stores a size,
  // colour, face, weight, slant or background on selected words as
  // `<mark data-text-size="20px">…</mark>` and draws it as inline style on its
  // canvas (aiconeo utils/nativeTextMarks.ts drawNativeTextMarks). This file
  // passed the mark through untouched, so the browser drew the user agent's
  // yellow highlight and ignored the size. Same drawing as the builder:
  //   - <mark data-text-*>: colour (inherit when none), size, face, weight,
  //     slant and background (transparent when none) as inline style;
  //   - <mark data-text-span>: the bare span it stands for;
  //   - <a data-text-color|data-text-line|data-text-weight>: added to its style;
  //   - <p data-align>: text-align; <small data-size>: that CSS absolute size.
  // Markup without these attributes comes back byte for byte.
  (function drawNativeTextMarksInRenderers() {
    var COLOR = '(#[0-9A-Fa-f]{3,8}|rgba?\\(\\s*\\d{1,3}\\s*,\\s*\\d{1,3}\\s*,\\s*\\d{1,3}\\s*(?:,\\s*(?:0|1|0?\\.\\d{1,3})\\s*)?\\)|hsla?\\(\\s*\\d{1,3}\\s*,\\s*\\d{1,3}%\\s*,\\s*\\d{1,3}%\\s*(?:,\\s*(?:0|1|0?\\.\\d{1,3})\\s*)?\\)|[A-Za-z]{3,20})';
    var SIZE = '(\\d{1,3}(?:\\.\\d{1,3})?(?:px|pt|em|rem|%))';
    var WEIGHT = '(normal|bold|[1-9]00)';
    var FACES = {
      'system-ui': 'Söhne, ui-sans-serif, system-ui, -apple-system, &quot;Segoe UI&quot;, Roboto, Ubuntu, Cantarell, &quot;Noto Sans&quot;, sans-serif, &quot;Helvetica Neue&quot;, Arial, &quot;Apple Color Emoji&quot;, &quot;Segoe UI Emoji&quot;, &quot;Segoe UI Symbol&quot;, &quot;Noto Color Emoji&quot;',
      raleway: 'Raleway',
      symbol: 'Symbol',
      'times-new-roman': '&quot;Times New Roman&quot;'
    };
    var MARK_TAG = /<mark((?:\s+(?:data-text-color|data-text-size|data-text-font|data-text-weight|data-text-style|data-text-background)="[^"]*")+)>/g;
    var LINK_TAG = new RegExp('<a\\b([^>]*?)\\sdata-text-color="' + COLOR + '"([^>]*)>', 'g');
    var SPAN_MARK = '<mark data-text-span>';

    function markStyle(attributes) {
      var out = [];
      var color = new RegExp('data-text-color="' + COLOR + '"').exec(attributes);
      out.push('color:' + (color ? color[1] : 'inherit'));
      var size = new RegExp('data-text-size="' + SIZE + '"').exec(attributes);
      if (size) out.push('font-size:' + size[1]);
      var face = /data-text-font="([a-z0-9-]{1,40})"/.exec(attributes);
      if (face && Object.prototype.hasOwnProperty.call(FACES, face[1])) out.push('font-family:' + FACES[face[1]]);
      var weight = new RegExp('data-text-weight="' + WEIGHT + '"').exec(attributes);
      if (weight) out.push('font-weight:' + weight[1]);
      var slant = /data-text-style="(normal|italic)"/.exec(attributes);
      if (slant) out.push('font-style:' + slant[1]);
      var background = new RegExp('data-text-background="' + COLOR + '"').exec(attributes);
      out.push('background-color:' + (background ? background[1] : 'transparent'));
      return out.join(';');
    }

    // Pair every tag of a bare-span mark with its closing tag; unpaired ones go.
    function drawSpanMarks(html) {
      if (html.indexOf(SPAN_MARK) === -1) return html;
      var tokens = [];
      var re = /<mark\b[^>]*>|<\/mark\s*>/gi;
      var m;
      while ((m = re.exec(html))) tokens.push({ tag: m[0], index: m.index });
      var openings = [];
      tokens.forEach(function (t) {
        if (t.tag.charAt(1) !== '/') {
          openings.push(t);
          t.out = t.tag === SPAN_MARK ? '' : t.tag;
          return;
        }
        var open = openings.pop();
        if (!open) { t.out = ''; return; }
        var span = open.tag === SPAN_MARK;
        open.out = span ? '<span>' : open.tag;
        t.out = span ? '</span>' : t.tag;
      });
      var drawn = '';
      var position = 0;
      tokens.forEach(function (t) {
        drawn += html.slice(position, t.index) + t.out;
        position = t.index + t.tag.length;
      });
      return drawn + html.slice(position);
    }

    // Append declarations to an opening <a>'s own style attribute (or add one).
    function addLinkStyle(attributes, added) {
      var style = /\sstyle="([^"]*)"/.exec(attributes);
      if (!style) return '<a' + attributes + ' style="' + added + '">';
      var own = style[1].trim().replace(/[;\s]+$/, '');
      // Already drawn (a container renders its children through this pass too).
      var have = own.split(';');
      if (added.split(';').every(function (d) { return have.indexOf(d) !== -1; })) return '<a' + attributes + '>';
      return '<a' + attributes.replace(style[0], ' style="' + (own ? own + ';' : '') + added + '"') + '>';
    }

    function drawNativeTextMarks(html) {
      if (typeof html !== 'string') return html;
      if (html.indexOf('data-text-') === -1 && html.indexOf('data-align="') === -1 && html.indexOf('<small data-size="') === -1) return html;
      var drawn = drawSpanMarks(html)
        .replace(MARK_TAG, function (_t, attributes) { return '<mark' + attributes + ' style="' + markStyle(attributes) + '">'; })
        .replace(LINK_TAG, function (_t, before, color, after) {
          return addLinkStyle(before + ' data-text-color="' + color + '"' + after, 'color:' + color);
        });
      if (drawn.indexOf('data-text-line="') !== -1 || (drawn.indexOf('<a') !== -1 && drawn.indexOf('data-text-weight="') !== -1)) {
        drawn = drawn.replace(/<a\b([^>]*)>/g, function (open, attributes) {
          var add = [];
          var line = /\sdata-text-line="(none)"/.exec(attributes);
          if (line) add.push('text-decoration:' + line[1]);
          var weight = new RegExp('\\sdata-text-weight="' + WEIGHT + '"').exec(attributes);
          if (weight) add.push('font-weight:' + weight[1]);
          return add.length ? addLinkStyle(attributes, add.join(';')) : open;
        });
      }
      return drawn
        .replace(/<p\b((?:(?!style=)[^>])*?)\sdata-align="(left|right|center|justify)"((?:(?!style=)[^>])*)>/g, '<p$1 data-align="$2" style="text-align:$2"$3>')
        .replace(/<small data-size="(x-small|small)">/g, '<small data-size="$1" style="font-size:$1">');
    }

    Neo.drawNativeTextMarks = drawNativeTextMarks;
    var registry = Neo._renderers;
    Object.keys(registry).forEach(function (type) {
      var original = registry[type];
      if (typeof original !== 'function') return;
      registry[type] = function (el) {
        return drawNativeTextMarks(original.apply(this, arguments));
      };
    });
  })();

  (function decorateRegistry() {
    var registry = Neo._renderers;
    Object.keys(registry).forEach(function (type) {
      var original = registry[type];
      if (typeof original !== 'function' || original.__neoDecorated) return;
      var decorated = function (el) {
        return decorateElementHtml(el, original(el));
      };
      decorated.__neoDecorated = true;
      registry[type] = decorated;
    });
  })();

  // ============================================================
  // Repaint the inline-data path
  //
  // The engine's own bootstrap runs the moment content-builder-neo.js executes:
  // a `defer` script runs with document.readyState === 'interactive', so its
  // `readyState === 'loading'` guard is false and it calls boot() immediately —
  // BEFORE this file has registered anything. An inline-data page would
  // therefore paint without container/linked-form. Repaint once here, now that
  // the registry is complete. Both scripts run back-to-back in the same
  // deferred-script phase, so this is not a visible reflow.
  //
  // The >64KB URL-fallback path renders later (snippets/aico-neo-content.liquid,
  // on DOMContentLoaded) and already picks up the full registry.
  // ============================================================

  (function repaintInlineData() {
    var mount = document.getElementById('aico-content');
    var dataEl = document.getElementById('aico-page-data');
    if (!mount || !dataEl) return;

    var data;
    try {
      data = JSON.parse(dataEl.textContent || dataEl.innerText || '{}');
    } catch (e) {
      revealThemeSlotSources();
      announceThemeBinds(false, [], []);
      return;
    }
    if (!Neo.hasContent(data)) {
      revealThemeSlotSources();
      announceThemeBinds(false, [], []);
      return;
    }

    mount.innerHTML = Neo.render(data);
    Neo.hydrate(mount);
  })();

  // ============================================================
  // 28. Secondary mounts — the aiconeo site chrome
  //
  // `snippets/aico-neo-chrome.liquid` mounts a SECOND Neo document, today the
  // footer's blue band (aiconeo global section, shop metafields
  // `aico_metafields.aico_site_chrome*`). It is independent of the page
  // document in every direction:
  //
  //   - it renders on pages that have no `#aico-content` at all (a product
  //     page), so this pass must not sit behind repaintInlineData's early
  //     return;
  //   - it never touches `html.ls-neo-loading`, `window.lsNeoRenderPending`,
  //     theme slots or theme binds (see hydrateRoot's `secondary` flag) — those
  //     are the page document's single-mount semantics;
  //   - it is idempotent: a mount is stamped `data-ls-neo-done` before any work
  //     starts, so the theme editor's `shopify:section:load` re-render only
  //     picks up the fresh mount that came back with the section.
  //
  // Inline JSON (`#<mount id>-data`) is the normal path and paints
  // synchronously, right here in the deferred-script phase. `data-ls-neo-url`
  // is the >64KB path: the same document as a public JSON file, fetched and
  // indexed by the slot name. That file is the shop-locale copy (the per
  // language resolution lives in the inline metafield keys), so a visitor on
  // another language sees the shop language there — logged, not "fixed", since
  // the file carries no `localeOverrides` to resolve.
  //
  // Failure (bad JSON, empty document, fetch error, renderer throw) hands over
  // to `window.lsChromeFallback` — the snippet's stamper, which replaces the
  // mount with the theme's own `<template data-ls-chrome-fallback>` band. The
  // snippet ALSO runs that stamper from a watchdog, so a failure of this file
  // itself (blocked asset, parse error) still shows the theme band.
  // ============================================================

  var SECONDARY_ATTR = 'data-ls-neo-secondary';
  var SECONDARY_DONE = 'data-ls-neo-done';

  function secondaryFallback(mount, reason, error) {
    if (window.console && console.warn) {
      console.warn('content-builder-neo-ext: secondary mount — ' + reason, error || '');
    }
    try {
      if (typeof window.lsChromeFallback === 'function') {
        window.lsChromeFallback(mount);
        return;
      }
      if (mount && mount.parentNode) mount.parentNode.removeChild(mount);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: secondary fallback failed', err);
      }
    }
  }

  function paintSecondaryMount(mount, data) {
    if (!Neo.hasContent(data)) {
      secondaryFallback(mount, 'document has no sections');
      return;
    }
    try {
      mount.innerHTML = Neo.render(data);
    } catch (err) {
      secondaryFallback(mount, 'render failed', err);
      return;
    }
    mount.setAttribute('data-ls-neo-rendered', '');
    try {
      linkSecondaryHomeImages(mount);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: chrome home link failed', err);
      }
    }
    try {
      hydrateRoot(mount, true);
    } catch (err) {
      // Painted but partly unhydrated: still far better than no footer.
      if (window.console && console.warn) {
        console.warn('content-builder-neo-ext: secondary hydration failed', err);
      }
    }
  }

  /**
   * The builder has no "image linked to a page in the same tab" element (its
   * `image` is a plain figure, its `button` is a link with its own chrome), so
   * the footer crest cannot be made clickable in the CMS. The mount carries the
   * storefront's own home URL; every image the author marked `ls-home-link` —
   * or the footer band's `ls-footer-neo__crest` — is wrapped in an <a> here.
   * Behaviour in the theme, content in the CMS (decision D4). Idempotent: an
   * image already inside a link is skipped.
   */
  function linkSecondaryHomeImages(mount) {
    var url = mount.getAttribute('data-ls-home-url');
    if (!url) return;
    var label = mount.getAttribute('data-ls-home-label') || '';
    var nodes = mount.querySelectorAll('.ls-home-link, .ls-footer-neo__crest');
    for (var i = 0; i < nodes.length; i++) {
      var img = nodes[i].tagName === 'IMG' ? nodes[i] : nodes[i].querySelector('img');
      if (!img || !img.parentNode) continue;
      if (img.closest && img.closest('a')) continue;
      var link = document.createElement('a');
      link.setAttribute('href', url);
      link.className = 'ls-footer-neo__crest-link';
      if (label) link.setAttribute('aria-label', label);
      img.parentNode.insertBefore(link, img);
      link.appendChild(img);
    }
  }

  function renderSecondaryMount(mount) {
    mount.setAttribute(SECONDARY_DONE, '');
    var slot = mount.getAttribute(SECONDARY_ATTR) || 'footer';

    var dataEl = mount.id ? document.getElementById(mount.id + '-data') : null;
    if (dataEl) {
      var data;
      try {
        data = JSON.parse(dataEl.textContent || dataEl.innerText || '{}');
      } catch (err) {
        secondaryFallback(mount, 'inline data is not valid JSON', err);
        return;
      }
      paintSecondaryMount(mount, data);
      return;
    }

    var url = mount.getAttribute('data-ls-neo-url');
    if (!url || typeof window.fetch !== 'function') {
      secondaryFallback(mount, 'no inline data and no document URL');
      return;
    }

    mount.setAttribute('data-ls-neo-fetching', '');
    fetch(url, { priority: 'high' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (doc) {
        mount.removeAttribute('data-ls-neo-fetching');
        var part = doc && doc[slot];
        var sections = part && part.sections;
        var docLocale = doc && doc.shop && doc.shop.locale;
        var pageLang = (document.documentElement.lang || '').split('-')[0].toLowerCase();
        if (docLocale && pageLang && String(docLocale).split(/[-_]/)[0].toLowerCase() !== pageLang) {
          if (window.console && console.info) {
            console.info('content-builder-neo-ext: site chrome served from the shop-locale file (' +
              docLocale + ') on a ' + pageLang + ' page');
          }
        }
        paintSecondaryMount(mount, { contentBuilderNeoJson: { sections: sections || [] } });
      })
      .catch(function (err) {
        mount.removeAttribute('data-ls-neo-fetching');
        secondaryFallback(mount, 'could not load the chrome document', err);
      });
  }

  function renderSecondaryMounts(scope) {
    var root = (scope && scope.querySelectorAll) ? scope : document;
    var mounts = root.querySelectorAll('[' + SECONDARY_ATTR + ']:not([' + SECONDARY_DONE + '])');
    for (var i = 0; i < mounts.length; i++) {
      try {
        renderSecondaryMount(mounts[i]);
      } catch (err) {
        secondaryFallback(mounts[i], 'mount failed', err);
      }
    }
  }

  renderSecondaryMounts(document);

  // ============================================================
  // 19. Locale resolution for the URL-fallback path
  //
  // The <64KB metafield keys (`custom_fields.content_builder_json_<lang>`) are
  // resolved to one language by the aico backend. The >64KB fallback fetches
  // the RAW Neo file instead, which still carries the builder's locale
  // structure:
  //
  //   baseLocale:      'fr_CH'                       (language of the base content)
  //   localeOverrides: { en: { elements: { <id>: { content: {…only differing fields} } },
  //                            seo: {…partial} },
  //                      en_GB: {…} }               (sparse, per locale)
  //
  // The engine only reads `sections`, so without this pass every visitor got
  // the base language regardless of the FR/EN switcher. `resolveLocale` is a
  // line-for-line port of aico's Modules/Storefront/Support/NeoLocaleResolver:
  //
  //   1. requested language IS the base language → nothing to apply;
  //   2. else apply the same-language entry (`en`) first and the exact entry
  //      (`en_GB`) on top, field-level `{…base, …override}` per element id,
  //      recursing into `children`;
  //   3. no entry for the requested language → the shop default locale's
  //      entries (unless the base already speaks it);
  //   4. none → base content.
  //
  // Then `localeOverrides` / `baseLocale` are stripped so the renderer stays
  // locale-unaware. A document WITHOUT the structure passes through untouched
  // (every page authored before translations existed). Mutates and returns the
  // given document — the fetched payload is ours.
  // ============================================================

  function localeNormalize(locale) {
    return String(locale == null ? '' : locale).trim().replace(/-/g, '_');
  }

  function localeLanguage(locale) {
    var normalized = localeNormalize(locale);
    var separator = normalized.indexOf('_');
    return (separator === -1 ? normalized : normalized.slice(0, separator)).toLowerCase();
  }

  function localeSameLanguage(first, second) {
    var language = localeLanguage(first);
    return language !== '' && language === localeLanguage(second);
  }

  /** Entries serving `locale`, weakest first: same-language keys, then the exact key last so it wins the merge. */
  function localeMatchKeys(availableKeys, locale) {
    var exact = null;
    var sameLanguage = [];
    var wanted = localeNormalize(locale).toLowerCase();
    for (var i = 0; i < availableKeys.length; i++) {
      var key = availableKeys[i];
      if (localeNormalize(key).toLowerCase() === wanted) {
        exact = key;
      } else if (localeSameLanguage(key, locale)) {
        sameLanguage.push(key);
      }
    }
    return exact === null ? sameLanguage : sameLanguage.concat([exact]);
  }

  function localePickOverrideKeys(availableKeys, locale, defaultLocale, baseLocale) {
    if (!availableKeys.length || !locale) return [];

    // The base content already speaks the requested language.
    if (baseLocale && localeSameLanguage(locale, baseLocale)) return [];

    var matches = localeMatchKeys(availableKeys, locale);
    if (matches.length) return matches;

    if (defaultLocale && !(baseLocale && localeSameLanguage(defaultLocale, baseLocale))) {
      matches = localeMatchKeys(availableKeys, defaultLocale);
      if (matches.length) return matches;
    }

    return [];
  }

  function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function localeApplyElementOverrides(elements, overridesByElementId) {
    for (var i = 0; i < elements.length; i++) {
      var element = elements[i];
      if (!isPlainObject(element)) continue;

      var elementId = element.id;
      var override = (typeof elementId === 'string' || typeof elementId === 'number')
        ? overridesByElementId[elementId]
        : null;
      if (isPlainObject(override) && isPlainObject(override.content)) {
        var merged = {};
        var base = isPlainObject(element.content) ? element.content : {};
        for (var baseKey in base) if (Object.prototype.hasOwnProperty.call(base, baseKey)) merged[baseKey] = base[baseKey];
        for (var overrideKey in override.content) if (Object.prototype.hasOwnProperty.call(override.content, overrideKey)) merged[overrideKey] = override.content[overrideKey];
        element.content = merged;
      }

      if (Array.isArray(element.children)) {
        localeApplyElementOverrides(element.children, overridesByElementId);
      }
    }
  }

  function hasLocaleStructure(document) {
    if (!isPlainObject(document)) return false;
    if (typeof document.baseLocale === 'string' && document.baseLocale.trim() !== '') return true;
    var overrides = document.localeOverrides;
    return isPlainObject(overrides) && Object.keys(overrides).length > 0;
  }

  function resolveLocale(document, locale, defaultLocale) {
    if (!isPlainObject(document)) return document;

    var overrides = isPlainObject(document.localeOverrides) ? document.localeOverrides : {};
    var baseLocale = typeof document.baseLocale === 'string' ? document.baseLocale : null;
    var overrideKeys = localePickOverrideKeys(Object.keys(overrides), locale, defaultLocale, baseLocale);

    for (var i = 0; i < overrideKeys.length; i++) {
      var override = overrides[overrideKeys[i]];
      if (!isPlainObject(override)) continue;

      var elementOverrides = isPlainObject(override.elements) ? override.elements : null;
      if (elementOverrides && Array.isArray(document.sections)) {
        for (var s = 0; s < document.sections.length; s++) {
          var section = document.sections[s];
          if (isPlainObject(section) && Array.isArray(section.elements)) {
            localeApplyElementOverrides(section.elements, elementOverrides);
          }
        }
      }

      if (isPlainObject(override.seo) && Object.keys(override.seo).length) {
        var seo = {};
        var baseSeo = isPlainObject(document.seo) ? document.seo : {};
        for (var seoKey in baseSeo) if (Object.prototype.hasOwnProperty.call(baseSeo, seoKey)) seo[seoKey] = baseSeo[seoKey];
        for (var overrideSeoKey in override.seo) if (Object.prototype.hasOwnProperty.call(override.seo, overrideSeoKey)) seo[overrideSeoKey] = override.seo[overrideSeoKey];
        document.seo = seo;
      }
    }

    delete document.localeOverrides;
    delete document.baseLocale;

    return document;
  }

  Neo.hasLocaleStructure = hasLocaleStructure;
  Neo.resolveLocale = resolveLocale;

  // ============================================================
  // 18. Ready signal for the URL-fallback path
  //
  // snippets/aico-neo-content.liquid fetches the >64KB document during parse
  // and must not paint before this registry is complete. It used to wait for
  // DOMContentLoaded, which on a heavy page fires long after this deferred
  // script ran (every other deferred script sits in between). Announce
  // readiness right here instead — the DOM is fully parsed by the time a
  // deferred script executes, so painting now is safe.
  // ============================================================

  window.aicoNeoExtReady = true;
  try {
    document.dispatchEvent(new CustomEvent('aico-neo:ext-ready'));
  } catch (err) { /* CustomEvent unsupported: the DOMContentLoaded fallback still fires */ }

})(window, document);
